import _extends from '@babel/runtime/helpers/esm/extends';
import React__default, { createContext, useMemo, useState, useContext, useCallback, useEffect, memo, useRef, Component, createElement } from 'react';
import PropTypes from 'prop-types';
import { createUserSessionStore } from '@zillow/user-session-store-core';
import queryString from 'query-string';
import styled, { css, ThemeProvider } from 'styled-components';
import { Text, Anchor, Flex, token as token$1, spaceMixin, VisuallyHidden, Heading, Paragraph, FormHelp, Input, FormField, Label, Form, FormActions, ButtonGroup, Button, TextButton, Checkbox, LabeledControl, IconCheckmark, IconCloseCircle, Popper, Popover, AdornedInput, Adornment, DataSelect, mediaBreakpointMixin, Tabs, Spacer, InlineFeedback, Image, IconCheckmarkCircleOutline, Spinner, ZillowHomeLogo, DetailedIconKeyHome, DetailedIconPencilDollar, ModalDialog, StyledDialog, StyledDialogBody, ThemeSkunkWorks, ThemeConstellation } from '@zillow/constellation';
import { isLocalStorageSupported } from '@zillow/is-local-storage-supported';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _inheritsLoose from '@babel/runtime/helpers/esm/inheritsLoose';
import { Provider } from 'react-redux';
import { withRouter, Switch, Route, MemoryRouter, BrowserRouter } from 'react-router-dom';
import { getInputProps as getInputProps$1, setInputs, resetInputs, updateAndValidate, ReduxInputsWrapper, validateInputs, createInputsReducer } from 'redux-inputs';
import { setUserLoggedIn, hideRegLoginLightbox, setUserAuthToken, setOnboardingConfig, showOnboardingQuestionnaire, setRegLoginPath, setRegLoginUiConfig, setRegLoginNode, showRegLoginLightbox, setRegLoginInputValues, setRegLoginRedirectUrl } from '@zillow/user-session-store-actions';
import { identifyUser, track, setCustomDimensions, event, page, isInitialized, requestUAScript } from '@zillow/universal-analytics';
import { trigger, listen } from '@zillow/event-utils';
import _assertThisInitialized from '@babel/runtime/helpers/esm/assertThisInitialized';
import _taggedTemplateLiteralLoose from '@babel/runtime/helpers/esm/taggedTemplateLiteralLoose';
import _omit from 'lodash/omit';
import _isNil from 'lodash/isNil';
import _isEqual from 'lodash/isEqual';
import { createPortal } from 'react-dom';
import { bindActionCreators, combineReducers, compose, createStore as createStore$1, applyMiddleware } from 'redux';
import { useYieldCallback, yieldCallback } from '@zillow/yield-callback';
import connectToUserSession from '@zillow/user-session-store-connect';
import { close, init } from '@zillow/google-one-tap';
import { initiateOktaLogin, handleOktaLoginRedirect } from '@zillow/okta-signin';
import _toConsumableArray from '@babel/runtime/helpers/esm/toConsumableArray';
import _typeof from '@babel/runtime/helpers/esm/typeof';
import request from 'superagent';
import { isCanadian } from '@zillow/state-region-id-map';
import { formUtils, FormHelpError } from '@zillow/reg-login-components';
import _mergeWith from 'lodash/mergeWith';
import _createClass from '@babel/runtime/helpers/esm/createClass';
import { trackAuth, AUTH_EVENT_TYPE, getInputSelector as getInputSelector$1, AUTH_METHOD as AUTH_METHOD$1, AUTH_TYPE, INPUT_SELECTOR as INPUT_SELECTOR$1, IDP, UNSPECIFIED, pageView } from '@zillow/auth-utils';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { zillowURL } from '@zillow/url';
import fetch$1 from '@zillow/fetchier';
import { profileIntervalBegin as profileIntervalBegin$1, profileIntervalEnd as profileIntervalEnd$1, profileIntervalFail } from '@zillow/client-profiler';
import { BlurInput } from '@zillow/zrs-blur-input';
import { dataLayerPush, REGISTRATION_EVENT } from '@zillow/data-layer-push';
import _debounce from 'lodash/debounce';
import clamp from 'lodash/clamp';
import _after from 'lodash/after';
import { PasswordRecovery as PasswordRecovery$1 } from '@zillow/account-password';
import { MFA_TEXT_METHOD, ChooseMFAMethod, EnterVerificationCode, AccountLocked, VerificationCodeError, CodeInput } from '@zillow/multi-factor-auth';
import { ZmaConsent } from '@zillow/zma-consent';
import thunk from 'redux-thunk';
import subscribeToUserSessionStore from '@zillow/user-session-store-subscribe';

var CONFIG_KEY = 'regLoginConfig';
var NEW_SESSION_LIFETIME = 1000 * 60 * 30;
var ONE_TAP_ON_LOAD_BREAK_DURATION = 1000 * 60 * 60 * 24 * 7;
var isLocalStorage = isLocalStorageSupported();
/**
 * Get regLogin local storage config
 * @returns {Object} config
 */

function getLocalStorageConfig(key) {
  if (key === void 0) {
    key = CONFIG_KEY;
  }

  if (isLocalStorage) {
    try {
      return JSON.parse(global.localStorage.getItem(key)) || {};
    } catch (e) {// config value is malformed
    }
  }

  return {};
}
/**
 * Set regLogin local storage config
 * @param {Object} config
 */

function setLocalStorageConfig(config, key) {
  if (key === void 0) {
    key = CONFIG_KEY;
  }

  if (isLocalStorage) {
    global.localStorage.setItem(key, JSON.stringify(Object.assign({}, getLocalStorageConfig(), config)));
  }
}
/**
 * If this is the user's first session or session has expired
 * @returns {Bool}
 */

function isNewSession() {
  var newSessionExpiration = getLocalStorageConfig().newSessionExpiration || 0;
  return Date.now() > newSessionExpiration;
}
/**
 * If oneTapOnLoad is taking a break
 */

var isOneTapOnLoadBreak = function isOneTapOnLoadBreak() {
  var breakDuration = getLocalStorageConfig().oneTapOnLoadBreak || 0;
  return Date.now() <= breakDuration;
};
/**
 * Set newSessionExpiration to 30 minutes from time
 */

function setNewSessionExpiration(duration) {
  if (duration === void 0) {
    duration = NEW_SESSION_LIFETIME + Date.now();
  }

  setLocalStorageConfig({
    newSessionExpiration: duration
  });
}
/**
 * Potentially get (and remove) stored redirectUrl
 * @param {Boolean} remove whether to remove the value after reading it
 * @return {String}
 */

function getRedirectUrl(remove, key) {
  if (remove === void 0) {
    remove = false;
  }

  if (key === void 0) {
    key = CONFIG_KEY;
  }

  var _getLocalStorageConfi = getLocalStorageConfig(key),
      redirectUrl = _getLocalStorageConfi.redirectUrl;

  if (remove) {
    setLocalStorageConfig({
      redirectUrl: null
    }, key);
  }

  return redirectUrl || '';
}
/**
 * Set redirectUrl in localStorage
 * @param {String} redirectUrl
 */

function setRedirectUrl(redirectUrl) {
  setLocalStorageConfig({
    redirectUrl: redirectUrl
  });
}
/**
 * Set oneTapOnLoadBreak to 1 week from date
 */

var setOneTapOnLoadBreak = function setOneTapOnLoadBreak() {
  setLocalStorageConfig({
    oneTapOnLoadBreak: Date.now() + ONE_TAP_ON_LOAD_BREAK_DURATION
  });
};

var FORBIDDEN_CHARACTERS_NAME_REGEX = /^[^±!@£$%^&*+¡€#¢§¶•ªº«\\/<>?:;|=,\n]*$/;
var LENGTH_NAME_REGEX = /^.{0,20}$/;

/**
 * Checks that the name is not an empty string
 *
 * @param name {string} - The name to check
 * @returns {{validated: boolean, message: string}} - validated value and message if validated
 */

var validateNameNotEmpty = function validateNameNotEmpty(name) {
  var validated = name.length > 0;
  return {
    validated: validated,
    message: validated ? '' : 'Please enter your name'
  };
};
/**
 * Checks that a name has the correct length
 *
 * @param name {string} - The name to check
 * @returns {{validated: boolean, message: string}} - validated value and message if validated
 */

var validateNameLength = function validateNameLength(name) {
  var validated = LENGTH_NAME_REGEX.test(name.trim());
  return {
    validated: validated,
    message: validated ? '' : 'Sorry name must be between 1 and 20 characters long. Please try again.'
  };
};
/**
 * Checks that a name cannot include forbidden characters
 *
 * @param name {string} - The name to check
 * @returns {{validated: boolean, message: string}} - validated value and message if validated
 */

var validateNameForbiddenCharacters = function validateNameForbiddenCharacters(name) {
  var validated = FORBIDDEN_CHARACTERS_NAME_REGEX.test(name);
  return {
    validated: validated,
    message: validated ? '' : 'Cannot contain forbidden characters'
  };
};
var NAME_VALIDATORS = [validateNameNotEmpty, validateNameLength, validateNameForbiddenCharacters];
/**
 * Checks that a name passes all validations
 *
 * @param name {string} - The name to check
 * @returns {{validated: boolean, message: string}} - validated value and message if validated
 */

var validateName = function validateName(name) {
  var validation = {
    validated: true,
    message: ''
  }; // Find the first validation that breaks

  var invalidValidator = NAME_VALIDATORS.find(function (validator) {
    return !validator(name).validated;
  });
  return typeof invalidValidator !== 'undefined' ? invalidValidator(name) : validation;
};

var ZcsSubHeader = function ZcsSubHeader(_ref) {
  var hasSubHeaderText = _ref.hasSubHeaderText;
  return /*#__PURE__*/React__default.createElement(Text, null, hasSubHeaderText && ' ', "For help signing in,", ' ', /*#__PURE__*/React__default.createElement(Anchor, {
    href: ZCS_FAQ_URL
  }, "read our FAQs"), ".");
};

ZcsSubHeader.propTypes = process.env.NODE_ENV !== "production" ? {
  hasSubHeaderText: PropTypes.bool
} : {};
ZcsSubHeader.defaultProps = {
  hasSubHeaderText: false
};

var GLOBAL_KEY = 'ProxiedReactAppConfig';
/**
 * Returns rendered react app config.
 * @kind function
 * @param  {String} bundleName
 *                    name of app bundle
 * @param  {String} configPath
 *                    location of config path within bundle
 * @param  {String} oldGlobalKey
 *                    name of legacy key to access config (used when subclassing)
 * @return {Object} app config object
 */

var getAppConfig = function getAppConfig(bundleName, configPath, oldGlobalKey) {
  if (window && window[GLOBAL_KEY] && window[GLOBAL_KEY][bundleName] && window[GLOBAL_KEY][bundleName][configPath]) {
    return window[GLOBAL_KEY][bundleName][configPath];
  }

  if (window && window[oldGlobalKey]) {
    return window[oldGlobalKey];
  }

  return {};
};
/**
 * Returns proTypes based on string object. This function reverses the key: value
 * as this is how redux inputs needs it to be. The reversal is done here instead of in the
 * backend to ensure that the options order is maintained.
 *
 * @kind function
 * @param  {String} proTypeString
 *                    proTypes string to be parsed as an object
 * @return {Object} proTypes
 */

var getProTypes = function getProTypes(proTypeString) {
  if (proTypeString) {
    try {
      var proTypesObject = JSON.parse(proTypeString);
      var proTypes = {}; // eslint-disable-next-line no-restricted-syntax

      for (var proValue in proTypesObject) {
        if (Object.prototype.hasOwnProperty.call(proTypesObject, proValue)) {
          var proName = proTypesObject[proValue];
          proTypes[proName] = proValue;
        }
      }

      return proTypes;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Unable to parse proTypes string from appConfig');
    }
  }

  return null;
};
/**
 * Checks if a property is a string value of "true" or a boolean value of `true`. This supports
 * config when the app is proxied via the PRE and can only send strings in the config object.
 * @param {Boolean|String} property
 */

var checkIsTruthyBoolOrString = function checkIsTruthyBoolOrString(property) {
  return property === 'true' || property === true;
};

// Keep ROUTES at top of file to make accessible to rest of constants
var rootPath = '/user/acct';
var apiPath = '/user/account/services';
var adminPath = '/user'; // ex: "forgot" - Links to ForgotPassword view
// ex: "forgotPost" - API url to ForgotPassword

var ROUTES = {
  confirmEmail: '/user/account/UpdatePassword.htm',
  confirmEmailPost: apiPath + "/UpdatePassword.htm",
  emailCheck: '/user/EmailCheck.htm',
  forgot: rootPath + "/forgot-password/",
  changePasswordForgot: '/myzillow/ChangePassword.htm',
  resetPasswordPost: '/myzillow/api/account/passwordreset',
  forgotPost: apiPath + "/ForgotPassword.htm",
  forgotSuccess: rootPath + "/ForgotPassword.htm/success",
  home: '/',
  invalidTokenMsg: rootPath + "/email-not-confirmed/",
  login: rootPath + "/login/",
  loginPost: apiPath + "/Login.htm",
  passwordlessSignin: rootPath + "/passwordless/signin/",
  passwordlessCreatePassword: rootPath + "/passwordless/create-password/",
  passwordlessSendEmailPost: '/ajax/account/SendPasswordlessAccountCreateEmail.htm',
  recovery: rootPath + "/password-recovery/",
  register: rootPath + "/register/",
  registerAdmin: adminPath + "/Register.htm",
  registerPost: apiPath + "/Register.htm",
  verifyPost: apiPath + "/Verify.htm",
  multiFactorAuth: rootPath + "/mfa/",
  zma: rootPath + "/zma/",
  createPassword: rootPath + "/create-password/",
  employeeLogin: rootPath + "/login/employee/",
  employeeLoginAuthCallback: rootPath + "/login/authorization-code/callback/",
  verifyAccount: rootPath + "/verify",
  verifyOnRegister: rootPath + "/verify-on-register/",
  externalAuth: apiPath + "/ExternalAuthentication.htm",
  OTPAuth: rootPath + "/passwordless-auth",
  sendOTP: apiPath + "/SendOtp.htm",
  OTPLogin: apiPath + "/OtpLogin.htm",
  OTPRegister: apiPath + "/OtpRegistration.htm",
  proRegister: rootPath + "/pro-register/"
};

var DEFAULT_REDIRECT_URL = ROUTES.home; // fallback callback

var NOOP = function NOOP() {}; // Profiler event names - for https://zwiki.zillowgroup.net/pages/viewpage.action?spaceKey=zproddev&title=How+to+Measure+User+Perceived+Performance

var PROFILER_APP_RENDER_EVENT = 'reglogin:app_render'; // Default Google client id (production)

var GOOGLE_CLIENT_ID = '238648973530.apps.googleusercontent.com'; // Default Facebook app id (production)

var FACEBOOK_APP_ID = '150195308367328'; // Context types

var INLINE_BUFFER = 10;
var MAX_INLINE_WIDTH = 448; // Button display text

var SEND = 'Send';
var SIGN_IN = 'Sign in';
var SIGN_UP = 'Sign up';
var SUBMIT = 'Submit';
var LATER = 'Do this later';
var CONTINUE = 'Continue'; // App config

var BUNDLE_NAME = 'static-reg-login';
var CONFIG_PATH = 'reg-login';
var GLOBAL_KEY$1 = 'RegProxiedReactAppGlobalConfig';
var APP_CONFIG = getAppConfig(BUNDLE_NAME, CONFIG_PATH, GLOBAL_KEY$1);
var PASSWORD_STRENGTH_ENDPOINT = '/user/account/services/Strength.htm';
var GEOIP_SERVICE_ENDPOINT = '/geo/GeoIPService.htm'; // Login header text

var PASSWORDLESS = 'PASSWORDLESS';
var LOGIN = 'LOGIN';
var PRO_REG = 'PRO_REG';
var REGISTER = 'REGISTER';
var SOCIAL = 'SOCIAL';
var APP_VIEWS = [PASSWORDLESS, LOGIN, PRO_REG, REGISTER, SOCIAL];
var DEFAULT_PRO_TYPE_OPTIONS = {
  'Select your category': '0',
  'Real Estate Agent/Broker': '1',
  'Mortgage Lender': '2',
  'Home Improvement Services': '3',
  'Landlord': '5',
  'Photographer': '6',
  'Home Builder': '7',
  'Home Inspector': '8',
  'Property Manager': '9',
  'Other Real Estate Professional': '11'
};
var ZCS_FAQ_URL = 'https://www.zillowclosings.com/faq';
var CONFIG_NAMES = {
  portal: 'portal',
  portal_zcs: 'portal_zcs',
  portal_tmcx: 'portal_tmcx'
};
var PORTAL_BASE_CONFIG = {
  views: [LOGIN, REGISTER],
  showProRegCheckbox: false,
  disableGoogleOneTap: true
};
var CUSTOM_CONFIGS = {
  portal: PORTAL_BASE_CONFIG,
  portal_zcs: Object.assign({}, PORTAL_BASE_CONFIG, {
    customSubHeader: ZcsSubHeader
  }),
  portal_tmcx: PORTAL_BASE_CONFIG
};
var EMAIL_VALIDATION_REGEX = /^(?=.{1,64}@)[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,20}$/i;
var CANADA_ZIPCODE_REGEX = /^\s*[A-CEGHJ-NPR-TVXY]\d[A-CEGHJ-NPR-TV-Z]\s?\d[A-CEGHJ-NPR-TV-Z]\d\s*$/i;
var SPECIAL_CHARACTER_REGEX = /[()~!@#$%^&*_\-+=`|\\{}[\]:;"'<>,./?]/;
var UPPERCASE_CHARACTER_REGEX = /[A-Z]/;
var LOWERCASE_CHARACTER_REGEX = /[a-z]/;
var BUTTON_TYPES = {
  Send: {
    type: 'submit',
    value: SEND,
    className: 'zsg-button_primary zsg-button_fullsize'
  },
  SignIn: {
    type: 'submit',
    value: SIGN_IN,
    className: 'zsg-button_primary zsg-button_fullsize'
  },
  Submit: {
    type: 'submit',
    value: SUBMIT,
    className: 'zsg-button_primary zsg-button_fullsize'
  },
  SubmitDisabled: {
    type: 'submit',
    value: SUBMIT,
    disabled: true,
    className: 'zsg-button_primary zsg-button_fullsize'
  },
  Later: {
    type: 'submit',
    value: LATER,
    className: 'zsg-button_ghost zsg-button zsg-button_fullsize'
  },
  Continue: {
    type: 'submit',
    value: CONTINUE,
    className: 'zsg-button_primary zsg-button_fullsize'
  }
};
/**
 *  Validates that input is not more than 128 characters
 * @param {*} v
 *            input value
 * @return {Object} input value validation and validation message
 */

var validateMaxLength = function validateMaxLength(v) {
  var validated = /^.{0,128}$/.test(v);
  return {
    validated: validated,
    message: validated ? '' : 'Cannot exceed 128 characters'
  };
};
/**
 *  Validates that input is 8 characters or more
 * @param {*} v
 *            input value
 * @return {Object} input value validation and validation message
 */

var validateLength = function validateLength(v) {
  return {
    validated: /^.{8,}$/.test(v),
    message: 'At least 8 characters'
  };
};
/**
 * Checks if input has at least 1 number and 1 letter, validation message updates based on what's
 * needed
 * @param {*} v
 *            input value
 * @return {Object} input value validation and validation message
 */

var validateAlphaNumeric = function validateAlphaNumeric(v) {
  var validated = /^(?=.*[0-9])(?=.*[a-zA-Z])(.+)$/.test(v);
  var message;

  if (!validated && /^[a-zA-Z]+$/.test(v)) {
    message = 'At least 1 number';
  } else if (!validated && /^[0-9]+$/.test(v)) {
    message = 'At least 1 letter';
  } else {
    message = 'Mix of letters and numbers';
  }

  return {
    validated: validated,
    message: message
  };
};
/**
 * Checks if input has at least 1 special character
 * @param {*} v
 *            input value
 * @return {Object} input value validation and validation message
 */

var validateSpecialCharacters = function validateSpecialCharacters(v) {
  var validated = SPECIAL_CHARACTER_REGEX.test(v);
  var message = !validated ? 'At least 1 special character' : 'Contains a special character';
  return {
    validated: validated,
    message: message
  };
};
/**
 * Checks if input has a mix of uppercase and lowercase letter
 * @param {*} v
 *            input value
 * @return {Object} input value validation and validation message
 */

var validateCapitalization = function validateCapitalization(v) {
  var uppercaseValidated = UPPERCASE_CHARACTER_REGEX.test(v);
  var lowercaseValidated = LOWERCASE_CHARACTER_REGEX.test(v);
  var validated = uppercaseValidated && lowercaseValidated;
  var message;

  if (!lowercaseValidated) {
    message = 'At least 1 lowercase letter';
  }

  if (!uppercaseValidated) {
    if (message) {
      message += ' and 1 uppercase letter';
    } else {
      message = 'At least 1 uppercase letter';
    }
  }

  message = validated ? 'Mix of uppercase and lowercase letters' : message;
  return {
    validated: validated,
    message: message
  };
};
/**
 * Checks if a value is equal to the email address or its alias (alias@domain)
 * @param {String} value
 *                  value to check against
 * @param {String} email
 *                  email address
 * @return {Boolean} value isn't equal to email or alias
 */

var validateValueNotEmailOrAlias = function validateValueNotEmailOrAlias(value, email) {
  var lowerCaseValue = typeof value === 'string' ? value.toLowerCase() : '';
  var lowerCaseEmail = typeof email === 'string' ? email.toLowerCase() : '';
  var alias = /^(.+)@/.exec(lowerCaseEmail);
  return lowerCaseValue !== lowerCaseEmail && (alias === null || lowerCaseValue !== alias[1].toLowerCase());
};
/**
 * Get the input value from a redux-input managed input object. returns value or error as fallback
 * @param {Object} inputObject {value, error}
 * @return {String} input value
 */

var getValueFromInputStateObject = function getValueFromInputStateObject(_ref) {
  var inputValue = _ref.value,
      fallbackInputValue = _ref.error;
  return inputValue || fallbackInputValue;
};
/**
 * Checks if input does not match the email address or email address alias
 * @param {*} v
 *                  input value
 * @param {Object} newEmail
 *                  inputs with newEmail value
 * @return {Object} input value validation and validation message
 */

var validatePasswordNotEmail = function validatePasswordNotEmail(password, _ref2) {
  var newEmail = _ref2.newEmail;
  var email = getValueFromInputStateObject(newEmail);
  var validated = validateValueNotEmailOrAlias(password, email);
  return {
    validated: validated,
    message: validated || !password ? '' : 'Cannot match your email address or alias'
  };
};
var PASSWORD_VALIDATORS = [validatePasswordNotEmail, validateMaxLength, validateLength, validateAlphaNumeric, validateSpecialCharacters, validateCapitalization];
/**
 * Redux inputs validator for the newPassword field. Validates against the array of
 * validators defined, and dispatches the validationMessages updates to the store
 *
 * @param {String} input
 *                  password field's value
 * @param {Object} inputs
 *                  The store's inputs state. Used to cross validate the password with email
 * @param {Object} state
 *                  Store, not used
 * @param {Function} dispatch
 *                  Dispatch
 *
 * @return {Boolean} value passes all validation or not
 */

var PASSWORD_VALIDATOR = function PASSWORD_VALIDATOR(input, inputs, state, dispatch) {
  var finalValidation = true;
  var validationMessages = [];
  PASSWORD_VALIDATORS.forEach(function (validator) {
    var result = validator(input, inputs);
    validationMessages.push(result);
    finalValidation = finalValidation && result.validated;
  });
  dispatch(setNewPasswordValidationMessages(validationMessages));
  return finalValidation;
};
/**
 * Primes the initial state for the validationMessages, which enables us to initially show the
 * visible rules
 * @return {Array} list of validationMessages object
 */

var getNewPasswordValidationMessagesInitialState = function getNewPasswordValidationMessagesInitialState(validators) {
  if (validators === void 0) {
    validators = PASSWORD_VALIDATORS;
  }

  var validationMessages = [];
  validators.forEach(function (validator) {
    var result = validator('', {
      newEmail: {}
    });
    validationMessages.push(result);
  });
  return validationMessages;
};
var LOGIN_INPUT_TYPES = {
  requiredTypes: ['email', 'password']
};
var REG_INPUT_TYPES = {
  requiredTypes: ['newEmail', 'newPassword']
};
var FORGOT_INPUT_TYPES = {
  requiredTypes: ['email']
};
var EMAIL_CONFIRMED_INPUT_TYPES = {
  requiredTypes: ['newPassword']
};
var PASSWORDLESS_SIGNIN_INPUT_TYPES = {
  requiredTypes: ['email']
};
var PASSWORDLESS_CREATE_PASSWORD_INPUT_TYPES = {
  requiredTypes: ['newPassword']
};
var REG_PRO_DEFAULT_INPUTS = ['phoneExt', 'phoneSuffix', 'phonePrefix', 'areaCode', 'zipCode', 'lastName', 'firstName', 'proSelect', 'proCheck', 'newPassword', 'newEmail'];
var REG_PRO_DEFAULT_REDIRECT_URL = '/user/EditProfile.htm?source=registration';
var REG_PRO_INPUT_TYPES = {
  redirectUrl: REG_PRO_DEFAULT_REDIRECT_URL,
  requiredTypes: REG_PRO_DEFAULT_INPUTS
};
var REAL_ESTATE_AGENT_INPUT_TYPES = {
  gaSuffix: 'agent',
  redirectUrl: REG_PRO_DEFAULT_REDIRECT_URL,
  requiredTypes: REG_PRO_DEFAULT_INPUTS
};
var MORTGAGE_LENDER_INPUT_TYPES = {
  gaSuffix: 'lender',
  redirectUrl: '/mortgage/EditLenderProfile.htm',
  requiredTypes: ['newEmail', 'newPassword', 'proSelect']
};
var HOME_IMPROVEMENT_INPUT_TYPES = {
  gaSuffix: 'homeimprovement',
  redirectUrl: REG_PRO_DEFAULT_REDIRECT_URL,
  requiredTypes: REG_PRO_DEFAULT_INPUTS
};
var LANDLORD_INPUT_TYPES = {
  gaSuffix: 'landlord',
  redirectUrl: REG_PRO_DEFAULT_REDIRECT_URL,
  requiredTypes: [].concat(REG_PRO_DEFAULT_INPUTS)
};
var PHOTOGRAPHER_INPUT_TYPES = {
  gaSuffix: 'photographer',
  redirectUrl: REG_PRO_DEFAULT_REDIRECT_URL,
  requiredTypes: REG_PRO_DEFAULT_INPUTS
};
var HOME_BUILDER_INPUT_TYPES = {
  gaSuffix: 'homebuilder',
  redirectUrl: REG_PRO_DEFAULT_REDIRECT_URL,
  requiredTypes: REG_PRO_DEFAULT_INPUTS
};
var HOME_INSPECTOR_INPUT_TYPES = {
  gaSuffix: 'homeinspector',
  redirectUrl: REG_PRO_DEFAULT_REDIRECT_URL,
  requiredTypes: REG_PRO_DEFAULT_INPUTS
};
var PROPERTY_MANAGEMENT_INPUT_TYPES = {
  gaSuffix: 'propertymanager',
  redirectUrl: REG_PRO_DEFAULT_REDIRECT_URL,
  requiredTypes: [].concat(REG_PRO_DEFAULT_INPUTS)
};
var INVESTOR_INPUT_TYPES = {
  gaSuffix: 'investor',
  redirectUrl: REG_PRO_DEFAULT_REDIRECT_URL,
  requiredTypes: REG_PRO_DEFAULT_INPUTS
};
var OTHER_REAL_ESTATE_PRO_INPUT_TYPES = {
  gaSuffix: 'other',
  redirectUrl: REG_PRO_DEFAULT_REDIRECT_URL,
  requiredTypes: [].concat(REG_PRO_DEFAULT_INPUTS)
}; // Used by reducer to determine current set of registration.
// In current implementation array order here should match
// dropdown select order.

var REG_PRO_TYPES = {
  // default option
  0: REG_PRO_INPUT_TYPES,
  1: REAL_ESTATE_AGENT_INPUT_TYPES,
  2: MORTGAGE_LENDER_INPUT_TYPES,
  3: HOME_IMPROVEMENT_INPUT_TYPES,
  4: OTHER_REAL_ESTATE_PRO_INPUT_TYPES,
  5: LANDLORD_INPUT_TYPES,
  6: PHOTOGRAPHER_INPUT_TYPES,
  7: HOME_BUILDER_INPUT_TYPES,
  8: HOME_INSPECTOR_INPUT_TYPES,
  9: PROPERTY_MANAGEMENT_INPUT_TYPES,
  10: INVESTOR_INPUT_TYPES,
  11: OTHER_REAL_ESTATE_PRO_INPUT_TYPES
};
var INPUT_TYPES = {
  areaCode: {
    defaultValue: '',
    validator: function validator(input, inputs, state) {
      var isError = /^\d{3}$/.test(input);

      if (!isError) {
        announceValidationMsg(state.appConfig.inputConfigs.areaCode.props.errorText, 'inputs-areaCode');
      }

      return isError;
    },
    props: {
      'type': 'tel',
      'placeholder': '555',
      'autoComplete': 'tel-area-code',
      'maxLength': 3,
      'errorText': 'Enter a valid 3-digit area code',
      'aria-describedby': 'inputs-areaCode-error'
    }
  },
  authProcess: {
    defaultValue: '',
    props: {
      type: 'hidden'
    }
  },
  authToken: {
    defaultValue: '',
    props: {
      type: 'hidden'
    }
  },
  email: {
    defaultValue: '',
    validator: function validator(input, inputs, state, dispatch) {
      var countryCode = state.login.countryCode;

      var _getInputProps = getInputProps$1(INPUT_TYPES, inputs),
          zipCode = _getInputProps.zipCode; // toggle CASL on passwordlessSignin


      dispatch(toggleEmailConsentDisplay({
        countryCode: countryCode,
        newEmail: input,
        zipCode: zipCode.value
      }));
      var isError = EMAIL_VALIDATION_REGEX.test(input);

      if (!isError) {
        announceValidationMsg(state.appConfig.inputConfigs.email.props.errorText, state.appConfig.inputConfigs.email.props.id);
      }

      return isError;
    },
    props: {
      'type': 'email',
      'placeholder': 'Enter email',
      'autoComplete': 'email',
      'errorText': 'Enter a valid email address',
      'id': 'reg-login-email',
      'aria-describedby': 'reg-login-email-error'
    }
  },
  emailConsent: {
    defaultValue: false,
    props: {
      id: 'emailConsent',
      label: 'By checking the box, I consent to receive emails from Zillow, including newsletters, alerts, updates, invitations, promotions and other news and notifications'
    }
  },
  firstName: {
    defaultValue: '',
    validator: function validator(input) {
      var validation = validateName(input);

      if (!validation.validated) {
        announceValidationMsg(validation.message, 'inputs-firstName');
      }

      return validation.validated ? true : validation.message;
    },
    props: {
      'type': 'text',
      'placeholder': 'First name',
      'autoComplete': 'given-name',
      'aria-describedby': 'inputs-firstName-error'
    }
  },
  lastName: {
    defaultValue: '',
    validator: function validator(input) {
      var validation = validateName(input);

      if (!validation.validated) {
        announceValidationMsg(validation.message, 'inputs-lastName');
      }

      return validation.validated ? true : validation.message;
    },
    props: {
      'type': 'text',
      'placeholder': 'Last name',
      'autoComplete': 'family-name',
      'aria-describedby': 'inputs-lastName-error'
    }
  },
  newEmail: {
    defaultValue: '',
    validator: function validator(input, inputs, state, dispatch) {
      var countryCode = state.login.countryCode;

      var _getInputProps2 = getInputProps$1(INPUT_TYPES, inputs),
          zipCode = _getInputProps2.zipCode;

      dispatch(toggleEmailConsentDisplay({
        countryCode: countryCode,
        newEmail: input,
        zipCode: zipCode.value
      }));

      if (!EMAIL_VALIDATION_REGEX.test(input)) {
        var errormsg = state.appConfig.inputConfigs.newEmail.props.errorText;
        announceValidationMsg(errormsg, 'inputs-newEmail');
        return errormsg;
      } // async email existence check


      return new Promise(function (resolve, reject) {
        validateEmailUniqueness(input, dispatch, resolve, reject);
      });
    },
    props: {
      'type': 'email',
      'placeholder': 'Enter email',
      'autoComplete': 'email',
      'errorText': 'Enter a valid email address',
      'aria-describedby': 'inputs-newEmail-error'
    }
  },
  newPassword: {
    defaultValue: '',
    validator: function validator(input) {
      return /^.{6,20}$/.test(input);
    },
    props: {
      type: 'password',
      id: 'password',
      placeholder: 'Create password',
      autoComplete: 'new-password',
      errorText: 'Password must be between 6 and 20 characters'
    }
  },
  password: {
    defaultValue: '',
    validator: function validator(input) {
      return /^.+$/.test(input);
    },
    props: {
      type: 'password',
      placeholder: 'Enter password',
      autoComplete: 'current-password',
      errorText: 'Please enter a password'
    }
  },
  phoneExt: {
    defaultValue: '',
    validator: function validator(input, inputs, state) {
      var isError = /^\d{0,5}$/.test(input);

      if (!isError) {
        announceValidationMsg(state.appConfig.inputConfigs.phoneExt.props.errorText, state.appConfig.inputConfigs.phoneExt.props.id);
      }

      return isError;
    },
    props: {
      'id': 'extension',
      'type': 'tel',
      'placeholder': '55555',
      'autoComplete': 'tel-extension',
      'maxLength': '5',
      'errorText': 'Extension cannot exceed 5 digits',
      'aria-describedby': 'extension-error'
    }
  },
  phonePrefix: {
    defaultValue: '',
    validator: function validator(input, inputs, state) {
      var isError = /^\d{3}$/.test(input);

      if (!isError) {
        announceValidationMsg(state.appConfig.inputConfigs.phonePrefix.props.errorText, 'inputs-phonePrefix');
      }

      return isError;
    },
    props: {
      'type': 'tel',
      'placeholder': '555',
      'autoComplete': 'tel-local-prefix',
      'maxLength': '3',
      'errorText': 'Enter a valid 3-digit prefix',
      'aria-describedby': 'inputs-phonePrefix-error'
    }
  },
  phoneSuffix: {
    defaultValue: '',
    validator: function validator(input, inputs, state) {
      var isError = /^\d{4}$/.test(input);

      if (!isError) {
        announceValidationMsg(state.appConfig.inputConfigs.phoneSuffix.props.errorText, 'inputs-phoneSuffix');
      }

      return isError;
    },
    props: {
      'type': 'tel',
      'placeholder': '5555',
      'autoComplete': 'tel-local-suffix',
      'maxLength': '4',
      'errorText': 'Enter a valid 4-digit suffix',
      'aria-describedby': 'inputs-phoneSuffix-error'
    }
  },
  proCheck: {
    defaultValue: false,
    props: {
      id: 'proCheck',
      label: 'I am a landlord or industry professional'
    }
  },

  /**
   * This property gets overwritten in our app entrypoint, but we still need this here as it is
   * referenced by our set/reset input fields actions
   */
  proSelect: {
    defaultValue: '0',
    validator: function validator(input, inputs, state) {
      var isError = input !== '0';

      if (!isError) {
        announceValidationMsg(state.appConfig.inputConfigs.proSelect.props.errorText, state.appConfig.inputConfigs.proSelect.props.id);
      }

      return isError;
    },
    props: {
      'id': 'proSelect',
      'options': DEFAULT_PRO_TYPE_OPTIONS,
      'errorText': 'Please select a professional category',
      'aria-describedby': 'proSelect-error'
    }
  },

  /**
   * zip code having format 5 digits for US validates 3 or 6 alphanumeric without space
   * (i.e. Canadian zipcode e.g: 'A1A1A1' / 'A1A 1A1' /'A1A')
   * @return True if valid zipcode in US or postalCode in Canada
   */
  zipCode: {
    defaultValue: '',
    validator: function validator(input, inputs, state, dispatch) {
      var countryCode = state.login.countryCode;

      var _getInputProps3 = getInputProps$1(INPUT_TYPES, inputs),
          newEmail = _getInputProps3.newEmail;

      dispatch(toggleEmailConsentDisplay({
        countryCode: countryCode,
        newEmail: newEmail.value,
        zipCode: input
      }));
      var isError = /^\d{5}$/.test(input) || CANADA_ZIPCODE_REGEX.test(input);

      if (!isError) {
        announceValidationMsg(state.appConfig.inputConfigs.zipCode.props.errorText, state.appConfig.inputConfigs.zipCode.props.id);
      }

      return isError;
    },
    props: {
      'id': 'inputs-zipCode',
      'placeholder': 'Zip/Postal',
      'autoComplete': 'postal-code',
      'maxLength': '7',
      'errorText': 'Enter a valid zip/postal code',
      'aria-describedby': 'inputs-zipCode-error'
    }
  }
};
var PASSWORD_INPUT_CONFIG = {
  defaultValue: '',
  validator: PASSWORD_VALIDATOR,
  props: {
    type: 'password',
    id: 'password',
    placeholder: 'Create password',
    autoComplete: 'new-password',
    errorText: ''
  }
};

var PROFESSIONAL_INPUTS = ['areaCode', 'firstName', 'lastName', 'phoneExt', 'phonePrefix', 'phoneSuffix', 'proSelect', 'zipCode']; // input fields that we are not allowed to be set through inputValues

var BLACKLISTED_INPUTS = ['password', 'newPassword'];
// to use the new ones?

var SERVER_ROUTES = {
  '/user/account/PasswordRecovery': ROUTES.recovery
};
var STATUS_ERROR_CODES = {
  codeSent: {
    code: 210
  },
  redirect: {
    code: 300,
    errorMessage: 'Redirect error'
  },
  validation: {
    code: 400,
    errorMessage: 'Validation error'
  },
  notFound: {
    code: 404,
    errorMessage: 'Page not found'
  },
  conflict: {
    code: 409,
    errorMessage: 'Data conflict'
  },
  tooManyRequests: {
    code: 429,
    errorMessage: 'Too many requests'
  },
  server: {
    code: 500,
    errorMessage: 'Server error'
  },
  accountLocked: {
    code: 480,
    errorMessage: 'Account locked'
  }
};
var FORM_TEXT = {
  createAPasswordText: 'Create a password',
  createAPasswordMessage: 'Create a password to access your account on your phone, tablet, or computer',
  emailConfirmedText: 'Email confirmed',
  forgotPasswordLink: 'Forgot your password?',
  forgotPasswordPasswordless: 'Create a password',
  invalidTokenMsgText: 'Email could not be confirmed',
  invalidTokenMsgMessage: 'Your email could not be confirmed because the link you clicked is expired or invalid. Please click below to try again.',
  knowPasswordText: 'Know your password?',
  loginText: 'Already have an account?',
  loginLink: SIGN_IN,
  registerText: "Don't have an account?",
  registerLink: SIGN_UP,
  passwordRecovery: 'For security, confirm that you own this account by clicking the link in the email we just sent you and then update your password.',
  welcomeBack: 'Welcome back to Zillow!'
};
var MFA_SCREENS = {
  methodChoose: {
    id: 'SCREEN_METHOD_CHOOSE',
    header: 'Choose Two-step verification method'
  },
  codeEnter: {
    id: 'SCREEN_ENTER_CODE',
    header: 'Enter verification code'
  },
  tooManyRequests: {
    id: 'SCREEN_TOO_MANY_REQUESTS',
    header: 'Account has been locked'
  },
  accountLocked: {
    id: 'SCREEN_ACCOUNT_LOCKED',
    header: 'Account locked'
  },
  codeError: {
    id: 'SCREEN_VERIFICATION_CODE_ERROR',
    header: 'Something went wrong'
  }
};

var PHONE_LABEL = 'Cell phone'; // Social error messages
var SOCIAL_ERROR_MESSAGE = 'Unable to login with 3rd-party service.'; // Default auth process event label
// made sure no one else relies on these

var APP_REDIRECT = 'auth:app_redirect';
var APP_LOAD = 'auth:app_load';
var LIGHTBOX_HIDDEN = 'auth:lightbox_hidden';
var AUTHENTICATED = 'auth:authenticated';

var LOGIN_PROFILE_SUBMIT = 'reglogin:login_submit';
var REGISTER_PROFILE_SUBMIT = 'reglogin:register_submit';
var OTP_CODE_SUBMIT = 'reglogin:otp_code_submit';
var OTP_PASSWORD_CREATE = 'reglogin:otp_password_create';
var OTP_PASSWORD_LOGIN = 'reglogin:otp_password_login'; // Local Storage
var AUTH_TOKEN = 'AUTH_TOKEN';
var FORM_VALIDATED = 'FORM_VALIDATED';
var HIDE_EMAIL_FORM = 'HIDE_EMAIL_FORM';
var HIDE_FB_CONNECT_ERROR = 'HIDE_FB_CONNECT_ERROR';
var HIDE_SOCIAL_ERROR = 'HIDE_SOCIAL_ERROR';
var NETWORK_ERROR = 'NETWORK_ERROR';
var REGISTER_CONTEXT = 'REGISTER_CONTEXT';
var RESET_FIELDS = 'RESET_FIELDS';
var RESET_FORM_ERROR = 'RESET_FORM_ERROR';
var SET_FB_CONNECT_ERROR = 'SET_FB_CONNECT_ERROR';
var SHOW_EMAIL_FORM = 'SHOW_EMAIL_FORM';
var SHOW_FB_CONNECT_ERROR = 'SHOW_FB_CONNECT_ERROR';
var SHOW_SOCIAL_ERROR = 'SHOW_SOCIAL_ERROR';
var SET_COUNTRY_CODE = 'SET_COUNTRY_CODE';
var SET_EMAIL_CONSENT_DISPLAY = 'SET_EMAIL_CONSENT_DISPLAY';
var HIDE_EMAIL_CONSENT_DISPLAY = 'HIDE_EMAIL_CONSENT_DISPLAY';
var SET_GEOIP_API_CALLED = 'SET_GEOIP_API_CALLED';
var VALIDATE_ON_KEYUP = 'VALIDATE_ON_KEYUP';
var UPDATE_VALID_EMAIL_TOKEN = 'UPDATE_VALID_EMAIL_TOKEN';
var SET_ON_CLOSE = 'SET_ON_CLOSE';
var SET_NEW_PASSWORD_VALIDATION_MESSAGES = 'SET_NEW_PASSWORD_VALIDATION_MESSAGES';
var OVERRIDE_PRO_SELECTION = 'OVERRIDE_PRO_SELECTION';
var US_COUNTRY_CODE = 'US';
var CAN_COUNTRY_CODE = 'CAN';
var SET_PHONE_NUMBER = 'SET_PHONE_NUMBER';
var SET_EMAIL_SUGGESTION = 'SET_EMAIL_SUGGESTION';
var HAS_SENT_RESET_PW_EMAIL = 'HAS_SENT_RESET_PW_EMAIL';
var AUTH_EVENT_CATEGORY = 'auth';
var DID_USER_EXIT_ACTIVATION_LOGIN_MODAL = 'didUserExitActivationLoginModal_V2';

var _hideEmailForm = function _hideEmailForm() {
  return {
    type: HIDE_EMAIL_FORM
  };
};

var _hideFbConnectError = function _hideFbConnectError() {
  return {
    type: HIDE_FB_CONNECT_ERROR
  };
};

var _networkError = function _networkError(errorMessage) {
  return {
    message: errorMessage,
    type: NETWORK_ERROR
  };
};

var _resetProfessionalForm = function _resetProfessionalForm() {
  return {
    type: RESET_FIELDS
  };
};

var _resetFormError = function _resetFormError() {
  return {
    type: RESET_FORM_ERROR
  };
};

var _switchToKeyupValidation = function _switchToKeyupValidation() {
  return {
    type: VALIDATE_ON_KEYUP
  };
};

var _setCountryCode = function _setCountryCode(countryCode) {
  return {
    countryCode: countryCode,
    type: SET_COUNTRY_CODE
  };
};

var _setEmailConsentDisplay = function _setEmailConsentDisplay() {
  return {
    type: SET_EMAIL_CONSENT_DISPLAY
  };
};

var _hideEmailConsentDisplay = function _hideEmailConsentDisplay() {
  return {
    type: HIDE_EMAIL_CONSENT_DISPLAY
  };
};

var _setGeoIPApiCalled = function _setGeoIPApiCalled() {
  return {
    type: SET_GEOIP_API_CALLED
  };
};

var _validateForm = function _validateForm(validated) {
  return {
    type: FORM_VALIDATED,
    validated: validated
  };
};

var _setNewPasswordValidationMessages = function _setNewPasswordValidationMessages(messages) {
  return {
    type: SET_NEW_PASSWORD_VALIDATION_MESSAGES,
    messages: messages
  };
};

var _setPhoneNumber = function _setPhoneNumber(phoneNumber) {
  return {
    type: SET_PHONE_NUMBER,
    phoneNumber: phoneNumber
  };
};

var _setEmailSuggestion = function _setEmailSuggestion(emailSuggestion) {
  return {
    type: SET_EMAIL_SUGGESTION,
    emailSuggestion: emailSuggestion
  };
};

var _overrideProSelection = function _overrideProSelection() {
  return {
    type: OVERRIDE_PRO_SELECTION
  };
};

var _hasSentResetPWEmail = function _hasSentResetPWEmail() {
  return {
    type: HAS_SENT_RESET_PW_EMAIL
  };
};
/**
 * Communicates error details to client.
 * @kind function
 * @param  {Number} code
 *                    network status code
 * @param  {String} message
 *                    display message associated with error
 * @return {Object} action payload
 */


var handleError = function handleError(code, message) {
  if (message) {
    return _networkError(message);
  }

  var redirect = STATUS_ERROR_CODES.redirect,
      validation = STATUS_ERROR_CODES.validation,
      notFound = STATUS_ERROR_CODES.notFound,
      conflict = STATUS_ERROR_CODES.conflict,
      server = STATUS_ERROR_CODES.server,
      accountLocked = STATUS_ERROR_CODES.accountLocked;

  switch (code) {
    case redirect.code:
      return _networkError(redirect.errorMessage);

    case validation.code:
      return _networkError(validation.errorMessage);

    case notFound.code:
      return _networkError(notFound.errorMessage);

    case conflict.code:
      return _networkError(conflict.errorMessage);

    case server.code:
      return _networkError(server.errorMessage);

    case accountLocked.code:
      return _networkError(accountLocked.errorMessage);

    default:
      return null;
  }
};
/**
 * Redirects based on redirect values from server
 * @param {Array}  router
 *                   array of visited routes
 * @param {String} redirectLink
 *                   link type to redirect to
 */

var handleServerRedirect = function handleServerRedirect(router, redirectLink) {
  var route = SERVER_ROUTES[redirectLink];

  if (route) {
    router.push(route);
  }
};
/**
 * Sets logged in to true and hides lightbox via user session dispatch.
 * NOTE: NOT A REDUX ACTION!
 * @kind function
 * @param {Boolean} persistLightbox - whether to keep lightbox showing after login
 * @param {Object}  payload - (DEPRECATED) custom event payload
 * @param {String}  redirectUrl - URL to redirect to when specified
 */

var handleLoggedIn = function handleLoggedIn(persistLightbox, payload, redirectUrl) {
  if (persistLightbox === void 0) {
    persistLightbox = false;
  }

  if (payload === void 0) {
    payload = {};
  }

  // redirect to designated URL
  if (window && redirectUrl) {
    window.location.href = redirectUrl;
    return;
  } // dispatch user session loggedIn action


  setUserLoggedIn(); // DEPRECATED

  trigger(AUTHENTICATED, payload); // dispatch user session hide lightbox actions

  if (!persistLightbox) {
    hideRegLoginLightbox();
  }
};
/**
 * Redirects successful forgot password attempt.
 * @kind function
 */

var handleForgotSuccess = function handleForgotSuccess(router) {
  return function () {
    router.push(ROUTES.forgotSuccess);
  };
};
/**
 * Redirects when server returns redirectLink
 * @kind function
 * @param {Array}  router
 *                   array of visited routes
 * @param {String} link
 *                   link type to redirect to
 * @param {Object} err
 */

var handleLoginRedirect = function handleLoginRedirect(router, link, err) {
  if (err === void 0) {
    err = {};
  }

  return function (dispatch) {
    var _err = err,
        status = _err.status;

    if (status === 300) {
      dispatch(_hasSentResetPWEmail());
    }

    handleServerRedirect(router, link);
  };
}; // TODO: keep/discard after Social Login AB test is resolved.

/**
 * Conceals email form inputs.
 * @kind function
 */

var hideEmailForm = function hideEmailForm() {
  return function (dispatch) {
    dispatch(_hideEmailForm());
  };
};
/**
 * Conceals Facebook error message.
 * @kind function
 */

var hideFacebookError = function hideFacebookError() {
  return function (dispatch) {
    dispatch(_hideFbConnectError());
  };
};
/**
 * Set input values
 * @kind function
 * @param {Object} inputValues
 *                  input values to be updated { inputName: inputValue }
 * @param  {Function} dispatch
 *                      store dispatch function
 *
 * @return {Function} dispatched callback
 */

var setInputValues = function setInputValues(inputValues) {
  return function (dispatch) {
    var inputs = Object.keys(inputValues).reduce(function (accu, key) {
      if (Object.prototype.hasOwnProperty.call(inputValues, key)) {
        var _Object$assign;

        return Object.assign({}, accu, (_Object$assign = {}, _Object$assign[key] = {
          value: inputValues[key]
        }, _Object$assign));
      }

      return accu;
    }, {});
    dispatch(setInputs(INPUT_TYPES, inputs));
  };
};
/**
 * Return true if any of the following conditions matches
 * 1. User has a Canadian IP Address
 * 2. User enters a Canadian Email
 * 3. User enters a Canadian postal code
 * @param {String} countryCode
 * @param {String} newEmail
 * @param {String} zipCode
 */

var isUserCanadian = function isUserCanadian(_ref) {
  var countryCode = _ref.countryCode,
      newEmail = _ref.newEmail,
      zipCode = _ref.zipCode;
  return countryCode === CAN_COUNTRY_CODE || /\.ca$/.test(newEmail) || CANADA_ZIPCODE_REGEX.test(zipCode);
};

var announceValidationMsg = function announceValidationMsg(errorMessage, inputNodeId) {
  var srMessageNode = document.getElementById('sr-message');

  if (srMessageNode) {
    srMessageNode.setAttribute('aria-live', 'assertive');
    srMessageNode.setAttribute('role', 'alert');
    srMessageNode.innerHTML = "<p>" + errorMessage + "</p>";
    setTimeout(function () {
      srMessageNode.setAttribute('aria-live', 'off');
      srMessageNode.removeAttribute('role');
    }, 250);
  }

  var inputNode = document.getElementById(inputNodeId);

  if (inputNode && inputNode.nextSibling) {
    inputNode.nextSibling.setAttribute('id', inputNodeId + "-error");
  }
};
var toggleEmailConsentDisplay = function toggleEmailConsentDisplay(_ref2) {
  var countryCode = _ref2.countryCode,
      newEmail = _ref2.newEmail,
      zipCode = _ref2.zipCode;
  return function (dispatch, getState) {
    var _getState = getState(),
        showEmailConsent = _getState.login.showEmailConsent;

    if (isUserCanadian({
      countryCode: countryCode,
      newEmail: newEmail,
      zipCode: zipCode
    })) {
      // uncheck emailConsentCheckbox before display if it's invisbile at the moment
      // the toggle of professional form will reset emailConsent if we don't check showEmailConsent first
      if (!showEmailConsent) {
        dispatch(setInputValues({
          emailConsent: false
        }));
      }

      dispatch(_setEmailConsentDisplay());
    } else {
      dispatch(_hideEmailConsentDisplay()); // check emailConsentCheckbox by default if hidden

      dispatch(setInputValues({
        emailConsent: true
      }));
    }
  };
};
/**
 * Calls GeoIPService endpoint and sets showEmailConsent when Canada country code
 * @kind function
 */

var setEmailConsentIfCanada = function setEmailConsentIfCanada() {
  return function (dispatch, getState) {
    return new Promise(function ($return, $error) {
      var countryCode, _getInputProps, newEmail, zipCode;

      countryCode = US_COUNTRY_CODE;
      _getInputProps = getInputProps$1(INPUT_TYPES, getState().inputs), newEmail = _getInputProps.newEmail, zipCode = _getInputProps.zipCode;
      return Promise.resolve(request.get(GEOIP_SERVICE_ENDPOINT).then(function (response) {
        dispatch(_setGeoIPApiCalled());

        if (isCanadian(response.body.countryCode)) {
          countryCode = response.body.countryCode;
        }

        dispatch(_setCountryCode(countryCode));
        dispatch(toggleEmailConsentDisplay({
          countryCode: countryCode,
          newEmail: newEmail.value,
          zipCode: zipCode.value
        }));
      }).catch(function (err) {
        console.error(err);
        dispatch(_setCountryCode(countryCode));
        dispatch(toggleEmailConsentDisplay({
          countryCode: countryCode,
          newEmail: newEmail.value,
          zipCode: zipCode.value
        }));
      })).then(function ($await_1) {
        try {
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      }, $error);
    });
  };
};
/**
 * Returns action payload with specified
 * inputs to be reset.
 * @kind function
 * @param  {Array}    inputKeys
 *                      array of keys in INPUT_TYPES to reset
 * @param  {Array}    preservedKeys
 *                      array of keys in INPUT_TYPES to preserve value (suppress validation error)
 * @return {Function} dispatch handler
 */

var resetFields = function resetFields(inputKeys, preservedKeys) {
  if (inputKeys === void 0) {
    inputKeys = [];
  }

  if (preservedKeys === void 0) {
    preservedKeys = [];
  }

  return function (dispatch, getState) {
    var props = getInputProps$1(INPUT_TYPES, getState().inputs);
    var changes = preservedKeys.reduce(function (accu, key) {
      if (Object.prototype.hasOwnProperty.call(props, key) && props[key] && Object.prototype.hasOwnProperty.call(props[key], 'value')) {
        var _Object$assign2;

        return Object.assign({}, accu, (_Object$assign2 = {}, _Object$assign2[key] = {
          value: props[key].value
        }, _Object$assign2));
      }

      return accu;
    }, {});
    var filtered = [].concat(_toConsumableArray(inputKeys), _toConsumableArray(preservedKeys)).reduce(function (accu, key) {
      if (Object.prototype.hasOwnProperty.call(INPUT_TYPES, key)) {
        var _Object$assign3;

        return Object.assign({}, accu, (_Object$assign3 = {}, _Object$assign3[key] = INPUT_TYPES[key], _Object$assign3));
      }

      return accu;
    }, {});
    dispatch(resetInputs(filtered));
    dispatch(setInputs(INPUT_TYPES, changes));
  };
};
/**
 * Clear error messages below form.
 * @kind function
 * @return {Function} dispatched callback
 */

var resetFormError = function resetFormError() {
  return function (dispatch) {
    dispatch(_resetFormError());
  };
};
/**
 * Returns action payload with specified
 * inputs to be reset on checkbox toggle.
 * @kind function
 * @param  {Array}    additionalKeys
 *                      array of keys in INPUT_TYPES to reset
 * @return {Function} dispatch handler
 */

var resetProfessionalForm = function resetProfessionalForm(additionalKeys, shouldOverrideProSelection) {
  if (additionalKeys === void 0) {
    additionalKeys = [];
  }

  if (shouldOverrideProSelection === void 0) {
    shouldOverrideProSelection = false;
  }

  return function (dispatch, getState) {
    dispatch(_resetProfessionalForm());

    if (shouldOverrideProSelection) {
      dispatch(_overrideProSelection());
    } // Need to dispatch as resetInputs returns an action object


    var ret = dispatch(resetFields([].concat(_toConsumableArray(PROFESSIONAL_INPUTS), _toConsumableArray(additionalKeys)))); // Need to trigger toggleEmailConsentDisplay as form reset will clear zipCode field

    var _getState2 = getState(),
        countryCode = _getState2.login.countryCode;

    var _getInputProps2 = getInputProps$1(INPUT_TYPES, getState().inputs),
        newEmail = _getInputProps2.newEmail,
        zipCode = _getInputProps2.zipCode;

    dispatch(toggleEmailConsentDisplay({
      countryCode: countryCode,
      newEmail: newEmail.value,
      zipCode: zipCode.value
    }));
    return ret;
  };
};
/**
 * Get token from redirectLink and updates state
 *
 * @kind function
 * @param {String} redirectLink
 *                  redirect string from response that contains token
 */

var _getAndUpdateAuthToken = function _getAndUpdateAuthToken(redirectLink) {
  if (typeof redirectLink === 'string') {
    var tokenMatch = redirectLink.match(/token=([\w-]*)&?/);

    if (tokenMatch !== null && tokenMatch[1]) {
      setUserAuthToken(tokenMatch[1]);
    }
  }
};
/**
 * Handler for passwordLessSignin success
 * Response returns a 300 and we route based on these scenarios:
 *  succeeded - email does not exist so uer is logged in through freepass, proceed
 *    to create a password form
 *
 * @kind function
 * @param {Object} data
 *                  response body with redirectLink props
 * @param {Object} router
 *                  router object
 */


var handlePasswordlessSigninSuccess = function handlePasswordlessSigninSuccess(data, router) {
  return function () {
    _getAndUpdateAuthToken(data.redirectLink);

    router.push(ROUTES.passwordlessCreatePassword);
    return null;
  };
};
/**
 * Handler for passwordLessSignin failure
 * Response returns a 300 and we route based on these scenarios:
 *  !succeeded && hasPassword - email exists and has a password, proceed to login
 *  !succeeded && !hasPassword - email exists but has no password. Redirect to
 *    password recovery
 *
 * @kind function
 * @param {Boolean} hasPassword
 *                  if submitted email has an existing pw or not
 * @param {Object} router
 *                  router object
 */

var handlePasswordlessSigninFailure = function handlePasswordlessSigninFailure(hasPassword, router) {
  return function () {
    if (hasPassword) {
      router.push(ROUTES.login);
    } else {
      router.push(ROUTES.recovery);
    }

    return null;
  };
}; // TODO: keep/discard after Social Login AB test is resolved.
/**
 * Set validate form
 * @kind function
 * @param  {Boolean} validated
 *                    if input values pass validation
 * @return {Function} dispatched callback
 */

var validateForm = function validateForm(validated) {
  return function (dispatch) {
    dispatch(_validateForm(validated));
  };
};
/**
 * Set new password validation messages
 * @param {Array} messages
 *                  array of valiation messages {status: 'pass', message: 'foo}
 * @return {Function} dispatched callback
 */

var setNewPasswordValidationMessages = function setNewPasswordValidationMessages(messages) {
  return function (dispatch) {
    dispatch(_setNewPasswordValidationMessages(messages));
  };
};
/**
 * Reset password validation messages to its initial state and set form validation state back to false
 */

var resetPasswordValidation = function resetPasswordValidation(validators) {
  return function (dispatch) {
    dispatch(_setNewPasswordValidationMessages(getNewPasswordValidationMessagesInitialState(validators)));
    dispatch(_validateForm(false));
  };
};
/**
 * Wrapper for form submit success handler
 * @param {Function} successHandler
 *                     success callback
 * @param {Object}   responseBody
 *                     response object passed
 * @param {String}   authToken
 *                     authToken to be set
 */

var handleFormSubmitSuccess = function handleFormSubmitSuccess(successHandler, responseBody, authToken) {
  // update authToken if provided
  if (authToken) {
    setUserAuthToken(authToken);
  }

  successHandler(responseBody);
};
/**
 * Wrapper for form submit error handler
 * @param {Function} errorHandler
 *                     error callback
 * @param {Object}   errorObject
 *                     error object passed
 * @param {Object}   responseBody
 *                     response object passed
 * @returns {Function} dispatcher
 */

var errorOnFormSubmitFailure = function errorOnFormSubmitFailure(errorHandler, errorObject, responseBody) {
  return function (dispatch) {
    var validationErrors = responseBody.validationErrors,
        redirectLink = responseBody.redirectLink,
        hasPassword = responseBody.hasPassword;

    if (redirectLink) {
      errorHandler(errorObject, {
        redirectLink: redirectLink,
        hasPassword: hasPassword
      });
    } else {
      // Unique error message text on multiple lines.
      var errorMessage = formUtils.uniqueObjectValuesToArray(validationErrors).join('\n'); // All errors in same element for now.

      dispatch(handleError(errorObject.status, errorMessage));
      errorHandler(errorObject, {
        errorMessage: errorMessage
      });
    }
  };
};
/**
 * Handler for MFA required response for logging in
 * Response returns a 210. We store the phone number and route to MFA view
 *
 * @param {string} phoneNumber phoneNumber MFA used
 * @param {Object} router router object
 */

var handleMfaRequired = function handleMfaRequired(phoneNumber, router) {
  return function (dispatch) {
    dispatch(_setPhoneNumber(phoneNumber));
    router.push(ROUTES.multiFactorAuth);
    return null;
  };
};
/**
 * Handles the response from the login and register API
 *
 * @param {Object} res The response
 * @param {*} err The error if any
 * @param {Object} body The body of the response
 * @param {Object} options The options passed when submitting
 */

var handleSubmitFormResponse = function handleSubmitFormResponse(_ref3) {
  var res = _ref3.res,
      err = _ref3.err,
      body = _ref3.body,
      options = _ref3.options;
  return function (dispatch) {
    var success = options.success || function (resBody) {
      return resBody;
    };

    var error = options.error || function (e) {
      return console.log(e.status);
    };

    var mfaRequired = options.mfaRequired || function () {};

    var router = options.history || {}; // PX redirect route, directs to captcha

    if (res.status === 200 && res.xhr && res.xhr.responseURL && res.xhr.responseURL.indexOf('captchaPerimeterX') > 0) {
      var rawURI = decodeURIComponent(res.xhr.responseURL);
      var redirectURI = new URL(rawURI);
      redirectURI.searchParams.set('url', '/user/acct');
      window.location.href = redirectURI.href;
    } // Requires MFA
    else if ((res.status === 209 || res.status === 210) && body.succeeded === true) {
        dispatch(handleMfaRequired(body.message, router));
        mfaRequired();
      } else if (res.ok || body && body.succeeded === true) {
        // Register endpoint used by freepass returns 300, which we are treating as a
        // success. So current error checking relies on succeeded flag in body.
        // TODO: update to handle errors from somewhere other than AUTH endpoints.
        handleFormSubmitSuccess(success, body, body && body.authToken);

        if (typeof identifyUser !== 'undefined') {
          identifyUser({
            userId: body.googleAnalyticsId
          });
        } else {
          console.warn('indentifyUser was not availble.');
        }
      } else {
        dispatch(errorOnFormSubmitFailure(error, err, body));
      }
  };
};
/**
 * Wires error handling into form submit.
 * @kind function
 * @param  {Object} options - submit endpoint, data payload, success and error callbacks
 * @return {Function} request handling with dispatched callback
 */

var submitForm = function submitForm(options) {
  if (options === void 0) {
    options = {};
  }

  return function (dispatch) {
    // Option defaults
    var path = options.path || '/';
    var data = options.data || {};
    request.post(path).send(formUtils.serialize(data)).end(function (err, res) {
      dispatch(handleSubmitFormResponse({
        res: res,
        err: err,
        body: res.body,
        options: options
      }));
    });
  };
};
/**
 * Updates list of clone inputs to mirror value of specified input object
 * @kind function
 * @param  {Object}    originalInput
 *                       input object whose value needs to be cloned
 * @param  {...String} clones
 *                       input keys from INPUT_TYPES config to update value
 * @return {Function}  request handling with dispatched callback
 */

var syncInputValues = function syncInputValues(originalInput) {
  for (var _len = arguments.length, clones = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    clones[_key - 1] = arguments[_key];
  }

  return function (dispatch) {
    var value = originalInput.value;
    var changes = clones.reduce(function (accu, key) {
      var _Object$assign4;

      return Object.assign({}, accu, (_Object$assign4 = {}, _Object$assign4[key] = value, _Object$assign4));
    }, {}); // Catching the exception with a NOOP to avoid an unhandled promise rejection
    // when syncing newEmail with an existing email/account

    dispatch(updateAndValidate(INPUT_TYPES, changes)).catch(NOOP);
  };
};
/**
 * Handler for invalid email check (generally duplicate or failed server-side validation)
 * @param {String}   invalidType
 *                     error type returned
 * @param {String}   emailAddress
 *                     invalid email
 * @param {Function} resolveHandler
 *                     resolve callback
 * @param {Function} rejectHandler
 *                     reject callback
 * @return {Function} dispatcher
 */

var handleInvalidEmailCheck = function handleInvalidEmailCheck(invalidType, emailAddress, resolveHandler, rejectHandler) {
  return function (dispatch) {
    switch (invalidType) {
      case 'DuplicateEmail':
        dispatch(_switchToKeyupValidation());
        dispatch(setInputs(INPUT_TYPES, {
          email: {
            value: emailAddress
          }
        }));
        rejectHandler('Email already exists');
        break;

      case 'InvalidEmail':
        dispatch(_switchToKeyupValidation());
        rejectHandler('Enter a valid email address');
        break;

      case 'Error':
        dispatch(_switchToKeyupValidation());
        rejectHandler('Something went wrong, please try again.');
        break;

      default:
        resolveHandler();
    }
  };
};
/**
 * Async database check on email existence.
 * @kind function
 * @param  {String}   emailAddress
 *                      email address to verify is unique
 * @param  {Function} dispatch
 *                      store dispatch function
 * @param  {Function} resolve
 *                      resolve callback provided by asyncValidator
 * @param  {Function} reject
 *                      reject callback provided by asyncValidator
 */

var validateEmailUniqueness = function validateEmailUniqueness(email, dispatch, resolve, reject) {
  request.post(ROUTES.emailCheck).send(formUtils.serialize({
    email: email
  })).end(function (err, res) {
    if (err || !res.ok) {
      var _res$body;

      var invalidTypeKey = (res === null || res === void 0 ? void 0 : (_res$body = res.body) === null || _res$body === void 0 ? void 0 : _res$body.key) || 'Error';
      dispatch(handleInvalidEmailCheck(invalidTypeKey, email, resolve, reject));
    } else {
      if (res && res.status && res.status === 211) {
        var _JSON$parse = JSON.parse(res.text),
            _JSON$parse$key = _JSON$parse.key,
            type = _JSON$parse$key === void 0 ? '' : _JSON$parse$key,
            _JSON$parse$value = _JSON$parse.value,
            suggestedEmail = _JSON$parse$value === void 0 ? '' : _JSON$parse$value;

        if (type === 'EmailSuggestion') {
          dispatch(_switchToKeyupValidation());
          dispatch(_setEmailSuggestion("Did you mean " + suggestedEmail + "?"));
        }
      } else {
        dispatch(_setEmailSuggestion(''));
      }

      resolve();
    }
  });
};

/**
 * Accepts an array of functions [fns] and returns a function
 * that applies the passed fns and returns the result
 * add = (a, b) => a+ b
 * double = (num) => num * 2
 * addThenDouble = pipe(add, double)
 * result = sumThenDouble(1,2) // 6
 * @param {Array} Array of functions. Return values
 * @return {Function}
 */
var pipe = function pipe() {
  for (var _len = arguments.length, fns = new Array(_len), _key = 0; _key < _len; _key++) {
    fns[_key] = arguments[_key];
  }

  return function (x) {
    return fns.reduce(function (y, f) {
      return f(y);
    }, x);
  };
};

function PageLoader() {
  return /*#__PURE__*/React__default.createElement("div", {
    className: "loading-container",
    style: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      minHeight: '354px'
    }
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "zsg-loading-spinner_lg"
  }));
}

/**
 * This is an event that captures data emitted when a customer clicks the final
 * button to complete a authentication login request that can be on multiple
 * forms and multiple zillow screens.
 */
var ACCOUNT_LOGIN = 'ACCOUNT_LOGIN';
/**
 * This is an event that captures data emitted when a customer clicks the final
 * button to complete a authentication registration request that can be on
 * multiple forms and multiple zillow screens.
 */

var ACCOUNT_REGISTER = 'ACCOUNT_REGISTER';
/**
 * These two events are both for event id 6675, but one for login and the other for register.
 */

var ACCOUNT_LOGIN_SUCCESS = 'ACCOUNT_LOGIN_SUCCESS';
/**
 * This is an event that captures data emitted when a customer sees an error
 * message after trying to complete an authentication logic request that can be
 * on multiple forms and multiple zillow screens.
 */

var ACCOUNT_LOGIN_ERROR = 'ACCOUNT_LOGIN_ERROR';
/**
 * This is an event that captures data emitted when a customer sees an error
 * message after trying to complete an authentication registration request that
 * can be on multiple forms and multiple zillow screens.
 */

var ACCOUNT_REGISTER_ERROR = 'ACCOUNT_REGISTER_ERROR';
/**
 * This is an event that captures data emited when a customer views the
 * standalone authentication page.
 */

var STANDALONE_AUTH_CONTENT_VIEW = 'STANDALONE_AUTH_CONTENT_VIEW';
/**
 * This is an event that captures data emited when a customer views an
 * authentication form (e.g. top nav sign in, save home login, etc).
 */

var LIGHTBOX_AUTH_CONTENT_VIEW = 'LIGHTBOX_AUTH_CONTENT_VIEW';
/**
 * This is an event that captures data emitted when a customer clicks the
 * forgot password button to reset the password that can be on multiple
 * forms and multiple zillow screens.
 */

var FORGOT_PASSWORD = 'FORGOT_PASSWORD';
/**
 * This is an event that captures data emitted when a customer clicks the
 * forgot password button for the signin
 */

var FORGET_PASSWORD_SIGNIN = 'FORGET_PASSWORD_SIGNIN';
/**
 * This is an event that captures data emitted when a customer clicks the
 * Submit button for the forgot password event
 */

var FORGET_PASSWORD_SUBMIT = 'FORGET_PASSWORD_SUBMIT';
/**
 * This is an event that captures data emitted when a customer clicks the
 * Submit button for the two steps verification event
 */

var TWO_STEP_SIGNIN = 'TWO_STEP_SIGNIN';
/**
 * This is an event that captures data emitted when a customer clicks the
 * 'Do this later' button for creating a password
 */

var DO_THIS_LATER_SUBMIT = 'DO_THIS_LATER_SUBMIT';
/**
 * This is an event that captures data emitted when a customer clicks the
 * Submit button for creating a password
 */

var PASSWORD_SUBMIT = 'PASSWORD_SUBMIT';
/**
 * This is an event that captures data emitted when a customer clicks the
 * 'Submit Query' button for creating a password
 */

var EMAIL_CONFIRM_SUBMIT = 'EMAIL_CONFIRM_SUBMIT';
/**
 * This is an event that captures data emitted when a
 * confirmation email has been sent to the customer
 */

var EMAIL_CONFIRM = 'EMAIL_CONFIRM';
/**
 * This is an event that captures data emitted when a customer clicks the 'Submit' button
 */

var EMAIL_SUBMIT = 'EMAIL_SUBMIT';
/**
 * This is an event that captures data emitted when the auth modal is closed
 */

var AUTH_CLOSE = 'AUTH_CLOSE';
/**
 * This is an event that captures data emitted when a customer starts an auth flow
 */

var AUTH_START = 'AUTH_START';
/**
 * This is an event that captures data emitted
 * when an email verification succeeds for registration.
 */

var VERIFY_EMAIL_COMPLETE = 'VERIFY_EMAIL_COMPLETE';
/**
 * This is an event that captures data emitted
 * when an email verification fails for registration.
 */

var VERIFY_EMAIL_ERROR = 'VERIFY_EMAIL_ERROR';
/**
 * This is an event that captures data emitted
 * when an email sent to verify the email address for registration.
 */

var VERIFY_EMAIL_SEND = 'VERIFY_EMAIL_SEND';
/**
 * This is an event that captures data emitted
 * when OTP option is selected. It can be either password sign in, passcode sign in, register OTP, or register with password.
 */

var OTP_OPTION_SELECTED = 'OTP_OPTION_SELECTED';
/**
 * This is an event that captures data emitted
 * when email change button is clicked
 */

var EMAIL_CHANGE = 'EMAIL_CHANGE';
/**
 * This is an event that captures data emitted
 * when resend OTP code button is clicked
 */

var RESEND_EMAIL_OTP_CODE = 'RESEND_EMAIL_OTP_CODE';
/**
 * @enum {string}
 */

var TRIGGER_TYPE = {
  Interaction: 'interaction',
  Impression: 'impression',
  View: 'view'
};
var AUTH_DEFAULT = 'authentication_form';
var STANDALONE_AUTH_DEFAULT = 'standalone_authentication';
var AUTH_TOPIC_TAG = {
  AccountLogin: 'account_login',
  AccountRegister: 'account_register'
};
var AUTH_METHOD = {
  EmailOTP: 'email_otp',
  EmailPassword: 'email_pw',
  AppleSSO: 'apple_sso',
  FacebookSSO: 'facebook_sso',
  GoogleSSO: 'google_sso',
  NA: 'n/a'
};
var TRIGGER_LOCATION = {
  LoginForm: 'login|auth_form',
  RegisterForm: 'register|auth_form',
  ProRegisterForm: 'pro_register|auth_form',
  UpdatePasswordForm: 'update_password|auth_form',
  ForgotPasswordForm: 'forgot_password|auth_form',
  ForgotPasswordSuccessForm: 'forgot_password_success|auth_form',
  MFAForm: 'mfa|auth_form',
  FreepassCreatePasswordForm: 'freepass_create_password|auth_form',
  EnterEmailForm: 'enter_email|auth_form',
  CreatePasswordForm: 'create_password|auth_form',
  OTPEnterEmailForm: 'otp_enter_email|auth_form'
};
var TRIGGER_SOURCE = {
  LoginForm: 'login|auth_form',
  RegisterForm: 'register|auth_form',
  UpdatePasswordForm: 'update_password|auth_form',
  ForgotPasswordForm: 'forgot_password|auth_form',
  ForgotPasswordSuccessForm: 'forgot_password_success|auth_form',
  MFAForm: 'mfa|auth_form',
  FreepassCreatePasswordForm: 'freepass_create_password|auth_form',
  EnterEmailForm: 'enter_email|auth_form',
  CreatePasswordForm: 'create_password|auth_form',
  ButtonToSubmitEmail: 'button_to_submit_email',
  OTPEnterEmailForm: 'otp_enter_email|auth_form'
};
var TRIGGER_OBJECT = {
  NoTriggerObject: 'no_trigger_object',
  AuthenticationForm: 'authentication_form'
};
/**
 * @enum {string}
 */

var INPUT_SELECTOR = {
  Mouse: 'mouse',
  Keyboard: 'keyboard',
  Other: 'other'
};
/**
 * Auth lightbox view trigger locations.  Not including locations that based on URL.
 * Defined here: https://dataportal.zillowgroup.net/event-registry/724.
 */

var LIGHTBOX_TRIGGER_LOCATIONS = ['login|auth_form', 'register|auth_form', 'update_password|auth_form', 'forgot_password|auth_form', 'forgot_password_success|auth_form', 'mfa|auth_form', 'freepass_create_password|auth_form', 'enter_email|auth_form', 'otp_enter_email|auth_form', 'otp_option|auth_form', 'otp_enter_code|auth_form', 'otp_enter_password|auth_form', 'otp_create_password|auth_form'];
/**
 * Auth standalone page trigger locations. Not including locations that based on URL.
 * Defined here: https://dataportal.zillowgroup.net/event-registry/701.
 */

var STANDALONE_TRIGGER_LOCATIONS = ['login|auth_form', 'register|auth_form', 'update_password|auth_form', 'forgot_password|auth_form', 'forgot_password_success|auth_form', 'mfa|auth_form', 'freepass_create_password|auth_form', 'enter_email|auth_form', 'otp_enter_email|auth_form', 'otp_option|auth_form', 'otp_enter_code|auth_form', 'otp_enter_password|auth_form', 'otp_create_password|auth_form'];
/**
 * @typedef { ACCOUNT_LOGIN |
 *            ACCOUNT_LOGIN_ERROR |
 *            ACCOUNT_REGISTER |
 *            ACCOUNT_REGISTER_ERROR |
 *            AUTH_CLOSE |
 *            AUTH_START |
 *            DO_THIS_LATER_SUBMIT |
 *            EMAIL_CONFIRM |
 *            EMAIL_CONFIRM_SUBMIT |
 *            EMAIL_SUBMIT |
 *            FORGOT_PASSWORD |
 *            FORGET_PASSWORD_SIGNIN |
 *            FORGET_PASSWORD_SUBMIT |
 *            APPLE_LOGIN |
 *            FACEBOOK_LOGIN |
 *            GOOGLE_LOGIN |
 *            LIGHTBOX_AUTH_CONTENT_VIEW |
 *            PASSWORD_SUBMIT |
 *            PRO_CHECK_BOX |
 *            STANDALONE_AUTH_CONTENT_VIEW |
 *            TWO_STEP_SIGNIN |
 *            VERIFY_EMAIL_COMPLETE |
 *            VERIFY_EMAIL_ERROR |
 *            VERIFY_EMAIL_SEND|
 *            OTP_OPTION_SELECTED |
 *            EMAIL_CHANGE |
 *            RESEND_EMAIL_OTP_CODE
 *  } authEventType
 */

/**
 * A util function that gets the new lane auth method.
 *
 * @param {authEventType} type new lane action type
 * @returns {string} The auth method value for the action type
 */

var getAuthMethod = function getAuthMethod(type) {
  var _EMAIL_CONFIRM_SUBMIT;

  return (_EMAIL_CONFIRM_SUBMIT = {
    EMAIL_CONFIRM_SUBMIT: AUTH_METHOD.NA,
    VERIFY_EMAIL_COMPLETE: AUTH_METHOD.NA,
    VERIFY_EMAIL_ERROR: AUTH_METHOD.NA,
    VERIFY_EMAIL_SEND: AUTH_METHOD.NA,
    AUTH_CLOSE: AUTH_METHOD.NA,
    EMAIL_CONFIRM: AUTH_METHOD.NA
  }[type]) !== null && _EMAIL_CONFIRM_SUBMIT !== void 0 ? _EMAIL_CONFIRM_SUBMIT : AUTH_METHOD.EmailPassword;
};
/**
 * A util function that determines the `topicTags` for auth new lane events.
 *
 * There are mainly 2 ways to set topic tags.
 * 1. `<InlineAuthView>`/`<RegLogin>` consumers use the `topicTag` prop on the component.
 *    The topic tags will be stored as `state.appConfig.topicTags` when the App initializes.
 *    See the init logic in [App component](../containers/App.js).
 * 2. For some use cases, topic tags are set by using `setRegLoginUiConfig` (e.g. save/hide home buttons).
 *    The topic tags will be set as `state.regLogin.uiConfig.topicTag`.
 *
 * @param {string[]} appConfigTopicTags topic tags set when reglogin is initialized
 * @param {string} uiConfigTopicTag topic tag set by using `setRegLoginUiConfig`
 * @returns {string[]} topic tags that will be sent to data portal
 */


var getTopicTags = function getTopicTags(appConfigTopicTags, uiConfigTopicTag) {
  if (appConfigTopicTags.length !== 0) {
    return appConfigTopicTags;
  }

  if (uiConfigTopicTag !== undefined) {
    return [uiConfigTopicTag];
  }

  return [];
};
/**
 * A util function that determines `auth_form.entry_point` for auth new lane events.
 *
 * There are mainly 2 ways to set topic tags.
 * 1. `<InlineAuthView>`/`<RegLogin>` consumers use the `entryPoint` prop on the component.
 *    Entry point will be stored as `state.appConfig.entryPoint` when the App initializes.
 *    See the init logic in [App component](../containers/App.js).
 * 2. For some use cases, topic tags are set by using `setRegLoginUiConfig` (e.g. save/hide home buttons).
 *    Entry point will be set as `state.regLogin.uiConfig.entryPoint`.
 *
 * @param {string} appConfigEntryPoint entry point set when reglogin is initialized
 * @param {string} uiConfigEntryPoint entry point set by using `setRegLoginUiConfig`
 * @returns {string} entry point that will be sent to clickstream
 */

var getEntryPoint = function getEntryPoint(appConfigEntryPoint, uiConfigEntryPoint) {
  if (uiConfigEntryPoint !== undefined) {
    return uiConfigEntryPoint;
  }

  return appConfigEntryPoint;
};
/**
 * A util function that gets the trigger location prop for clickstream events based on URL.
 *
 * For some events, the trigger location is based on URL (e.g. `home`, `home_details`).
 * If a new URL-based location is added to data portal, it should be added here as well.
 *
 * For other events that have its only trigger location values, this is the last effort we make to
 * get the location before using the default value `authentication_form` for authentication forms.
 *
 * TBD `main_connect_landing` and `building_details`
 *
 * @param {string} path URL path
 * @param {string} defaultLocation `authentication_form` or `standalone_authentication`
 * @returns {string} the `trigger_location_nm` value
 */

var getTriggerLocationByURL = function getTriggerLocationByURL(path, defaultLocation) {
  if (defaultLocation === void 0) {
    defaultLocation = AUTH_DEFAULT;
  }

  if (path.startsWith('/user/acct') || path.startsWith('/user/account')) {
    return STANDALONE_AUTH_DEFAULT;
  }

  if (path === '/') {
    return 'home';
  }

  if (path.startsWith('/homes')) {
    return 'search_results';
  }

  if (path.startsWith('/homedetails')) {
    return 'home_details';
  }

  return defaultLocation;
};
/**
 * A general util function that gets the trigger location prop for clickstream events.
 *
 * Specialized versions available:
 * 1. for standalone view event (701), `getStandaloneLocation`.
 * 2. for lightbox impression event (724), `getLightboxLocation`.
 *
 * Logic:
 * - If the trigger location is a predefined location (`authFormTriggerLocations`), return it.
 * - Otherwise, ditch the location, instead return a URL-based location.
 *
 * If a new location value is created and added to data portal, we should update the corresponding
 * location list as well.
 *
 * @param {string[]} authFormTriggerLocations predefined auth trigger locations that are not based on URL
 * @param {string} defaultLocation `authentication_form` or `standalone_authentication`
 * @returns {(triggerLocation: string) => string} a specialized `getTriggerLocation` function
 */

var getTriggerLocation = function getTriggerLocation(authFormTriggerLocations, defaultLocation) {
  if (defaultLocation === void 0) {
    defaultLocation = AUTH_DEFAULT;
  }

  return function (triggerLocation) {
    return authFormTriggerLocations.includes(triggerLocation) ? triggerLocation : getTriggerLocationByURL(window.location.pathname, defaultLocation);
  };
};
/**
 * Get trigger location for clickstream event 701 (auth standalone page view event).
 */

var getStandaloneLocation = getTriggerLocation(STANDALONE_TRIGGER_LOCATIONS, STANDALONE_AUTH_DEFAULT);
/**
 * Get trigger location for clickstream event 724 (auth lightbox impression event).
 */

var getLightboxLocation = getTriggerLocation(LIGHTBOX_TRIGGER_LOCATIONS);
/**
 * A util function that gets the input selector based on how many times was the mouse clicked.
 *
 * @param {number|undefined} clickCount how many times was the mouse clicked
 * @returns {INPUT_SELECTOR} input selector that triggers the click event
 */

var getInputSelector = function getInputSelector(clickCount) {
  if (clickCount === undefined) {
    return INPUT_SELECTOR.Other;
  }

  return clickCount === 0 ? INPUT_SELECTOR.Keyboard : INPUT_SELECTOR.Mouse;
};
/**
 * A util function that gets the trigger source for auth close events.
 *
 * @param {string} closeType the close type for auth close events
 * @returns {string} the trigger source for auth close events
 */

var authCloseTriggerSource = function authCloseTriggerSource(closeType) {
  return {
    'authclose': 'button_to_close_form',
    'authclose/esc': 'esc_close_form',
    'authclose/bg': 'bg_close_form'
  }[closeType];
};
/**
 * A util function that gets the input selector for auth close events.
 *
 * @param {string} closeType the close type for auth close events
 * @returns {INPUT_SELECTOR} the input selector for auth close events
 */

var authCloseInputSelector = function authCloseInputSelector(closeType) {
  return {
    'authclose': INPUT_SELECTOR.Mouse,
    'authclose/esc': INPUT_SELECTOR.Keyboard,
    'authclose/bg': INPUT_SELECTOR.Mouse
  }[closeType];
};
/**
 * @typedef {import("../constants/clickstream").authEventType} authEventType
 */

/**
 * A util function that gets the new lane envelope props for different action types.
 *
 * @param {authEventType} type new lane action type
 * @returns {Omit<envelope, 'event_client_start_dtm'>} envelope props for auth new lane events
 */

var getNewLaneEnvelopeProps = function getNewLaneEnvelopeProps(type) {
  return {
    ACCOUNT_LOGIN: {
      event_type_id: '722',
      event_type_version_id: '6',
      event_template_id: '5',
      event_template_version_id: '1'
    },
    ACCOUNT_REGISTER: {
      event_type_id: '39',
      event_type_version_id: '7',
      event_template_id: '5',
      event_template_version_id: '1'
    },
    ACCOUNT_LOGIN_SUCCESS: {
      event_type_id: '6675',
      event_type_version_id: '3',
      event_template_id: '5',
      event_template_version_id: '1'
    },
    ACCOUNT_LOGIN_ERROR: {
      event_type_id: '41',
      event_type_version_id: '5',
      event_template_id: '5',
      event_template_version_id: '1'
    },
    ACCOUNT_REGISTER_ERROR: {
      event_type_id: '723',
      event_type_version_id: '5',
      event_template_id: '5',
      event_template_version_id: '1'
    },
    STANDALONE_AUTH_CONTENT_VIEW: {
      event_type_id: '701',
      event_type_version_id: '6',
      event_template_id: '5',
      event_template_version_id: '1'
    },
    LIGHTBOX_AUTH_CONTENT_VIEW: {
      event_type_id: '724',
      event_type_version_id: '6',
      event_template_id: '5',
      event_template_version_id: '1'
    },
    FORGOT_PASSWORD: {
      event_type_id: '2943',
      event_type_version_id: '3',
      event_template_id: '5',
      event_template_version_id: '1'
    },
    APPLE_LOGIN: {
      event_type_id: '2622',
      event_type_version_id: '4',
      event_template_id: '5',
      event_template_version_id: '1'
    },
    FACEBOOK_LOGIN: {
      event_type_id: '2633',
      event_type_version_id: '4',
      event_template_id: '5',
      event_template_version_id: '1'
    },
    GOOGLE_LOGIN: {
      event_type_id: '2634',
      event_type_version_id: '4',
      event_template_id: '5',
      event_template_version_id: '1'
    },
    FORGET_PASSWORD_SIGNIN: {
      event_type_id: '2941',
      event_type_version_id: '4',
      event_template_id: '5',
      event_template_version_id: '1'
    },
    FORGET_PASSWORD_SUBMIT: {
      event_type_id: '2942',
      event_type_version_id: '3',
      event_template_id: '5',
      event_template_version_id: '1'
    },
    TWO_STEP_SIGNIN: {
      event_type_id: '2945',
      event_type_version_id: '2',
      event_template_id: '5',
      event_template_version_id: '1'
    },
    PRO_CHECK_BOX: {
      event_type_id: '2946',
      event_type_version_id: '2',
      event_template_id: '5',
      event_template_version_id: '1'
    },
    DO_THIS_LATER_SUBMIT: {
      event_type_id: '2948',
      event_type_version_id: '5',
      event_template_id: '5',
      event_template_version_id: '1'
    },
    PASSWORD_SUBMIT: {
      event_type_id: '2950',
      event_type_version_id: '4',
      event_template_id: '5',
      event_template_version_id: '1'
    },
    EMAIL_CONFIRM_SUBMIT: {
      event_type_id: '2972',
      event_type_version_id: '3',
      event_template_id: '5',
      event_template_version_id: '1'
    },
    EMAIL_SUBMIT: {
      event_type_id: '2947',
      event_type_version_id: '4',
      event_template_id: '5',
      event_template_version_id: '1'
    },
    EMAIL_CONFIRM: {
      event_type_id: '2969',
      event_type_version_id: '2',
      event_template_id: '5',
      event_template_version_id: '1'
    },
    VERIFY_EMAIL_COMPLETE: {
      event_type_id: '2996',
      event_type_version_id: '1',
      event_template_id: '5',
      event_template_version_id: '1'
    },
    VERIFY_EMAIL_ERROR: {
      event_type_id: '2997',
      event_type_version_id: '1',
      event_template_id: '5',
      event_template_version_id: '1'
    },
    VERIFY_EMAIL_SEND: {
      event_type_id: '2995',
      event_type_version_id: '1',
      event_template_id: '5',
      event_template_version_id: '1'
    },
    AUTH_CLOSE: {
      event_type_id: '3065',
      event_type_version_id: '2',
      event_template_id: '5',
      event_template_version_id: '1'
    },
    AUTH_START: {
      event_type_id: '3747',
      event_type_version_id: '2',
      event_template_id: '5',
      event_template_version_id: '1'
    },
    OTP_OPTION_SELECTED: {
      event_type_id: '3855',
      event_type_version_id: '3',
      event_template_id: '5',
      event_template_version_id: '1'
    },
    EMAIL_CHANGE: {
      event_type_id: '3854',
      event_type_version_id: '3',
      event_template_id: '5',
      event_template_version_id: '1'
    },
    RESEND_EMAIL_OTP_CODE: {
      event_type_id: '3861',
      event_type_version_id: '1',
      event_template_id: '5',
      event_template_version_id: '1'
    }
  }[type];
};
var triggerLocationDefaultValues = [STANDALONE_AUTH_DEFAULT, AUTH_DEFAULT];
/**
 * A util function that gets the new lane clickstream_trigger props for interaction/impression
 * events.
 *
 * @param {authEventType} type new lane action type
 * @returns {(trigger: Partial<consumerTrigger>) => Omit<clickstream_trigger, 'input_selector_nm'>}
 *      clickstream_trigger props for auth new lane events
 */

var getNewLaneClickstreamTriggerProps = function getNewLaneClickstreamTriggerProps(type) {
  return function (_ref) {
    var _ref$triggerLocation = _ref.triggerLocation,
        triggerLocation = _ref$triggerLocation === void 0 ? type === STANDALONE_AUTH_CONTENT_VIEW ? STANDALONE_AUTH_DEFAULT : AUTH_DEFAULT : _ref$triggerLocation,
        _ref$triggerSource = _ref.triggerSource,
        triggerSource = _ref$triggerSource === void 0 ? type === STANDALONE_AUTH_CONTENT_VIEW ? STANDALONE_AUTH_DEFAULT : AUTH_DEFAULT : _ref$triggerSource,
        _ref$triggerObject = _ref.triggerObject,
        triggerObject = _ref$triggerObject === void 0 ? AUTH_DEFAULT : _ref$triggerObject;
    return {
      ACCOUNT_LOGIN: {
        trigger_type_nm: TRIGGER_TYPE.Interaction,
        trigger_location_nm: triggerLocation,
        trigger_source_nm: 'button_to_login',
        trigger_object_nm: triggerObject
      },
      ACCOUNT_REGISTER: {
        trigger_type_nm: TRIGGER_TYPE.Interaction,
        trigger_location_nm: triggerLocation,
        // for passwordless auth form, the trigger source is button_to_submit_email
        trigger_source_nm: triggerSource === AUTH_DEFAULT ? 'button_to_register' : triggerSource,
        trigger_object_nm: triggerObject
      },
      ACCOUNT_LOGIN_SUCCESS: {
        trigger_type_nm: TRIGGER_TYPE.Impression,
        trigger_object_nm: TRIGGER_OBJECT.NoTriggerObject
      },
      ACCOUNT_LOGIN_ERROR: {
        trigger_type_nm: TRIGGER_TYPE.Impression,
        trigger_location_nm: triggerLocation,
        trigger_source_nm: triggerSource,
        trigger_object_nm: TRIGGER_OBJECT.NoTriggerObject
      },
      ACCOUNT_REGISTER_ERROR: {
        trigger_type_nm: TRIGGER_TYPE.Impression,
        trigger_location_nm: triggerLocation,
        trigger_source_nm: triggerSource,
        trigger_object_nm: TRIGGER_OBJECT.NoTriggerObject
      },
      STANDALONE_AUTH_CONTENT_VIEW: {
        trigger_type_nm: TRIGGER_TYPE.View,
        trigger_location_nm: getStandaloneLocation(triggerLocation),
        trigger_source_nm: triggerSource,
        trigger_object_nm: TRIGGER_OBJECT.NoTriggerObject
      },
      LIGHTBOX_AUTH_CONTENT_VIEW: {
        trigger_type_nm: TRIGGER_TYPE.Impression,
        trigger_location_nm: triggerLocation,
        trigger_source_nm: triggerSource,
        trigger_object_nm: TRIGGER_OBJECT.NoTriggerObject
      },
      FORGOT_PASSWORD: {
        trigger_type_nm: TRIGGER_TYPE.Interaction,
        trigger_location_nm: triggerLocation,
        trigger_source_nm: triggerSource,
        trigger_object_nm: triggerObject
      },
      FORGET_PASSWORD_SIGNIN: {
        trigger_type_nm: TRIGGER_TYPE.Interaction,
        trigger_location_nm: triggerLocation,
        trigger_source_nm: triggerSource,
        trigger_object_nm: triggerObject
      },
      FORGET_PASSWORD_SUBMIT: {
        trigger_type_nm: TRIGGER_TYPE.Interaction,
        trigger_location_nm: triggerLocation,
        trigger_source_nm: triggerSource,
        trigger_object_nm: triggerObject
      },
      TWO_STEP_SIGNIN: {
        trigger_type_nm: TRIGGER_TYPE.Interaction,
        trigger_location_nm: triggerLocationDefaultValues.includes(triggerLocation) ? getTriggerLocationByURL(window.location.pathname) : triggerLocation,
        trigger_source_nm: 'button_to_login',
        trigger_object_nm: triggerObject
      },
      PRO_CHECK_BOX: {
        trigger_type_nm: TRIGGER_TYPE.Interaction,
        trigger_location_nm: getTriggerLocationByURL(window.location.pathname),
        trigger_source_nm: 'checkbox_pro_register',
        trigger_object_nm: triggerObject
      },
      DO_THIS_LATER_SUBMIT: {
        trigger_type_nm: TRIGGER_TYPE.Interaction,
        trigger_location_nm: triggerLocation,
        trigger_source_nm: triggerSource,
        trigger_object_nm: triggerObject
      },
      PASSWORD_SUBMIT: {
        trigger_type_nm: TRIGGER_TYPE.Interaction,
        trigger_location_nm: triggerLocation,
        trigger_source_nm: triggerSource,
        trigger_object_nm: triggerObject
      },
      EMAIL_CONFIRM_SUBMIT: {
        trigger_type_nm: TRIGGER_TYPE.Interaction,
        trigger_location_nm: triggerLocation === 'authentication_form|update_password' ? triggerLocation : getTriggerLocationByURL(window.location.pathname),
        trigger_source_nm: 'button_to_create_password',
        trigger_object_nm: triggerObject
      },
      EMAIL_SUBMIT: {
        trigger_type_nm: TRIGGER_TYPE.Interaction,
        trigger_location_nm: triggerLocation,
        trigger_source_nm: triggerSource,
        trigger_object_nm: triggerObject
      },
      EMAIL_CONFIRM: {
        trigger_type_nm: TRIGGER_TYPE.Impression,
        trigger_location_nm: getTriggerLocationByURL(window.location.pathname),
        trigger_source_nm: triggerSource,
        trigger_object_nm: TRIGGER_OBJECT.NoTriggerObject
      },
      VERIFY_EMAIL_COMPLETE: {
        trigger_type_nm: TRIGGER_TYPE.Impression,
        trigger_location_nm: 'authentication_form|verify_email_complete',
        trigger_source_nm: 'authentication_form|verify_email_complete',
        trigger_object_nm: TRIGGER_OBJECT.NoTriggerObject
      },
      VERIFY_EMAIL_ERROR: {
        trigger_type_nm: TRIGGER_TYPE.Impression,
        trigger_location_nm: 'authentication_form|verify_email_error',
        trigger_source_nm: 'authentication_form|verify_email_error',
        trigger_object_nm: TRIGGER_OBJECT.NoTriggerObject
      },
      VERIFY_EMAIL_SEND: {
        trigger_type_nm: TRIGGER_TYPE.Impression,
        trigger_location_nm: 'authentication_form|verify_email_sent',
        trigger_source_nm: 'authentication_form|verify_email_sent',
        trigger_object_nm: TRIGGER_OBJECT.NoTriggerObject
      },
      AUTH_CLOSE: {
        trigger_type_nm: TRIGGER_TYPE.Interaction,
        trigger_location_nm: triggerLocationDefaultValues.includes(triggerLocation) ? getTriggerLocationByURL(window.location.pathname) : triggerLocation,
        trigger_source_nm: triggerSource,
        trigger_object_nm: triggerObject
      },
      AUTH_START: {
        trigger_type_nm: TRIGGER_TYPE.Interaction,
        trigger_location_nm: triggerLocationDefaultValues.includes(triggerLocation) ? getTriggerLocationByURL(window.location.pathname) : triggerLocation,
        trigger_source_nm: triggerSource,
        trigger_object_nm: triggerObject
      },
      OTP_OPTION_SELECTED: {
        trigger_type_nm: TRIGGER_TYPE.Interaction,
        trigger_location_nm: triggerLocation,
        trigger_source_nm: triggerSource,
        trigger_object_nm: TRIGGER_OBJECT.NoTriggerObject
      },
      EMAIL_CHANGE: {
        trigger_type_nm: TRIGGER_TYPE.Interaction,
        trigger_location_nm: triggerLocation,
        trigger_source_nm: 'button_to_change_email',
        trigger_object_nm: TRIGGER_OBJECT.AuthenticationForm
      },
      RESEND_EMAIL_OTP_CODE: {
        trigger_type_nm: TRIGGER_TYPE.Interaction,
        trigger_location_nm: triggerLocation,
        trigger_source_nm: 'button_to_resend_code',
        trigger_object_nm: TRIGGER_OBJECT.NoTriggerObject
      }
    }[type];
  };
};
/**
 * A util function that gets the new lane semantic props for different action types.
 *
 * @param {authEventType} type auth new lane action type
 * @returns {semantic} semantic props for auth new lane events
 */

var getNewLaneSemanticProps = function getNewLaneSemanticProps(type) {
  return {
    ACCOUNT_LOGIN: {
      semantic_event_nm: 'account_login_start',
      topic_tag_txt: [AUTH_TOPIC_TAG.AccountLogin]
    },
    ACCOUNT_REGISTER: {
      semantic_event_nm: 'account_register_start',
      topic_tag_txt: [AUTH_TOPIC_TAG.AccountRegister]
    },
    ACCOUNT_LOGIN_SUCCESS: {
      semantic_event_nm: 'account_auth_success'
    },
    ACCOUNT_LOGIN_ERROR: {
      semantic_event_nm: 'account_login_error',
      topic_tag_txt: [AUTH_TOPIC_TAG.AccountLogin]
    },
    ACCOUNT_REGISTER_ERROR: {
      semantic_event_nm: 'account_register_error',
      topic_tag_txt: [AUTH_TOPIC_TAG.AccountRegister]
    },
    STANDALONE_AUTH_CONTENT_VIEW: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: [AUTH_TOPIC_TAG.AccountLogin, AUTH_TOPIC_TAG.AccountRegister]
    },
    LIGHTBOX_AUTH_CONTENT_VIEW: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: [AUTH_TOPIC_TAG.AccountLogin, AUTH_TOPIC_TAG.AccountRegister]
    },
    FORGOT_PASSWORD: {
      semantic_event_nm: 'forgot_password_open',
      topic_tag_txt: [AUTH_TOPIC_TAG.AccountLogin]
    },
    FORGET_PASSWORD_SIGNIN: {
      semantic_event_nm: 'account_login_open',
      topic_tag_txt: [AUTH_TOPIC_TAG.AccountLogin]
    },
    FORGET_PASSWORD_SUBMIT: {
      semantic_event_nm: 'forgot_password_start',
      topic_tag_txt: [AUTH_TOPIC_TAG.AccountLogin]
    },
    TWO_STEP_SIGNIN: {
      semantic_event_nm: '2_step_login_start',
      topic_tag_txt: [AUTH_TOPIC_TAG.AccountLogin]
    },
    PRO_CHECK_BOX: {
      semantic_event_nm: 'account_pro_register',
      topic_tag_txt: [AUTH_TOPIC_TAG.AccountRegister]
    },
    DO_THIS_LATER_SUBMIT: {
      semantic_event_nm: 'account_password_creation_defer',
      topic_tag_txt: [AUTH_TOPIC_TAG.AccountRegister]
    },
    PASSWORD_SUBMIT: {
      semantic_event_nm: 'account_password_create',
      topic_tag_txt: [AUTH_TOPIC_TAG.AccountRegister]
    },
    EMAIL_CONFIRM_SUBMIT: {
      semantic_event_nm: 'email_confirmation_password_submit',
      topic_tag_txt: [AUTH_TOPIC_TAG.AccountLogin, AUTH_TOPIC_TAG.AccountRegister]
    },
    EMAIL_CONFIRM: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: [AUTH_TOPIC_TAG.AccountLogin]
    },
    EMAIL_SUBMIT: {
      semantic_event_nm: 'account_email_submit',
      topic_tag_txt: [AUTH_TOPIC_TAG.AccountLogin, AUTH_TOPIC_TAG.AccountRegister]
    },
    AUTH_CLOSE: {
      semantic_event_nm: 'auth_form_close',
      topic_tag_txt: [AUTH_TOPIC_TAG.AccountLogin, AUTH_TOPIC_TAG.AccountRegister]
    },
    AUTH_START: {
      semantic_event_nm: 'auth_start',
      topic_tag_txt: [AUTH_TOPIC_TAG.AccountLogin, AUTH_TOPIC_TAG.AccountRegister]
    },
    VERIFY_EMAIL_COMPLETE: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: [AUTH_TOPIC_TAG.AccountRegister]
    },
    VERIFY_EMAIL_ERROR: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: [AUTH_TOPIC_TAG.AccountRegister]
    },
    VERIFY_EMAIL_SEND: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: [AUTH_TOPIC_TAG.AccountRegister]
    },
    OTP_OPTION_SELECTED: {
      semantic_event_nm: 'email_otp_prompt',
      topic_tag_txt: [AUTH_TOPIC_TAG.AccountLogin, AUTH_TOPIC_TAG.AccountRegister]
    },
    EMAIL_CHANGE: {
      semantic_event_nm: 'change_email',
      topic_tag_txt: [AUTH_TOPIC_TAG.AccountLogin, AUTH_TOPIC_TAG.AccountRegister]
    },
    RESEND_EMAIL_OTP_CODE: {
      semantic_event_nm: 'resend_email_otp_code',
      topic_tag_txt: [AUTH_TOPIC_TAG.AccountLogin, AUTH_TOPIC_TAG.AccountRegister]
    }
  }[type];
};
/**
 * A general util function that returns helper functions that can generate new lane props
 * based on the event type.
 *
 * @param {authEventType} type new lane action type
 * @returns {({clickstreamTrigger, inputSelector, authForm, semantic}:
 *      {
 *          clickstreamTrigger?: Partial<consumerTrigger>,
 *          inputSelector?: string,
 *          authForm?: Partial<auth_form>,
 *          semantic?: Partial<semantic>,
 *      }) => authClickstreamSchema}
 *      a helper function that generate new lane props object
 */

var getNewLaneProps = function getNewLaneProps(type) {
  return function (_ref2) {
    var _consumerTrigger$inpu;

    var _ref2$clickstreamTrig = _ref2.clickstreamTrigger,
        consumerTrigger = _ref2$clickstreamTrig === void 0 ? {} : _ref2$clickstreamTrig,
        _ref2$inputSelector = _ref2.inputSelector,
        inputSelector = _ref2$inputSelector === void 0 ? INPUT_SELECTOR.Other : _ref2$inputSelector,
        _ref2$authForm = _ref2.authForm,
        authForm = _ref2$authForm === void 0 ? {} : _ref2$authForm,
        _ref2$semantic = _ref2.semantic,
        semantic = _ref2$semantic === void 0 ? {} : _ref2$semantic;
    return {
      envelope: Object.assign({}, getNewLaneEnvelopeProps(type), {
        event_client_start_dtm: new Date().toISOString()
      }),
      clickstream_trigger: Object.assign({}, getNewLaneClickstreamTriggerProps(type)(consumerTrigger), {
        input_selector_nm: (_consumerTrigger$inpu = consumerTrigger.inputSelector) !== null && _consumerTrigger$inpu !== void 0 ? _consumerTrigger$inpu : inputSelector
      }),
      semantic: _mergeWith(getNewLaneSemanticProps(type), semantic, mergeSemanticProps),
      auth_form: Object.assign({
        auth_method_cd: getAuthMethod(type)
      }, authForm)
    };
  };
};
/**
 * A util function that merges the page props and event props for new lane events.
 *
 * auth new lane props:
 * page props (auth page-specific props, e.g. trigger_location, topic_tag_txt)
 * +
 * event props (auth event-specific props, e.g. trigger_source, input_selector_nm)
 *
 * TODO: update the type to Omit<authClickstreamSchema, 'envelope'>.
 * @param {Object} pageProps page props for new lane events (clickstream_trigger, auth_form, and semantic)
 * @param {Object} eventProps event props for new lane events (clickstream_trigger and auth_form)
 * @returns {Object} merged new lane props
 */

var mergeNewLaneProps = function mergeNewLaneProps(pageProps, eventProps) {
  return {
    clickstreamTrigger: Object.assign({}, pageProps.clickstreamTrigger, eventProps.clickstreamTrigger),
    authForm: Object.assign({}, pageProps.authForm, eventProps.authForm),
    semantic: pageProps.semantic
  };
};
/**
 * A customizer for merging `topic_tag_txt`.
 *
 * The specific rule for `topic_tag_txt` is:
 * When the consumer semantic props contains `account_login` or `account_register`,
 * which means we are able to figure out the auth type of the event (login/register),
 * we will use the consumer semantic props.
 * Otherwise, we will merge the default semantic props with the consumer semantic props.
 *
 * @param {string[]} defaultTopicTags default auth topic tags
 * @param {string[]} consumerTopicTags consumer topic tags
 * @returns topic tags for the event
 */

function mergeTopicTags(defaultTopicTags, consumerTopicTags) {
  if (consumerTopicTags.includes(AUTH_TOPIC_TAG.AccountLogin) || consumerTopicTags.includes(AUTH_TOPIC_TAG.AccountRegister)) {
    return consumerTopicTags;
  }

  return defaultTopicTags.concat(consumerTopicTags);
}
/**
 * Used when generating the final semantic props to send to data portal.
 *
 * @param {string[]|string} defaultSemanticProp default semantic properties
 * @param {string[]|string} consumerSemanticProp consumer semantic properties
 * @param {string} key semantic property key
 * @returns {string[]|string} merged semantic property value
 */


function mergeSemanticProps(defaultSemanticProp, consumerSemanticProp, key) {
  if (key === 'topic_tag_txt') {
    return mergeTopicTags(defaultSemanticProp, consumerSemanticProp);
  }

  return defaultSemanticProp;
}

var getAccountLoginNewLaneProps = getNewLaneProps(ACCOUNT_LOGIN);
var getAccountLoginErrorNewLaneProps = getNewLaneProps(ACCOUNT_LOGIN_ERROR);
var getAccountLoginSuccessNewLaneProps = getNewLaneProps(ACCOUNT_LOGIN_SUCCESS);
var getAccountRegisterNewLaneProps = getNewLaneProps(ACCOUNT_REGISTER);
var getAccountRegisterErrorNewLaneProps = getNewLaneProps(ACCOUNT_REGISTER_ERROR);
var getForgotPasswordNewLaneProps = getNewLaneProps(FORGOT_PASSWORD);
var getTwoStepSigninNewLaneProps = getNewLaneProps(TWO_STEP_SIGNIN);
var getForgotPasswordSigninNewLaneProps = getNewLaneProps(FORGET_PASSWORD_SIGNIN);
var getForgotPasswordSubmitNewLaneProps = getNewLaneProps(FORGET_PASSWORD_SUBMIT);
var getDoThisLaterSubmitNewLaneProps = getNewLaneProps(DO_THIS_LATER_SUBMIT);
var getPasswordSubmitNewLaneProps = getNewLaneProps(PASSWORD_SUBMIT);
var getEmailConfirmSubmitNewLaneProps = getNewLaneProps(EMAIL_CONFIRM_SUBMIT);
var getEmailConfirmNewLaneProps = getNewLaneProps(EMAIL_CONFIRM);
var getEmailSubmitNewLaneProps = getNewLaneProps(EMAIL_SUBMIT);
var getAuthCloseNewLaneProps = getNewLaneProps(AUTH_CLOSE);
var getAuthStartNewLaneProps = getNewLaneProps(AUTH_START);
var getVerifyEmailCompleteNewLaneProps = getNewLaneProps(VERIFY_EMAIL_COMPLETE);
var getVerifyEmailErrorNewLaneProps = getNewLaneProps(VERIFY_EMAIL_ERROR);
var getVerifyEmailSendNewLaneProps = getNewLaneProps(VERIFY_EMAIL_SEND);
var getSignInOtpOptionSelectedNewLaneProps = getNewLaneProps(OTP_OPTION_SELECTED);
var getEmailChangeNewLaneProps = getNewLaneProps(EMAIL_CHANGE);
var getResendEmailOTPCodeNewLaneProps = getNewLaneProps(RESEND_EMAIL_OTP_CODE);
/**
 * A util function that gets the new lane props for `view_content` events (impression 724/view 701).
 *
 * @param {boolean} isLightbox
 * @returns a util function that generates props for view events
 */
// eslint-disable-next-line no-confusing-arrow

var getViewContentNewLaneProps = function getViewContentNewLaneProps(isLightbox) {
  return isLightbox ? getNewLaneProps(LIGHTBOX_AUTH_CONTENT_VIEW) : getNewLaneProps(STANDALONE_AUTH_CONTENT_VIEW);
};
var socialNewLaneProps = function socialNewLaneProps(action) {
  if (action.startsWith('signin')) {
    return getAccountLoginNewLaneProps;
  }

  if (action.startsWith('register')) {
    return getAccountRegisterNewLaneProps;
  }

  if (action.startsWith('success')) {
    return getAccountLoginSuccessNewLaneProps;
  } // social button click


  return getAuthStartNewLaneProps;
};
/**
 * A util function for tracking social auth events.
 *
 * @param {{
 *      gaLabel: string,
 *      pageTrackingProps: {
 *          clickstream_trigger?: Partial<consumerTrigger>,
 *          auth_form?: Partial<auth_form>,
 *          semantic?: Partial<semantic>,
 *      }
 * }} trackingProps
 * @returns {(action: string)
 *     => (eventNewLaneProps: {
 *          clickstream_trigger?: Partial<consumerTrigger>,
 *          auth_form?: Partial<auth_form>,
 *          semantic?: Partial<semantic>,
 *        })
 *     => void}
 */

var trackSocial = function trackSocial(_ref3) {
  var gaLabel = _ref3.gaLabel,
      pageTrackingProps = _ref3.pageTrackingProps;
  return function (action) {
    return function (eventNewLaneProps) {
      track({
        action: action,
        category: 'auth',
        label: gaLabel
      }, {
        newLaneEvent: socialNewLaneProps(action)(mergeNewLaneProps(pageTrackingProps, eventNewLaneProps))
      });
    };
  };
};
/**
 * @enum {string}
 */

var ACCOUNT_TYPE = {
  New: 'new',
  FreePass: 'free pass',
  ExistingUser: 'existing user'
};
/**
 * @param {boolean} isNewUser
 * @param {boolean} isFreePass
 * @returns {string} account type name
 */

var getAccountTypeName = function getAccountTypeName(isNewUser, isFreePass) {
  if (isNewUser) {
    return ACCOUNT_TYPE.New;
  }

  return isFreePass ? ACCOUNT_TYPE.FreePass : ACCOUNT_TYPE.ExistingUser;
};

var AppleIcon = function AppleIcon(props) {
  return /*#__PURE__*/React__default.createElement("svg", _extends({
    className: props.className,
    viewBox: "0 0 170 170",
    fill: "currentColor",
    preserveAspectRatio: "xMinYMin meet",
    focusable: "false"
  }, props), /*#__PURE__*/React__default.createElement("title", null, "Apple Logo"), /*#__PURE__*/React__default.createElement("path", {
    d: "M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.197-2.12-9.973-3.17-14.34-3.17-4.58 0-9.492 1.05-14.746 3.17-5.262 2.13-9.501 3.24-12.742 3.35-4.929.21-9.842-1.96-14.746-6.52-3.13-2.73-7.045-7.41-11.735-14.04-5.032-7.08-9.169-15.29-12.41-24.65-3.471-10.11-5.211-19.9-5.211-29.378 0-10.857 2.346-20.221 7.045-28.068 3.693-6.303 8.606-11.275 14.755-14.925s12.793-5.51 19.948-5.629c3.915 0 9.049 1.211 15.429 3.591 6.362 2.388 10.447 3.599 12.238 3.599 1.339 0 5.877-1.416 13.57-4.239 7.275-2.618 13.415-3.702 18.445-3.275 13.63 1.1 23.87 6.473 30.68 16.153-12.19 7.386-18.22 17.731-18.1 31.002.11 10.337 3.86 18.939 11.23 25.769 3.34 3.17 7.07 5.62 11.22 7.36-.9 2.61-1.85 5.11-2.86 7.51zM119.11 7.24c0 8.102-2.96 15.667-8.86 22.669-7.12 8.324-15.732 13.134-25.071 12.375a25.222 25.222 0 0 1-.188-3.07c0-7.778 3.386-16.102 9.399-22.908 3.002-3.446 6.82-6.311 11.45-8.597 4.62-2.252 8.99-3.497 13.1-3.71.12 1.083.17 2.166.17 3.24z"
  }));
};

AppleIcon.propTypes = process.env.NODE_ENV !== "production" ? {
  className: PropTypes.string
} : {};

/**
 * A util function that loads the script needed to enable social auth functionalities.
 *
 * @param {Object} config
 * @param {string} config.scriptURL
 * @param {string} config.scriptID
 * @param {Function} config.onLoadSuccess
 * @param {Function} config.onLoadError
 */
function loadScript(_ref) {
  var scriptURL = _ref.scriptURL,
      scriptID = _ref.scriptID,
      _ref$onLoadSuccess = _ref.onLoadSuccess,
      onLoadSuccess = _ref$onLoadSuccess === void 0 ? function () {} : _ref$onLoadSuccess,
      _ref$onLoadError = _ref.onLoadError,
      onLoadError = _ref$onLoadError === void 0 ? function () {} : _ref$onLoadError;
  var script = document.createElement('script');
  script.src = scriptURL;
  script.id = scriptID;
  script.async = true;
  script.defer = true;
  script.onload = onLoadSuccess;
  script.onerror = onLoadError;
  document.body.appendChild(script);
}

/**
 * @enum {string}
 */
var SOCIAL_PROVIDER = {
  Apple: 'Apple',
  Facebook: 'Facebook',
  Google: 'Google'
};
/**
 * @enum {string}
 */

var SOCIAL_BUTTON_TEXT = {
  ContinueWith: 'Continue with',
  SignInWith: 'Sign in with',
  SignUpWith: 'Sign up with'
};

/**
 * @typedef SocialAuthContext
 * @property {() => void} onBtnClick
 * @property {(response?: Object) => void} onSuccess
 * @property {(error?: Object) => void} onFailure
 * @property {SOCIAL_BUTTON_TEXT} buttonText
 * @property {Object} trackingProps
 * @property {string} selectedTab
 * @property {string} entryPoint
 */

/**
 * @type {import('react').Context<SocialAuthContext>}
 */

var SocialAuthContext = /*#__PURE__*/createContext({
  onBtnClick: NOOP,
  onSuccess: NOOP,
  onFailure: NOOP,
  trackingProps: {},
  selectedTab: 'login-tab',
  buttonText: SOCIAL_BUTTON_TEXT.ContinueWith
});
function SocialSignIns(_ref) {
  var children = _ref.children,
      _ref$buttonText = _ref.buttonText,
      buttonText = _ref$buttonText === void 0 ? SOCIAL_BUTTON_TEXT.ContinueWith : _ref$buttonText,
      _ref$socialAuth = _slicedToArray(_ref.socialAuth, 2),
      _ref$socialAuth$ = _ref$socialAuth[0],
      socialErrorMessage = _ref$socialAuth$ === void 0 ? '' : _ref$socialAuth$,
      _ref$socialAuth$2 = _ref$socialAuth[1],
      onBtnClick = _ref$socialAuth$2.onBtnClick,
      onSuccess = _ref$socialAuth$2.onSuccess,
      onFailure = _ref$socialAuth$2.onFailure,
      selectedTab = _ref.selectedTab,
      trackingProps = _ref.trackingProps,
      entryPoint = _ref.entryPoint;

  var socialAuthContext = useMemo(function () {
    return {
      buttonText: buttonText,
      onBtnClick: onBtnClick,
      onSuccess: onSuccess,
      onFailure: onFailure,
      trackingProps: trackingProps,
      selectedTab: selectedTab,
      entryPoint: entryPoint
    };
  }, [buttonText, onBtnClick, onFailure, onSuccess, trackingProps, selectedTab, entryPoint]);
  return /*#__PURE__*/React__default.createElement(SocialAuthContext.Provider, {
    value: Object.assign({}, socialAuthContext)
  }, /*#__PURE__*/React__default.createElement(Flex, {
    className: "social-signins-container"
  }, children, socialErrorMessage && /*#__PURE__*/React__default.createElement("div", {
    className: "social-error-msg"
  }, socialErrorMessage)));
}
SocialSignIns.propTypes = process.env.NODE_ENV !== "production" ? {
  children: PropTypes.node,
  buttonText: PropTypes.string,
  // eslint-disable-next-line zillow/react/forbid-prop-types
  socialAuth: PropTypes.array,
  selectedTab: PropTypes.string,
  trackingProps: PropTypes.shape({
    gaLabel: PropTypes.string,
    entryPoint: PropTypes.string,
    pageProps: PropTypes.shape({
      clickstream_trigger: PropTypes.shape({
        trigger_location_nm: PropTypes.string
      }),
      auth_form: PropTypes.shape({
        title_nm: PropTypes.string,
        idp_cd: PropTypes.string,
        entry_point_nm: PropTypes.string,
        auth_type_cd: PropTypes.string
      })
    })
  }),

  /**
   * entry point for login metrics
   */
  entryPoint: PropTypes.string
} : {};

var APPLE_AUTH_LIB = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
function AppleSignIn(_ref) {
  var _ref$clientId = _ref.clientId,
      clientId = _ref$clientId === void 0 ? 'com.zillow.zillowweb' : _ref$clientId,
      _ref$redirectURI = _ref.redirectURI,
      redirectURI = _ref$redirectURI === void 0 ? zillowURL('/user/account/services/AppleRedirectHandler.htm') : _ref$redirectURI,
      _ref$externalAuthURL = _ref.externalAuthURL,
      externalAuthURL = _ref$externalAuthURL === void 0 ? ROUTES.externalAuth : _ref$externalAuthURL,
      _ref$scope = _ref.scope,
      scope = _ref$scope === void 0 ? 'name email' : _ref$scope;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isScriptLoadSuccessfully = _useState2[0],
      setIsScriptLoadSuccessfully = _useState2[1];

  var _useContext = useContext(SocialAuthContext),
      onBtnClick = _useContext.onBtnClick,
      onSuccess = _useContext.onSuccess,
      onFailure = _useContext.onFailure,
      buttonText = _useContext.buttonText,
      trackingProps = _useContext.trackingProps,
      selectedTab = _useContext.selectedTab,
      entryPoint = _useContext.entryPoint;

  var FULL_BUTTON_TEXT = buttonText + " " + SOCIAL_PROVIDER.Apple;

  var handleSignInSuccess = function handleSignInSuccess(_ref2) {
    return new Promise(function ($return, $error) {
      var authorization, user, idToken, authURL, response, responseData;
      authorization = _ref2.authorization, user = _ref2.user;
      idToken = authorization.id_token;
      authURL = externalAuthURL + "?at=apple&entryPoint=" + entryPoint + "&idToken=" + idToken + "&user=" + (user ? JSON.stringify(user) : '');

      var $Try_1_Post = function () {
        try {
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      var $Try_1_Catch = function (e) {
        try {
          onFailure(e);
          return $Try_1_Post();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      try {
        return Promise.resolve(fetch$1(authURL, {
          method: 'POST',
          credentials: 'same-origin'
        })).then(function ($await_3) {
          try {
            response = $await_3;

            if (!response.ok) {
              throw response;
            } else {
              trackAuth(AUTH_EVENT_TYPE.AuthSuccess)({
                pageProps: trackingProps.pageProps,
                componentProps: {
                  auth_form: {
                    displayed_button_txt: FULL_BUTTON_TEXT,
                    auth_method_cd: AUTH_METHOD$1.AppleSSO,
                    // There does not seem to be a good way to figure out if login or register with AppleSSO
                    auth_type_cd: selectedTab === 'login-tab' ? AUTH_TYPE.AccountLogin : AUTH_TYPE.AccountRegister
                  }
                },
                gaProps: {
                  action: 'success/apple/',
                  category: 'auth',
                  label: trackingProps.gaLabel
                }
              });
              return Promise.resolve(response.json()).then(function ($await_4) {
                try {
                  responseData = $await_4;
                  onSuccess(responseData);
                  return $Try_1_Post();
                } catch ($boundEx) {
                  return $Try_1_Catch($boundEx);
                }
              }.bind(this), $Try_1_Catch);
            }
          } catch ($boundEx) {
            return $Try_1_Catch($boundEx);
          }
        }.bind(this), $Try_1_Catch);
      } catch (e) {
        $Try_1_Catch(e);
      }
    });
  };

  var handleSignInFailure = useCallback(function (_ref3) {
    var error = _ref3.error;
    onFailure(error);
  }, [onFailure]);
  /**
   * Generates a random string that can be used to validate the response we get back correlates
   * to the request we originally made. Comes from: https://gist.github.com/6174/6062387
   */

  var generateSecretString = function generateSecretString() {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  };
  /**
   * AppleID.auth.signIn() by default redirects in current window. we need to open
   * in a new window so we dont lose state. This function loads sdk, initializes and
   * calls signIn within the new window.
   */


  var launchInNewWindow = function launchInNewWindow() {
    var state = generateSecretString();
    global.AppleID.auth.init({
      clientId: clientId,
      scope: scope,
      redirectURI: redirectURI,
      state: state,
      usePopup: true
    });
    global.AppleID.auth.signIn().then(handleSignInSuccess).catch(handleSignInFailure);
  };

  var signInWithApple = useYieldCallback(function (detail) {
    trackAuth(selectedTab === 'login-tab' ? AUTH_EVENT_TYPE.LoginAttempt : AUTH_EVENT_TYPE.RegisterAttempt)({
      pageProps: trackingProps.pageProps,
      componentProps: {
        clickstream_trigger: {
          trigger_source_nm: 'button_apple_sso',
          input_selector_nm: getInputSelector$1(detail)
        },
        auth_form: {
          displayed_button_txt: FULL_BUTTON_TEXT,
          auth_method_cd: AUTH_METHOD$1.AppleSSO
        }
      },
      gaProps: {
        action: 'socialclick/apple/',
        category: 'auth',
        label: trackingProps.gaLabel
      }
    });
    onBtnClick();
    launchInNewWindow();
  }, [FULL_BUTTON_TEXT, launchInNewWindow, onBtnClick, selectedTab, trackingProps.gaLabel, trackingProps.pageProps]);
  /**
   * Click handler to initiate GA tracking and launches new window for apple auth
   */

  var handleButtonClick = useCallback(function (e) {
    var _e$nativeEvent;

    e.preventDefault();
    signInWithApple((_e$nativeEvent = e.nativeEvent) === null || _e$nativeEvent === void 0 ? void 0 : _e$nativeEvent.detail);
  }, [signInWithApple]);
  useEffect(function () {
    if (global.AppleID) {
      setIsScriptLoadSuccessfully(true);
    } else {
      loadScript({
        scriptURL: APPLE_AUTH_LIB,
        scriptID: 'appleid-signin',
        onLoadSuccess: function onLoadSuccess() {
          setIsScriptLoadSuccessfully(true);
        },
        onLoadError: function onLoadError() {
          setIsScriptLoadSuccessfully(false);
        }
      });
    }
  }, []);
  return isScriptLoadSuccessfully ? /*#__PURE__*/React__default.createElement("button", {
    type: "submit",
    className: "btn-social-connect btn-apple",
    onClick: handleButtonClick
  }, /*#__PURE__*/React__default.createElement(AppleIcon, {
    "aria-hidden": true
  }), /*#__PURE__*/React__default.createElement("span", {
    className: "btn-text"
  }, FULL_BUTTON_TEXT)) : null;
}
AppleSignIn.propTypes = process.env.NODE_ENV !== "production" ? {
  externalAuthURL: PropTypes.string,

  /**
   * Used to identify ourselves as Zillow. Passed to Apple API.
   */
  clientId: PropTypes.string,

  /**
   * Where Apple sends users upon successful authentication
   */
  redirectURI: PropTypes.string,

  /**
   * Determines what data is requested from Apple
   */
  scope: PropTypes.string
} : {};

var BodyWrapper = function BodyWrapper(_ref) {
  var children = _ref.children;
  return /*#__PURE__*/React__default.createElement("div", null, children);
};

BodyWrapper.propTypes = process.env.NODE_ENV !== "production" ? {
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.object])
} : {};

/**
 * Returns GA prefix for social tracking based on current auth process.
 * @kind function
 * @param  {String} authProcess
 *                    string representing auth process context
 * @return {String} corresponding label prefix, defaults to 'signin'
 */
/**
 * Determine GA prefix based on message in the response obj from externalAuthentication endpoint.
 * The auth endpoint returns "logged in" for existing users and "new Zillow account
 * created with external auth" for new users
 *
 * @kind function
 * @param  {String} message
 *                    response message from extneralAuth endpoin
 * @return {String} corresponding label prefix
 */

var parseGAPrefixFromMessage = function parseGAPrefixFromMessage(message) {
  return message === 'logged in' ? 'signin' : 'register';
};
/**
 * Determine GA prefix based on response from externalAuthentication endpoint.
 * endpoint returns "logged in" for existing users and "new Zillow account
 * created with external auth" for new users
 *
 * @kind function
 * @param  {Object} res
 *                    response from extneralAuth endpoin
 * @return {String} corresponding label prefix
 */

var parseGAPrefixFromResponse = function parseGAPrefixFromResponse(res) {
  try {
    var message = res.body.message;
    return parseGAPrefixFromMessage(message);
  } catch (e) {
    return 'signin';
  }
};

var FacebookIcon = function FacebookIcon(props) {
  return /*#__PURE__*/React__default.createElement("svg", _extends({
    viewBox: "0 0 200 200",
    "aria-hidden": "true",
    className: props.className,
    fill: "currentColor",
    preserveAspectRatio: "xMinYMin meet",
    focusable: "false"
  }, props), /*#__PURE__*/React__default.createElement("title", null, "Facebook"), /*#__PURE__*/React__default.createElement("path", {
    d: "M200 100a100 100 0 1 0-115.6 98.8v-69.9H59V100h25.4V78c0-25 14.9-39 37.7-39 11 0 22.4 2 22.4 2v24.6H132c-12.4 0-16.3 7.7-16.3 15.6V100h27.8l-4.5 29h-23.3v69.8A100 100 0 0 0 200 100",
    stroke: "none"
  }));
};

FacebookIcon.propTypes = process.env.NODE_ENV !== "production" ? {
  className: PropTypes.string
} : {};

function FacebookSignIn(_ref) {
  var appId = _ref.appId,
      _ref$externalAuthURL = _ref.externalAuthURL,
      externalAuthURL = _ref$externalAuthURL === void 0 ? ROUTES.externalAuth : _ref$externalAuthURL,
      _ref$scope = _ref.scope,
      scope = _ref$scope === void 0 ? ['email'] : _ref$scope,
      _ref$doAutoConnect = _ref.doAutoConnect,
      doAutoConnect = _ref$doAutoConnect === void 0 ? false : _ref$doAutoConnect,
      _ref$authType = _ref.authType,
      authType = _ref$authType === void 0 ? 'FACEBOOK_DEFAULT' : _ref$authType,
      fields = _ref.fields,
      xfbml = _ref.xfbml,
      cookie = _ref.cookie,
      version = _ref.version;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isScriptLoadSuccessfully = _useState2[0],
      setIsScriptLoadSuccessfully = _useState2[1];

  var _useContext = useContext(SocialAuthContext),
      onBtnClick = _useContext.onBtnClick,
      onSuccess = _useContext.onSuccess,
      onFailure = _useContext.onFailure,
      buttonText = _useContext.buttonText,
      trackingProps = _useContext.trackingProps,
      selectedTab = _useContext.selectedTab,
      entryPoint = _useContext.entryPoint;

  var FULL_BUTTON_TEXT = buttonText + " " + SOCIAL_PROVIDER.Facebook;
  /**
   * Connect FB account with Zillow account
   * @param {Object} params
   *   ex: {
   *       externalID: userID,
   *       authToken: accessToken,
   *       expiresIn: expiresIn,
   *       type: 'some external auth type'
   *   }
   */

  var connectAccount = useCallback(function (_ref2) {
    var id = _ref2.id,
        accessToken = _ref2.accessToken,
        expiresIn = _ref2.expiresIn;
    request.post(externalAuthURL).field('accessToken', accessToken).field('expiresIn', expiresIn).field('authID', id).field('ap', authType).field('at', 'facebook').field('entrypoint', entryPoint).end(function (err, res) {
      var prefix = parseGAPrefixFromResponse(res);

      if (err) {
        onFailure(err);
      } else {
        trackAuth(AUTH_EVENT_TYPE.AuthSuccess)({
          pageProps: trackingProps.pageProps,
          componentProps: {
            auth_form: {
              displayed_button_txt: FULL_BUTTON_TEXT,
              auth_method_cd: AUTH_METHOD$1.FacebookSSO,
              auth_type_cd: prefix.startsWith('signin') ? AUTH_TYPE.AccountLogin : AUTH_TYPE.AccountRegister
            }
          },
          gaProps: {
            action: 'success/facebook/',
            category: 'auth',
            label: trackingProps.gaLabel
          }
        });
        onSuccess(res);

        if (res && res.body) {
          identifyUser({
            userId: res.body.googleAnalyticsId
          });
        }
      }
    });
  }, [FULL_BUTTON_TEXT, authType, entryPoint, externalAuthURL, onFailure, onSuccess, trackingProps.gaLabel, trackingProps.pageProps]);
  /**
   * Hit the FB API and connect with Zillow
   * @param {Object} authResponse
   */

  var responseApi = function responseApi(authResponse) {
    /* eslint-disable no-param-reassign */
    window.FB.api('/me', {
      fields: fields
    }, function (me) {
      me.accessToken = authResponse.accessToken;
      me.expiresIn = authResponse.expiresIn;
      connectAccount(me);
    });
  };
  /**
   * Login callback
   * @param {Object} response object detailing Facebook login state
   */


  var onLoginAttempt = function onLoginAttempt(_ref3) {
    var authResponse = _ref3.authResponse,
        status = _ref3.status;

    if (typeof authResponse !== 'undefined') {
      responseApi(authResponse);
    } else {
      onFailure({
        status: status
      });
    }
  };

  var signInWithFacebook = useYieldCallback(function (detail) {
    trackAuth(selectedTab === 'login-tab' ? AUTH_EVENT_TYPE.LoginAttempt : AUTH_EVENT_TYPE.RegisterAttempt)({
      pageProps: trackingProps.pageProps,
      componentProps: {
        clickstream_trigger: {
          trigger_source_nm: 'button_facebook_sso',
          input_selector_nm: getInputSelector$1(detail)
        },
        auth_form: {
          displayed_button_txt: FULL_BUTTON_TEXT,
          auth_method_cd: AUTH_METHOD$1.FacebookSSO
        }
      },
      gaProps: {
        action: 'socialclick/facebook/',
        category: 'auth',
        label: trackingProps.gaLabel
      }
    });
    onBtnClick();
    window.FB.login(onLoginAttempt, {
      scope: scope.toString(),
      auth_type: 'rerequest'
    });
  }, [FULL_BUTTON_TEXT, onBtnClick, onLoginAttempt, selectedTab, trackingProps.gaLabel, trackingProps.pageProps]);
  /**
   * Click handler
   * @param {Object} response
   */

  var handleButtonClick = useCallback(function (e) {
    var _e$nativeEvent;

    e.preventDefault();
    signInWithFacebook((_e$nativeEvent = e.nativeEvent) === null || _e$nativeEvent === void 0 ? void 0 : _e$nativeEvent.detail);
  }, [signInWithFacebook]);
  /**
   * FB status change callback
   * @param {Object} response object detailing Facebook connection state
   */

  var onStatusChange = useCallback(function (response) {
    switch (response.status) {
      case 'connected':
        if (doAutoConnect) {
          // Logged into your app and Facebook.
          connectAccount(response.authResponse);
        }

        break;
    }
  }, [connectAccount, doAutoConnect]);
  /**
   * Do the facebooky init stuff
   */

  var fbInit = useCallback(function () {
    window.fbAsyncInit = function () {
      if (xfbml) {
        window.FB.Event.subscribe('xfbml.render');
      } // All options need to be defined or FB.init will fail silently


      window.FB.init({
        appId: appId,
        xfbml: xfbml,
        cookie: cookie,
        version: version
      });
      window.FB.getLoginStatus(onStatusChange);
    };

    if (window.FB) {
      setIsScriptLoadSuccessfully(true);
    } else {
      loadScript({
        scriptURL: 'https://connect.facebook.net/en_US/all.js',
        scriptID: 'facebook-jssdk',
        onLoadSuccess: function onLoadSuccess() {
          setIsScriptLoadSuccessfully(true);
        },
        onLoadError: function onLoadError() {
          setIsScriptLoadSuccessfully(false);
        }
      });
    }
  }, [appId, cookie, onStatusChange, version, xfbml]);
  useEffect(function () {
    fbInit();
  }, [fbInit]);
  return isScriptLoadSuccessfully ? /*#__PURE__*/React__default.createElement("button", {
    type: "submit",
    className: "btn-social-connect btn-facebook",
    onClick: handleButtonClick
  }, /*#__PURE__*/React__default.createElement(FacebookIcon, {
    "aria-hidden": true
  }), /*#__PURE__*/React__default.createElement("span", {
    className: "btn-text"
  }, FULL_BUTTON_TEXT)) : null;
}
FacebookSignIn.propTypes = process.env.NODE_ENV !== "production" ? {
  appId: PropTypes.string.isRequired,
  authType: PropTypes.string,
  cookie: PropTypes.bool.isRequired,
  doAutoConnect: PropTypes.bool,
  externalAuthURL: PropTypes.string,
  fields: PropTypes.string,
  scope: PropTypes.arrayOf(PropTypes.string),
  version: PropTypes.string.isRequired,
  xfbml: PropTypes.bool.isRequired
} : {};

function _templateObject() {
  var data = _taggedTemplateLiteralLoose(["\n    border-top: 1px solid ", ";\n    text-align: center;\n    padding: ", ";\n    padding-bottom: 0;\n    display: flex;\n    flex-direction: column;\n    align-items: center;\n"]);

  _templateObject = function _templateObject() {
    return data;
  };

  return data;
}
var StyledFooter = styled.div(_templateObject(), token$1('colors.borderLight'), spaceMixin('sm'));

var Footer = function Footer(_ref) {
  var text = _ref.text,
      children = _ref.children;
  return /*#__PURE__*/React__default.createElement(StyledFooter, null, text && /*#__PURE__*/React__default.createElement(Text, null, text), children);
};

Footer.propTypes = process.env.NODE_ENV !== "production" ? {
  children: PropTypes.node,
  text: PropTypes.string
} : {};

var GSI_SCRIPT_URL = 'https://accounts.google.com/gsi/client';

function GoogleSignIn(_ref) {
  var clientId = _ref.clientId,
      _ref$externalAuthURL = _ref.externalAuthURL,
      externalAuthURL = _ref$externalAuthURL === void 0 ? ROUTES.externalAuth : _ref$externalAuthURL;
  var googleSigninButton = useRef(null);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isScriptLoadSuccessfully = _useState2[0],
      setIsScriptLoadSuccessfully = _useState2[1];

  var _useContext = useContext(SocialAuthContext),
      onBtnClick = _useContext.onBtnClick,
      onSuccess = _useContext.onSuccess,
      onFailure = _useContext.onFailure,
      buttonText = _useContext.buttonText,
      trackingProps = _useContext.trackingProps,
      selectedTab = _useContext.selectedTab,
      entryPoint = _useContext.entryPoint;

  var FULL_BUTTON_TEXT = buttonText + " " + SOCIAL_PROVIDER.Google;
  /**
   * Send google signin ID token to server for auth
   *
   * @param {Object} response
   */

  var syncGoogleAuth = useCallback(function (_ref2) {
    var credential = _ref2.credential;
    request.post(externalAuthURL).field('idToken', credential).field('entrypoint', entryPoint).end(function (err, res) {
      var prefix = parseGAPrefixFromResponse(res);

      if (err || !res.ok) {
        onFailure(err);
      } else {
        trackAuth(AUTH_EVENT_TYPE.AuthSuccess)({
          pageProps: trackingProps.pageProps,
          componentProps: {
            auth_form: {
              displayed_button_txt: FULL_BUTTON_TEXT,
              auth_method_cd: AUTH_METHOD$1.GoogleSSO,
              auth_type_cd: prefix.startsWith('signin') ? AUTH_TYPE.AccountLogin : AUTH_TYPE.AccountRegister
            }
          },
          gaProps: {
            action: 'success/google/',
            category: 'auth',
            label: trackingProps.gaLabel
          }
        });
        onSuccess(res);

        if (res && res.body) {
          identifyUser({
            userId: res.body.googleAnalyticsId
          });
        }
      }
    });
  }, [FULL_BUTTON_TEXT, entryPoint, externalAuthURL, onFailure, onSuccess, trackingProps.gaLabel, trackingProps.pageProps]);
  var handleButtonClick = useYieldCallback(function () {
    trackAuth(selectedTab === 'login-tab' ? AUTH_EVENT_TYPE.LoginAttempt : AUTH_EVENT_TYPE.RegisterAttempt)({
      pageProps: trackingProps.pageProps,
      componentProps: {
        clickstream_trigger: {
          trigger_source_nm: 'button_google_sso'
        },
        auth_form: {
          displayed_button_txt: FULL_BUTTON_TEXT,
          auth_method_cd: AUTH_METHOD$1.GoogleSSO
        }
      },
      gaProps: {
        action: 'socialclick/google/',
        category: 'auth',
        label: trackingProps.gaLabel
      }
    });
    onBtnClick();
  }, [FULL_BUTTON_TEXT, onBtnClick, selectedTab, trackingProps.gaLabel, trackingProps.pageProps]);
  var renderButton = useCallback(function () {
    var _SOCIAL_BUTTON_TEXT$C, _document$querySelect;

    global.google.accounts.id.initialize({
      client_id: clientId,
      callback: syncGoogleAuth
    });
    global.google.accounts.id.renderButton(googleSigninButton.current, {
      text: (_SOCIAL_BUTTON_TEXT$C = {}, _SOCIAL_BUTTON_TEXT$C[SOCIAL_BUTTON_TEXT.ContinueWith] = 'continue_with', _SOCIAL_BUTTON_TEXT$C[SOCIAL_BUTTON_TEXT.SignInWith] = 'signin_with', _SOCIAL_BUTTON_TEXT$C[SOCIAL_BUTTON_TEXT.SignUpWith] = 'signup_with', _SOCIAL_BUTTON_TEXT$C)[buttonText],
      width: ((_document$querySelect = document.querySelector('.social-signins-container')) === null || _document$querySelect === void 0 ? void 0 : _document$querySelect.clientWidth) || 368,
      click_listener: handleButtonClick
    });
  }, [buttonText, clientId, handleButtonClick, syncGoogleAuth]);
  useEffect(function () {
    var _global$google;

    if (((_global$google = global.google) === null || _global$google === void 0 ? void 0 : _global$google.accounts) && document.querySelector("script[src=\"" + GSI_SCRIPT_URL + "\"]")) {
      renderButton();
      setIsScriptLoadSuccessfully(true);
    } else {
      loadScript({
        scriptURL: GSI_SCRIPT_URL,
        scriptID: 'google-gsi',
        onLoadSuccess: function onLoadSuccess() {
          renderButton();
          setIsScriptLoadSuccessfully(true);
        },
        onLoadError: function onLoadError() {
          setIsScriptLoadSuccessfully(false);
        }
      });
    }
  }, [onFailure, renderButton]);
  return isScriptLoadSuccessfully ? /*#__PURE__*/React__default.createElement("div", {
    ref: googleSigninButton,
    className: "btn-social-connect btn-google"
  }) : null;
}

GoogleSignIn.propTypes = process.env.NODE_ENV !== "production" ? {
  clientId: PropTypes.string.isRequired,
  externalAuthURL: PropTypes.string
} : {};
var GoogleSignIn$1 = /*#__PURE__*/memo(GoogleSignIn);

function _templateObject$1() {
  var data = _taggedTemplateLiteralLoose(["\n    text-align: center;\n    margin-bottom: ", ";\n"]);

  _templateObject$1 = function _templateObject() {
    return data;
  };

  return data;
}
var StyledHeader = styled.div(_templateObject$1(), spaceMixin('sm'));

var Header = function Header(_ref) {
  var children = _ref.children,
      text = _ref.text;
  return /*#__PURE__*/React__default.createElement(StyledHeader, null, /*#__PURE__*/React__default.createElement(VisuallyHidden, {
    id: "sr-message",
    "aria-live": "off"
  }), /*#__PURE__*/React__default.createElement(Heading, {
    as: "h2",
    level: 4,
    fontFamily: "serif",
    marginBottom: "xs"
  }, text), children && /*#__PURE__*/React__default.createElement(Paragraph, null, children));
};

Header.propTypes = process.env.NODE_ENV !== "production" ? {
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.object, PropTypes.string]),
  text: PropTypes.string.isRequired
} : {};

var ALLOWED_EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,20}$/i;
var MAX_LENGTH_REGEX = /^.{0,128}$/;
var PASSWORD_LENGTH_REGEX = /^.{8,}$/;
var ALPHANUMERIC_REGEX = /^(?=.*[0-9])(?=.*[a-zA-Z])(.+)$/;
var UPPERCASE_CHARACTER_REGEX$1 = /[A-Z]/;
var LOWERCASE_CHARACTER_REGEX$1 = /[a-z]/;
var SPECIAL_CHARACTER_REGEX$1 = /[()~!@#$%^&*_\-+=`|\\{}[\]:;"'<>,./?]/;
/* eslint-disable no-unused-vars */


function isEmployeeDomain(email) {
  return false; // Comment the employee path out until the Salesforce impersonation issue is sorted out
  // const domain = getAddressDomain(email);
  // return ZILLOW_GROUP_EMPLOYEE_EMAIL_DOMAIN.includes(domain);
} // function getAddressDomain(email) {
//     return email.split('@')[1];
// }


function validateEmail(email) {
  var validated = ALLOWED_EMAIL_REGEX.test(email);
  return {
    validated: validated,
    message: validated ? '' : 'Enter a valid email address'
  };
}
/**
 * Checks whether a value is at most 128 characters
 *
 * @param {string} value - The value to check
 * @returns {{validated: boolean, message: (string)}} - validated value and message if validated
 */


var validateMaxLength$1 = function validateMaxLength(value) {
  var validated = MAX_LENGTH_REGEX.test(value);
  return {
    validated: validated,
    message: validated ? '' : 'Cannot exceed 128 characters'
  };
};
/**
 * Checks whether a password is equal to a provided email address or its alias (alias@domain)
 *
 * @param {string} password - The password to check
 * @param {string} email - The email that might be contained in the password
 * @returns {{validated: boolean, message: string}} - validated value and message if validated
 */


var validatePasswordIsNotEmail = function validatePasswordIsNotEmail(password, email) {
  var lowerCasePassword = typeof password === 'string' ? password.toLowerCase() : '';
  var lowerCaseEmail = typeof email === 'string' ? email.toLowerCase() : '';
  var alias = /^(.+)@/.exec(lowerCaseEmail);
  var validated = lowerCasePassword !== lowerCaseEmail && (alias === null || lowerCasePassword !== alias[1].toLowerCase());
  return {
    validated: validated,
    message: validated || !password ? '' : 'Cannot match your email address or alias'
  };
};
/**
 * Checks whether a value is at most 128 characters
 *
 * @param {string} password - The password to check
 * @returns {{validated: boolean, message: (string)}} - validated value and message if validated
 */


var validatePasswordMinLength = function validatePasswordMinLength(password) {
  var validated = PASSWORD_LENGTH_REGEX.test(password);
  return {
    validated: validated,
    message: 'At least 8 characters'
  };
};
/**
 * Checks whether password has at least 1 number and 1 letter
 *
 * @param {string} password - The password to check
 * @returns {{validated: boolean, message: (string)}} - validated value and message if validated
 */


var validatePasswordAlphaNumeric = function validatePasswordAlphaNumeric(password) {
  var validated = ALPHANUMERIC_REGEX.test(password);
  var message;

  if (!validated && /^[a-zA-Z]+$/.test(password)) {
    message = 'At least 1 number';
  } else if (!validated && /^[0-9]+$/.test(password)) {
    message = 'At least 1 letter';
  } else {
    message = 'Mix of letters and numbers';
  }

  return {
    validated: validated,
    message: message
  };
};
/**
 * Checks whether password has at least 1 special character
 *
 * @param {string} password - The password to check
 * @returns {{validated: boolean, message: (string)}} - validated value and message if validated
 */


var validatePasswordSpecialCharacters = function validatePasswordSpecialCharacters(password) {
  var validated = SPECIAL_CHARACTER_REGEX$1.test(password);
  var message = !validated ? 'At least 1 special character' : 'Contains a special character';
  return {
    validated: validated,
    message: message
  };
};
/**
 * Checks whether password has a mix of uppercase and lowercase letter
 *
 * @param {string} password - The password to check
 * @returns {{validated: boolean, message: (string)}} - validated value and message if validated
 */


var validatePasswordCapitalization = function validatePasswordCapitalization(password) {
  var uppercaseValidated = UPPERCASE_CHARACTER_REGEX$1.test(password);
  var lowercaseValidated = LOWERCASE_CHARACTER_REGEX$1.test(password);
  var validated = uppercaseValidated && lowercaseValidated;
  var message;

  if (!lowercaseValidated) {
    message = 'At least 1 lowercase letter';
  }

  if (!uppercaseValidated) {
    if (message) {
      message += ' and 1 uppercase letter';
    } else {
      message = 'At least 1 uppercase letter';
    }
  }

  message = validated ? 'Mix of uppercase and lowercase letters' : message;
  return {
    validated: validated,
    message: message
  };
};

var PASSWORD_VALIDATORS$1 = [validatePasswordIsNotEmail, validateMaxLength$1, validatePasswordMinLength, validatePasswordAlphaNumeric, validatePasswordSpecialCharacters, validatePasswordCapitalization];

var validatePasswordWithMessages = function validatePasswordWithMessages(password, email) {
  var finalValidation = {
    validated: true,
    messages: []
  }; // Find the first validation that breaks

  PASSWORD_VALIDATORS$1.forEach(function (validator) {
    var validation = validator(password, email);
    finalValidation.messages.push(validation);
    finalValidation.validated = finalValidation.validated && validation.validated;
  });
  return finalValidation;
};

// Verify if the HDP is open for Onboarding
var isHDPOpen = function isHDPOpen() {
  var currentURL = window.location.href; // Check for existing HPDs
  // TODO: Check for new construction HDPs

  return currentURL.includes('/homedetails/');
}; // utils function that checks the custom auth eligibility


var isEligibleForCustomAuth = function isEligibleForCustomAuth(isActivationOnboardingEnabled, isOnboardingEligible, isActivationCustomAuthEnabled) {
  return isActivationOnboardingEnabled && isOnboardingEligible && isHDPOpen() && isActivationCustomAuthEnabled;
}; // Override header text if & only when activation onboarding is enabled and user is eligible for onboarding

var getOnboardingHeaderText = function getOnboardingHeaderText(defaultHeaderText, isActivationOnboardingEnabled, isOnboardingEligible, isActivationCustomAuthEnabled) {
  var ONBOARDING_HEADER_TEXT = 'Sign in to save this home and start tailoring your search.';
  return isEligibleForCustomAuth(isActivationOnboardingEnabled, isOnboardingEligible, isActivationCustomAuthEnabled) ? ONBOARDING_HEADER_TEXT : defaultHeaderText;
}; // Override Submit button text if & only when activation onboarding is enabled and user is eligible for onboarding

var getOnboardingSubmitButtonText = function getOnboardingSubmitButtonText(defaultSubmitButtonText, isActivationOnboardingEnabled, isOnboardingEligible, isActivationCustomAuthEnabled) {
  var ONBOARDING_SUBMIT_BUTTON_TEXT = 'Sign in to save this home';
  return isEligibleForCustomAuth(isActivationOnboardingEnabled, isOnboardingEligible, isActivationCustomAuthEnabled) ? ONBOARDING_SUBMIT_BUTTON_TEXT : defaultSubmitButtonText;
}; // Override input label text if & only when activation onboarding is enabled and user is eligible for onboarding

var getOnboardingInputLabelText = function getOnboardingInputLabelText(defaultInputLabelText, isActivationOnboardingEnabled, isOnboardingEligible, isActivationCustomAuthEnabled) {
  var ONBOARDING_INPUT_LABEL_TEXT = 'Email address';
  return isEligibleForCustomAuth(isActivationOnboardingEnabled, isOnboardingEligible, isActivationCustomAuthEnabled) ? ONBOARDING_INPUT_LABEL_TEXT : defaultInputLabelText;
}; // Override Passwordless Create password modal subheader text if & only when activation onboarding is enabled and user is eligible for onboarding

var getPasswordlessCreatePasswordSubheaderText = function getPasswordlessCreatePasswordSubheaderText(defaultCreatePasswordSubheaderText, isActivationOnboardingEnabled, isOnboardingEligible, isActivationCustomAuthEnabled) {
  var ONBOARDING_CREATE_PASSWORD_SUBHEADER_TEXT = 'Choose a password to securely log into your account from any device.';
  return isEligibleForCustomAuth(isActivationOnboardingEnabled, isOnboardingEligible, isActivationCustomAuthEnabled) ? ONBOARDING_CREATE_PASSWORD_SUBHEADER_TEXT : defaultCreatePasswordSubheaderText;
}; // Override Passwordless Create password modal submit button text if & only when activation onboarding is enabled and user is eligible for onboarding

var getPasswordlessCreatePasswordSubmitButtonText = function getPasswordlessCreatePasswordSubmitButtonText(defaultCreatePasswordSubmitButtonText, isActivationOnboardingEnabled, isOnboardingEligible, isActivationCustomAuthEnabled) {
  var ONBOARDING_CREATE_PASSWORD_SUBMIT_BUTTON_TEXT = 'Create account';
  return isEligibleForCustomAuth(isActivationOnboardingEnabled, isOnboardingEligible, isActivationCustomAuthEnabled) ? ONBOARDING_CREATE_PASSWORD_SUBMIT_BUTTON_TEXT : defaultCreatePasswordSubmitButtonText;
};

/**
 * Login success callback.
 *
 * @param {Object}  res response body from login endpoint
 */

var handleLogin = function handleLogin(_ref) {
  return new Promise(function ($return, $error) {
    var _ref$persistLightbox, persistLightbox, res, onSuccess, redirectUrl;

    _ref$persistLightbox = _ref.persistLightbox, persistLightbox = _ref$persistLightbox === void 0 ? false : _ref$persistLightbox, res = _ref.res, onSuccess = _ref.onSuccess, redirectUrl = _ref.redirectUrl;

    if (onSuccess) {
      return Promise.resolve(onSuccess(res, redirectUrl)).then(function ($await_3) {
        try {
          return $If_1.call(this);
        } catch ($boundEx) {
          return $error($boundEx);
        }
      }.bind(this), $error);
    }

    function $If_1() {
      handleLoggedIn(persistLightbox, res, redirectUrl);
      return $return();
    }

    return $If_1.call(this);
  });
};
/**
 * Login success event proper 6675 event.
 *
 * This function differs from the rest in that it is not providing any onSuccess or redirectUrl handling.
 *
 * Just the object input that is used to call trackAuth
 */

var handleLoginSuccessTracking = function handleLoginSuccessTracking(trackAuthParams) {
  trackAuth(AUTH_EVENT_TYPE.AuthSuccess)(trackAuthParams);
};
var handleLoginAttemptTracking = function handleLoginAttemptTracking(trackAuthParams) {
  trackAuth(AUTH_EVENT_TYPE.LoginAttempt)(trackAuthParams);
};
/**
 * Login success callback with New Lane tracking.
 *
 * Modified to keep the onSuccess and handleLoggedIn behavior, but no longer does trackAuth calls.
 *
 * This is because event 722 is not supposed to fire at this point.
 *
 * @param {Object}  res response body from login endpoint
 */

var handleLoginActions = function handleLoginActions(_ref2) {
  return new Promise(function ($return, $error) {
    var onSuccess, _ref2$persistLightbox, persistLightbox, redirectUrl, res;

    onSuccess = _ref2.onSuccess, _ref2$persistLightbox = _ref2.persistLightbox, persistLightbox = _ref2$persistLightbox === void 0 ? false : _ref2$persistLightbox, redirectUrl = _ref2.redirectUrl, res = _ref2.res;

    if (onSuccess) {
      return Promise.resolve(onSuccess(res, redirectUrl)).then(function ($await_4) {
        try {
          return $If_2.call(this);
        } catch ($boundEx) {
          return $error($boundEx);
        }
      }.bind(this), $error);
    }

    function $If_2() {
      handleLoggedIn(persistLightbox, res, redirectUrl);
      return $return();
    }

    return $If_2.call(this);
  });
};

var DEFAULT_GROUPS = '&groups=1%3A1%2C3%3A1%2C4%3A0';
/**
 * https://www.quirksmode.org/js/cookies.html
 * Standard cookie methods
 */

var createCookie = function createCookie(name, value, expiration) {
  var expires = expiration ? "; expires=" + expiration : '';
  document.cookie = name + "=" + value + expires + "; path=/; domain=.zillow.com; SameSite=None; Secure";
};

var readCookie = function readCookie(name) {
  var nameEQ = name + "=";
  var ca = document.cookie.split(';');

  for (var i = 0; i < ca.length; i += 1) {
    var c = ca[i];

    while (c.charAt(0) === ' ') {
      c = c.substring(1, c.length);
    }

    if (c.indexOf(nameEQ) === 0) {
      return c.substring(nameEQ.length, c.length);
    }
  }

  return null;
};

var eraseCookie = function eraseCookie(name) {
  createCookie(name, '', '');
};
/* End of cookie methods */


var defaultOptanonConsent = function defaultOptanonConsent() {
  var creationDate = new Date();
  return "isIABGlobal=false&datestamp=" + creationDate + "&version=5.11.0&landingPath=NotLandingPage" + DEFAULT_GROUPS;
};

var optoutOptanonConsent = function optoutOptanonConsent(cookieString) {
  if (cookieString) {
    if (cookieString.indexOf('groups=') > -1) {
      return cookieString.split('&').map(function (item) {
        if (item.indexOf('groups=') > -1) {
          var decodedGroups = decodeURIComponent(item);
          return "groups=" + encodeURIComponent(decodedGroups.slice(7, -1) + "0");
        }

        return item;
      }).join('&');
    }

    return "" + cookieString + DEFAULT_GROUPS;
  }

  return defaultOptanonConsent();
};

var getExpirationInYears = function getExpirationInYears(n) {
  var date = new Date();
  date.setFullYear(date.getFullYear() + n);
  return date.toUTCString();
};

var optoutOneTrust = function optoutOneTrust() {
  var optanonConsent = readCookie('OptanonConsent');
  eraseCookie('OptanonConsent');
  createCookie('OptanonConsent', optoutOptanonConsent(optanonConsent), getExpirationInYears(1));
};

var setRegLoginDimensions = function setRegLoginDimensions(encodedZuid) {
  if (encodedZuid === void 0) {
    encodedZuid = '';
  }

  var zguid = window.customDimension1 || '';
  var customDimensions = {
    dimension1: "" + zguid + (encodedZuid && '/') + encodedZuid,
    dimension191: encodedZuid
  };
  setCustomDimensions(customDimensions);
};

var FormError = function FormError(_ref) {
  var text = _ref.text;
  announceValidationMsg(text);
  return /*#__PURE__*/React__default.createElement(FormHelp, {
    marginTop: "xs",
    error: true
  }, text);
};

FormError.propTypes = process.env.NODE_ENV !== "production" ? {
  text: PropTypes.string.isRequired
} : {};

var ReduxInput = function ReduxInput(_ref) {
  var errorText = _ref.errorText,
      onChange = _ref.onChange,
      rest = _objectWithoutProperties(_ref, ["errorText", "onChange"]);

  var onInputChange = React__default.useCallback(function (e) {
    return onChange(e.target.value);
  }, [onChange]);
  return /*#__PURE__*/React__default.createElement(Input, _extends({}, rest, {
    onChange: onInputChange
  }));
};

var ReduxInput$1 = ReduxInputsWrapper(ReduxInput);

var ReduxInputField = function ReduxInputField(_ref) {
  var id = _ref.id,
      label = _ref.label,
      error = _ref.error,
      errorText = _ref.errorText,
      helpText = _ref.helpText,
      onChange = _ref.onChange,
      rest = _objectWithoutProperties(_ref, ["id", "label", "error", "errorText", "helpText", "onChange"]);

  var onInputChange = React__default.useCallback(function (e) {
    return onChange(e.target.value);
  }, [onChange]);
  var description;

  if (error && errorText) {
    description = /*#__PURE__*/React__default.createElement(FormHelp, null, errorText);
  } else if (helpText) {
    description = /*#__PURE__*/React__default.createElement(FormHelp, null, helpText);
  }

  return /*#__PURE__*/React__default.createElement(FormField, {
    error: error,
    label: label && /*#__PURE__*/React__default.createElement(Label, {
      htmlFor: id
    }, label),
    control: /*#__PURE__*/React__default.createElement(BlurInput, _extends({
      component: Input
    }, rest, {
      id: id,
      onChange: onInputChange
    })),
    description: description
  });
};

ReduxInputField.propTypes = process.env.NODE_ENV !== "production" ? {
  id: PropTypes.string,
  label: PropTypes.string,
  onChange: PropTypes.func,
  error: PropTypes.bool,
  errorText: PropTypes.string,
  helpText: PropTypes.string
} : {};
var ReduxInputField$1 = ReduxInputsWrapper(ReduxInputField);

var LoginForm = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(LoginForm, _React$Component);

  function LoginForm() {
    var _this$props$entryPoin;

    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _React$Component.call.apply(_React$Component, [this].concat(args)) || this;
    _this.state = {
      inputSelector: INPUT_SELECTOR$1.Other
    };
    _this.pageTrackingProps = {
      clickstream_trigger: {
        trigger_location_nm: TRIGGER_LOCATION.LoginForm
      },
      auth_form: {
        title_nm: _this.props.headerText,
        idp_cd: IDP.Zillow,
        entry_point_nm: (_this$props$entryPoin = _this.props.entryPoint) !== null && _this$props$entryPoin !== void 0 ? _this$props$entryPoin : UNSPECIFIED,
        auth_type_cd: AUTH_TYPE.AccountLogin
      },
      semantic: {
        // deprecated in favor of auth_form.entry_point_nm
        topic_tag_txt: [AUTH_TOPIC_TAG.AccountLogin].concat(_this.props.topicTags)
      }
    };
    _this.pageGAProps = {
      category: 'auth',
      label: _this.props.gaLabel
    };
    _this.handleForgotPasswordClick = yieldCallback(function (_ref) {
      var clientX = _ref.clientX,
          clientY = _ref.clientY;
      trackAuth(AUTH_EVENT_TYPE.ForgotPasswordLinkClick)({
        gaProps: Object.assign({}, _this.pageGAProps, {
          action: 'forgotpassword//'
        }),
        pageProps: _this.pageTrackingProps,
        componentProps: {
          clickstream_trigger: {
            trigger_source_nm: 'button_forgot_password',
            input_selector_nm: getInputSelector$1(clientX, clientY)
          },
          auth_form: {
            displayed_button_txt: FORM_TEXT.forgotPasswordLink
          }
        }
      });

      _this.props.history.push(ROUTES.forgot);
    });
    _this.onSubmit = yieldCallback(function () {
      var _this$props = _this.props,
          authAP = _this$props.authAP,
          clientId = _this$props.clientId,
          entryPoint = _this$props.entryPoint,
          gaLabel = _this$props.gaLabel,
          hasSentResetPWEmail = _this$props.hasSentResetPWEmail,
          history = _this$props.history,
          inputConfigs = _this$props.inputConfigs,
          inputs = _this$props.inputs,
          isActivationGAMetricsEnabled = _this$props.isActivationGAMetricsEnabled,
          isActivationOnboardingEnabled = _this$props.isActivationOnboardingEnabled,
          isOnboardingEligible = _this$props.isOnboardingEligible,
          onFailure = _this$props.onFailure,
          onSuccess = _this$props.onSuccess,
          redirectUrl = _this$props.redirectUrl,
          requiresPassword = _this$props.requiresPassword,
          zmaHandlerFns = _this$props.zmaHandlerFns;
      /**
       * get the number of hdpviews stored in localStorage
       */

      var hdpViews = typeof window !== 'undefined' && window.localStorage ? localStorage.getItem('hdpViews') : 0;

      var _this$props$getInputP = _this.props.getInputProps(inputConfigs, inputs),
          email = _this$props$getInputP.email;

      handleLoginAttemptTracking({
        pageProps: _this.pageTrackingProps,
        componentProps: {
          clickstream_trigger: {
            // They could have not clicked the button, but pressed enter to submit as well.
            // still a login attempt, but worth the differentiation
            trigger_source_nm: 'button_to_login',
            input_selector_nm: _this.state.inputSelector
          },
          auth_form: {
            displayed_button_txt: BUTTON_TYPES.SignIn.value,
            auth_method_cd: AUTH_METHOD$1.EmailPassword
          }
        }
      });

      if (isEmployeeDomain(email.value)) ; else {
        _this.props.validateInputs(inputConfigs, LOGIN_INPUT_TYPES.requiredTypes).then(function () {
          if (!_this.isSubmitting) {
            _this.isSubmitting = true;
            profileIntervalBegin$1(LOGIN_PROFILE_SUBMIT);
          }

          var _this$props$getInputP2 = _this.props.getInputProps(inputConfigs, inputs),
              authToken = _this$props$getInputP2.authToken,
              password = _this$props$getInputP2.password;

          var hasZmaHandler = (zmaHandlerFns === null || zmaHandlerFns === void 0 ? void 0 : zmaHandlerFns.setZMAConsent) && typeof zmaHandlerFns.setZMAConsent === 'function';

          _this.props.submitForm({
            path: ROUTES.loginPost,
            data: {
              ap: authAP,
              authToken: authToken.value,
              email: email.value,
              entrypoint: entryPoint !== null && entryPoint !== void 0 ? entryPoint : 'reglogin',
              password: password.value,
              destination: hasZmaHandler ? 'zma' : clientId,
              createPasswordContinueUrl: hasZmaHandler ? zmaHandlerFns.passwordContinueUrl : null
            },
            mfaRequired: function mfaRequired() {
              _this.isSubmitting = false;
              profileIntervalEnd$1(LOGIN_PROFILE_SUBMIT);
              handleLoginSuccessTracking({
                pageProps: _this.pageTrackingProps,
                componentProps: {
                  clickstream_trigger: {
                    trigger_type_nm: 'impression',
                    trigger_object_nm: TRIGGER_LOCATION.LoginForm,
                    // only change from the non-mfa login,
                    // to be able to identify the 6675 is coming from MFA
                    trigger_source_nm: TRIGGER_LOCATION.MFAForm
                  },
                  auth_form: {
                    // other values provided by pageProps already
                    auth_method_cd: AUTH_METHOD$1.EmailPassword
                  }
                }
              });
            },
            success: function success(res) {
              _this.isSubmitting = false;
              profileIntervalEnd$1(LOGIN_PROFILE_SUBMIT);

              if (isActivationOnboardingEnabled && isOnboardingEligible) {
                try {
                  setOnboardingConfig({
                    email: email.value
                  });
                  showOnboardingQuestionnaire();
                } catch (err) {
                  console.error(err);
                }
              }

              var googleAnalyticsId = res.googleAnalyticsId,
                  updateOptanonCookie = res.updateOptanonCookie;
              setRegLoginDimensions(googleAnalyticsId);

              if (isActivationGAMetricsEnabled) {
                track({
                  category: 'activation',
                  action: 'hdpviews/signin',
                  label: "Number_of_unauthenticated_hdpviews",
                  value: hdpViews
                });
              } // Success login entrypoint for AUTH_METHOD.EmailPassword


              handleLoginSuccessTracking({
                pageProps: _this.pageTrackingProps,
                componentProps: {
                  clickstream_trigger: {
                    trigger_type_nm: 'impression',
                    trigger_object_nm: TRIGGER_LOCATION.LoginForm,
                    trigger_source_nm: TRIGGER_LOCATION.LoginForm
                  },
                  auth_form: {
                    // other values provided by pageProps already
                    auth_method_cd: AUTH_METHOD$1.EmailPassword
                  }
                }
              });
              handleLoginActions({
                onSuccess: onSuccess,
                persistLightbox: hasZmaHandler,
                redirectUrl: redirectUrl,
                res: res
              });

              if (checkIsTruthyBoolOrString(updateOptanonCookie)) {
                optoutOneTrust();
              }

              if (hasZmaHandler && !hasSentResetPWEmail) {
                var resetPassword = requiresPassword && !res.hasPassword;
                setRegLoginPath(resetPassword ? '/user/acct/password-recovery' : '/user/acct/zma');
              }
            },
            error: function error(err, _ref2) {
              var redirectLink = _ref2.redirectLink,
                  errorMessage = _ref2.errorMessage;

              // We have different non-200 events that are not failures,
              // but all 500+ are bad
              if (err.status >= 500 && _this.isSubmitting) {
                profileIntervalFail(LOGIN_PROFILE_SUBMIT);
              }

              _this.isSubmitting = false;
              trackAuth(AUTH_EVENT_TYPE.LoginError)({
                pageProps: _this.pageTrackingProps,
                componentProps: {
                  clickstream_trigger: {
                    trigger_source_nm: TRIGGER_SOURCE.LoginForm
                  },
                  auth_form: {
                    error_message_txt: errorMessage
                  }
                },
                gaProps: {
                  action: 'error//',
                  category: 'auth',
                  label: gaLabel
                }
              });

              if (onFailure) {
                onFailure(err);
              } else {
                _this.props.handleLoginRedirect(history, redirectLink, err);
              }
            },
            history: history
          });
        }, function () {});
      }
    });

    _this.handleEmployeeLogin = function () {
      var _this$props2 = _this.props,
          inputConfigs = _this$props2.inputConfigs,
          redirectUrl = _this$props2.redirectUrl;

      _this.props.validateInputs(inputConfigs, ['email']).then(function (dispatch) {
        return initiateOktaLogin(function (res) {
          handleLoggedIn(false, res, redirectUrl);
        }, function (err) {
          dispatch(handleError(err.status));
        });
      }, function () {});
    };

    _this.handleFormSubmit = function (event) {
      event.preventDefault();

      _this.onSubmit();
    };

    return _this;
  }

  var _proto = LoginForm.prototype;

  _proto.componentDidMount = function componentDidMount() {
    pageView({
      pageSpecificProps: this.pageTrackingProps,
      pageURL: this.props.history.location.pathname,
      isLightbox: this.props.isLightbox
    });
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    var props = this.props;
    var email = props.inputs.email; // Clear any error messages

    props.hideFacebookError();
    props.resetFormError(); // Hide expanded email form

    props.hideEmailForm(); // Update newEmail input with current email value

    if (email.value) {
      props.syncInputValues(email, 'newEmail');
    }

    props.resetFields([], ['email', 'newEmail']);
  };

  _proto.render = function render() {
    var _this2 = this;

    var _this$props3 = this.props,
        disableEmailForm = _this$props3.disableEmailForm,
        inputConfigs = _this$props3.inputConfigs,
        inputs = _this$props3.inputs,
        login = _this$props3.login,
        isActivationOnboardingEnabled = _this$props3.isActivationOnboardingEnabled,
        isActivationCustomAuthEnabled = _this$props3.isActivationCustomAuthEnabled,
        isOnboardingEligible = _this$props3.isOnboardingEligible;

    var _this$props$getInputP3 = this.props.getInputProps(inputConfigs, inputs),
        authToken = _this$props$getInputP3.authToken,
        email = _this$props$getInputP3.email,
        password = _this$props$getInputP3.password;

    var formErrorMessage = login.formErrorMessage;
    var isEmployeeLogin = isEmployeeDomain(email.value);
    var submitButtonText = getOnboardingSubmitButtonText( BUTTON_TYPES.SignIn.value, isActivationOnboardingEnabled, isOnboardingEligible, isActivationCustomAuthEnabled);
    return /*#__PURE__*/React__default.createElement(Form, {
      onSubmit: this.handleFormSubmit
    }, /*#__PURE__*/React__default.createElement(ReduxInput$1, authToken), /*#__PURE__*/React__default.createElement(ReduxInputField$1, _extends({
      label: getOnboardingInputLabelText('Email', isActivationOnboardingEnabled, isOnboardingEligible, isActivationCustomAuthEnabled)
    }, email, {
      disabled: disableEmailForm
    })), /*#__PURE__*/React__default.createElement(ReduxInputField$1, _extends({
      label: "Password"
    }, password, {
      disabled: isEmployeeLogin
    })), /*#__PURE__*/React__default.createElement(FormActions, null, /*#__PURE__*/React__default.createElement(ButtonGroup, {
      direction: "column",
      "aria-label": "Sign in actions"
    }, /*#__PURE__*/React__default.createElement(Button, {
      as: "input",
      type: "submit",
      value: submitButtonText,
      buttonType: "primary",
      onClick: function onClick(e) {
        var _e$nativeEvent;

        return _this2.setState({
          inputSelector: getInputSelector$1((_e$nativeEvent = e.nativeEvent) === null || _e$nativeEvent === void 0 ? void 0 : _e$nativeEvent.detail)
        });
      }
    }), /*#__PURE__*/React__default.createElement(TextButton, {
      onClick: function onClick(event) {
        event.preventDefault();

        _this2.handleForgotPasswordClick({
          clientX: event.clientX,
          clientY: event.clientY
        });
      }
    }, FORM_TEXT.forgotPasswordLink))), formErrorMessage && /*#__PURE__*/React__default.createElement(FormError, {
      text: formErrorMessage
    }));
  };

  return LoginForm;
}(React__default.Component);

LoginForm.propTypes = process.env.NODE_ENV !== "production" ? {
  authAP: PropTypes.string,

  /**
   * The history object provides by react router's withRouter HOC:
   * https://reactrouter.com/web/api/withRouter
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  history: PropTypes.object,

  /** **********************************************************
   * Props defined inside this LoginForm component
   *********************************************************** */
  clientId: PropTypes.string,

  /**
   * Redirect action
   */
  handleLoginRedirect: PropTypes.func.isRequired,
  hasSentResetPWEmail: PropTypes.bool,
  isActivationGAMetricsEnabled: PropTypes.bool,
  requiresPassword: PropTypes.bool,
  resetFields: PropTypes.func,
  resetFormError: PropTypes.func,

  /** **********************************************************
   * Props from the direct parent component (LoginRegister)
   *********************************************************** */

  /**
   * failure callback for submitForm
   */
  onFailure: PropTypes.func,

  /**
   * success callback for submitForm
   */
  onSuccess: PropTypes.func,

  /**
   * Redirect URL used after login.
   */
  redirectUrl: PropTypes.string,

  /**
   * Input validation action
   */
  validateInputs: PropTypes.func.isRequired,

  /**
   * Submit form action
   */
  submitForm: PropTypes.func.isRequired,

  /**
   * disable the email form entry field (requires a valid email)
   */
  disableEmailForm: PropTypes.bool.isRequired,
  getInputProps: PropTypes.func.isRequired,
  // eslint-disable-next-line zillow/react/forbid-prop-types
  inputs: PropTypes.object,
  // eslint-disable-next-line zillow/react/forbid-prop-types
  inputConfigs: PropTypes.object,
  // eslint-disable-next-line zillow/react/forbid-prop-types
  login: PropTypes.object,
  hideEmailForm: PropTypes.func,
  hideFacebookError: PropTypes.func,
  syncInputValues: PropTypes.func,

  /**
   * whether onboarding modal experiment is enabled
   */
  isActivationOnboardingEnabled: PropTypes.bool,

  /**
   * whether activation custom auth is enabled
   */
  isActivationCustomAuthEnabled: PropTypes.bool,

  /**
   * whether user is eligible for onboarding experiment
   */
  isOnboardingEligible: PropTypes.bool,
  gaLabel: PropTypes.string.isRequired,
  headerText: PropTypes.string,
  isLightbox: PropTypes.bool,
  topicTags: PropTypes.arrayOf(PropTypes.string),
  entryPoint: PropTypes.string,
  zmaHandlerFns: PropTypes.shape({
    getZMAConsent: PropTypes.func,
    setZMAConsent: PropTypes.func,
    passwordContinueUrl: PropTypes.string
  })
} : {};
var mapDispatchToProps = {
  handleLoginRedirect: handleLoginRedirect,
  resetFields: resetFields,
  resetFormError: resetFormError
};

var mapStateToProps = function mapStateToProps(state) {
  return {
    hasSentResetPWEmail: state.login && state.login.hasSentResetPWEmail,
    isActivationGAMetricsEnabled: state.appConfig.isActivationGAMetricsEnabled,
    requiresPassword: state.regLogin.requiresPassword
  };
};

var LoginForm$1 = withRouter(connectToUserSession(mapStateToProps, mapDispatchToProps)(LoginForm));

var AB_AUTH_VERIFY_ON_REGISTER = {
  CONTROL: 'CONTROL',
  OPTIONAL: 'OPTIONAL',
  REQUIRED: 'REQUIRED'
};

/* eslint-disable zillow/import/prefer-default-export */

var appSubView = PropTypes.arrayOf(function (propVal, key, componentName, location, propFullName) {
  if (APP_VIEWS.indexOf(propVal[key]) === -1) {
    return new Error("Invalid prop " + propFullName + " supplied to " + componentName + ".\n            Must be subest of " + APP_VIEWS + ".");
  }

  return true;
});

/**
 * Register success callback with New Lane tracking.
 *
 * Modified to no longer do tracking. It will only handle the onSuccess (for redirection) and
 * the log-in
 *
 * @param {Object}  res response body from register endpoint
 */

var handleRegisterActions = function handleRegisterActions(_ref) {
  return new Promise(function ($return, $error) {
    var _ref$allowLogin, allowLogin, onSuccess, _ref$persistLightbox, persistLightbox, redirectURL, res;

    _ref$allowLogin = _ref.allowLogin, allowLogin = _ref$allowLogin === void 0 ? true : _ref$allowLogin, onSuccess = _ref.onSuccess, _ref$persistLightbox = _ref.persistLightbox, persistLightbox = _ref$persistLightbox === void 0 ? false : _ref$persistLightbox, redirectURL = _ref.redirectURL, res = _ref.res;
    dataLayerPush(REGISTRATION_EVENT);

    if (onSuccess) {
      return Promise.resolve(onSuccess(res, redirectURL)).then(function ($await_2) {
        try {
          return $If_1.call(this);
        } catch ($boundEx) {
          return $error($boundEx);
        }
      }.bind(this), $error);
    }

    function $If_1() {
      if (allowLogin) {
        handleLoggedIn(persistLightbox, res, redirectURL);
      }

      return $return();
    }

    return $If_1.call(this);
  });
};
var handleRegisterAttemptTracking = function handleRegisterAttemptTracking(trackAuthParams) {
  trackAuth(AUTH_EVENT_TYPE.RegisterAttempt)(trackAuthParams);
};

function _templateObject2() {
  var data = _taggedTemplateLiteralLoose(["\n    width: 20px !important;\n    height: 20px !important;\n    border: 2px solid ", " !important;\n    position: static !important;\n    left: auto !important;\n"]);

  _templateObject2 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$2() {
  var data = _taggedTemplateLiteralLoose(["\n    &:after,\n    &::after,\n    &:before,\n    &::before {\n        content: none !important;\n    }\n"]);

  _templateObject$2 = function _templateObject() {
    return data;
  };

  return data;
}

var CleanLabel = styled(Label)(_templateObject$2());
var StandardCheckbox = styled(Checkbox)(_templateObject2(), token$1('colors.borderDark'));

var ReduxCheckboxField = function ReduxCheckboxField(_ref) {
  var label = _ref.label,
      onChange = _ref.onChange,
      disabled = _ref.disabled,
      id = _ref.id,
      error = _ref.error,
      errorText = _ref.errorText,
      rest = _objectWithoutProperties(_ref, ["label", "onChange", "disabled", "id", "error", "errorText"]);

  var onCheckboxChange = React__default.useCallback(function (e) {
    return onChange(e.target.checked);
  }, [onChange]);
  return /*#__PURE__*/React__default.createElement(LabeledControl, {
    disabled: disabled,
    controlId: id,
    label: /*#__PURE__*/React__default.createElement(CleanLabel, null, label),
    control: /*#__PURE__*/React__default.createElement(StandardCheckbox, _extends({}, rest, {
      onChange: onCheckboxChange
    }))
  });
};

ReduxCheckboxField.propTypes = process.env.NODE_ENV !== "production" ? {
  label: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  error: PropTypes.bool,
  errorText: PropTypes.string
} : {};
var ReduxCheckboxField$1 = ReduxInputsWrapper(ReduxCheckboxField);

function _templateObject$3() {
  var data = _taggedTemplateLiteralLoose(["\n    display: inline-block;\n    width: ", "px;\n"]);

  _templateObject$3 = function _templateObject() {
    return data;
  };

  return data;
}
var VoidIcon = styled.span(_templateObject$3(), token$1('fontSizes.finePrint'));
var VALIDATION_ICON = {
  neutral: /*#__PURE__*/React__default.createElement(VoidIcon, null),
  pass: /*#__PURE__*/React__default.createElement(IconCheckmark, {
    fontColor: "green500"
  }),
  fail: /*#__PURE__*/React__default.createElement(IconCloseCircle, null)
};

var ValidationMessage = function ValidationMessage(_ref) {
  var text = _ref.text,
      status = _ref.status,
      id = _ref.id;
  return /*#__PURE__*/React__default.createElement(FormHelp, {
    id: id,
    error: status === 'fail'
  }, /*#__PURE__*/React__default.createElement(VisuallyHidden, {
    as: "span"
  }, "validation " + status), VALIDATION_ICON[status], " ", text);
};

ValidationMessage.propTypes = process.env.NODE_ENV !== "production" ? {
  text: PropTypes.string.isRequired,
  status: PropTypes.oneOf(['pass', 'fail', 'neutral']),
  id: PropTypes.string.isRequired
} : {};
ValidationMessage.defaultProps = {
  status: 'neutral'
};

var RADIUS = 50;
var DIAMETER = RADIUS * 2;

function clampPercent(data) {
  return clamp(data, 0, 100);
}

function clampWidth(data) {
  return clamp(data, 1, RADIUS);
} // stolen from https://github.com/chenglou/tween-functions/blob/master/index.js#L5


function linear(currentTime, beginValue, finalValue, duration) {
  return (finalValue - beginValue) * currentTime / duration + beginValue;
} // stolen from https://stash.atl.zillow.net/projects/NPM/repos/three-sphere-trackball-controls/browse/lib/makeTween.js


function makeTween(callback, startValue, endValue, duration) {
  var startTime = null;
  var context = {};

  var tick = function tick(timestamp) {
    startTime = startTime || timestamp;
    var delta = timestamp - startTime; // to arrive precisely on endValue,
    // ensure delta is no larger than duration

    delta = Math.min(delta, duration);

    if (delta >= duration) {
      context.completed = true;
    }

    return callback(delta, startValue, endValue, duration);
  };

  context.tick = tick;
  return context;
} // This method accepts a percentage (between 0 and 100) and returns a string that will be set as the
// value for an svg path's d attribute.
// (eg, <svg><path d="the return value from this method" /></svg>).
// Using this method, the svg path will draw part of a donut.  input=25 generates one fourth of a
// donut, input=50 generates half a donut etc


function getPathD(percentage, width) {
  var halfWidth = width / 2;
  var paddedRadius = RADIUS - halfWidth;
  var alpha = 360 / 100 * percentage;
  var largeArcFlag = alpha > 180 ? 1 : 0;
  var x;
  var y;

  if (percentage === 100) {
    x = RADIUS - 0.00001;
    y = halfWidth;
  } else {
    var a = (90 - alpha) * Math.PI / 180;
    x = RADIUS + paddedRadius * Math.cos(a);
    y = RADIUS - paddedRadius * Math.sin(a);
  }

  return "M" + RADIUS + "," + halfWidth + " A" + paddedRadius + "," + paddedRadius + ",0," + largeArcFlag + ",1," + x + "," + y;
}
/**
 * A styled, circular graph component.
 * The circular graph is filled to a user-specified percentage with animation.
 */

var Donut = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(Donut, _React$Component);

  function Donut(props) {
    var _this;

    _this = _React$Component.call(this, props) || this;
    _this.state = {
      fillToPercent: clampPercent(props.percent),
      pathWidth: clampWidth(props.width),
      progressPercent: 0
    };
    _this.onTick = _this.onTick.bind(_assertThisInitialized(_this));
    _this.requestFrameStep = _this.requestFrameStep.bind(_assertThisInitialized(_this));
    return _this;
  } // start animation after delay when DOM is available


  var _proto = Donut.prototype;

  _proto.componentDidMount = function componentDidMount() {
    var _this2 = this;

    setTimeout(function () {
      return _this2.startAnimation();
    }, this.props.animationDelay);
  } // update state with clamped props when they change
  ;

  _proto.componentWillReceiveProps = function componentWillReceiveProps(nextProps) {
    var fillToPercent = clampPercent(nextProps.percent);
    var pathWidth = clampWidth(nextProps.width);

    if (this.state.fillToPercent !== fillToPercent || this.state.pathWidth !== pathWidth) {
      this.setState({
        fillToPercent: fillToPercent,
        pathWidth: pathWidth
      });
    }
  } // start animation after clamped props.percent changed
  ;

  _proto.componentDidUpdate = function componentDidUpdate(prevProps, prevState) {
    if (this.state.fillToPercent !== prevState.fillToPercent) {
      if (typeof requestAnimationFrame === 'function') {
        this.startAnimation();
      }
    }
  } // ensure animation is cancelled when unmounted
  ;

  _proto.componentWillUnmount = function componentWillUnmount() {
    if (typeof requestAnimationFrame === 'function') {
      this.cancelFrameStep();
    }
  } // update state with new tick value, lazily re-entering loop if needed
  ;

  _proto.onTick = function onTick(timestamp) {
    var _this3 = this;

    this.setState({
      progressPercent: this.tween.tick(timestamp)
    }, function () {
      if (!_this3.tween.completed) {
        _this3.requestFrameStep();
      }
    });
  };

  _proto.cancelFrameStep = function cancelFrameStep() {
    if (this.rafToken) {
      cancelAnimationFrame(this.rafToken);
      this.rafToken = null;
    }
  };

  _proto.requestFrameStep = function requestFrameStep() {
    this.rafToken = requestAnimationFrame(this.onTick);
  } // setup tween and start requestAnimationFrame loop
  ;

  _proto.startAnimation = function startAnimation() {
    var _this$state = this.state,
        progressPercent = _this$state.progressPercent,
        fillToPercent = _this$state.fillToPercent;
    var animationDuration = this.props.animationDuration; // might be replacing in-flight

    this.cancelFrameStep();
    this.tween = makeTween(linear, progressPercent, fillToPercent, animationDuration);
    this.requestFrameStep();
  };

  _proto.render = function render() {
    var _this$props = this.props,
        color = _this$props.color,
        backgroundColor = _this$props.backgroundColor,
        className = _this$props.className;
    var _this$state2 = this.state,
        pathWidth = _this$state2.pathWidth,
        progressPercent = _this$state2.progressPercent;
    var foregroundPathD = getPathD(progressPercent, pathWidth);
    var backgroundPathD = getPathD(100, pathWidth);
    var foregroundStyle = {
      strokeWidth: pathWidth + "px",
      stroke: color
    };
    var backgroundStyle = {
      strokeWidth: pathWidth + "px",
      stroke: backgroundColor
    };
    return /*#__PURE__*/React__default.createElement("div", {
      className: className
    }, /*#__PURE__*/React__default.createElement("svg", {
      viewBox: "0 0 " + DIAMETER + " " + DIAMETER,
      xmlns: "http://www.w3.org/2000/svg"
    }, /*#__PURE__*/React__default.createElement("path", {
      fill: "none",
      style: backgroundStyle,
      d: backgroundPathD
    }), /*#__PURE__*/React__default.createElement("path", {
      fill: "none",
      style: foregroundStyle,
      d: foregroundPathD
    })), /*#__PURE__*/React__default.createElement("div", null, this.props.children));
  };

  return Donut;
}(React__default.Component);
Donut.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The Donut animates from 0% to this value.  Must be between 0 and 100 inclusive.
   */
  percent: PropTypes.number.isRequired,

  /**
   * Width of the Donut ring in pixels.  Must be between 0 and 50 inclusive.
   */
  width: PropTypes.number,

  /**
   * Number of milliseconds that the Donut will take to perform its animation.
   */
  animationDuration: PropTypes.number,

  /**
   * Number of milliseconds after mounting that the Donut animation will begin.
   */
  animationDelay: PropTypes.number,

  /**
   * Color of the animating ring.
   */
  color: PropTypes.string,

  /**
   * Color of the background ring.
   */
  backgroundColor: PropTypes.string,

  /**
   * Additional classes to add to the root element.
   */
  className: PropTypes.string,

  /**
   * Nodes to be placed inside the Donut
   */
  children: PropTypes.node
} : {};
Donut.defaultProps = {
  width: 5,
  animationDuration: 1000,
  animationDelay: 500,
  backgroundColor: '#F1F1F4',
  color: '#17B036'
};

function _templateObject$4() {
  var data = _taggedTemplateLiteralLoose(["\n    display: flex;\n    align-items: center;\n    background: none;\n    border: none;\n    padding: 0;\n    cursor: pointer;\n\n    svg {\n        height: 22px;\n        width: 22px;\n    }\n"]);

  _templateObject$4 = function _templateObject() {
    return data;
  };

  return data;
}
var METER_CONFIG = {
  null: {
    color: '#FFFFFF',
    text: '',
    percent: 0
  },
  0: {
    color: '#F10000',
    text: 'Poor',
    percent: 0
  },
  1: {
    color: '#F10000',
    text: 'Weak',
    percent: 25
  },
  2: {
    color: '#FF6E01',
    text: 'Fair',
    percent: 50
  },
  3: {
    color: '#3ACE09',
    text: 'Good',
    percent: 75
  },
  4: {
    color: '#3ACE09',
    text: 'Excellent',
    percent: 100
  }
}; // TODO (ACT-1600): Add/Ensure test coverage for getPasswordStrength in StrengthMeter.js

function getPasswordStrength(password) {
  return new Promise(function (resolve, reject) {
    if (!password || password.length === 0) {
      return reject(new Error('Password too short'));
    }

    return request.post(PASSWORD_STRENGTH_ENDPOINT).type('form').send({
      password: password
    }).end(function (err, res) {
      if (err) {
        return reject(err);
      }

      return resolve(res.body.score);
    });
  });
}

var TOOLTIP_MESSAGE = 'Good passwords are hard to guess. Use uncommon words, non-standard uPPercasing, numb3rs or symbol$ in place of letters, or creative spelling.';
var MAX_STRENGTH_METER_SCORE = 4;
var StrengthMeterButton = styled.button(_templateObject$4()); // TODO (ACT-1601): move Overlay to its own component and add unit tests

var Overlay = function Overlay(props) {
  var _ref = METER_CONFIG[props.score] || {},
      color = _ref.color,
      text = _ref.text,
      percent = _ref.percent;

  var popover = /*#__PURE__*/React__default.createElement(Popover, {
    header: /*#__PURE__*/React__default.createElement(Heading, {
      level: 3
    }, text || 'Password strength'),
    body: /*#__PURE__*/React__default.createElement(Paragraph, null, TOOLTIP_MESSAGE)
  });
  return /*#__PURE__*/React__default.createElement(Popper, {
    triggered: popover
  }, /*#__PURE__*/React__default.createElement(StrengthMeterButton, {
    type: "button"
  }, /*#__PURE__*/React__default.createElement(VisuallyHidden, {
    as: "span"
  }, "password strength information"), /*#__PURE__*/React__default.createElement(Text, {
    fontType: "finePrint",
    marginRight: 0.5
  }, text), /*#__PURE__*/React__default.createElement(Donut, {
    percent: percent,
    width: 15,
    color: color,
    animationDuration: 300,
    animationDelay: 100
  })));
};

Overlay.propTypes = process.env.NODE_ENV !== "production" ? {
  score: PropTypes.number
} : {};
Overlay.defaultProps = {
  score: null
}; // eslint-disable-next-line zillow/react/no-multi-comp

var StrengthMeter = /*#__PURE__*/function (_Component) {
  _inheritsLoose(StrengthMeter, _Component);

  function StrengthMeter() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _Component.call.apply(_Component, [this].concat(args)) || this;
    _this.updatePasswordStrength = _debounce(function (newPassword) {
      getPasswordStrength(newPassword).then(function (score) {
        if (score === MAX_STRENGTH_METER_SCORE && !_this.props.validated) {
          // eslint-disable-next-line zillow/react/no-unused-state
          _this.setState({
            score: score - 1
          });
        } else {
          // eslint-disable-next-line zillow/react/no-unused-state
          _this.setState({
            score: score
          });
        }
      });
    }, 500);
    return _this;
  }

  var _proto = StrengthMeter.prototype;

  _proto.componentWillReceiveProps = function componentWillReceiveProps(nextProps) {
    if (this.props.password !== nextProps.password) {
      this.updatePasswordStrength(nextProps.password);
    }
  };

  _proto.render = function render() {
    var _ref2 = this.state || {},
        score = _ref2.score;

    return /*#__PURE__*/React__default.createElement("div", {
      className: "password-strength-meter"
    }, /*#__PURE__*/React__default.createElement(Overlay, {
      score: score
    }));
  };

  return StrengthMeter;
}(Component);

StrengthMeter.propTypes = process.env.NODE_ENV !== "production" ? {
  password: PropTypes.string.isRequired,
  validated: PropTypes.bool.isRequired
} : {};

var ReduxPasswordField = function ReduxPasswordField(_ref) {
  var id = _ref.id,
      error = _ref.error,
      errorText = _ref.errorText,
      onChange = _ref.onChange,
      validationMessages = _ref.validationMessages,
      value = _ref.value,
      rest = _objectWithoutProperties(_ref, ["id", "error", "errorText", "onChange", "validationMessages", "value"]);

  var onInputChange = React__default.useCallback(function (e) {
    return onChange(e.target.value);
  }, [onChange]);
  var validationMarkup = null;
  var passwordInstructionIds = [];

  if (validationMessages) {
    validationMarkup = validationMessages.map(function (_ref2, i) {
      var validated = _ref2.validated,
          message = _ref2.message;
      var status = 'pass';

      if (!validated && value) {
        status = 'fail';
      } else if (!validated && !value) {
        status = 'neutral';
      }

      if (message) {
        var instructionId = "validation-text-" + i;
        passwordInstructionIds.push(instructionId);
        return /*#__PURE__*/React__default.createElement(ValidationMessage, {
          key: instructionId,
          text: message,
          status: status,
          id: instructionId
        });
      }

      return null;
    });
  }

  var ariaDescribedBy;

  if (passwordInstructionIds.length) {
    ariaDescribedBy = passwordInstructionIds.join(' ');
  }

  return /*#__PURE__*/React__default.createElement(FormField, {
    controlId: id,
    error: error
  }, /*#__PURE__*/React__default.createElement(Label, null, "Password"), /*#__PURE__*/React__default.createElement(AdornedInput, {
    error: false,
    input: /*#__PURE__*/React__default.createElement(Input, _extends({}, rest, {
      onChange: onInputChange,
      value: value,
      "aria-describedby": ariaDescribedBy
    })),
    rightAdornment: /*#__PURE__*/React__default.createElement(Adornment, null, /*#__PURE__*/React__default.createElement(StrengthMeter, {
      password: value
    }))
  }), validationMarkup);
};

ReduxPasswordField.propTypes = process.env.NODE_ENV !== "production" ? {
  onChange: PropTypes.func,
  id: PropTypes.string,
  error: PropTypes.bool,
  errorText: PropTypes.string,
  value: PropTypes.string.isRequired,
  // eslint-disable-next-line zillow/react/forbid-prop-types
  validationMessages: PropTypes.array
} : {};
var ReduxPasswordField$1 = ReduxInputsWrapper(ReduxPasswordField);

var ReduxSelectField = function ReduxSelectField(_ref) {
  var id = _ref.id,
      label = _ref.label,
      error = _ref.error,
      errorText = _ref.errorText,
      onChange = _ref.onChange,
      options = _ref.options,
      rest = _objectWithoutProperties(_ref, ["id", "label", "error", "errorText", "onChange", "options"]);

  var onSelectChange = React__default.useCallback(function (e) {
    return onChange(e.target.value);
  }, [onChange]);
  var description;

  if (error && errorText) {
    description = /*#__PURE__*/React__default.createElement(FormHelp, null, errorText);
  }

  return /*#__PURE__*/React__default.createElement(FormField, {
    error: error,
    label: /*#__PURE__*/React__default.createElement(Label, {
      htmlFor: id
    }, label),
    control: /*#__PURE__*/React__default.createElement(DataSelect, _extends({}, rest, {
      data: options,
      id: id,
      onChange: onSelectChange
    })),
    description: description
  });
};

ReduxSelectField.propTypes = process.env.NODE_ENV !== "production" ? {
  id: PropTypes.string,
  label: PropTypes.string,
  onChange: PropTypes.func,
  error: PropTypes.bool,
  errorText: PropTypes.string,
  // eslint-disable-next-line zillow/react/forbid-prop-types
  options: PropTypes.object
} : {};
var ReduxSelectField$1 = ReduxInputsWrapper(ReduxSelectField);

var TermsOfUse = function TermsOfUse() {
  return /*#__PURE__*/React__default.createElement(FormHelp, null, "By submitting, I accept Zillow's", ' ', /*#__PURE__*/React__default.createElement(Anchor, {
    href: "/corp/Terms.htm",
    target: "_blank"
  }, "terms of use"), ".");
};
var TermsOfUseBlock = function TermsOfUseBlock(props) {
  return /*#__PURE__*/React__default.createElement(Flex, _extends({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "xs",
    marginTop: 0.5
  }, props), /*#__PURE__*/React__default.createElement(TermsOfUse, null));
};

var RegisterForm = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(RegisterForm, _React$Component);

  function RegisterForm() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _React$Component.call.apply(_React$Component, [this].concat(args)) || this;
    _this.state = {
      inputSelector: INPUT_SELECTOR$1.Other
    };
    _this.isPro = _this.props.views.indexOf(PRO_REG) !== -1;
    _this.handleProCheck = yieldCallback(function (checked) {
      if (checked) {
        var currReducer = _this.props.login || _this.props.register;
        trackAuth(AUTH_EVENT_TYPE.CheckProRegBox)({
          pageProps: _this.pageTrackingProps,
          componentProps: {
            clickstream_trigger: {
              trigger_source_nm: 'checkbox_pro_register'
            },
            auth_form: {
              auth_method_cd: AUTH_METHOD$1.EmailPassword
            }
          },
          gaProps: {
            action: 'procheckbox//',
            category: 'auth',
            label: currReducer.authProcess
          }
        });
      }

      _this.props.resetProfessionalForm([], true);
    });
    _this.onSubmit = yieldCallback(function () {
      var _this$props = _this.props,
          alwaysPro = _this$props.alwaysPro,
          gaLabel = _this$props.gaLabel,
          hideConfirmPassword = _this$props.hideConfirmPassword,
          history = _this$props.history,
          inputConfigs = _this$props.inputConfigs,
          inputs = _this$props.inputs,
          isLightbox = _this$props.isLightbox,
          login = _this$props.login,
          onFailure = _this$props.onFailure,
          onSuccess = _this$props.onSuccess,
          showProRegCheckbox = _this$props.showProRegCheckbox,
          verifyOnRegisterTreatment = _this$props.verifyOnRegisterTreatment,
          zmaHandlerFns = _this$props.zmaHandlerFns;
      var showEmailConsent = login.showEmailConsent,
          overrideProSelection = login.overrideProSelection;

      var _this$props$getInputP = _this.props.getInputProps(inputConfigs, inputs),
          areaCode = _this$props$getInputP.areaCode,
          authToken = _this$props$getInputP.authToken,
          emailConsent = _this$props$getInputP.emailConsent,
          firstName = _this$props$getInputP.firstName,
          lastName = _this$props$getInputP.lastName,
          newEmail = _this$props$getInputP.newEmail,
          newPassword = _this$props$getInputP.newPassword,
          phoneExt = _this$props$getInputP.phoneExt,
          phonePrefix = _this$props$getInputP.phonePrefix,
          phoneSuffix = _this$props$getInputP.phoneSuffix,
          proCheck = _this$props$getInputP.proCheck,
          proSelect = _this$props$getInputP.proSelect,
          zipCode = _this$props$getInputP.zipCode;

      proCheck.value = (proCheck.value || alwaysPro && !overrideProSelection) && showProRegCheckbox;
      var inputTypes = REG_INPUT_TYPES.requiredTypes;

      if (proCheck.value) {
        inputTypes = REG_PRO_TYPES[proSelect.value] ? REG_PRO_TYPES[proSelect.value].requiredTypes : REG_PRO_TYPES[0].requiredTypes;

        if (proSelect.value !== DEFAULT_PRO_TYPE_OPTIONS['Mortgage Lender']) {
          inputTypes.push('zipCode');
        }
      }

      handleRegisterAttemptTracking({
        pageProps: _this.pageTrackingProps,
        componentProps: {
          clickstream_trigger: {
            trigger_source_nm: 'button_to_register',
            input_selector_nm: _this.state.inputSelector
          },
          auth_form: {
            displayed_button_txt: BUTTON_TYPES.Submit.value,
            auth_method_cd: AUTH_METHOD$1.EmailPassword
          }
        }
      });

      if (hideConfirmPassword) {
        var index = inputTypes.indexOf('newPassword');

        if (index > -1) {
          inputTypes.splice(index, 1);
        }
      }

      var hasRequiredVerification = verifyOnRegisterTreatment && verifyOnRegisterTreatment === AB_AUTH_VERIFY_ON_REGISTER.REQUIRED;
      var hasOptionalVerification = verifyOnRegisterTreatment && verifyOnRegisterTreatment === AB_AUTH_VERIFY_ON_REGISTER.OPTIONAL;
      var hasVerification = isLightbox === 'true' && !proCheck.value && (hasOptionalVerification || hasRequiredVerification);

      var redirectURL = _this.getRedirectURL(proCheck.value, proSelect.value); // Force validation of form inputs.


      _this.props.validateInputs(inputConfigs, inputTypes).then(function () {
        if (!_this.isSubmitting) {
          _this.isSubmitting = true;
          profileIntervalBegin$1(REGISTER_PROFILE_SUBMIT);
        }

        _this.props.submitForm({
          path: ROUTES.registerPost,
          data: {
            ap: gaLabel,
            areaCode: areaCode.value,
            authToken: authToken.value,
            registerCountryCode: showEmailConsent ? 'CAN' : 'US',
            email: newEmail.value,
            emailOptInCheckbox: emailConsent.value,
            extension: phoneExt.value,
            firstName: firstName.value,
            lastName: lastName.value,
            number: phoneSuffix.value,
            password: newPassword.value,
            prefix: phonePrefix.value,
            proCheckbox: _this.isPro || proCheck.value ? 1 : 0,
            proSelect: proSelect.value,
            suppressredirect: true,
            zip: zipCode.value.toUpperCase().replace(/\s/g, ''),
            redirectURL: redirectURL,
            isLightbox: isLightbox === 'true'
          },
          success: function success(res) {
            var googleAnalyticsId = res.googleAnalyticsId;
            _this.isSubmitting = false;
            profileIntervalEnd$1(REGISTER_PROFILE_SUBMIT);
            var hasZmaHandler = (zmaHandlerFns === null || zmaHandlerFns === void 0 ? void 0 : zmaHandlerFns.setZMAConsent) && typeof zmaHandlerFns.setZMAConsent === 'function';
            setRegLoginDimensions(googleAnalyticsId); // Success login entrypoint for AUTH_METHOD.EmailPassword

            trackAuth(AUTH_EVENT_TYPE.AuthSuccess)({
              pageProps: _this.pageTrackingProps,
              componentProps: {
                clickstream_trigger: {
                  trigger_source_nm: TRIGGER_SOURCE.RegisterForm
                },
                auth_form: {
                  // other values provided by pageProps already
                  auth_method_cd: AUTH_METHOD$1.EmailPassword
                }
              }
            });
            handleRegisterActions({
              allowLogin: !hasRequiredVerification,
              onSuccess: onSuccess,
              persistLightbox: hasZmaHandler || hasVerification,
              redirectURL: hasVerification ? '' : redirectURL,
              res: res
            });

            if (hasVerification) {
              history.push(ROUTES.verifyOnRegister);
            }

            if (hasZmaHandler) {
              setRegLoginPath('/user/acct/zma');
            }
          },
          error: function error(err, _ref) {
            var errorMessage = _ref.errorMessage;

            // We have different non-200 events that are not failures, but all 500+ are bad
            if (err.status >= 500 && _this.isSubmitting) {
              profileIntervalFail(REGISTER_PROFILE_SUBMIT);
            }

            _this.isSubmitting = false;
            trackAuth(AUTH_EVENT_TYPE.RegisterError)({
              pageProps: _this.pageTrackingProps,
              componentProps: {
                clickstream_trigger: {
                  trigger_source_nm: TRIGGER_SOURCE.RegisterForm
                },
                auth_form: {
                  error_message_txt: errorMessage
                }
              },
              gaProps: {
                action: 'error//',
                category: 'auth',
                label: gaLabel
              }
            });
            onFailure();
          },
          history: history
        });
      }, function () {});
    });

    _this.getRedirectURL = function (isPro, proLevel) {
      var _this$props2 = _this.props,
          redirectUrl = _this$props2.redirectUrl,
          hideConfirmPassword = _this$props2.hideConfirmPassword;
      var url = redirectUrl; // If redirectUrl not explicitly set and user is a pro try to follow the corresponding redirectUrl

      if (isPro && (!url || url === DEFAULT_REDIRECT_URL) && !hideConfirmPassword) {
        url = REG_PRO_TYPES[proLevel].redirectUrl;
      }

      return url;
    };

    _this.handleFormSubmit = function (event) {
      event.preventDefault();

      _this.onSubmit();
    };

    return _this;
  }

  var _proto = RegisterForm.prototype;

  _proto.componentWillMount = function componentWillMount() {
    var props = this.props;
    var newEmail = props.inputs.newEmail,
        register = props.register; // Reset any social errors

    props.hideFacebookError(); // Hide expanded email form

    props.hideEmailForm(); // Update email input with current newEmail value

    if (newEmail.value) {
      props.syncInputValues(newEmail, 'email');
    } // Clear password field and professional checkbox when leaving


    props.resetProfessionalForm(['newPassword', 'proCheck']); // reset validation for password

    props.resetPasswordValidation(PASSWORD_VALIDATORS);

    if (!register.isGeoIPApiCalled) {
      props.setEmailConsentIfCanada();
    }
  };

  _proto.componentDidMount = function componentDidMount() {
    pageView({
      pageSpecificProps: this.pageTrackingProps,
      pageURL: this.props.history.location.pathname,
      isLightbox: this.props.isLightbox
    });
  };

  _proto.componentWillReceiveProps = function componentWillReceiveProps(nextProps) {
    if (this.props.disableSubmitOnValidate) {
      this.updateFormValidation(nextProps);
    }
  };

  /**
   * Check whether both email and password fields pass validation, and set the validated state
   * @param {Object} props
   */
  _proto.updateFormValidation = function updateFormValidation(_ref2) {
    var inputs = _ref2.inputs,
        inputConfigs = _ref2.inputConfigs,
        register = _ref2.register;

    var _this$props$getInputP2 = this.props.getInputProps(inputConfigs, inputs),
        newEmail = _this$props$getInputP2.newEmail,
        newPassword = _this$props$getInputP2.newPassword;

    var hideConfirmPassword = this.props.hideConfirmPassword;
    var validated = newEmail.value && !newEmail.error;

    if (!hideConfirmPassword) {
      validated = validated && newPassword.value && !newPassword.error;
    }

    if (register.validated !== validated) {
      this.props.validateForm(validated);
    }
  };

  _proto.render = function render() {
    var _this2 = this;

    var _this$props3 = this.props,
        disableEmailForm = _this$props3.disableEmailForm,
        hideConfirmPassword = _this$props3.hideConfirmPassword,
        showProRegCheckbox = _this$props3.showProRegCheckbox,
        showEmailOptIn = _this$props3.showEmailOptIn,
        inputs = _this$props3.inputs,
        inputConfigs = _this$props3.inputConfigs,
        views = _this$props3.views,
        disableSubmitOnValidate = _this$props3.disableSubmitOnValidate,
        alwaysPro = _this$props3.alwaysPro,
        register = _this$props3.register,
        emailValidationEnabled = _this$props3.emailValidationEnabled;

    var _this$props$getInputP3 = this.props.getInputProps(inputConfigs, inputs),
        areaCode = _this$props$getInputP3.areaCode,
        authToken = _this$props$getInputP3.authToken,
        emailConsent = _this$props$getInputP3.emailConsent,
        firstName = _this$props$getInputP3.firstName,
        lastName = _this$props$getInputP3.lastName,
        newEmail = _this$props$getInputP3.newEmail,
        newPassword = _this$props$getInputP3.newPassword,
        phoneExt = _this$props$getInputP3.phoneExt,
        phonePrefix = _this$props$getInputP3.phonePrefix,
        phoneSuffix = _this$props$getInputP3.phoneSuffix,
        proCheck = _this$props$getInputP3.proCheck,
        proSelect = _this$props$getInputP3.proSelect,
        zipCode = _this$props$getInputP3.zipCode;

    var formErrorMessage = register.formErrorMessage,
        newPasswordValidationMessages = register.newPasswordValidationMessages,
        showEmailConsent = register.showEmailConsent,
        validated = register.validated,
        overrideProSelection = register.overrideProSelection,
        emailSuggestion = register.emailSuggestion;
    var isSubmitEnabled = !disableSubmitOnValidate || validated;
    var isPro = views.indexOf(PRO_REG) !== -1;
    var isRealEstateAgent = proSelect.value === '1';
    var isMortgageLender = proSelect.value === '2';
    var showEmailConsentCheckbox = showEmailConsent || showEmailOptIn; // handling for manually enforced pro login

    if (!showProRegCheckbox) {
      proCheck.value = false;
    } else if (isPro || alwaysPro && !overrideProSelection) {
      proCheck.value = true;
    }

    newPassword.validationMessages = newPasswordValidationMessages;
    return /*#__PURE__*/React__default.createElement(Form, {
      onSubmit: this.handleFormSubmit
    }, /*#__PURE__*/React__default.createElement(ReduxInput$1, authToken), /*#__PURE__*/React__default.createElement(ReduxInputField$1, _extends({
      label: "Email"
    }, newEmail, {
      disabled: disableEmailForm,
      helpText: emailValidationEnabled ? emailSuggestion : ''
    })), !hideConfirmPassword && /*#__PURE__*/React__default.createElement(ReduxPasswordField$1, newPassword), showProRegCheckbox && /*#__PURE__*/React__default.createElement(ReduxCheckboxField$1, _extends({}, proCheck, {
      onChange: this.handleProCheck,
      disabled: proCheck.value && isPro
    })), proCheck.value && /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(Heading, {
      marginY: "sm",
      as: "h3",
      level: 5
    }, "Professional Information"), /*#__PURE__*/React__default.createElement(ReduxSelectField$1, _extends({
      label: "Professional type"
    }, proSelect)), !isMortgageLender && /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(Flex, {
      display: "flex",
      marginBottom: "sm"
    }, /*#__PURE__*/React__default.createElement(Flex, {
      flex: "1 1 100%",
      marginRight: "sm"
    }, /*#__PURE__*/React__default.createElement(ReduxInputField$1, _extends({
      label: "First name"
    }, firstName))), /*#__PURE__*/React__default.createElement(Flex, {
      flex: "1 1 100%"
    }, /*#__PURE__*/React__default.createElement(ReduxInputField$1, _extends({
      label: "Last name"
    }, lastName)))), /*#__PURE__*/React__default.createElement(ReduxInputField$1, _extends({
      label: "Zip/Postal"
    }, zipCode)), /*#__PURE__*/React__default.createElement(Flex, {
      display: "flex",
      marginBottom: "sm"
    }, /*#__PURE__*/React__default.createElement(Flex, {
      flexGrow: 3,
      marginRight: isRealEstateAgent ? undefined : 'sm'
    }, /*#__PURE__*/React__default.createElement(FormField, null, /*#__PURE__*/React__default.createElement(Label, {
      htmlFor: areaCode._id
    }, isRealEstateAgent ? PHONE_LABEL : 'Phone number'), /*#__PURE__*/React__default.createElement(Flex, {
      display: "flex"
    }, /*#__PURE__*/React__default.createElement(Flex, {
      flexGrow: 1,
      marginRight: "sm"
    }, /*#__PURE__*/React__default.createElement(ReduxInputField$1, areaCode)), /*#__PURE__*/React__default.createElement(Flex, {
      flexGrow: 1,
      marginRight: "sm"
    }, /*#__PURE__*/React__default.createElement(ReduxInputField$1, phonePrefix)), /*#__PURE__*/React__default.createElement(Flex, {
      flexGrow: 1
    }, /*#__PURE__*/React__default.createElement(ReduxInputField$1, phoneSuffix))))), !isRealEstateAgent && /*#__PURE__*/React__default.createElement(Flex, {
      flexGrow: 1
    }, /*#__PURE__*/React__default.createElement(ReduxInputField$1, _extends({
      label: "Ext"
    }, phoneExt)))))), showEmailConsentCheckbox && /*#__PURE__*/React__default.createElement(ReduxCheckboxField$1, emailConsent), /*#__PURE__*/React__default.createElement(FormActions, null, /*#__PURE__*/React__default.createElement(Button, {
      as: "input",
      type: "submit",
      fluid: true,
      buttonType: "primary",
      value: // If the "Professional" checkbox is checked, change the button text from "Submit" to "Continue"
      proCheck.value ? BUTTON_TYPES.Continue.value : BUTTON_TYPES.Submit.value,
      disabled: !isSubmitEnabled,
      onClick: function onClick(e) {
        var _e$nativeEvent;

        return _this2.setState({
          inputSelector: getInputSelector$1((_e$nativeEvent = e.nativeEvent) === null || _e$nativeEvent === void 0 ? void 0 : _e$nativeEvent.detail)
        });
      }
    })), /*#__PURE__*/React__default.createElement(TermsOfUseBlock, null), formErrorMessage && /*#__PURE__*/React__default.createElement(FormError, {
      text: formErrorMessage
    }));
  };

  _createClass(RegisterForm, [{
    key: "pageTrackingProps",
    get: function get() {
      var _this$props$entryPoin;

      var _this$props$getInputP4 = this.props.getInputProps(this.props.inputConfigs, this.props.inputs),
          proCheck = _this$props$getInputP4.proCheck;

      return {
        clickstream_trigger: {
          trigger_location_nm: TRIGGER_LOCATION.RegisterForm
        },
        auth_form: {
          title_nm: this.props.headerText,
          idp_cd: IDP.Zillow,
          entry_point_nm: (_this$props$entryPoin = this.props.entryPoint) !== null && _this$props$entryPoin !== void 0 ? _this$props$entryPoin : UNSPECIFIED,
          auth_type_cd: this.isPro || proCheck.value ? AUTH_TYPE.ProRegister : AUTH_TYPE.AccountRegister
        },
        semantic: {
          // deprecated in favor of auth_form.entry_point_nm
          topic_tag_txt: [AUTH_TOPIC_TAG.AccountRegister].concat(this.props.topicTags)
        }
      };
    }
  }]);

  return RegisterForm;
}(React__default.Component);

RegisterForm.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The history object provides by react router's withRouter HOC:
   * https://reactrouter.com/web/api/withRouter
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  history: PropTypes.object,

  /** **********************************************************
   * Props defined inside this RegisterForm component
   *********************************************************** */

  /**
   * Whether to check pro checkbox
   */
  alwaysPro: PropTypes.bool,

  /**
   * Prevent user from clicking on the submit button if the inputs fail validation
   */
  disableSubmitOnValidate: PropTypes.bool.isRequired,
  emailValidationEnabled: PropTypes.bool,

  /**
   * Whether to hide confirm password input
   */
  hideConfirmPassword: PropTypes.bool,

  /**
   * Register state
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  register: PropTypes.object,
  resetPasswordValidation: PropTypes.func,

  /**
   * Pro form reset action
   */
  resetProfessionalForm: PropTypes.func.isRequired,
  setEmailConsentIfCanada: PropTypes.func,

  /**
   * Whether to show email opt in checkbox
   */
  showEmailOptIn: PropTypes.bool,

  /**
   * Show/Hide the professional registration checkbox
   */
  showProRegCheckbox: PropTypes.bool.isRequired,
  validateForm: PropTypes.func,
  verifyOnRegisterTreatment: PropTypes.string,

  /** **********************************************************
   * Props from the direct parent component (LoginRegister)
   *********************************************************** */

  /**
   * Login state
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  login: PropTypes.object,

  /**
   * failure callback for submitForm
   */
  onFailure: PropTypes.func,

  /**
   * success callback for submitForm
   */
  onSuccess: PropTypes.func,

  /**
   * Redirect URL used after login.
   */
  redirectUrl: PropTypes.string,

  /**
   * Input validation action
   */
  validateInputs: PropTypes.func.isRequired,

  /**
   * Submit form action
   */
  submitForm: PropTypes.func.isRequired,

  /**
   * Input props action
   */
  getInputProps: PropTypes.func.isRequired,

  /**
   * input fields
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  inputs: PropTypes.object,

  /**
   * Input configs that might be altered subject to AB tests or other external factors.
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  inputConfigs: PropTypes.object,

  /**
   * disable the email form entry field (requires a valid email)
   */
  disableEmailForm: PropTypes.bool.isRequired,
  views: appSubView,
  hideFacebookError: PropTypes.func,
  hideEmailForm: PropTypes.func,
  syncInputValues: PropTypes.func,
  gaLabel: PropTypes.string.isRequired,
  headerText: PropTypes.string,
  isLightbox: PropTypes.bool,
  topicTags: PropTypes.arrayOf(PropTypes.string),
  entryPoint: PropTypes.string,
  zmaHandlerFns: PropTypes.shape({
    getZMAConsent: PropTypes.func,
    setZMAConsent: PropTypes.func,
    passwordContinueUrl: PropTypes.string
  })
} : {};
var mapDispatchToProps$1 = {
  resetPasswordValidation: resetPasswordValidation,
  resetProfessionalForm: resetProfessionalForm,
  setEmailConsentIfCanada: setEmailConsentIfCanada,
  validateForm: validateForm
};

var mapStateToProps$1 = function mapStateToProps(state) {
  return {
    alwaysPro: state.appConfig.alwaysPro,
    disableSubmitOnValidate: state.appConfig.disableSubmitOnValidate,
    emailValidationEnabled: state.appConfig.emailValidationEnabled,
    hideConfirmPassword: state.appConfig.hideConfirmPassword,
    register: state.login,
    showEmailOptIn: state.appConfig.showEmailOptIn,
    showProRegCheckbox: state.regLogin.uiConfig.showProRegCheckbox,
    verifyOnRegisterTreatment: state.appConfig.verifyOnRegisterTreatment
  };
};

var RegisterForm$1 = withRouter(connectToUserSession(mapStateToProps$1, mapDispatchToProps$1)(RegisterForm));

function _templateObject$5() {
  var data = _taggedTemplateLiteralLoose(["\n    padding: ", " ", ";\n    width: auto;\n    max-width: 448px;\n    min-width: 288px;\n\n    @media ", " {\n        margin: 0 auto;\n    }\n"]);

  _templateObject$5 = function _templateObject() {
    return data;
  };

  return data;
}
var WrapperDiv = styled.div(_templateObject$5(), spaceMixin('md'), spaceMixin('xs'), mediaBreakpointMixin('md'));

var ViewWrapper = function ViewWrapper(props) {
  return /*#__PURE__*/React__default.createElement(WrapperDiv, props);
};

/* eslint-disable zillow/import/prefer-default-export */

var delayedDispatch = function delayedDispatch(dispatch) {
  return function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    return new Promise(function (resolve, reject) {
      yieldCallback(function () {
        dispatch.apply(void 0, args).then(resolve).catch(function (e) {
          reject(e);
        });
      })();
    });
  };
};

var getInputProps = function getInputProps(types, inputs) {
  return function (dispatch) {
    return getInputProps$1(types, inputs, delayedDispatch(dispatch));
  };
};

/**
 * A custom hook that setup social buttons including handlers and tracking utils.
 *
 * @param {Object} config Config for social buttons
 * @param {Function} config.onSuccess
 * @param {Function} config.onFailure
 * @returns {[
 *      socialErrorMessage: string,
 *      {
 *          onBtnClick: Function,
 *          onSuccess: Function,
 *          onFailure: Function,
 *      }
 * ]}
 */

function useSocialAuth(_ref) {
  var onSuccess = _ref.onSuccess,
      onFailure = _ref.onFailure;

  var _useState = useState(''),
      _useState2 = _slicedToArray(_useState, 2),
      socialErrorMessage = _useState2[0],
      setSocialErrorMessage = _useState2[1];

  return [socialErrorMessage, {
    onBtnClick: useCallback(function () {
      setSocialErrorMessage('');
    }, []),
    onSuccess: onSuccess,
    onFailure: useCallback(function (e) {
      setSocialErrorMessage(SOCIAL_ERROR_MESSAGE);
      onFailure(e);
    }, [onFailure])
  }];
}

/**
 * Log in onSuccess handler based on user session store
 *
 * @param {object} res the response object
 * @param {function|undefined} onSuccess the onSuccess callback
 * @param {string} redirectUrl the redirect URL when logged in successfully
 * @param {boolean} persistLightbox whether to keep lightbox showing after login
 */

var handleSocialLoggedIn = function handleSocialLoggedIn(res, onSuccess, redirectUrl, persistLightbox) {
  return new Promise(function ($return, $error) {
    var _ref, googleAnalyticsId;

    if (persistLightbox === void 0) {
      persistLightbox = false;
    }

    _ref = res.body || {}, googleAnalyticsId = _ref.googleAnalyticsId;
    setRegLoginDimensions(googleAnalyticsId);

    if (onSuccess) {
      return Promise.resolve(onSuccess(res, redirectUrl)).then(function ($await_2) {
        try {
          return $If_1.call(this);
        } catch ($boundEx) {
          return $error($boundEx);
        }
      }.bind(this), $error);
    } // redirect to designated URL


    function $If_1() {
      if (window && redirectUrl) {
        window.location.href = redirectUrl;
        return $return();
      } // dispatch user session loggedIn action


      setUserLoggedIn(); // dispatch user session hide lightbox actions

      if (!persistLightbox) {
        hideRegLoginLightbox();
      }

      return $return();
    }

    return $If_1.call(this);
  });
};
var withSocialAuth = function withSocialAuth(Component) {
  return function (props) {
    var _props$buttonText = props.buttonText,
        buttonText = _props$buttonText === void 0 ? SOCIAL_BUTTON_TEXT.ContinueWith : _props$buttonText,
        children = props.children,
        _props$entryPoint = props.entryPoint,
        entryPoint = _props$entryPoint === void 0 ? 'reglogin' : _props$entryPoint,
        gaLabel = props.gaLabel,
        headerText = props.headerText,
        onFailure = props.onFailure,
        _onSuccess = props.onSuccess,
        redirectUrl = props.redirectUrl,
        selectedTab = props.selectedTab;
    var socialAuth = useSocialAuth({
      onSuccess: function onSuccess(res) {
        return handleSocialLoggedIn(res, _onSuccess, redirectUrl);
      },
      onFailure: onFailure !== null && onFailure !== void 0 ? onFailure : NOOP
    });
    return /*#__PURE__*/React__default.createElement(Component, {
      buttonText: buttonText,
      socialAuth: socialAuth,
      selectedTab: selectedTab,
      trackingProps: {
        gaLabel: gaLabel,
        pageProps: {
          clickstream_trigger: {
            trigger_location_nm: selectedTab === 'login-tab' ? TRIGGER_LOCATION.LoginForm : TRIGGER_LOCATION.RegisterForm
          },
          auth_form: {
            title_nm: headerText,
            idp_cd: IDP.Zillow,
            entry_point_nm: entryPoint !== null && entryPoint !== void 0 ? entryPoint : UNSPECIFIED,
            auth_type_cd: selectedTab === 'login-tab' ? AUTH_TYPE.AccountLogin : AUTH_TYPE.AccountRegister
          }
        }
      } // Pipe entryPoint to account-service calls for tracking
      ,
      entryPoint: entryPoint
    }, children);
  };
};
var SocialSignInsWith = withSocialAuth(SocialSignIns);

var LoginRegister = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(LoginRegister, _React$Component);

  function LoginRegister() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _React$Component.call.apply(_React$Component, [this].concat(args)) || this;
    _this.state = {
      selectedTab: _this.props.defaultSelected + "-tab"
    };
    _this.hasLogin = _this.props.views.includes(LOGIN);
    _this.hasRegister = _this.props.views.includes(REGISTER);
    _this.hasSocial = _this.props.views.includes(SOCIAL);
    _this.isSubmitting = false;
    _this.onTabSelected = yieldCallback(function (selectedTab) {
      _this.setState({
        selectedTab: selectedTab
      });
    });
    _this.onTabClicked = yieldCallback(function (e) {
      var _e$currentTarget;

      var isLoginTab = _this.state.selectedTab === 'login-tab';
      var tabType = isLoginTab ? 'login' : 'register'; // try getting text from the clicked element, otherwise fallback to known values

      var buttonText = ((_e$currentTarget = e.currentTarget) === null || _e$currentTarget === void 0 ? void 0 : _e$currentTarget.innerText) || (isLoginTab ? 'Sign in' : 'New account');
      trackAuth(AUTH_EVENT_TYPE.SwitchAuthType)({
        pageProps: _this.pageTrackingProps,
        componentProps: {
          clickstream_trigger: {
            trigger_source_nm: "tab_to_switch_to_" + tabType,
            input_selector_nm: getInputSelector$1(e.detail)
          },
          auth_form: {
            displayed_button_txt: buttonText
          }
        }
      });
    });
    return _this;
  }

  var _proto = LoginRegister.prototype;

  _proto.render = function render() {
    var _CUSTOM_CONFIGS$clien;

    var _this$props = this.props,
        facebookAppId = _this$props.facebookAppId,
        googleClientId = _this$props.googleClientId,
        headerText = _this$props.headerText,
        login = _this$props.login,
        showTabs = _this$props.showTabs,
        subHeaderText = _this$props.subHeaderText,
        clientId = _this$props.clientId,
        isActivationOnboardingEnabled = _this$props.isActivationOnboardingEnabled,
        isOnboardingEligible = _this$props.isOnboardingEligible,
        isActivationCustomAuthEnabled = _this$props.isActivationCustomAuthEnabled;
    var contextData = login.contextData;
    var CustomSubHeader = (_CUSTOM_CONFIGS$clien = CUSTOM_CONFIGS[clientId]) === null || _CUSTOM_CONFIGS$clien === void 0 ? void 0 : _CUSTOM_CONFIGS$clien.customSubHeader;
    var loginHeaderText = getOnboardingHeaderText(headerText, isActivationOnboardingEnabled, isOnboardingEligible, isActivationCustomAuthEnabled);
    var loginForm;
    var registerForm;
    var body; // Only mount the form node if the tab is selected

    if (this.state.selectedTab === 'login-tab') {
      loginForm = /*#__PURE__*/React__default.createElement(LoginForm$1, this.props);
      body = loginForm;
    } else {
      registerForm = /*#__PURE__*/React__default.createElement(RegisterForm$1, this.props);
      body = registerForm;
    }

    if (showTabs && this.hasLogin && this.hasRegister) {
      body = /*#__PURE__*/React__default.createElement(Tabs, {
        selected: this.state.selectedTab,
        onSelected: this.onTabSelected
      }, /*#__PURE__*/React__default.createElement(Tabs.List, {
        "aria-label": "Authentication tabs"
      }, /*#__PURE__*/React__default.createElement(Tabs.Tab, {
        id: "login-tab",
        onClick: this.onTabClicked
      }, "Sign in"), /*#__PURE__*/React__default.createElement(Tabs.Tab, {
        id: "register-tab",
        onClick: this.onTabClicked
      }, "New account")), /*#__PURE__*/React__default.createElement(Tabs.Panels, null, /*#__PURE__*/React__default.createElement(Tabs.Panel, {
        "aria-labelledby": "login-tab",
        tabIndex: undefined
      }, loginForm), /*#__PURE__*/React__default.createElement(Tabs.Panel, {
        "aria-labelledby": "register-tab",
        tabIndex: undefined
      }, registerForm)));
    }

    return /*#__PURE__*/React__default.createElement(ViewWrapper, null, /*#__PURE__*/React__default.createElement(Header, {
      text: loginHeaderText
    }, subHeaderText, clientId === CONFIG_NAMES.portal_zcs && /*#__PURE__*/React__default.createElement(CustomSubHeader, {
      hasSubHeaderText: !!subHeaderText
    }), contextData && /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("br", null), contextData.message)), /*#__PURE__*/React__default.createElement(BodyWrapper, null, body), this.hasSocial && /*#__PURE__*/React__default.createElement(Footer, {
      text: "Or connect with: "
    }, /*#__PURE__*/React__default.createElement(Spacer, {
      marginTop: "sm",
      style: {
        width: '100%'
      }
    }, /*#__PURE__*/React__default.createElement(SocialSignInsWith, _extends({}, this.props, {
      selectedTab: this.state.selectedTab,
      buttonText: SOCIAL_BUTTON_TEXT.ContinueWith
    }), /*#__PURE__*/React__default.createElement(AppleSignIn, null), /*#__PURE__*/React__default.createElement(FacebookSignIn, {
      appId: facebookAppId
    }), /*#__PURE__*/React__default.createElement(GoogleSignIn$1, {
      clientId: googleClientId
    })))));
  };

  _createClass(LoginRegister, [{
    key: "pageTrackingProps",

    /**
     * Generates tracking properties for the current page state
     */
    get: function get() {
      var _this$props$entryPoin;

      var isLoginTab = this.state.selectedTab === 'login-tab';
      var pageId = isLoginTab ? 'login' : 'register';
      var authType = isLoginTab ? AUTH_TYPE.AccountLogin : AUTH_TYPE.AccountRegister;
      return {
        clickstream_trigger: {
          trigger_location_nm: pageId + "|auth_form"
        },
        auth_form: {
          title_nm: this.props.headerText,
          idp_cd: IDP.Zillow,
          entry_point_nm: (_this$props$entryPoin = this.props.entryPoint) !== null && _this$props$entryPoin !== void 0 ? _this$props$entryPoin : UNSPECIFIED,
          auth_type_cd: authType
        }
      };
    }
  }]);

  return LoginRegister;
}(React__default.Component);

LoginRegister.propTypes = process.env.NODE_ENV !== "production" ? {
  defaultSelected: PropTypes.oneOf(['login', 'register']),

  /**
   * Facebook app id used for social reg/login
   */
  facebookAppId: PropTypes.string.isRequired,

  /**
   * Google client id used for social reg/login
   */
  googleClientId: PropTypes.string.isRequired,

  /**
   * GA Label for our events
   */
  gaLabel: PropTypes.string.isRequired,

  /**
   * Header text for view
   */
  headerText: PropTypes.string.isRequired,

  /**
   * subheader text for view
   */
  subHeaderText: PropTypes.string,

  /**
   * failure callback for submitForm
   */
  onFailure: PropTypes.func,

  /**
   * success callback for submitForm
   */
  onSuccess: PropTypes.func,

  /**
   * Redirect URL used after login.
   */
  redirectUrl: PropTypes.string,

  /**
   * Login state
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  login: PropTypes.object,

  /**
   * Render Sign in/New account tabs
   */
  showTabs: PropTypes.bool,

  /**
   * whether onboarding modal experiment is enabled
   */
  isActivationOnboardingEnabled: PropTypes.bool,

  /**
   * whether activation custom auth is enabled
   */
  isActivationCustomAuthEnabled: PropTypes.bool,

  /**
   * whether user is eligible for onboarding experiment
   */
  isOnboardingEligible: PropTypes.bool,
  views: appSubView,
  clientId: PropTypes.string,
  topicTags: PropTypes.arrayOf(PropTypes.string),
  entryPoint: PropTypes.string
} : {};
LoginRegister.defaultProps = {
  defaultSelected: 'login',
  showTabs: true
};
var mapDispatchToProps$2 = {
  getInputProps: getInputProps,
  hideEmailForm: hideEmailForm,
  hideFacebookError: hideFacebookError,
  submitForm: submitForm,
  syncInputValues: syncInputValues,
  validateInputs: validateInputs
};

var mapStateToProps$2 = function mapStateToProps(state) {
  var _state$onboarding;

  return {
    clientId: state.appConfig.clientId,
    disableEmailForm: state.regLogin.uiConfig.disableEmailForm,
    facebookAppId: state.appConfig.facebookAppId.toString(),
    gaLabel: state.regLogin.uiConfig.gaLabel,
    googleClientId: state.appConfig.googleClientId,
    headerText: state.regLogin.uiConfig.headerText,
    inputConfigs: state.appConfig.inputConfigs,
    inputs: state.inputs,
    isActivationCustomAuthEnabled: (state === null || state === void 0 ? void 0 : (_state$onboarding = state.onboarding) === null || _state$onboarding === void 0 ? void 0 : _state$onboarding.variant) === 'SAVED_SEARCH_D',
    isActivationOnboardingEnabled: state.appConfig.isActivationOnboardingEnabled,
    isLightbox: checkIsTruthyBoolOrString(state.appConfig.isLightbox),
    isOnboardingEligible: state.appConfig.isActivationOnboardingEnabled && state.onboarding && state.onboarding.eligible,
    login: state.login,
    onFailure: state.appConfig.onFailure,
    onSuccess: state.appConfig.onSuccess,
    redirectUrl: state.regLogin.redirectUrl,
    showTabs: state.regLogin.uiConfig.showTabs,
    subHeaderText: state.regLogin.uiConfig.subHeaderText,
    topicTags: state.appConfig.topicTags,
    entryPoint: getEntryPoint(state.appConfig.entryPoint, state.regLogin.uiConfig.entryPoint),
    views: state.appConfig.views,
    zmaHandlerFns: state.regLogin.zmaHandlerFns
  };
};

var LoginRegister$1 = withRouter(connectToUserSession(mapStateToProps$2, mapDispatchToProps$2)(LoginRegister));

var Login = function Login(props) {
  return /*#__PURE__*/React__default.createElement(LoginRegister$1, _extends({}, props, {
    defaultSelected: "login"
  }));
};

var Register = function Register(props) {
  return /*#__PURE__*/React__default.createElement(LoginRegister$1, _extends({}, props, {
    defaultSelected: "register"
  }));
};

// in user-session-store before initial app load.

var syncAfterInitialLoad = _after(2, function (path) {
  setRegLoginPath(path);
});
/**
 * @typedef {(pageURL: string)
 *      => (overrideNewLaneProps: {
 *              clickstreamTrigger?: Partial<consumerTrigger>,
 *              authForm?: Partial<auth_form>,
 *              semantic?: Partial<semantic>,
 *         })
 *      => void} pageViewTrackingFunction
 */

/**
 * Clickstream tracking for view_content events (impression 724/view 701).
 *
 * @param {boolean} isLightbox indicate whether the view is a pop-up modal or a standalone page
 * @return {pageViewTrackingFunction}
 */


var pageViewTrack = function pageViewTrack(isLightbox) {
  if (isLightbox === void 0) {
    isLightbox = APP_CONFIG.isLightbox;
  }

  return function (pageURL) {
    return function (_ref) {
      var _ref$clickstreamTrigg = _ref.clickstreamTrigger,
          clickstreamTrigger = _ref$clickstreamTrigg === void 0 ? {} : _ref$clickstreamTrigg,
          _ref$authForm = _ref.authForm,
          authForm = _ref$authForm === void 0 ? {} : _ref$authForm,
          _ref$semantic = _ref.semantic,
          semantic = _ref$semantic === void 0 ? {} : _ref$semantic;
      // sync any react-router path changes with the global store's regLogin path
      syncAfterInitialLoad(pageURL);
      var newLaneProps = getViewContentNewLaneProps(isLightbox)({
        clickstreamTrigger: clickstreamTrigger,
        authForm: authForm,
        semantic: semantic
      });

      if (isLightbox) {
        // clickstream event 724
        event(newLaneProps);
      } else {
        // clickstream event 701
        page(pageURL, {}, {
          newLaneEvent: newLaneProps
        });
      }
    };
  };
};

var ForgotPassword = /*#__PURE__*/function (_Component) {
  _inheritsLoose(ForgotPassword, _Component);

  function ForgotPassword() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _Component.call.apply(_Component, [this].concat(args)) || this;
    _this.state = {
      inputSelector: INPUT_SELECTOR.Other
    };
    _this.headerText = checkIsTruthyBoolOrString(_this.props.passwordless) ? FORM_TEXT.forgotPasswordPasswordless : FORM_TEXT.forgotPasswordLink;
    _this.pageTrackingProps = {
      clickstreamTrigger: {
        triggerLocation: TRIGGER_LOCATION.ForgotPasswordForm,
        triggerSource: TRIGGER_SOURCE.ForgotPasswordForm
      },
      authForm: {
        title_nm: _this.headerText
      },
      semantic: {
        topic_tag_txt: [AUTH_TOPIC_TAG.AccountLogin].concat(_this.props.topicTags)
      }
    };
    _this.beforeSignIn = yieldCallback(function (_ref) {
      var clientX = _ref.clientX,
          clientY = _ref.clientY;
      var _this$props = _this.props,
          enableOTP = _this$props.enableOTP,
          history = _this$props.history;
      track({
        action: 'forgotpasswordsigninnow//',
        category: 'auth',
        label: _this.props.gaLabel
      }, {
        newLaneEvent: getForgotPasswordSigninNewLaneProps(mergeNewLaneProps(_this.pageTrackingProps, {
          clickstreamTrigger: {
            triggerSource: 'button_to_open_login_form',
            inputSelector: clientX === 0 && clientY === 0 ? INPUT_SELECTOR.Keyboard : INPUT_SELECTOR.Mouse
          },
          authForm: {
            displayed_button_txt: BUTTON_TYPES.SignIn.value
          }
        }))
      });
      var backToLoginURL = enableOTP ? ROUTES.OTPAuth : ROUTES.login;
      history.push(backToLoginURL);
    });

    _this.onSocialLoginSuccess = function (e) {
      handleLoggedIn(false, e);
    };

    _this.onEmailSend = yieldCallback(function () {
      var _this$props2 = _this.props,
          authAP = _this$props2.authAP,
          gaLabel = _this$props2.gaLabel,
          history = _this$props2.history,
          inputs = _this$props2.inputs,
          inputConfigs = _this$props2.inputConfigs;

      _this.props.validateInputs(inputConfigs, FORGOT_INPUT_TYPES.requiredTypes).then(function () {
        var _this$props$getInputP = _this.props.getInputProps(inputConfigs, inputs),
            authToken = _this$props$getInputP.authToken,
            email = _this$props$getInputP.email;

        track({
          action: 'forgotpasswordsubmit//',
          category: 'auth',
          label: gaLabel
        }, {
          newLaneEvent: getForgotPasswordSubmitNewLaneProps(mergeNewLaneProps(_this.pageTrackingProps, {
            clickstreamTrigger: {
              triggerSource: 'button_to_send_email',
              inputSelector: _this.state.inputSelector
            },
            authForm: {
              displayed_button_txt: BUTTON_TYPES.Send.value
            }
          }))
        });

        _this.props.submitForm({
          path: ROUTES.forgotPost,
          data: {
            ap: authAP,
            authToken: authToken.value,
            email: email.value
          },
          success: function success() {
            _this.props.handleForgotSuccess(history);
          },
          history: history
        });
      }, function () {});
    });

    _this.handleSubmit = function (e) {
      e.preventDefault();

      _this.onEmailSend();
    };

    _this.handleClick = function (e) {
      var _e$nativeEvent;

      _this.setState({
        inputSelector: getInputSelector((_e$nativeEvent = e.nativeEvent) === null || _e$nativeEvent === void 0 ? void 0 : _e$nativeEvent.detail)
      });
    };

    return _this;
  }

  var _proto = ForgotPassword.prototype;

  _proto.componentDidMount = function componentDidMount() {
    var _this$props3 = this.props,
        isLightbox = _this$props3.isLightbox,
        history = _this$props3.history;
    pageViewTrack(isLightbox)(history.location.pathname)(this.pageTrackingProps);
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    var props = this.props; // Clear any error messages

    props.resetFormError(); // Suppress email field error

    props.resetFields([], ['email']);
  } // eslint-disable-next-line class-methods-use-this
  ;

  _proto.render = function render() {
    var _this2 = this;

    var _this$props4 = this.props,
        forgot = _this$props4.forgot,
        inputs = _this$props4.inputs,
        inputConfigs = _this$props4.inputConfigs,
        passwordless = _this$props4.passwordless;

    var _this$props$getInputP2 = this.props.getInputProps(inputConfigs, inputs),
        authToken = _this$props$getInputP2.authToken,
        email = _this$props$getInputP2.email;

    var formErrorMessage = forgot.formErrorMessage;
    return /*#__PURE__*/React__default.createElement(ViewWrapper, null, /*#__PURE__*/React__default.createElement(Header, {
      text: this.headerText
    }, "Enter your email address and we'll send you a link to set your password."), /*#__PURE__*/React__default.createElement(BodyWrapper, null, /*#__PURE__*/React__default.createElement(Spacer, {
      marginBottom: "sm",
      paddingX: "sm"
    }, /*#__PURE__*/React__default.createElement(Form, {
      onSubmit: this.handleSubmit
    }, /*#__PURE__*/React__default.createElement(ReduxInput$1, authToken), /*#__PURE__*/React__default.createElement(ReduxInputField$1, _extends({
      label: "Email"
    }, email)), /*#__PURE__*/React__default.createElement(FormActions, null, /*#__PURE__*/React__default.createElement(Button, {
      as: "input",
      type: "submit",
      value: BUTTON_TYPES.Send.value,
      buttonType: "primary",
      fluid: true,
      onClick: this.handleClick
    }), /*#__PURE__*/React__default.createElement(Paragraph, {
      marginTop: "xs",
      style: {
        textAlign: 'center'
      }
    }, "Know your password?", ' ', /*#__PURE__*/React__default.createElement(TextButton, {
      onClick: function onClick(event) {
        event.preventDefault();

        _this2.beforeSignIn({
          clientX: event.clientX,
          clientY: event.clientY
        });
      }
    }, BUTTON_TYPES.SignIn.value)))), formErrorMessage && /*#__PURE__*/React__default.createElement(FormError, {
      text: formErrorMessage
    }), checkIsTruthyBoolOrString(passwordless) && /*#__PURE__*/React__default.createElement(InlineFeedback, {
      appearance: "warning",
      marginTop: "sm"
    }, "You've reached this page because you don't have a password set."))));
  };

  return ForgotPassword;
}(Component);

ForgotPassword.propTypes = process.env.NODE_ENV !== "production" ? {
  authAP: PropTypes.string,
  facebookAppId: PropTypes.string.isRequired,
  forgot: PropTypes.object,
  gaLabel: PropTypes.string.isRequired,
  getInputProps: PropTypes.func.isRequired,
  handleForgotSuccess: PropTypes.func.isRequired,
  history: PropTypes.object,
  inputs: PropTypes.object,
  inputConfigs: PropTypes.object,
  submitForm: PropTypes.func.isRequired,
  validateInputs: PropTypes.func.isRequired,
  views: appSubView,
  passwordless: PropTypes.bool,
  resetFormError: PropTypes.func,
  resetFields: PropTypes.func,
  isLightbox: PropTypes.bool,
  enableOTP: PropTypes.bool,
  topicTags: PropTypes.arrayOf(PropTypes.string)
} : {};
ForgotPassword.defaultProps = {
  passwordless: false
};
ForgotPassword.contextTypes = {
  store: PropTypes.object
};
var mapDispatchToProps$3 = {
  getInputProps: getInputProps,
  handleForgotSuccess: handleForgotSuccess,
  resetFields: resetFields,
  resetFormError: resetFormError,
  submitForm: submitForm,
  validateInputs: validateInputs
};

var mapStateToProps$3 = function mapStateToProps(state) {
  return {
    facebookAppId: state.appConfig.facebookAppId.toString(),
    forgot: state.login,
    inputs: state.inputs,
    inputConfigs: state.appConfig.inputConfigs,
    gaLabel: state.regLogin.uiConfig.gaLabel,
    views: state.appConfig.views,
    passwordless: state.appConfig.passwordless,
    isLightbox: checkIsTruthyBoolOrString(state.appConfig.isLightbox),
    enableOTP: state.appConfig.enableOTP,
    topicTags: state.appConfig.topicTags
  };
};

var ForgotPasswordRoute = withRouter(connectToUserSession(mapStateToProps$3, mapDispatchToProps$3)(ForgotPassword));

var FORGET_PASSWORD_SUCCESS_HEADER_TEXT = 'Your forgot password email was sent!';
var FORGET_PASSWORD_SIGNIN_BUTTON_TEXT = 'Sign in now';

var ForgotPasswordSuccess = /*#__PURE__*/function (_Component) {
  _inheritsLoose(ForgotPasswordSuccess, _Component);

  function ForgotPasswordSuccess() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _Component.call.apply(_Component, [this].concat(args)) || this;
    _this.pageTrackingProps = {
      clickstreamTrigger: {
        triggerLocation: TRIGGER_LOCATION.ForgotPasswordSuccessForm,
        triggerSource: TRIGGER_SOURCE.ForgotPasswordSuccessForm
      },
      authForm: {
        title_nm: FORGET_PASSWORD_SUCCESS_HEADER_TEXT
      },
      semantic: {
        topic_tag_txt: [AUTH_TOPIC_TAG.AccountLogin].concat(_this.props.topicTags)
      }
    };
    _this.beforeSignIn = yieldCallback(function (_ref) {
      var clientX = _ref.clientX,
          clientY = _ref.clientY;
      var _this$props = _this.props,
          enableOTP = _this$props.enableOTP,
          history = _this$props.history;
      track({
        action: 'forgotpasswordsigninnow//',
        category: 'auth',
        label: _this.props.gaLabel
      }, {
        newLaneEvent: getForgotPasswordSigninNewLaneProps(mergeNewLaneProps(_this.pageTrackingProps, {
          clickstreamTrigger: {
            triggerSource: 'button_to_open_login_form',
            inputSelector: clientX === 0 && clientY === 0 ? INPUT_SELECTOR.Keyboard : INPUT_SELECTOR.Mouse
          },
          authForm: {
            displayed_button_txt: FORGET_PASSWORD_SIGNIN_BUTTON_TEXT
          }
        }))
      });
      var backToLoginURL = enableOTP ? ROUTES.OTPAuth : ROUTES.login;
      history.push(backToLoginURL);
    });
    return _this;
  }

  var _proto = ForgotPasswordSuccess.prototype;

  _proto.componentDidMount = function componentDidMount() {
    var _this$props2 = this.props,
        isLightbox = _this$props2.isLightbox,
        history = _this$props2.history;
    pageViewTrack(isLightbox)(history.location.pathname)(this.pageTrackingProps);
  };

  _proto.render = function render() {
    var _this2 = this;

    var _this$props3 = this.props,
        inputs = _this$props3.inputs,
        inputConfigs = _this$props3.inputConfigs;

    var _this$props$getInputP = this.props.getInputProps(inputConfigs, inputs),
        email = _this$props$getInputP.email;

    return /*#__PURE__*/React__default.createElement(ViewWrapper, null, /*#__PURE__*/React__default.createElement(Header, {
      text: FORGET_PASSWORD_SUCCESS_HEADER_TEXT
    }, "If the email address has an account, an email will be sent with a link to reset your password."), /*#__PURE__*/React__default.createElement(BodyWrapper, null, email.value && /*#__PURE__*/React__default.createElement(Paragraph, {
      marginBottom: "sm",
      style: {
        textAlign: 'center'
      }
    }, "Email sent to: ", /*#__PURE__*/React__default.createElement("strong", null, email.value))), /*#__PURE__*/React__default.createElement(Footer, {
      text: "Know your password? "
    }, /*#__PURE__*/React__default.createElement(Text, null, /*#__PURE__*/React__default.createElement(TextButton, {
      onClick: function onClick(event) {
        event.preventDefault();

        _this2.beforeSignIn({
          clientX: event.clientX,
          clientY: event.clientY
        });
      }
    }, FORGET_PASSWORD_SIGNIN_BUTTON_TEXT))));
  };

  return ForgotPasswordSuccess;
}(Component);

ForgotPasswordSuccess.propTypes = process.env.NODE_ENV !== "production" ? {
  history: PropTypes.object,
  inputs: PropTypes.object.isRequired,
  inputConfigs: PropTypes.object,
  gaLabel: PropTypes.string.isRequired,
  getInputProps: PropTypes.func.isRequired,
  isLightbox: PropTypes.bool,
  enableOTP: PropTypes.bool,
  topicTags: PropTypes.arrayOf(PropTypes.string)
} : {};
var mapDispatchToProps$4 = {
  getInputProps: getInputProps
};

var mapStateToProps$4 = function mapStateToProps(state) {
  return {
    inputs: state.inputs,
    inputConfigs: state.appConfig.inputConfigs,
    gaLabel: state.regLogin.uiConfig.gaLabel,
    isLightbox: checkIsTruthyBoolOrString(state.appConfig.isLightbox),
    topicTags: state.appConfig.topicTags,
    enableOTP: state.appConfig.enableOTP
  };
};

var ForgotPasswordSuccessRoute = withRouter(connectToUserSession(mapStateToProps$4, mapDispatchToProps$4)(ForgotPasswordSuccess));

var TRIGGERED_BY_INPUT_SELECTOR = 'TRIGGERED_BY_INPUT_SELECTOR';
var UPDATE_CLICKSTREAM_TRIGGER = 'UPDATE_CLICKSTREAM_TRIGGER';
/**
 * The action to update global trigger values
 *
 * @returns {{
 *     type: authEventActionType,
 *     payload: import("../store/clickstreamReducer").clickstreamTrigger
 * }}
 */

var updateClickstreamTrigger = function updateClickstreamTrigger(clickstreamTrigger) {
  return {
    type: UPDATE_CLICKSTREAM_TRIGGER,
    payload: clickstreamTrigger
  };
};

/**
 * @typedef { TRIGGERED_BY_INPUT_SELECTOR, UPDATE_CLICKSTREAM_TRIGGER } authEventActionType
 */

/**
 * @typedef {object} inputSelectorOptions
 * @property {boolean} inputSelectorMouse
 * @property {boolean} inputSelectorKeyboard
 */

/** @type {{inputSelector: inputSelectorOptions}} */

var initialState = {
  inputSelector: {
    inputSelectorMouse: false,
    inputSelectorKeyboard: false
  },
  clickstreamTrigger: {}
};
/**
 * A selector function that derives the input_selector value from the redux state.
 *
 * @param {inputSelectorOptions} inputSelectorOptions input selector states for auth clickstream
 *                                                    events
 * @returns {string} the input_selector_nm value
 */

var selectEventInputSelector = function selectEventInputSelector(inputSelectorOptions) {
  if (inputSelectorOptions.inputSelectorKeyboard) {
    return 'keyboard';
  }

  if (inputSelectorOptions.inputSelectorMouse) {
    return 'mouse';
  }

  return 'other';
};
/**
 * @typedef {object} clickstreamTrigger
 * @property {string} triggerLocation
 * @property {string} triggerSource
 * @property {string} triggerObject
 */

/**
 * Reducer for authentication clickstream events.
 *
 * @param {object} state The auth event state that captured in redux store
 * @param {object} action Allowed clickstream actions
 * @param {authEventActionType} action.type Predefined authentication clickstream action type
 * @param {Partial<inputSelectorOptions | clickstreamTrigger>} action.payload
 *     The auth event state that captured by the trigger component
 * @returns {object} Updated clickstream event state that is ready to send
 */

var clickstreamAuthFormReducer = function clickstreamAuthFormReducer(state, action) {
  if (state === void 0) {
    state = initialState;
  }

  switch (action.type) {
    case TRIGGERED_BY_INPUT_SELECTOR:
      return Object.assign({}, state, {
        inputSelector: Object.assign({}, state.inputSelector, action.payload)
      });

    case UPDATE_CLICKSTREAM_TRIGGER:
      return Object.assign({}, state, {
        clickstreamTrigger: Object.assign({}, state.clickstreamTrigger, action.payload)
      });

    default:
      return state;
  }
};

var PasswordRecovery = function PasswordRecovery(_ref) {
  var gaLabel = _ref.gaLabel,
      email = _ref.email,
      resetPasswordLinkUrl = _ref.resetPasswordLinkUrl,
      hasSentResetPWEmail = _ref.hasSentResetPWEmail,
      clickstreamTrigger = _ref.clickstreamTrigger,
      inputSelector = _ref.inputSelector;
  return /*#__PURE__*/React__default.createElement(ViewWrapper, null, /*#__PURE__*/React__default.createElement(Header, {
    text: PasswordRecovery$1.headerText
  }), /*#__PURE__*/React__default.createElement(BodyWrapper, null, /*#__PURE__*/React__default.createElement(PasswordRecovery$1, {
    email: email,
    resetPasswordLinkUrl: resetPasswordLinkUrl,
    hasSentResetPWEmail: hasSentResetPWEmail,
    newLaneTrack: function newLaneTrack() {
      return track({
        category: 'auth',
        action: 'confirmemail//',
        label: gaLabel
      }, {
        newLaneEvent: getEmailConfirmNewLaneProps({
          clickstreamTrigger: clickstreamTrigger,
          inputSelector: inputSelector,
          authForm: {
            title_nm: PasswordRecovery$1.headerText,
            displayed_button_txt: BUTTON_TYPES.Submit.value
          }
        })
      });
    }
  })));
};

PasswordRecovery.propTypes = process.env.NODE_ENV !== "production" ? {
  gaLabel: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  resetPasswordLinkUrl: PropTypes.string,
  hasSentResetPWEmail: PropTypes.bool,

  /**
   * reg-login consumer clickstream trigger data
   */
  clickstreamTrigger: PropTypes.shape({
    // if no value, a util function (`getTriggerLocation`) based on the URL will be used
    triggerLocation: PropTypes.string,
    // for interaction events
    triggerObject: PropTypes.string,
    // for impression/view events
    triggerSource: PropTypes.string,
    // for the triggers that are not in reg-login (e.g. save search button)
    // no value means the consumer won't be able to determine the input selector or the
    // trigger is in reg-login (e.g. sign in button)
    inputSelector: PropTypes.string
  }),

  /**
   * for input selectors that are inside reg-login
   */
  inputSelector: PropTypes.string
} : {};

var mapStateToProps$5 = function mapStateToProps(state) {
  return {
    email: state.inputs.email.value,
    gaLabel: state.regLogin.uiConfig.gaLabel,
    resetPasswordLinkUrl: state.regLogin.resetPasswordLinkUrl,
    hasSentResetPWEmail: state.login && state.login.hasSentResetPWEmail,
    inputSelector: selectEventInputSelector(state.clickstream.inputSelector),
    clickstreamTrigger: state.appConfig.clickstreamTrigger
  };
};

var PasswordRecoveryRoute = connectToUserSession(mapStateToProps$5)(PasswordRecovery);

var PasswordlessSignin = /*#__PURE__*/function (_Component) {
  _inheritsLoose(PasswordlessSignin, _Component);

  function PasswordlessSignin() {
    var _this$props$entryPoin;

    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _Component.call.apply(_Component, [this].concat(args)) || this;
    _this.state = {
      inputSelector: INPUT_SELECTOR.Other
    };
    _this.hasSocial = _this.props.views.indexOf(SOCIAL) !== -1;
    _this.pageTrackingProps = {
      clickstream_trigger: {
        trigger_location_nm: TRIGGER_LOCATION.EnterEmailForm,
        trigger_source_nm: TRIGGER_SOURCE.EnterEmailForm
      },
      auth_form: {
        title_nm: _this.props.headerText,
        idp_cd: IDP.Zillow,
        entry_point_nm: (_this$props$entryPoin = _this.props.entryPoint) !== null && _this$props$entryPoin !== void 0 ? _this$props$entryPoin : UNSPECIFIED
      },
      semantic: {
        topic_tag_txt: _this.props.topicTags
      }
    };
    _this.handleSubmit = yieldCallback(function () {
      var _this$props = _this.props,
          authAP = _this$props.authAP,
          gaLabel = _this$props.gaLabel,
          history = _this$props.history,
          inputs = _this$props.inputs,
          inputConfigs = _this$props.inputConfigs,
          showEmailConsent = _this$props.showEmailConsent;

      _this.props.validateInputs(inputConfigs, PASSWORDLESS_SIGNIN_INPUT_TYPES.requiredTypes).then(function () {
        var _this$props$getInputP = _this.props.getInputProps(inputConfigs, inputs),
            authToken = _this$props$getInputP.authToken,
            email = _this$props$getInputP.email,
            emailConsent = _this$props$getInputP.emailConsent;

        trackAuth(AUTH_EVENT_TYPE.EmailSubmit)({
          pageProps: _this.pageTrackingProps,
          componentProps: {
            clickstream_trigger: {
              trigger_source_nm: TRIGGER_SOURCE.ButtonToSubmitEmail,
              input_selector_nm: _this.state.inputSelector
            },
            auth_form: {
              displayed_button_txt: BUTTON_TYPES.Submit.value
            }
          },
          gaProps: {
            action: 'emailsubmit//',
            category: 'auth',
            label: gaLabel
          }
        });

        _this.props.submitForm({
          path: ROUTES.registerPost,
          data: {
            ap: authAP,
            authToken: authToken.value,
            registerCountryCode: showEmailConsent ? 'CAN' : 'US',
            email: email.value,
            emailOptInCheckbox: emailConsent.value
          },
          success: function success(data) {
            // this is only triggered when an new freepass account is created
            var googleAnalyticsId = data.googleAnalyticsId;
            setRegLoginDimensions(googleAnalyticsId);

            _this.props.handlePasswordlessSigninSuccess(data, history);

            trackAuth(AUTH_EVENT_TYPE.AuthSuccess)({
              pageProps: _this.pageTrackingProps,
              componentProps: {
                clickstream_trigger: {
                  trigger_source_nm: TRIGGER_SOURCE.ButtonToSubmitEmail,
                  input_selector_nm: _this.state.inputSelector
                },
                auth_form: {
                  displayed_button_txt: BUTTON_TYPES.Submit.value,
                  auth_type_cd: AUTH_TYPE.AccountRegister
                }
              },
              gaProps: {
                category: 'auth',
                action: 'register/email/',
                label: gaLabel
              }
            });
            dataLayerPush(REGISTRATION_EVENT);
          },
          error: function error(_err, _ref) {
            var hasPassword = _ref.hasPassword;

            _this.props.handlePasswordlessSigninFailure(hasPassword, history);
          },
          history: history
        });
      }, function () {});
    });
    _this.handleClick = yieldCallback(function (detail) {
      _this.setState({
        inputSelector: getInputSelector(detail)
      });
    });
    return _this;
  }

  var _proto = PasswordlessSignin.prototype;

  _proto.componentWillMount = function componentWillMount() {
    var isGeoIPApiCalled = this.props.isGeoIPApiCalled;

    if (!isGeoIPApiCalled) {
      this.props.setEmailConsentIfCanada();
    }
  };

  _proto.componentDidMount = function componentDidMount() {
    var _this$props2 = this.props,
        history = _this$props2.history,
        isLightbox = _this$props2.isLightbox;
    pageView({
      pageSpecificProps: this.pageTrackingProps,
      pageURL: history.location.pathname,
      isLightbox: isLightbox
    });
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    var props = this.props,
        email = this.props.inputs.email; // Update newEmail input with current email value, used for validation

    if (email.value) {
      props.syncInputValues(email, 'newEmail');
    }
  };

  _proto.render = function render() {
    var _this2 = this;

    var _this$props3 = this.props,
        _this$props3$entryPoi = _this$props3.entryPoint,
        entryPoint = _this$props3$entryPoi === void 0 ? 'reglogin' : _this$props3$entryPoi,
        facebookAppId = _this$props3.facebookAppId,
        googleClientId = _this$props3.googleClientId,
        gaLabel = _this$props3.gaLabel,
        headerText = _this$props3.headerText,
        subHeaderText = _this$props3.subHeaderText,
        inputs = _this$props3.inputs,
        inputConfigs = _this$props3.inputConfigs,
        showEmailConsent = _this$props3.showEmailConsent,
        showEmailOptIn = _this$props3.showEmailOptIn,
        disableEmailForm = _this$props3.disableEmailForm,
        redirectUrl = _this$props3.redirectUrl,
        _onSuccess = _this$props3.onSuccess,
        onFailure = _this$props3.onFailure;

    var _this$props$getInputP2 = this.props.getInputProps(inputConfigs, inputs),
        authToken = _this$props$getInputP2.authToken,
        email = _this$props$getInputP2.email,
        emailConsent = _this$props$getInputP2.emailConsent;

    var showEmailConsentCheckbox = showEmailConsent || showEmailOptIn;
    return /*#__PURE__*/React__default.createElement(ViewWrapper, null, /*#__PURE__*/React__default.createElement(Header, {
      text: headerText
    }, subHeaderText), /*#__PURE__*/React__default.createElement(BodyWrapper, null, /*#__PURE__*/React__default.createElement(Spacer, {
      paddingX: "sm"
    }, /*#__PURE__*/React__default.createElement(Form, {
      onSubmit: function onSubmit(event) {
        event.preventDefault();

        _this2.handleSubmit();
      }
    }, /*#__PURE__*/React__default.createElement(ReduxInput$1, authToken), /*#__PURE__*/React__default.createElement(ReduxInputField$1, _extends({
      label: getOnboardingInputLabelText('Email', this.props.isActivationOnboardingEnabled, this.props.isOnboardingEligible, this.props.isActivationCustomAuthEnabled)
    }, email, {
      disabled: disableEmailForm
    })), showEmailConsentCheckbox && /*#__PURE__*/React__default.createElement(ReduxCheckboxField$1, emailConsent), /*#__PURE__*/React__default.createElement(FormActions, null, /*#__PURE__*/React__default.createElement(Button, {
      as: "input",
      type: "submit",
      fluid: true,
      buttonType: "primary",
      "aria-describedby": "terms-of-use-text",
      value: getOnboardingSubmitButtonText(BUTTON_TYPES.Submit.value, this.props.isActivationOnboardingEnabled, this.props.isActivationCustomAuthEnabled, this.props.isOnboardingEligible),
      onClick: function onClick(event) {
        var _event$nativeEvent;

        _this2.handleClick((_event$nativeEvent = event.nativeEvent) === null || _event$nativeEvent === void 0 ? void 0 : _event$nativeEvent.detail);
      }
    })), /*#__PURE__*/React__default.createElement(TermsOfUseBlock, {
      marginBottom: "sm"
    })))), this.hasSocial && /*#__PURE__*/React__default.createElement(Footer, {
      text: "Or connect with:"
    }, /*#__PURE__*/React__default.createElement(Spacer, {
      marginTop: "sm",
      style: {
        width: '100%'
      }
    }, /*#__PURE__*/React__default.createElement(SocialSignInsWith, {
      buttonText: SOCIAL_BUTTON_TEXT.ContinueWith,
      onSuccess: function onSuccess(res) {
        return handleSocialLoggedIn(res, _onSuccess, redirectUrl);
      },
      onFailure: onFailure !== null && onFailure !== void 0 ? onFailure : NOOP,
      trackSocial: trackSocial({
        gaLabel: gaLabel,
        pageTrackingProps: this.pageTrackingProps
      })
    }, /*#__PURE__*/React__default.createElement(AppleSignIn, {
      entryPoint: entryPoint
    }), /*#__PURE__*/React__default.createElement(FacebookSignIn, {
      appId: facebookAppId,
      entryPoint: entryPoint
    }), /*#__PURE__*/React__default.createElement(GoogleSignIn$1, {
      clientId: googleClientId,
      entryPoint: entryPoint
    })))));
  };

  return PasswordlessSignin;
}(Component);
PasswordlessSignin.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Auth process needed for the register/auth endpoint
   */
  authAP: PropTypes.string,
  facebookAppId: PropTypes.string.isRequired,
  googleClientId: PropTypes.string.isRequired,
  gaLabel: PropTypes.string.isRequired,
  getInputProps: PropTypes.func.isRequired,
  handlePasswordlessSigninSuccess: PropTypes.func.isRequired,
  handlePasswordlessSigninFailure: PropTypes.func.isRequired,
  headerText: PropTypes.string.isRequired,
  history: PropTypes.object,
  subHeaderText: PropTypes.string,
  inputs: PropTypes.object,
  inputConfigs: PropTypes.object,
  onFailure: PropTypes.func,
  onSuccess: PropTypes.func,
  redirectUrl: PropTypes.string,
  setEmailConsentIfCanada: PropTypes.func.isRequired,

  /**
   * Whether to show email opt in checkbox
   */
  showEmailOptIn: PropTypes.bool,
  submitForm: PropTypes.func.isRequired,
  showEmailConsent: PropTypes.bool,
  validateInputs: PropTypes.func.isRequired,
  views: appSubView,
  isGeoIPApiCalled: PropTypes.bool,
  syncInputValues: PropTypes.func,

  /**
   * disable the email form entry field (requires a valid email)
   */
  disableEmailForm: PropTypes.bool.isRequired,

  /**
   * whether onboarding modal experiment is enabled
   */
  isActivationOnboardingEnabled: PropTypes.bool,

  /**
   * whether activation custom auth is enabled
   */
  isActivationCustomAuthEnabled: PropTypes.bool,

  /**
   * whether user is eligible for onboarding experiment
   */
  isOnboardingEligible: PropTypes.bool,
  isLightbox: PropTypes.bool,
  topicTags: PropTypes.arrayOf(PropTypes.string),
  entryPoint: PropTypes.string
} : {};
var mapDispatchToProps$5 = {
  getInputProps: getInputProps,
  handlePasswordlessSigninSuccess: handlePasswordlessSigninSuccess,
  handlePasswordlessSigninFailure: handlePasswordlessSigninFailure,
  setEmailConsentIfCanada: setEmailConsentIfCanada,
  submitForm: submitForm,
  syncInputValues: syncInputValues,
  validateInputs: validateInputs
};

var mapStateToProps$6 = function mapStateToProps(state) {
  var _state$onboarding, _state$onboarding2;

  return {
    authAP: state.regLogin.uiConfig.authAP,
    facebookAppId: state.appConfig.facebookAppId.toString(),
    googleClientId: state.appConfig.googleClientId,
    inputs: state.inputs,
    inputConfigs: state.appConfig.inputConfigs,
    onFailure: state.appConfig.onFailure,
    onSuccess: state.appConfig.onSuccess,
    redirectUrl: state.regLogin.redirectUrl,
    headerText: getOnboardingHeaderText(state.regLogin.uiConfig.headerText, state.appConfig.isActivationOnboardingEnabled, (state === null || state === void 0 ? void 0 : (_state$onboarding = state.onboarding) === null || _state$onboarding === void 0 ? void 0 : _state$onboarding.variant) === 'SAVED_SEARCH_D', state.onboarding && state.onboarding.eligible),
    subHeaderText: state.regLogin.uiConfig.subHeaderText,
    gaLabel: state.regLogin.uiConfig.gaLabel,
    showEmailConsent: state.login.showEmailConsent,
    showEmailOptIn: state.appConfig.showEmailOptIn,
    views: state.appConfig.views,
    isGeoIPApiCalled: state.login.isGeoIPApiCalled,
    disableEmailForm: state.regLogin.uiConfig.disableEmailForm,
    isLightbox: checkIsTruthyBoolOrString(state.appConfig.isLightbox),
    topicTags: getTopicTags(state.appConfig.topicTags, state.regLogin.uiConfig.topicTags),
    isActivationOnboardingEnabled: state.appConfig.isActivationOnboardingEnabled,
    isActivationCustomAuthEnabled: (state === null || state === void 0 ? void 0 : (_state$onboarding2 = state.onboarding) === null || _state$onboarding2 === void 0 ? void 0 : _state$onboarding2.variant) === 'SAVED_SEARCH_D',
    isOnboardingEligible: state.appConfig.isActivationOnboardingEnabled && state.onboarding && state.onboarding.eligible,
    entryPoint: getEntryPoint(state.appConfig.entryPoint, state.regLogin.uiConfig.entryPoint)
  };
};

var PasswordlessSigninRoute = withRouter(connectToUserSession(mapStateToProps$6, mapDispatchToProps$5)(PasswordlessSignin));

var PasswordlessCreatePassword = /*#__PURE__*/function (_Component) {
  _inheritsLoose(PasswordlessCreatePassword, _Component);

  function PasswordlessCreatePassword() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _Component.call.apply(_Component, [this].concat(args)) || this;
    _this.state = {
      inputSelector: INPUT_SELECTOR.Other
    };
    _this.pageTrackingProps = {
      clickstreamTrigger: {
        triggerLocation: TRIGGER_LOCATION.FreepassCreatePasswordForm,
        triggerSource: TRIGGER_SOURCE.FreepassCreatePasswordForm
      },
      authForm: {
        title_nm: FORM_TEXT.createAPasswordText
      },
      semantic: {
        topic_tag_txt: _this.props.topicTags
      }
    };
    _this.onSubmit = yieldCallback(function () {
      var hdpViews = typeof window !== 'undefined' && window.localStorage ? localStorage.getItem('hdpViews') : 0;
      var _this$props = _this.props,
          authAP = _this$props.authAP,
          authToken = _this$props.authToken,
          gaLabel = _this$props.gaLabel,
          inputs = _this$props.inputs,
          inputConfigs = _this$props.inputConfigs,
          redirectUrl = _this$props.redirectUrl,
          doRedirect = _this$props.doRedirect,
          isActivationGAMetricsEnabled = _this$props.isActivationGAMetricsEnabled,
          isActivationOnboardingEnabled = _this$props.isActivationOnboardingEnabled,
          isOnboardingEligible = _this$props.isOnboardingEligible;

      _this.props.validateInputs(inputConfigs, PASSWORDLESS_CREATE_PASSWORD_INPUT_TYPES.requiredTypes).then(function () {
        var _this$props$getInputP = _this.props.getInputProps(inputConfigs, inputs),
            newPassword = _this$props$getInputP.newPassword,
            email = _this$props$getInputP.email;

        _this.props.submitForm({
          path: ROUTES.confirmEmailPost,
          data: {
            ap: authAP,
            token: authToken,
            email: email.value,
            password: newPassword.value,
            passwordless: true
          },
          success: function success() {
            track({
              category: 'auth',
              action: 'passwordsubmit//',
              label: gaLabel
            }, {
              newLaneEvent: getPasswordSubmitNewLaneProps(mergeNewLaneProps(_this.pageTrackingProps, {
                clickstreamTrigger: {
                  triggerSource: 'button_to_create_password',
                  inputSelector: _this.state.inputSelector
                },
                authForm: {
                  displayed_button_txt: BUTTON_TYPES.Submit.value
                }
              }))
            });

            if (isActivationGAMetricsEnabled) {
              track({
                category: 'activation',
                action: 'hdpviews/register',
                label: 'Number_of_unauthenticated_hdpviews',
                value: hdpViews
              });
            }

            if (isActivationOnboardingEnabled && isOnboardingEligible) {
              try {
                setOnboardingConfig({
                  email: email.value
                });
                showOnboardingQuestionnaire();
              } catch (err) {
                console.log(err);
              }
            }

            handleLoggedIn(false, {}, doRedirect && redirectUrl);
          }
        });
      }, function () {});
    });

    _this.handleSubmit = function (e) {
      e.preventDefault();

      _this.onSubmit();
    };

    _this.handleLater = function (e) {
      var _e$nativeEvent;

      e.preventDefault();
      var _this$props2 = _this.props,
          authAP = _this$props2.authAP,
          authToken = _this$props2.authToken,
          gaLabel = _this$props2.gaLabel,
          history = _this$props2.history,
          inputs = _this$props2.inputs,
          inputConfigs = _this$props2.inputConfigs,
          redirectUrl = _this$props2.redirectUrl,
          isActivationOnboardingEnabled = _this$props2.isActivationOnboardingEnabled,
          isOnboardingEligible = _this$props2.isOnboardingEligible;

      var _this$props$getInputP2 = _this.props.getInputProps(inputConfigs, inputs),
          email = _this$props$getInputP2.email;

      _this.props.submitForm({
        path: ROUTES.passwordlessSendEmailPost,
        data: {
          ap: authAP,
          token: authToken,
          email: email.value,
          passwordless: true,
          skipPasswordCreation: true
        },
        success: function success() {
          handleLoggedIn(false, {}, redirectUrl);
        },
        history: history
      });

      track({
        category: 'auth',
        action: 'dothislatersubmit//',
        label: gaLabel
      }, {
        newLaneEvent: getDoThisLaterSubmitNewLaneProps(mergeNewLaneProps(_this.pageTrackingProps, {
          clickstreamTrigger: {
            triggerSource: 'button_to_defer_password_creation',
            inputSelector: getInputSelector((_e$nativeEvent = e.nativeEvent) === null || _e$nativeEvent === void 0 ? void 0 : _e$nativeEvent.detail)
          },
          authForm: {
            displayed_button_txt: BUTTON_TYPES.Later.value
          }
        }))
      });

      if (isActivationOnboardingEnabled && isOnboardingEligible) {
        setOnboardingConfig({
          email: email.value
        });
        showOnboardingQuestionnaire();
      }
    };

    _this.handleClick = function (e) {
      var _e$nativeEvent2;

      _this.setState({
        inputSelector: getInputSelector((_e$nativeEvent2 = e.nativeEvent) === null || _e$nativeEvent2 === void 0 ? void 0 : _e$nativeEvent2.detail)
      });
    };

    return _this;
  }

  var _proto = PasswordlessCreatePassword.prototype;

  _proto.componentWillMount = function componentWillMount() {
    // reset validation for password
    this.props.resetPasswordValidation(PASSWORD_VALIDATORS);
  };

  _proto.componentDidMount = function componentDidMount() {
    var _this$props3 = this.props,
        history = _this$props3.history,
        isLightbox = _this$props3.isLightbox;
    pageViewTrack(isLightbox)(history.location.pathname)(this.pageTrackingProps);
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    handleLoggedIn();
  } // Force validation of form inputs.
  ;

  _proto.render = function render() {
    var _this$props4 = this.props,
        disableSubmitOnValidate = _this$props4.disableSubmitOnValidate,
        inputs = _this$props4.inputs,
        inputConfigs = _this$props4.inputConfigs,
        register = _this$props4.register,
        subHeader = _this$props4.subHeader,
        isActivationOnboardingEnabled = _this$props4.isActivationOnboardingEnabled,
        isActivationCustomAuthEnabled = _this$props4.isActivationCustomAuthEnabled,
        isOnboardingEligible = _this$props4.isOnboardingEligible;

    var _this$props$getInputP3 = this.props.getInputProps(inputConfigs, inputs),
        authToken = _this$props$getInputP3.authToken,
        newPassword = _this$props$getInputP3.newPassword;

    var isSubmitEnabled = !((!inputs.newPassword.value || Object.prototype.hasOwnProperty.call(inputs.newPassword, 'error')) && disableSubmitOnValidate);
    var subHeaderText = getPasswordlessCreatePasswordSubheaderText(subHeader, isActivationOnboardingEnabled, isOnboardingEligible, isActivationCustomAuthEnabled);
    var submitButtonText = getPasswordlessCreatePasswordSubmitButtonText(BUTTON_TYPES.Submit.value, isActivationOnboardingEnabled, isOnboardingEligible, isActivationCustomAuthEnabled);
    newPassword.validationMessages = register.newPasswordValidationMessages;
    return /*#__PURE__*/React__default.createElement(ViewWrapper, null, /*#__PURE__*/React__default.createElement(Header, {
      text: FORM_TEXT.createAPasswordText
    }, subHeaderText), /*#__PURE__*/React__default.createElement(BodyWrapper, null, /*#__PURE__*/React__default.createElement(Form, {
      onSubmit: this.handleSubmit
    }, /*#__PURE__*/React__default.createElement(ReduxInput$1, authToken), /*#__PURE__*/React__default.createElement(ReduxPasswordField$1, newPassword), /*#__PURE__*/React__default.createElement(FormActions, null, /*#__PURE__*/React__default.createElement(ButtonGroup, {
      direction: "column"
    }, /*#__PURE__*/React__default.createElement(Button, {
      as: "input",
      type: "submit",
      fluid: true,
      buttonType: "primary",
      "aria-describedby": "terms-of-use-text",
      value: submitButtonText,
      disabled: !isSubmitEnabled,
      onClick: this.handleClick
    }), // if activation onboarding experiment is enabled and user is eligible,
    // show "do this later" button as a text button
    isActivationOnboardingEnabled && isOnboardingEligible ? /*#__PURE__*/React__default.createElement(TextButton, {
      fluid: true,
      fontType: "bodySmallHeading",
      onClick: this.handleLater
    }, BUTTON_TYPES.Later.value) : /*#__PURE__*/React__default.createElement(Button, {
      as: "input",
      type: "submit",
      fluid: true,
      buttonType: "secondary",
      value: BUTTON_TYPES.Later.value,
      onClick: this.handleLater
    }))), /*#__PURE__*/React__default.createElement(TermsOfUseBlock, null))));
  };

  return PasswordlessCreatePassword;
}(Component);
PasswordlessCreatePassword.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Text that appears in the subheader of the page
   */
  subHeader: PropTypes.string,

  /**
   * Auth process needed for the register/auth endpoint
   */
  authAP: PropTypes.string,

  /**
   * Auth token needed for the register/auth endpoint
   */
  authToken: PropTypes.string,

  /**
   * Prevent user from clicking on the submit button if the inputs fail validation
   */
  disableSubmitOnValidate: PropTypes.bool.isRequired,

  /**
   * Facebook app id used for social reg/login
   */
  facebookAppId: PropTypes.string.isRequired,

  /**
   * GA Label for our events
   */
  gaLabel: PropTypes.string.isRequired,

  /**
   * Helper method for retrieving props from redux-inputs
   */
  getInputProps: PropTypes.func.isRequired,
  history: PropTypes.object,

  /**
   * input fields
   */
  inputs: PropTypes.object,

  /**
   * Input configs that might be altered subject
   * to AB tests or other external factors.
   */
  inputConfigs: PropTypes.object,

  /**
   * submit form action
   */
  submitForm: PropTypes.func.isRequired,

  /**
   * register state
   */
  register: PropTypes.object,
  redirectUrl: PropTypes.string,
  doRedirect: PropTypes.bool,
  validateInputs: PropTypes.func.isRequired,
  views: appSubView,
  resetPasswordValidation: PropTypes.func,
  isActivationGAMetricsEnabled: PropTypes.bool,

  /**
   * whether onboarding modal experiment is enabled
   */
  isActivationOnboardingEnabled: PropTypes.bool,

  /**
   * whether activation custom auth is enabled
   */
  isActivationCustomAuthEnabled: PropTypes.bool,

  /**
   * whether user is eligible for onboarding experiment
   */
  isOnboardingEligible: PropTypes.bool,
  isLightbox: PropTypes.bool,
  topicTags: PropTypes.arrayOf(PropTypes.string)
} : {};
PasswordlessCreatePassword.defaultProps = {
  subHeader: FORM_TEXT.createAPasswordMessage
};
PasswordlessCreatePassword.contextTypes = {
  store: PropTypes.object
};

var mapDispatchToProps$6 = function mapDispatchToProps(dispatch) {
  return Object.assign({
    dispatch: dispatch
  }, bindActionCreators({
    getInputProps: getInputProps,
    submitForm: submitForm,
    validateInputs: validateInputs,
    resetPasswordValidation: resetPasswordValidation
  }, dispatch));
};

var mapStateToProps$7 = function mapStateToProps(state) {
  var _state$onboarding;

  return {
    authAP: state.regLogin.uiConfig.authAP,
    subHeader: state.regLogin.uiConfig.globalFormSubHeader,
    authToken: state.user.authToken,
    disableSubmitOnValidate: state.appConfig.disableSubmitOnValidate,
    facebookAppId: state.appConfig.facebookAppId.toString(),
    inputs: state.inputs,
    inputConfigs: state.appConfig.inputConfigs,
    register: state.login,
    redirectUrl: state.appConfig.isLightbox ? null : state.regLogin.redirectUrl,
    gaLabel: state.regLogin.uiConfig.gaLabel,
    doRedirect: !state.appConfig.showLightbox,
    isActivationGAMetricsEnabled: state.appConfig.isActivationGAMetricsEnabled,
    isActivationOnboardingEnabled: state.appConfig.isActivationOnboardingEnabled,
    isActivationCustomAuthEnabled: (state === null || state === void 0 ? void 0 : (_state$onboarding = state.onboarding) === null || _state$onboarding === void 0 ? void 0 : _state$onboarding.variant) === 'SAVED_SEARCH_D',
    isOnboardingEligible: state.appConfig.isActivationOnboardingEnabled && state.onboarding && state.onboarding.eligible,
    isLightbox: checkIsTruthyBoolOrString(state.appConfig.isLightbox),
    topicTags: state.appConfig.topicTags
  };
};

var PasswordlessCreatePasswordRoute = withRouter(connectToUserSession(mapStateToProps$7, mapDispatchToProps$6)(PasswordlessCreatePassword));

var _updateIsValidEmailToken = function _updateIsValidEmailToken(isValid) {
  return {
    type: UPDATE_VALID_EMAIL_TOKEN,
    isValid: isValid
  };
};

var updateIsValidEmailToken = function updateIsValidEmailToken(isValid) {
  return function (dispatch) {
    dispatch(_updateIsValidEmailToken, isValid);
  };
};
/**
 * User has successfully added password
 */

var handleConfirmEmailSuccess = function handleConfirmEmailSuccess(redirectUrl) {
  return function () {
    handleLoggedIn(false, {}, redirectUrl);
  };
};
var handleNotFromEmail = function handleNotFromEmail(router) {
  return function () {
    router.push(ROUTES.passwordlessCreatePassword);
  };
};
var handleInvalidEmailToken = function handleInvalidEmailToken(router) {
  return function () {
    router.push(ROUTES.invalidTokenMsg);
  };
};
var handleInvalidEmailTokenSubmit = function handleInvalidEmailTokenSubmit(router) {
  router.push(ROUTES.forgot);
};

var ConfirmEmail = /*#__PURE__*/function (_Component) {
  _inheritsLoose(ConfirmEmail, _Component);

  function ConfirmEmail(props) {
    var _this$props$entryPoin;

    var _this;

    _this = _Component.call(this, props) || this;
    _this.pageTrackingProps = {
      clickstream_trigger: {
        trigger_location_nm: TRIGGER_LOCATION.UpdatePasswordForm,
        trigger_source_nm: TRIGGER_SOURCE.UpdatePasswordForm
      },
      auth_form: {
        title_nm: FORM_TEXT.emailConfirmedText,
        displayed_button_txt: BUTTON_TYPES.Submit.value,
        idp_cd: IDP.Zillow,
        entry_point_nm: (_this$props$entryPoin = _this.props.entryPoint) !== null && _this$props$entryPoin !== void 0 ? _this$props$entryPoin : UNSPECIFIED
      },
      semantic: {
        // deprecated in favor of auth_form.entry_point_nm
        topic_tag_txt: _this.props.topicTags
      }
    };
    var isValidEmailToken = props.emailConfirmed.isValidEmailToken,
        history = props.history,
        fromEmail = props.fromEmail;

    if (!isValidEmailToken) {
      props.handleInvalidEmailToken(history);
    } else if (!fromEmail) {
      props.handleNotFromEmail(history);
    }

    _this.state = {
      inputSelector: 'other'
    };
    return _this;
  }

  var _proto = ConfirmEmail.prototype;

  _proto.componentWillMount = function componentWillMount() {
    // reset validation for password
    this.props.resetPasswordValidation(PASSWORD_VALIDATORS);
  };

  _proto.componentDidMount = function componentDidMount() {
    var _this$props = this.props,
        isLightbox = _this$props.isLightbox,
        history = _this$props.history;
    pageView({
      pageSpecificProps: this.pageTrackingProps,
      pageURL: history.location.pathname,
      isLightbox: isLightbox
    });
  };

  _proto.componentDidUpdate = function componentDidUpdate(prevProps) {
    var isValidEmailToken = this.props.emailConfirmed.isValidEmailToken;
    var history = prevProps.history; // isValidEmailToken could be undefined initially so need to do explicit comparison

    if (isValidEmailToken === false) {
      this.props.handleInvalidEmailToken(history);
    }

    this.updateFormValidation(this.props);
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    this.props.resetFormError();
  } // Force validation of form inputs.
  ;

  _proto.handleSubmit = function handleSubmit(e) {
    var _this2 = this;

    e.preventDefault();
    var _this$props2 = this.props,
        authToken = _this$props2.authToken,
        gaLabel = _this$props2.gaLabel,
        history = _this$props2.history,
        inputs = _this$props2.inputs,
        inputConfigs = _this$props2.inputConfigs,
        redirectUrl = _this$props2.redirectUrl;
    this.props.validateInputs(inputConfigs, EMAIL_CONFIRMED_INPUT_TYPES.requiredTypes).then(function () {
      var _this2$props$getInput = _this2.props.getInputProps(inputConfigs, inputs),
          newPassword = _this2$props$getInput.newPassword;

      track({
        action: 'confirmemailsubmit//',
        category: 'auth',
        label: gaLabel
      }, {
        newLaneEvent: getEmailConfirmSubmitNewLaneProps({
          clickstreamTrigger: {
            triggerLocation: 'authentication_form|update_password',
            triggerObject: 'authentication_form|update_password'
          },
          inputSelector: _this2.state.inputSelector,
          authForm: {
            title_nm: FORM_TEXT.emailConfirmedText,
            displayed_button_txt: BUTTON_TYPES.Submit.value
          }
        })
      });

      _this2.props.submitForm({
        path: ROUTES.confirmEmailPost,
        data: {
          password: newPassword.value,
          passwordless: true,
          token: authToken
        },
        success: function success() {
          _this2.props.handleConfirmEmailSuccess(redirectUrl);
        },
        error: function error(err) {
          if (err.response.body.message === 'Invalid token') {
            _this2.props.updateIsValidEmailToken(false);
          }
        },
        history: history
      });
    }, function () {});
  };

  _proto.handleClick = function handleClick(event) {
    var _event$nativeEvent;

    this.setState({
      inputSelector: getInputSelector((_event$nativeEvent = event.nativeEvent) === null || _event$nativeEvent === void 0 ? void 0 : _event$nativeEvent.detail)
    });
  }
  /**
   * Check whether both email and password fields pass validation, and set the validated state
   * @param {Object} props
   */
  ;

  _proto.updateFormValidation = function updateFormValidation(_ref) {
    var inputs = _ref.inputs,
        inputConfigs = _ref.inputConfigs,
        emailConfirmed = _ref.emailConfirmed,
        dispatch = _ref.dispatch;

    var _this$props$getInputP = this.props.getInputProps(inputConfigs, inputs),
        newPassword = _this$props$getInputP.newPassword;

    var validated = newPassword.value && !newPassword.error;

    if (emailConfirmed.validated !== validated) {
      validateForm(validated)(dispatch);
    }
  };

  _proto.render = function render() {
    var _this3 = this;

    var _this$props3 = this.props,
        emailConfirmed = _this$props3.emailConfirmed,
        inputs = _this$props3.inputs,
        inputConfigs = _this$props3.inputConfigs;

    var _this$props$getInputP2 = this.props.getInputProps(inputConfigs, inputs),
        newPassword = _this$props$getInputP2.newPassword;

    var formErrorMessage = emailConfirmed.formErrorMessage,
        newPasswordValidationMessages = emailConfirmed.newPasswordValidationMessages,
        validated = emailConfirmed.validated;
    newPassword.validationMessages = newPasswordValidationMessages;
    return /*#__PURE__*/React__default.createElement(ViewWrapper, null, /*#__PURE__*/React__default.createElement(Header, {
      text: FORM_TEXT.emailConfirmedText
    }, FORM_TEXT.createAPasswordMessage), /*#__PURE__*/React__default.createElement(BodyWrapper, null, /*#__PURE__*/React__default.createElement(Form, {
      onSubmit: function onSubmit(e) {
        return _this3.handleSubmit(e);
      }
    }, /*#__PURE__*/React__default.createElement(ReduxPasswordField$1, newPassword), /*#__PURE__*/React__default.createElement(FormActions, null, /*#__PURE__*/React__default.createElement(Button, {
      as: "input",
      type: "submit",
      fluid: true,
      buttonType: "primary",
      value: BUTTON_TYPES.Submit.value,
      disabled: !validated,
      onClick: function onClick(e) {
        return _this3.handleClick(e);
      }
    })), /*#__PURE__*/React__default.createElement(TermsOfUseBlock, null)), formErrorMessage && /*#__PURE__*/React__default.createElement(FormError, {
      text: formErrorMessage
    })));
  };

  return ConfirmEmail;
}(Component);
ConfirmEmail.propTypes = process.env.NODE_ENV !== "production" ? {
  authAP: PropTypes.string,
  authToken: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  emailConfirmed: PropTypes.object,
  fromEmail: PropTypes.bool,
  gaLabel: PropTypes.string.isRequired,
  getInputProps: PropTypes.func.isRequired,
  handleConfirmEmailSuccess: PropTypes.func.isRequired,
  handleInvalidEmailToken: PropTypes.func,
  handleNotFromEmail: PropTypes.func,
  history: PropTypes.object,
  inputs: PropTypes.object,
  inputConfigs: PropTypes.object,
  passwordless: PropTypes.bool,
  redirectUrl: PropTypes.string,
  resetFormError: PropTypes.func,
  submitForm: PropTypes.func.isRequired,
  updateIsValidEmailToken: PropTypes.func,
  validateInputs: PropTypes.func.isRequired,
  resetPasswordValidation: PropTypes.func,
  topicTags: PropTypes.arrayOf(PropTypes.string),
  isLightbox: PropTypes.bool,
  entryPoint: PropTypes.string
} : {};
ConfirmEmail.contextTypes = {
  store: PropTypes.object
};

var mapDispatchToProps$7 = function mapDispatchToProps(dispatch) {
  return Object.assign({
    dispatch: dispatch
  }, bindActionCreators({
    getInputProps: getInputProps,
    handleConfirmEmailSuccess: handleConfirmEmailSuccess,
    handleInvalidEmailToken: handleInvalidEmailToken,
    handleNotFromEmail: handleNotFromEmail,
    resetFormError: resetFormError,
    submitForm: submitForm,
    validateInputs: validateInputs,
    updateIsValidEmailToken: updateIsValidEmailToken,
    resetPasswordValidation: resetPasswordValidation
  }, dispatch));
};

var mapStateToProps$8 = function mapStateToProps(state) {
  return {
    authToken: state.appConfig.authToken,
    emailConfirmed: state.login,
    fromEmail: state.appConfig.fromEmail,
    inputs: state.inputs,
    inputConfigs: state.appConfig.inputConfigs,
    gaLabel: state.regLogin.uiConfig.gaLabel,
    redirectUrl: state.regLogin.redirectUrl,
    topicTags: state.appConfig.topicTags,
    entryPoint: getEntryPoint(state.appConfig.entryPoint, state.regLogin.uiConfig.entryPoint),
    isLightbox: checkIsTruthyBoolOrString(state.appConfig.isLightbox)
  };
};

var ConfirmEmailRoute = withRouter(connectToUserSession(mapStateToProps$8, mapDispatchToProps$7)(ConfirmEmail));

/* eslint-disable zillow/react/forbid-prop-types */
function InvalidTokenMsg(_ref) {
  var history = _ref.history,
      headerText = _ref.headerText,
      headerMsg = _ref.headerMsg;
  return /*#__PURE__*/React__default.createElement(ViewWrapper, null, /*#__PURE__*/React__default.createElement(Header, {
    text: headerText
  }, headerMsg), /*#__PURE__*/React__default.createElement(BodyWrapper, null, /*#__PURE__*/React__default.createElement(Form, {
    onSubmit: function onSubmit(e) {
      e.preventDefault();
      handleInvalidEmailTokenSubmit(history);
    }
  }, /*#__PURE__*/React__default.createElement(FormActions, null, /*#__PURE__*/React__default.createElement(Button, {
    as: "input",
    type: "submit",
    fluid: true,
    buttonType: "primary",
    value: BUTTON_TYPES.Send.value
  })))));
}
InvalidTokenMsg.propTypes = process.env.NODE_ENV !== "production" ? {
  history: PropTypes.object,
  headerText: PropTypes.string,
  headerMsg: PropTypes.string
} : {};
InvalidTokenMsg.defaultProps = {
  headerText: FORM_TEXT.invalidTokenMsgText,
  headerMsg: FORM_TEXT.invalidTokenMsgMessage
};
var InvalidTokenMsgRoute = withRouter(InvalidTokenMsg);

var STATUS_SENDING_CODE = 'STATUS_SENDING_CODE';
var STATUS_DEFAULT = 'STATUS_DEFAULT';

function MFAMethodChooseScreen(props) {
  var onCancel = props.onCancel,
      sendCode = props.sendCode,
      onMethodConfirm = props.onMethodConfirm;

  var _React$useState = React__default.useState(MFA_TEXT_METHOD),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      method = _React$useState2[0],
      setMethod = _React$useState2[1];

  var _React$useState3 = React__default.useState(STATUS_DEFAULT),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      status = _React$useState4[0],
      setStatus = _React$useState4[1];

  var handleSubmit = function handleSubmit(event) {
    return new Promise(function ($return, $error) {
      var $Try_1_Finally = function ($Try_1_Exit) {
        return function ($Try_1_Value) {
          try {
            setStatus(STATUS_DEFAULT);
            return $Try_1_Exit && $Try_1_Exit.call(this, $Try_1_Value);
          } catch ($boundEx) {
            return $error($boundEx);
          }
        }.bind(this);
      }.bind(this);

      var response, body, sentTo;
      event.preventDefault();
      setStatus(STATUS_SENDING_CODE);

      var $Try_1_Post = function () {
        try {
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      var $Try_1_Catch = function (error) {
        try {
          console.error(error);
          return $Try_1_Finally($Try_1_Post)();
        } catch ($boundEx) {
          return $Try_1_Finally($error)($boundEx);
        }
      };

      try {
        return Promise.resolve(sendCode(method)).then(function ($await_2) {
          try {
            response = $await_2;
            return Promise.resolve(response.json()).then(function ($await_3) {
              try {
                body = $await_3;

                if (response.ok && response.status === STATUS_ERROR_CODES.codeSent.code) {
                  sentTo = body.message;
                  onMethodConfirm(method, sentTo);
                } else {
                  throw new Error('Error sending code');
                }

                return $Try_1_Finally($Try_1_Post)();
              } catch ($boundEx) {
                return $Try_1_Catch($boundEx);
              }
            }, $Try_1_Catch);
          } catch ($boundEx) {
            return $Try_1_Catch($boundEx);
          }
        }, $Try_1_Catch);
      } catch (error) {
        $Try_1_Catch(error);
      }
    });
  };

  return /*#__PURE__*/React__default.createElement("form", {
    onSubmit: handleSubmit
  }, /*#__PURE__*/React__default.createElement(ChooseMFAMethod, {
    onMethodChange: setMethod,
    method: method,
    allowEmail: true
  }), /*#__PURE__*/React__default.createElement(ButtonGroup, {
    marginTop: "lg",
    direction: "row-reverse",
    "aria-label": "Method choose actions"
  }, /*#__PURE__*/React__default.createElement(Button, {
    type: "submit",
    buttonType: "primary",
    disabled: status === STATUS_SENDING_CODE
  }, status === STATUS_SENDING_CODE ? 'Sending' : 'Next'), /*#__PURE__*/React__default.createElement(TextButton, {
    type: "button",
    onClick: onCancel
  }, "Cancel")));
}

MFAMethodChooseScreen.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Function to call when the cancel button is clicked
   */
  onCancel: PropTypes.func.isRequired,

  /**
   * Function to call when the method is confirmed
   */
  onMethodConfirm: PropTypes.func.isRequired,

  /**
   * Function to send in the code
   */
  sendCode: PropTypes.func.isRequired
} : {};

var STATUS_DEFAULT$1 = 'STATUS_DEFAULT';
var STATUS_VALIDATING = 'STATUS_VALIDATING';
var STATUS_INVALID_CODE = 'STATUS_INVALID_CODE';

var fireEvent = function fireEvent(action, clickstreamTrigger, inputSelector, headerText) {
  track({
    category: AUTH_EVENT_CATEGORY,
    action: action,
    label: '2-step/signin'
  }, {
    newLaneEvent: getTwoStepSigninNewLaneProps({
      clickstreamTrigger: clickstreamTrigger,
      inputSelector: inputSelector,
      authForm: {
        title_nm: headerText,
        displayed_button_txt: 'Submit'
      }
    })
  });
};

function MFAEnterCodeScreen(props) {
  var resendCode = props.resendCode,
      verifyCode = props.verifyCode,
      onDifferentMethodClick = props.onDifferentMethodClick,
      method = props.method,
      onCancel = props.onCancel,
      onSuccess = props.onSuccess,
      onError = props.onError,
      sentTo = props.sentTo,
      clickstreamTrigger = props.clickstreamTrigger,
      inputSelector = props.inputSelector,
      headerText = props.headerText;

  var _React$useState = React__default.useState(STATUS_DEFAULT$1),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      status = _React$useState2[0],
      setStatus = _React$useState2[1];

  var _React$useState3 = React__default.useState(5),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      attempts = _React$useState4[0],
      setAttempts = _React$useState4[1];

  var handleSubmit = function handleSubmit(event) {
    return new Promise(function ($return, $error) {
      var target, code, rememberDevice, response, body, _ref, _ref$attemptsRemainin, attemptsRemaining;

      event.preventDefault();
      fireEvent("submit_2-step_verification", clickstreamTrigger, inputSelector, headerText);
      target = event.target;
      code = target.code.value;
      rememberDevice = target.rememberDevice && target.rememberDevice.checked || false;
      setStatus(STATUS_VALIDATING);

      var $Try_1_Post = function () {
        try {
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      var $Try_1_Catch = function (error) {
        try {
          onError(response, body, error);
          setStatus(STATUS_DEFAULT$1);
          return $Try_1_Post();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      try {
        return Promise.resolve(verifyCode(code, rememberDevice)).then(function ($await_3) {
          try {
            response = $await_3;
            return Promise.resolve(response.json()).then(function ($await_4) {
              try {
                body = $await_4;

                if (!response.ok) {
                  _ref = body || {}, _ref$attemptsRemainin = _ref.attemptsRemaining, attemptsRemaining = _ref$attemptsRemainin === void 0 ? 0 : _ref$attemptsRemainin;

                  if (attemptsRemaining > 0) {
                    setAttempts(attemptsRemaining);
                    setStatus(STATUS_INVALID_CODE);
                  } else {
                    throw new Error('Unable to verify your identity');
                  }
                } else {
                  onSuccess(response, body);
                }

                return $Try_1_Post();
              } catch ($boundEx) {
                return $Try_1_Catch($boundEx);
              }
            }, $Try_1_Catch);
          } catch ($boundEx) {
            return $Try_1_Catch($boundEx);
          }
        }, $Try_1_Catch);
      } catch (error) {
        $Try_1_Catch(error);
      }
    });
  };

  var handleResendClick = function handleResendClick() {
    return new Promise(function ($return, $error) {
      var response;

      var $Try_2_Post = function () {
        try {
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      var $Try_2_Catch = function (response) {
        try {
          setStatus(STATUS_DEFAULT$1);
          return $Try_2_Post();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      try {
        return Promise.resolve(resendCode()).then(function ($await_5) {
          try {
            response = $await_5;

            if (response.ok) {
              setStatus(STATUS_DEFAULT$1);
            } else {
              throw new Error(response);
            }

            return $Try_2_Post();
          } catch ($boundEx) {
            return $Try_2_Catch($boundEx);
          }
        }, $Try_2_Catch);
      } catch (response) {
        $Try_2_Catch();
      }
    });
  };

  var handleRememberDeviceChange = function handleRememberDeviceChange(event) {
    fireEvent("2-step/" + (event.target.checked ? '' : 'un') + "check_remember");
  };

  var errorMessage = status === STATUS_INVALID_CODE ? "Invalid code. You have " + attempts + " more attempts." : null;
  var disabledControls = status === STATUS_VALIDATING;
  return /*#__PURE__*/React__default.createElement("form", {
    onSubmit: handleSubmit,
    autoComplete: "off"
  }, /*#__PURE__*/React__default.createElement(EnterVerificationCode, {
    onResendClick: handleResendClick,
    showDifferentMethodOption: false,
    onDifferentMethodClick: onDifferentMethodClick,
    onRememberDeviceChange: handleRememberDeviceChange,
    inputError: errorMessage,
    method: method,
    sentTo: sentTo
  }), /*#__PURE__*/React__default.createElement(ButtonGroup, {
    marginTop: "lg",
    direction: "row-reverse",
    "aria-label": "Enter code actions"
  }, /*#__PURE__*/React__default.createElement(TextButton, {
    type: "button",
    onClick: onCancel
  }, "Cancel"), /*#__PURE__*/React__default.createElement(Button, {
    type: "submit",
    buttonType: "primary",
    disabled: disabledControls
  }, status === STATUS_VALIDATING ? 'Submitting' : 'Submit')));
}

MFAEnterCodeScreen.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Function to call when the cancel button is clicked
   */
  onCancel: PropTypes.func.isRequired,

  /**
   * Function to call when resend of the code is requested
   */
  resendCode: PropTypes.func.isRequired,

  /**
   * Function to call when code is submitted
   */
  verifyCode: PropTypes.func.isRequired,

  /**
   * Function to call when the different method button is clicked
   */
  onDifferentMethodClick: PropTypes.func.isRequired,

  /**
   * The method in which the code was sent
   */
  method: PropTypes.string.isRequired,

  /**
   * Function to call when the code has been verified
   */
  onSuccess: PropTypes.func.isRequired,

  /**
   * Function to call when the call results in an error
   */
  onError: PropTypes.func.isRequired,

  /**
   * The destination of the code
   */
  sentTo: PropTypes.string,

  /**
   * Clickstream
   * trigger object for interaction events
   * trigger source for impression/view events
   */
  clickstreamTrigger: PropTypes.string,
  inputSelector: PropTypes.string,
  headerText: PropTypes.string
} : {};

function AccountLockedScreen(props) {
  var onClose = props.onClose,
      alreadyLocked = props.alreadyLocked;
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(AccountLocked, {
    alreadyLocked: alreadyLocked
  }), /*#__PURE__*/React__default.createElement(ButtonGroup, {
    marginTop: "lg",
    direction: "row-reverse",
    "aria-label": "Account locked actions"
  }, /*#__PURE__*/React__default.createElement(Button, {
    type: "button",
    buttonType: "primary",
    onClick: onClose
  }, "Ok")));
}

AccountLockedScreen.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Function to call when the Ok button is clicked
   */
  onClose: PropTypes.func.isRequired,

  /**
   * If true, screen will show already locked message
   */
  alreadyLocked: PropTypes.bool
} : {};

function MfaCodeErrorScreen(props) {
  var onClose = props.onClose,
      onResend = props.onResend,
      _props$isResending = props.isResending,
      isResending = _props$isResending === void 0 ? false : _props$isResending;
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(VerificationCodeError, {
    onCodeResend: onResend,
    resendingIsDisabled: isResending
  }), /*#__PURE__*/React__default.createElement(ButtonGroup, {
    marginTop: "lg",
    direction: "row-reverse",
    "aria-label": "Account locked actions"
  }, /*#__PURE__*/React__default.createElement(Button, {
    type: "button",
    buttonType: "primary",
    onClick: onClose,
    disabled: isResending
  }, "Ok")));
}

MfaCodeErrorScreen.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Function to call when Cancel is clicked
   */
  onClose: PropTypes.func.isRequired,

  /**
   * Function to call when resend is clicked
   */
  onResend: PropTypes.func.isRequired,

  /**
   * If true, the send button will be disabled
   */
  isResending: PropTypes.bool
} : {};

function MfaSignIn(props) {
  var _MFA_SCREENS$methodCh;

  var sendCode = props.sendCode,
      verifyCode = props.verifyCode,
      onSuccess = props.onSuccess,
      onCancel = props.onCancel,
      onError = props.onError,
      _props$codeSent = props.codeSent,
      codeSent = _props$codeSent === void 0 ? false : _props$codeSent;

  var _React$useState = React__default.useState(codeSent ? MFA_SCREENS.codeEnter : MFA_SCREENS.methodChoose),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      screen = _React$useState2[0],
      setScreen = _React$useState2[1];

  var _React$useState3 = React__default.useState(MFA_TEXT_METHOD),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      method = _React$useState4[0],
      setMethod = _React$useState4[1];

  var _React$useState5 = React__default.useState(false),
      _React$useState6 = _slicedToArray(_React$useState5, 2),
      isSendingCode = _React$useState6[0],
      setIsSendingCode = _React$useState6[1];

  var _React$useState7 = React__default.useState(''),
      _React$useState8 = _slicedToArray(_React$useState7, 2),
      sentTo = _React$useState8[0],
      setSentTo = _React$useState8[1];

  var handleMethodConfirm = function handleMethodConfirm(newMethod, codeSentTo) {
    setMethod(newMethod);
    setSentTo(codeSentTo);
    setScreen(MFA_SCREENS.codeEnter);
  };

  var handleCodeSend = function handleCodeSend(newMethod) {
    return new Promise(function ($return, $error) {
      var response;
      setIsSendingCode(true);
      return Promise.resolve(sendCode(newMethod)).then(function ($await_1) {
        try {
          response = $await_1;
          setIsSendingCode(false);

          if (response.status === STATUS_ERROR_CODES.tooManyRequests.code) {
            setScreen(MFA_SCREENS.tooManyRequests);
          } else if (response.status === STATUS_ERROR_CODES.accountLocked.code) {
            setScreen(MFA_SCREENS.accountLocked);
          } else if (response.status === STATUS_ERROR_CODES.codeSent.code && screen === MFA_SCREENS.codeError) {
            setScreen(MFA_SCREENS.codeEnter);
          }

          return $return(response);
        } catch ($boundEx) {
          return $error($boundEx);
        }
      }, $error);
    });
  };

  var handleResendCode = function handleResendCode() {
    return handleCodeSend(method);
  };

  var handleDifferentMethodClick = function handleDifferentMethodClick() {
    setScreen(MFA_SCREENS.methodChoose);
  };

  var handleCodeError = function handleCodeError(response, body, error) {
    setScreen(MFA_SCREENS.codeError);
    onError(response, body, error);
  };

  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(Header, {
    text: screen.header
  }), /*#__PURE__*/React__default.createElement(BodyWrapper, null, (_MFA_SCREENS$methodCh = {}, _MFA_SCREENS$methodCh[MFA_SCREENS.methodChoose.id] = /*#__PURE__*/React__default.createElement(MFAMethodChooseScreen, {
    onMethodConfirm: handleMethodConfirm,
    sendCode: handleCodeSend,
    onCancel: onCancel
  }), _MFA_SCREENS$methodCh[MFA_SCREENS.codeEnter.id] = /*#__PURE__*/React__default.createElement(MFAEnterCodeScreen, {
    onSuccess: onSuccess,
    onError: handleCodeError,
    resendCode: handleResendCode,
    verifyCode: verifyCode,
    onCancel: onCancel,
    onDifferentMethodClick: handleDifferentMethodClick,
    method: method,
    sentTo: sentTo
  }), _MFA_SCREENS$methodCh[MFA_SCREENS.codeError.id] = /*#__PURE__*/React__default.createElement(MfaCodeErrorScreen, {
    onClose: onCancel,
    isResending: isSendingCode,
    onResend: handleResendCode
  }), _MFA_SCREENS$methodCh[MFA_SCREENS.tooManyRequests.id] = /*#__PURE__*/React__default.createElement(AccountLockedScreen, {
    onClose: onCancel
  }), _MFA_SCREENS$methodCh[MFA_SCREENS.accountLocked.id] = /*#__PURE__*/React__default.createElement(AccountLockedScreen, {
    onClose: onCancel,
    alreadyLocked: true
  }), _MFA_SCREENS$methodCh)[screen.id]));
}

MfaSignIn.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Function to send in the code
   */
  sendCode: PropTypes.func.isRequired,

  /**
   * Function to verify the code
   */
  verifyCode: PropTypes.func.isRequired,

  /**
   * Function to call once the user has signed in
   */
  onSuccess: PropTypes.func.isRequired,

  /**
   * Function to call when the sign in failed
   */
  onError: PropTypes.func.isRequired,

  /**
   * Function to call when MFA process is cancelled
   */
  onCancel: PropTypes.func.isRequired,
  // TODO: Remove once MFA V2 is deployed
  codeSent: PropTypes.bool
} : {};

var Mfa = /*#__PURE__*/function (_Component) {
  _inheritsLoose(Mfa, _Component);

  function Mfa(props) {
    var _this;

    _this = _Component.call(this, props) || this;
    _this.handleCodeSend = _this.handleCodeSend.bind(_assertThisInitialized(_this));
    _this.handleMfaSuccess = _this.handleMfaSuccess.bind(_assertThisInitialized(_this));
    _this.handleMfaError = _this.handleMfaError.bind(_assertThisInitialized(_this));
    _this.handleCodeVerify = _this.handleCodeVerify.bind(_assertThisInitialized(_this));
    _this.handleCancel = _this.handleCancel.bind(_assertThisInitialized(_this));
    return _this;
  }

  var _proto = Mfa.prototype;

  _proto.componentWillMount = function componentWillMount() {
    var _this$props = this.props,
        history = _this$props.history,
        phoneNumber = _this$props.phoneNumber; // User shouldn't land here without being on the Login view first.
    // TODO: Remove once MFA V2 is deployed

    if (!phoneNumber) {
      history.push(ROUTES.login);
    }
  };

  _proto.componentDidMount = function componentDidMount() {
    var _this$props2 = this.props,
        history = _this$props2.history,
        isLightbox = _this$props2.isLightbox,
        topicTags = _this$props2.topicTags;
    pageViewTrack(isLightbox)(history.location.pathname)({
      clickstreamTrigger: {
        triggerLocation: TRIGGER_LOCATION.MFAForm,
        triggerSource: TRIGGER_SOURCE.MFAForm
      },
      authForm: {
        title_nm: MFA_SCREENS.methodChoose.header
      },
      semantic: {
        topic_tag_txt: [AUTH_TOPIC_TAG.AccountLogin].concat(topicTags)
      }
    });
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    // Clear password field on leaving
    this.props.resetFields(['password'], ['email', 'newEmail']);
  };

  _proto.handleCodeSend = function handleCodeSend(method) {
    var _this$props3 = this.props,
        authAP = _this$props3.authAP,
        entryPoint = _this$props3.entryPoint,
        inputs = _this$props3.inputs,
        inputConfigs = _this$props3.inputConfigs;

    var _this$props$getInputP = this.props.getInputProps(inputConfigs, inputs),
        authToken = _this$props$getInputP.authToken,
        email = _this$props$getInputP.email,
        password = _this$props$getInputP.password;

    var loginFormData = new FormData();
    loginFormData.append('ap', authAP);
    loginFormData.append('authToken', authToken.value);
    loginFormData.append('email', email.value);
    loginFormData.append('password', password.value);
    loginFormData.append('sendMethod', method);
    loginFormData.append('entrypoint', entryPoint);
    return fetch$1(ROUTES.loginPost, {
      method: 'POST',
      credentials: 'same-origin',
      body: loginFormData
    });
  };

  _proto.handleCodeVerify = function handleCodeVerify(code, rememberTheDevice) {
    if (rememberTheDevice === void 0) {
      rememberTheDevice = false;
    }

    var _this$props4 = this.props,
        authAP = _this$props4.authAP,
        entryPoint = _this$props4.entryPoint,
        inputs = _this$props4.inputs,
        inputConfigs = _this$props4.inputConfigs;

    var _this$props$getInputP2 = this.props.getInputProps(inputConfigs, inputs),
        authToken = _this$props$getInputP2.authToken,
        email = _this$props$getInputP2.email,
        password = _this$props$getInputP2.password;

    var loginFormData = new FormData();
    loginFormData.append('ap', authAP);
    loginFormData.append('authToken', authToken.value);
    loginFormData.append('email', email.value);
    loginFormData.append('password', password.value);
    loginFormData.append('mfaCode', code);
    loginFormData.append('rememberDeviceForMfa', rememberTheDevice);
    loginFormData.append('entrypoint', entryPoint);
    return fetch$1(ROUTES.loginPost, {
      method: 'POST',
      credentials: 'same-origin',
      body: loginFormData
    });
  };

  _proto.handleMfaSuccess = function handleMfaSuccess(response, body) {
    var _this$props5 = this.props,
        onSuccess = _this$props5.onSuccess,
        redirectUrl = _this$props5.redirectUrl;
    this.props.handleSubmitFormResponse({
      res: response,
      err: null,
      body: body,
      options: {
        success: function success(res) {
          handleLogin({
            res: res,
            onSuccess: onSuccess,
            redirectUrl: redirectUrl
          });
        }
      }
    });
  };

  _proto.handleMfaError = function handleMfaError(response, body, error) {
    var _this2 = this;

    var _this$props6 = this.props,
        history = _this$props6.history,
        onFailure = _this$props6.onFailure;
    this.props.errorOnFormSubmitFailure(function (err, link) {
      if (onFailure) {
        onFailure(error);
      } else {
        _this2.props.handleLoginRedirect(history, link, err);
      }
    }, error, body);
  };

  _proto.handleCancel = function handleCancel() {
    var isLightbox = this.props.isLightbox;

    if (isLightbox === 'true') {
      hideRegLoginLightbox();
    } else {
      window.location.href = ROUTES.home;
    }
  };

  _proto.render = function render() {
    // TODO: Remove once MFA V2 is deployed
    var codeSent = this.props.phoneNumber !== 'Send Method expected now';
    return /*#__PURE__*/React__default.createElement(ViewWrapper, null, /*#__PURE__*/React__default.createElement(MfaSignIn, {
      verifyCode: this.handleCodeVerify,
      sendCode: this.handleCodeSend,
      onCancel: this.handleCancel,
      codeSent: codeSent,
      onSuccess: this.handleMfaSuccess,
      onError: this.handleMfaError
    }));
  };

  return Mfa;
}(Component);
Mfa.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Auth process used by the register endpoint for passwordless registrations
   */
  authAP: PropTypes.string,

  /**
   * Helper method for retrieving props from redux-inputs
   */
  getInputProps: PropTypes.func.isRequired,
  history: PropTypes.object,

  /**
   * input fields
   */
  inputs: PropTypes.object,

  /**
   * Input configs that might be altered subject
   * to AB tests or other external factors.
   */
  inputConfigs: PropTypes.object,

  /**
   * Indicates whether the RegLogin app is opened in a lightbox
   */
  isLightbox: PropTypes.string,

  /**
   * Function to execute when MFA has a failure
   */
  onFailure: PropTypes.func,

  /**
   * Function to execute after a successful MFA code
   */
  onSuccess: PropTypes.func,

  /**
   * Phone number used for MFA
   */
  phoneNumber: PropTypes.string,

  /**
   * Redirect URL used after login.
   */
  redirectUrl: PropTypes.string,

  /**
   * Redirect action
   */
  handleLoginRedirect: PropTypes.func.isRequired,
  resetFields: PropTypes.func,

  /**
   * Function to call when MFA is successfully enabled
   */
  handleSubmitFormResponse: PropTypes.func.isRequired,

  /**
   * Function to call when MFA is submitted with failure
   */
  errorOnFormSubmitFailure: PropTypes.func.isRequired,
  topicTags: PropTypes.arrayOf(PropTypes.string),
  entryPoint: PropTypes.string
} : {};
var mapDispatchToProps$8 = {
  getInputProps: getInputProps,
  errorOnFormSubmitFailure: errorOnFormSubmitFailure,
  handleLoginRedirect: handleLoginRedirect,
  resetFields: resetFields,
  handleSubmitFormResponse: handleSubmitFormResponse
};

var mapStateToProps$9 = function mapStateToProps(state) {
  return {
    inputs: state.inputs,
    inputConfigs: state.appConfig.inputConfigs,
    isLightbox: state.appConfig.isLightbox,
    onFailure: state.appConfig.onFailure,
    onSuccess: state.appConfig.onSuccess,
    // TODO: Remove once MFA V2 is deployed
    phoneNumber: state.login.phoneNumber,
    redirectUrl: state.regLogin.redirectUrl,
    topicTags: state.appConfig.topicTags,
    entryPoint: getEntryPoint(state.appConfig.entryPoint, state.regLogin.uiConfig.entryPoint)
  };
};

var MfaRoute = withRouter(connectToUserSession(mapStateToProps$9, mapDispatchToProps$8)(Mfa));

function _templateObject$6() {
  var data = _taggedTemplateLiteralLoose(["\n    margin-bottom: ", ";\n    border-bottom: 1px solid ", ";\n"]);

  _templateObject$6 = function _templateObject() {
    return data;
  };

  return data;
}
var StyledSpacer = styled.div(_templateObject$6(), spaceMixin('sm'), token$1('colors.gray300'));

var Zma = function Zma(_ref) {
  var initialFirstName = _ref.initialFirstName,
      initialLastName = _ref.initialLastName,
      _ref$zmaHandlerFns = _ref.zmaHandlerFns,
      zmaHandlerFns = _ref$zmaHandlerFns === void 0 ? {} : _ref$zmaHandlerFns;
  var getZMAConsent = zmaHandlerFns.getZMAConsent;

  var _useState = useState(typeof getZMAConsent !== 'function'),
      _useState2 = _slicedToArray(_useState, 2),
      shouldShowForm = _useState2[0],
      setShouldShowForm = _useState2[1]; // If getZMAConsent is set, wait to see if the user already has consent.
  // If they do, close the reg login lightbox. Otherwise, continue to return ZMA consent form.


  if (getZMAConsent && typeof getZMAConsent === 'function') {
    getZMAConsent().then(function (hasZMAConsent) {
      // If user has already consented, close the form
      if (hasZMAConsent) {
        hideRegLoginLightbox();
      } else {
        setShouldShowForm(true);
      }
    });
  }

  if (!shouldShowForm) {
    return null;
  }

  return /*#__PURE__*/React__default.createElement(ViewWrapper, null, /*#__PURE__*/React__default.createElement(StyledSpacer, null, /*#__PURE__*/React__default.createElement(Header, {
    text: "Zestimate\xAE Market Analysis"
  })), /*#__PURE__*/React__default.createElement(BodyWrapper, null, /*#__PURE__*/React__default.createElement(ZmaConsent, {
    initialFirstName: initialFirstName,
    initialLastName: initialLastName,
    zmaHandlerFns: zmaHandlerFns
  })));
};

Zma.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Handler for submitting name for ZMA consent
   */
  zmaHandlerFns: PropTypes.shape({
    getZMAConsent: PropTypes.func,
    setZMAConsent: PropTypes.func
  }).isRequired,

  /**
   * First name to prepopulate the field
   */
  initialFirstName: PropTypes.string,

  /**
   * Last name to prepopulate the field
   */
  initialLastName: PropTypes.string
} : {};
Zma.defaultProps = {
  initialFirstName: '',
  initialLastName: ''
};

var mapStateToProps$a = function mapStateToProps(state) {
  return {
    zmaHandlerFns: state.regLogin.zmaHandlerFns
  };
};

var ZmaRoute = connectToUserSession(mapStateToProps$a)(Zma);

/* eslint-disable zillow/react/no-array-index-key */

var CreatePasswordInput = function CreatePasswordInput(_ref) {
  var value = _ref.value,
      validationMessages = _ref.validationMessages,
      onChange = _ref.onChange;
  var validationMarkup = null;
  var passwordInstructionIds = null;
  var passwordValidation = false;

  if (validationMessages) {
    passwordValidation = true;
    validationMarkup = validationMessages.map(function (_ref2, i) {
      var validated = _ref2.validated,
          message = _ref2.message;
      passwordValidation = validated ? passwordValidation : false;
      var status = 'pass';

      if (!validated && value) {
        status = 'fail';
      } else if (!validated && !value) {
        status = 'neutral';
      }

      return message ? /*#__PURE__*/React__default.createElement("li", {
        key: i
      }, /*#__PURE__*/React__default.createElement(ValidationMessage, {
        text: message,
        status: status,
        keyCount: i
      })) : null;
    });
  }

  if (typeof document !== 'undefined') {
    var idList = [];

    if (document.getElementById('validation-text-2')) {
      idList.push('validation-text-2');
    }

    if (document.getElementById('validation-text-3')) {
      idList.push('validation-text-3');
    }

    if (document.getElementById('validation-text-4')) {
      idList.push('validation-text-4');
    }

    if (document.getElementById('validation-text-5')) {
      idList.push('validation-text-5');
    }

    passwordInstructionIds = idList.length ? idList.join(' ') : null;
  }

  return /*#__PURE__*/React__default.createElement(FormField, null, /*#__PURE__*/React__default.createElement(Label, {
    htmlFor: "newPassword"
  }, "Password"), /*#__PURE__*/React__default.createElement(AdornedInput, {
    input: /*#__PURE__*/React__default.createElement(Input, {
      type: "password",
      id: "newPassword",
      placeholder: "Create password",
      errorText: "",
      onChange: onChange,
      defaultValue: value,
      "aria-describedby": passwordInstructionIds,
      required: true
    }),
    rightAdornment: /*#__PURE__*/React__default.createElement(Adornment, {
      className: "adornment"
    }, /*#__PURE__*/React__default.createElement(StrengthMeter, {
      password: value,
      validated: passwordValidation
    }))
  }), /*#__PURE__*/React__default.createElement("ul", {
    className: "validation-messages"
  }, validationMarkup));
};

CreatePasswordInput.propTypes = process.env.NODE_ENV !== "production" ? {
  value: PropTypes.string.isRequired,
  // eslint-disable-next-line zillow/react/forbid-prop-types
  validationMessages: PropTypes.array,
  onChange: PropTypes.func.isRequired
} : {};

var CreatePassword = /*#__PURE__*/function (_Component) {
  _inheritsLoose(CreatePassword, _Component);

  function CreatePassword(props) {
    var _this;

    _this = _Component.call(this, props) || this;

    _this.handleClick = function (e) {
      var _e$nativeEvent;

      _this.setState({
        inputSelector: getInputSelector((_e$nativeEvent = e.nativeEvent) === null || _e$nativeEvent === void 0 ? void 0 : _e$nativeEvent.detail)
      });
    };

    props.resetPasswordValidation(PASSWORD_VALIDATORS);
    _this.state = {
      newPassword: '',
      confirmPassword: '',
      errorText: '',
      inputSelector: INPUT_SELECTOR.Other
    };
    _this.pageTrackingProps = {
      clickstreamTrigger: {
        triggerLocation: TRIGGER_LOCATION.CreatePasswordForm,
        triggerSource: TRIGGER_SOURCE.CreatePasswordForm
      },
      authForm: {
        title_nm: props.headerText
      },
      semantic: {
        topic_tag_txt: props.topicTags
      }
    };
    return _this;
  }

  var _proto = CreatePassword.prototype;

  _proto.validatePasswords = function validatePasswords(tempNewPassword, tempConfirmPassword) {
    if (tempNewPassword === void 0) {
      tempNewPassword = null;
    }

    if (tempConfirmPassword === void 0) {
      tempConfirmPassword = null;
    }

    var _this$props = this.props,
        validatePassword = _this$props.validatePassword,
        inputs = _this$props.inputs;
    var newPassword = tempNewPassword || this.state.newPassword;
    var confirmPassword = tempConfirmPassword || this.state.confirmPassword; // Validate password against the validator - this will update the rules display if needed

    if (!validatePassword(newPassword, inputs)) {
      return false;
    } // Make sure that password and confirmPassword are the same


    if (newPassword !== confirmPassword) {
      // Let's only show the error that the passwords don't match if this came from a submit action
      if (tempNewPassword === null && tempConfirmPassword === null) {
        this.setState({
          errorText: 'Passwords do not match'
        });
      }

      return false;
    } // Reset error message


    this.setState({
      errorText: ''
    });
    return true;
  };

  _proto.handleSubmit = function handleSubmit() {
    var _this2 = this;

    var _this$props2 = this.props,
        authToken = _this$props2.authToken,
        redirectUrl = _this$props2.redirectUrl,
        zmaHandlerFns = _this$props2.zmaHandlerFns,
        gaLabel = _this$props2.gaLabel; // Only continue on to submit if valid

    if (!this.validatePasswords()) {
      return;
    }

    this.props.submitForm({
      path: ROUTES.confirmEmailPost,
      data: {
        token: authToken,
        password: this.state.newPassword
      },
      success: function success() {
        track({
          category: 'auth',
          action: 'passwordsubmit//',
          label: gaLabel
        }, {
          newLaneEvent: getPasswordSubmitNewLaneProps(mergeNewLaneProps(_this2.pageTrackingProps, {
            clickstreamTrigger: {
              triggerSource: 'button_to_create_password',
              inputSelector: _this2.state.inputSelector
            },
            authForm: {
              displayed_button_txt: BUTTON_TYPES.Submit.value
            }
          }))
        });

        if ((zmaHandlerFns === null || zmaHandlerFns === void 0 ? void 0 : zmaHandlerFns.setZMAConsent) && typeof zmaHandlerFns.setZMAConsent === 'function') {
          handleLoggedIn(true, {}, '');
          setRegLoginPath('/user/acct/zma');
        } else {
          handleLoggedIn(false, {}, redirectUrl);
        }
      }
    });
  };

  _proto.render = function render() {
    var _this3 = this;

    var _this$props3 = this.props,
        newPasswordValidationMessages = _this$props3.newPasswordValidationMessages,
        headerText = _this$props3.headerText,
        descriptionText = _this$props3.descriptionText,
        authToken = _this$props3.authToken;
    return /*#__PURE__*/React__default.createElement(ViewWrapper, {
      className: "create-password"
    }, /*#__PURE__*/React__default.createElement(Header, {
      text: headerText
    }, descriptionText), /*#__PURE__*/React__default.createElement(BodyWrapper, null, /*#__PURE__*/React__default.createElement(Form, {
      onSubmit: function onSubmit(e) {
        e.preventDefault();

        _this3.handleSubmit();
      }
    }, /*#__PURE__*/React__default.createElement(Input, {
      type: "hidden",
      value: authToken
    }), /*#__PURE__*/React__default.createElement(CreatePasswordInput, {
      onChange: function onChange(e) {
        _this3.setState({
          newPassword: e.target.value
        });

        _this3.validatePasswords(e.target.value);
      },
      validationMessages: newPasswordValidationMessages,
      value: this.state.newPassword
    }), /*#__PURE__*/React__default.createElement(FormField, null, /*#__PURE__*/React__default.createElement(Label, {
      htmlFor: "confirmPassword"
    }, "Confirm password"), /*#__PURE__*/React__default.createElement(Input, {
      type: "password",
      id: "confirmPassword",
      placeholder: "Confirm password",
      onChange: function onChange(e) {
        _this3.setState({
          confirmPassword: e.target.value
        });

        _this3.validatePasswords(null, e.target.value);
      },
      defaultValue: this.state.confirmPassword,
      required: true
    })), /*#__PURE__*/React__default.createElement(FormActions, {
      className: "form-actions"
    }, /*#__PURE__*/React__default.createElement(Button, {
      buttonType: "primary",
      as: "input",
      type: "submit",
      fluid: true,
      onClick: this.handleClick
    }, "Submit")), this.state.errorText && /*#__PURE__*/React__default.createElement(FormHelpError, {
      text: this.state.errorText
    }), /*#__PURE__*/React__default.createElement(TermsOfUseBlock, null))));
  };

  return CreatePassword;
}(Component);
CreatePassword.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Text that appears in the header of the page
   */
  headerText: PropTypes.string,

  /**
   * Text that appears in the subheader of the page
   */
  descriptionText: PropTypes.string,

  /**
   * Auth token needed for the register/auth endpoint
   */
  authToken: PropTypes.string.isRequired,

  /**
   * GA Label for our events
   */
  gaLabel: PropTypes.string.isRequired,

  /**
   * Validation Messages for the password
   */
  newPasswordValidationMessages: PropTypes.array,

  /**
   * Input values
   */
  inputs: PropTypes.object,

  /**
   * Function to submit password form
   */
  submitForm: PropTypes.func,

  /**
   * Function used to reset the password validation
   */
  resetPasswordValidation: PropTypes.func,

  /**
   * Handle for zma consent
   */
  zmaHandlerFns: PropTypes.shape({
    getZMAConsent: PropTypes.func,
    setZMAConsent: PropTypes.func
  }),

  /**
   * RedirectUrl for after login
   */
  redirectUrl: PropTypes.string,

  /**
   * Function to validate the password meets requirements
   */
  validatePassword: PropTypes.func,
  topicTags: PropTypes.arrayOf(PropTypes.string)
} : {};
CreatePassword.defaultProps = {
  headerText: 'Create Password',
  descriptionText: ''
};

var mapDispatchToProps$9 = function mapDispatchToProps(dispatch) {
  return Object.assign({
    dispatch: dispatch
  }, bindActionCreators({
    submitForm: submitForm,
    resetPasswordValidation: resetPasswordValidation
  }, dispatch), {
    validatePassword: function validatePassword(input, inputs) {
      return PASSWORD_VALIDATOR(input, inputs, null, dispatch);
    }
  });
};

var mapStateToProps$b = function mapStateToProps(state) {
  return {
    authToken: state.user.authToken,
    redirectUrl: state.regLogin.redirectUrl,
    gaLabel: state.regLogin.uiConfig.gaLabel,
    newPasswordValidationMessages: state.login.newPasswordValidationMessages,
    inputs: state.inputs,
    zmaHandlerFns: state.regLogin.zmaHandlerFns,
    topicTags: state.appConfig.topicTags
  };
};

var CreatePasswordRoute = connectToUserSession(mapStateToProps$b, mapDispatchToProps$9)(CreatePassword);

var VERIFY_STATUS = {
  LOADING: {
    HEADER: 'Verifying your account...',
    SUBHEADER: 'Stay on this page while we verify your account',
    IMAGE_URL: 'https://www.zillowstatic.com/s3/homepage/static/Rent_a_home.png',
    IMAGE_ALT: 'Verifying your account'
  },
  SUCCESS: {
    HEADER: 'Success. Account Verified',
    SUBHEADER: 'You will be re-directed to your last page',
    IMAGE_URL: 'https://www.zillowstatic.com/s3/homepage/static/Buy_a_home.png',
    IMAGE_ALT: 'Account verified'
  },
  ERROR: {
    HEADER: 'Uh oh, something went wrong.',
    BUTTON_TEXT: 'Try again',
    IMAGE_URL: 'https://www.zillowstatic.com/s3/homepage/static/Buy_a_home.png',
    IMAGE_ALT: 'Error verifying account'
  }
};
var REDIRECT_NOW_BUTTON = 'Redirect now';
var TRY_AGAIN = 'Try again';
var REDIRECT_TIMEOUT_DURATION = 5000;

function _templateObject$7() {
  var data = _taggedTemplateLiteralLoose(["\n    max-width: 100%;\n    margin-top: ", ";\n"]);

  _templateObject$7 = function _templateObject() {
    return data;
  };

  return data;
}
var StyledImage = styled(Image)(_templateObject$7(), spaceMixin('lg'));
var params = new URL(window.location).searchParams;
var redirectURL = decodeURI(params.get('redirectURL'));
var token = params.get('token');

function VerifyAccount(_ref) {
  var gaLabel = _ref.gaLabel;

  var _React$useState = React__default.useState(VERIFY_STATUS.LOADING),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      status = _React$useState2[0],
      setStatus = _React$useState2[1];

  var verifyAccount = function verifyAccount() {
    return new Promise(function ($return, $error) {
      var formData, response;

      if (!token) {
        setStatus(VERIFY_STATUS.ERROR);
        return $return();
      }

      var $Try_1_Post = function () {
        try {
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      var $Try_1_Catch = function (error) {
        try {
          track({
            action: 'verifyemail/error/',
            category: 'auth',
            label: gaLabel
          }, {
            newLaneEvent: getVerifyEmailErrorNewLaneProps({
              authForm: {
                title_nm: VERIFY_STATUS.ERROR.HEADER
              }
            })
          });
          setStatus(VERIFY_STATUS.ERROR);
          return $Try_1_Post();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      try {
        formData = new FormData();
        formData.append('emailToken', token);
        return Promise.resolve(fetch(ROUTES.verifyPost, {
          method: 'POST',
          body: formData
        })).then(function ($await_2) {
          try {
            response = $await_2;

            if (!response.ok) {
              throw response;
            }

            track({
              action: 'verifyemail/complete/',
              category: 'auth',
              label: gaLabel
            }, {
              newLaneEvent: getVerifyEmailCompleteNewLaneProps({
                authForm: {
                  title_nm: VERIFY_STATUS.SUCCESS.HEADER
                }
              })
            });
            setStatus(VERIFY_STATUS.SUCCESS);
            return $Try_1_Post();
          } catch ($boundEx) {
            return $Try_1_Catch($boundEx);
          }
        }, $Try_1_Catch);
      } catch (error) {
        $Try_1_Catch();
      }
    });
  };

  React__default.useEffect(function () {
    var timeout;

    if (status === VERIFY_STATUS.SUCCESS) {
      timeout = setTimeout(function () {
        window.location.href = redirectURL;
      }, REDIRECT_TIMEOUT_DURATION);
    }

    return function () {
      clearTimeout(timeout);
    };
  }, [status]);
  React__default.useEffect(verifyAccount, []);

  var handleTryAgain = function handleTryAgain() {
    setStatus(VERIFY_STATUS.LOADING);
    verifyAccount();
  };

  return /*#__PURE__*/React__default.createElement(Flex, {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginY: "md",
    marginX: "sm"
  }, /*#__PURE__*/React__default.createElement(Heading, {
    level: "3",
    fontFamily: "serif",
    fontType: {
      default: 'h4',
      md: 'h3'
    }
  }, status === VERIFY_STATUS.SUCCESS && /*#__PURE__*/React__default.createElement(IconCheckmarkCircleOutline, {
    fontColor: "green500",
    marginRight: "xs"
  }), status === VERIFY_STATUS.LOADING && /*#__PURE__*/React__default.createElement(Spinner, {
    marginRight: "xs"
  }), status.HEADER), status.SUBHEADER && /*#__PURE__*/React__default.createElement(Paragraph, {
    fontType: {
      default: 'h6',
      md: 'h5'
    },
    marginTop: "xs"
  }, status.SUBHEADER), status === VERIFY_STATUS.SUCCESS && /*#__PURE__*/React__default.createElement(Button, {
    as: "a",
    buttonType: "primary",
    href: redirectURL,
    marginTop: "md"
  }, REDIRECT_NOW_BUTTON), status === VERIFY_STATUS.ERROR && /*#__PURE__*/React__default.createElement(Button, {
    buttonType: "primary",
    onClick: handleTryAgain,
    marginTop: "md"
  }, TRY_AGAIN), /*#__PURE__*/React__default.createElement(StyledImage, {
    alt: status.IMAGE_ALT,
    src: status.IMAGE_URL
  }));
}

VerifyAccount.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * GA Label for our events
   */
  gaLabel: PropTypes.string.isRequired
} : {};

var HEADER_TEXT = 'Almost done';
var SUBHEADER_TEXT = 'Verify your email to get started by confirming that you are the owner. Click the link in the email we just sent you.';
var EMAIL_SENT_TO = 'Email sent to: ';

function VerifyOnRegisterScreen(props) {
  // const [error, setError] = React.useState('');
  // const [isLoading, setIsLoading] = React.useState();
  var registerInfo = props.getInputProps(props.inputConfigs, props.inputs);
  var newEmail = registerInfo.newEmail; // const handleEmailResend = () => {
  //     setIsLoading(true);
  //     setError('');
  //
  //     // Call API
  // };

  useEffect(function () {
    track({
      action: 'verifyemail/send/',
      category: 'auth',
      label: props.gaLabel
    }, {
      newLaneEvent: getVerifyEmailSendNewLaneProps({
        authForm: {
          title_nm: HEADER_TEXT
        }
      })
    });
  }, [props.gaLabel]);
  return /*#__PURE__*/React__default.createElement(ViewWrapper, null, /*#__PURE__*/React__default.createElement(Header, {
    text: HEADER_TEXT
  }, SUBHEADER_TEXT), /*#__PURE__*/React__default.createElement(Flex, {
    alignItems: "center",
    flexDirection: "column",
    display: "flex"
  }, /*#__PURE__*/React__default.createElement(Text, null, EMAIL_SENT_TO, /*#__PURE__*/React__default.createElement("strong", null, newEmail.value))));
}

VerifyOnRegisterScreen.propTypes = process.env.NODE_ENV !== "production" ? {
  getInputProps: PropTypes.func.isRequired,
  // eslint-disable-next-line zillow/react/forbid-prop-types
  inputs: PropTypes.object,
  // eslint-disable-next-line zillow/react/forbid-prop-types
  inputConfigs: PropTypes.object,

  /**
   * GA Label for our events
   */
  gaLabel: PropTypes.string.isRequired
} : {};

var mapStateToProps$c = function mapStateToProps(state) {
  return {
    redirectUrl: state.regLogin.redirectUrl,
    inputs: state.inputs,
    inputConfigs: state.appConfig.inputConfigs
  };
};

var mapDispatchToProps$a = {
  getInputProps: getInputProps,
  handleSubmitFormResponse: handleSubmitFormResponse
};
var VerifyOnRegisterRoute = withRouter(connectToUserSession(mapStateToProps$c, mapDispatchToProps$a)(VerifyOnRegisterScreen));

var OTP_SCREENS = {
  WELCOME: 'WELCOME',
  CHOOSE_SIGN_IN_METHOD: 'CHOOSE_SIGN_IN_METHOD',
  SENDING_CODE: 'SENDING_CODE',
  ENTER_CODE: 'ENTER_CODE',
  PASSWORD_SIGN_IN: 'PASSWORD_SIGN_IN',
  CHOOSE_REGISTER_METHOD: 'CHOOSE_REGISTER_METHOD',
  CREATE_PASSWORD: 'CREATE_PASSWORD',
  EMAIL_SIGN_UP: 'EMAIL_SIGN_UP'
};
var ACTIONS = {
  START_FLOW: 'START_FLOW',
  CHECK_EMAIL_LOADING: 'CHECK_EMAIL_LOADING',
  CHECK_EMAIL_SUCCESS: 'CHECK_EMAIL_SUCCESS',
  CHOOSE_SIGN_IN_METHOD: 'CHOOSE_SIGN_IN_METHOD',
  USE_PASSWORD: 'USE_PASSWORD',
  SET_IS_LOADING: 'SET_IS_LOADING',
  CHANGE_EMAIL: 'CHANGE_EMAIL',
  SEND_CODE: 'SEND_CODE',
  RESEND_CODE: 'RESEND_CODE',
  CODE_SENT: 'CODE_SENT',
  SET_ERROR: 'SET_ERROR',
  SET_EMAIL_SUGGESTION: 'SET_EMAIL_SUGGESTION',
  SET_IS_REGISTER: 'SET_IS_REGISTER'
};
var CHECK_EMAIL_RETURN_CODES = {
  DuplicateEmail: {
    id: 'DuplicateEmail',
    message: 'Email already exists'
  },
  EmailSuggestion: {
    id: 'EmailSuggestion',
    message: 'Did you mean'
  },
  InvalidEmail: {
    id: 'InvalidEmail',
    message: 'Enter a valid email address'
  },
  GenericError: {
    message: 'There was an error while checking your email'
  }
};
var OTP_ERROR_MESSAGES = {
  TOO_MANY_REQUESTS: {
    code: 429,
    message: 'Too many codes have been requested. Try again in 15 minutes.'
  },
  GENERIC: {
    message: 'There was an error sending the code'
  },
  INVALID_CODE: {
    message: 'Incorrect code, please try again. If the code you entered matches the code in the email, click “Resend” to have a new code sent.'
  }
};

var LOGIN_RESPONSE_CODES = {
  SUCCESS: 'SUCCESS',
  HAS_CAPTCHA: 'HAS_CAPTCHA',
  HAS_MFA: 'HAS_MFA'
};
var REGISTER_RESPONSE_CODES = {
  SUCCESS: 'SUCCESS',
  HAS_CAPTCHA: 'HAS_CAPTCHA'
};

/* eslint-disable no-throw-literal */
function checkEmail(email) {
  return new Promise(function ($return, $error) {
    var formData, response, responseData, _responseData$key, type, _responseData$value, suggestedEmail, responseBody;

    formData = new FormData();
    formData.append('email', email);

    var $Try_1_Catch = function (e) {
      try {
        if (e.body) {
          return Promise.resolve(e.json()).then(function ($await_12) {
            try {
              responseBody = $await_12;
              throw {
                key: responseBody.key,
                isFreePass: !!responseBody.isFreePass
              };
            } catch ($boundEx) {
              return $error($boundEx);
            }
          }.bind(this), $error);
        }

        throw Error(e);
      } catch ($boundEx) {
        return $error($boundEx);
      }
    }.bind(this);

    try {
      return Promise.resolve(fetch(ROUTES.emailCheck, {
        method: 'POST',
        body: formData
      })).then(function ($await_13) {
        try {
          response = $await_13;

          if (!response.ok) {
            throw response;
          }

          if (response.status !== 211) {
            return $return({});
          }

          return Promise.resolve(response.text()).then(function ($await_14) {
            try {
              responseData = JSON.parse($await_14);
              _responseData$key = responseData.key, type = _responseData$key === void 0 ? '' : _responseData$key, _responseData$value = responseData.value, suggestedEmail = _responseData$value === void 0 ? '' : _responseData$value;

              if (type === 'EmailSuggestion') {
                return $return({
                  suggestion: suggestedEmail
                });
              }

              return $return({});
            } catch ($boundEx) {
              return $Try_1_Catch($boundEx);
            }
          }, $Try_1_Catch);
        } catch ($boundEx) {
          return $Try_1_Catch($boundEx);
        }
      }, $Try_1_Catch);
    } catch (e) {
      $Try_1_Catch(e);
    }
  });
}
function sendOTP(_ref) {
  return new Promise(function ($return, $error) {
    var email, entryPoint, formData, response;
    email = _ref.email, entryPoint = _ref.entryPoint;
    formData = new FormData();
    formData.append('email', email);
    formData.append('entrypoint', entryPoint);

    var $Try_2_Post = function () {
      try {
        return $return();
      } catch ($boundEx) {
        return $error($boundEx);
      }
    };

    var $Try_2_Catch = function (e) {
      try {
        if (e.json) {
          // eslint-disable-next-line no-throw-literal
          throw {
            status: e.status
          };
        }

        throw Error(e);
      } catch ($boundEx) {
        return $error($boundEx);
      }
    };

    try {
      return Promise.resolve(fetch(ROUTES.sendOTP, {
        method: 'POST',
        body: formData
      })).then(function ($await_15) {
        try {
          response = $await_15;

          if (!response.ok) {
            throw response;
          }

          return $Try_2_Post();
        } catch ($boundEx) {
          return $Try_2_Catch($boundEx);
        }
      }, $Try_2_Catch);
    } catch (e) {
      $Try_2_Catch(e);
    }
  });
}
function login(_ref2) {
  return new Promise(function ($return, $error) {
    var email, password, authProcess, entryPoint, formData, _response$url, response, hasCaptcha, redirectURI, data, hasMFA, errorBody;

    email = _ref2.email, password = _ref2.password, authProcess = _ref2.authProcess, entryPoint = _ref2.entryPoint;
    formData = new FormData();
    formData.append('email', email);
    formData.append('ap', authProcess);
    formData.append('password', password);
    formData.append('entrypoint', entryPoint !== null && entryPoint !== void 0 ? entryPoint : 'reglogin');

    var $Try_3_Post = function () {
      try {
        return $return();
      } catch ($boundEx) {
        return $error($boundEx);
      }
    };

    var $Try_3_Catch = function (e) {
      try {
        if (e.json) {
          return Promise.resolve(e.json()).then(function ($await_16) {
            try {
              errorBody = $await_16;
              throw {
                status: e.status,
                error: errorBody
              };
            } catch ($boundEx) {
              return $error($boundEx);
            }
          }.bind(this), $error);
        } else {
          throw {
            error: e
          };
        }

        function $If_8() {
          return $Try_3_Post();
        }
      } catch ($boundEx) {
        return $error($boundEx);
      }
    }.bind(this);

    try {
      return Promise.resolve(fetch(ROUTES.loginPost, {
        method: 'POST',
        body: formData
      })).then(function ($await_17) {
        try {
          response = $await_17;

          if (!response.ok) {
            throw response;
          }

          hasCaptcha = response.status === 200 && (response === null || response === void 0 ? void 0 : (_response$url = response.url) === null || _response$url === void 0 ? void 0 : _response$url.includes('captchaPerimeterX'));

          if (hasCaptcha) {
            redirectURI = decodeURIComponent(response.url);
            return $return({
              code: LOGIN_RESPONSE_CODES.HAS_CAPTCHA,
              redirectURI: redirectURI
            });
          }

          return Promise.resolve(response.json()).then(function ($await_18) {
            try {
              data = $await_18;
              hasMFA = response.status === 209 || response.status === 210;

              if (hasMFA) {
                return $return({
                  code: LOGIN_RESPONSE_CODES.HAS_MFA,
                  phoneNumber: data.message
                });
              }

              return $return({
                code: LOGIN_RESPONSE_CODES.SUCCESS,
                data: data
              });
            } catch ($boundEx) {
              return $Try_3_Catch($boundEx);
            }
          }, $Try_3_Catch);
        } catch ($boundEx) {
          return $Try_3_Catch($boundEx);
        }
      }, $Try_3_Catch);
    } catch (e) {
      $Try_3_Catch(e);
    }
  });
} // This function might need to be merged with the one above to handle MFA once the API is ready

function otpLogin(_ref3) {
  return new Promise(function ($return, $error) {
    var email, otp, authProcess, entryPoint, formData, response, data, errorBody;
    email = _ref3.email, otp = _ref3.otp, authProcess = _ref3.authProcess, entryPoint = _ref3.entryPoint;
    formData = new FormData();
    formData.append('email', email);
    formData.append('ap', authProcess);
    formData.append('otp', otp);
    formData.append('entrypoint', entryPoint !== null && entryPoint !== void 0 ? entryPoint : 'reglogin');

    var $Try_4_Post = function () {
      try {
        return $return();
      } catch ($boundEx) {
        return $error($boundEx);
      }
    };

    var $Try_4_Catch = function (e) {
      try {
        if (e.json) {
          return Promise.resolve(e.json()).then(function ($await_19) {
            try {
              errorBody = $await_19;
              throw {
                status: e.status,
                error: errorBody
              };
            } catch ($boundEx) {
              return $error($boundEx);
            }
          }.bind(this), $error);
        } else {
          throw {
            error: e
          };
        }

        function $If_9() {
          return $Try_4_Post();
        }
      } catch ($boundEx) {
        return $error($boundEx);
      }
    }.bind(this);

    try {
      return Promise.resolve(fetch(ROUTES.OTPLogin, {
        method: 'POST',
        body: formData
      })).then(function ($await_20) {
        try {
          response = $await_20;

          if (!response.ok) {
            throw response;
          }

          return Promise.resolve(response.json()).then(function ($await_21) {
            try {
              data = $await_21;
              return $return({
                code: LOGIN_RESPONSE_CODES.SUCCESS,
                data: data
              });
            } catch ($boundEx) {
              return $Try_4_Catch($boundEx);
            }
          }, $Try_4_Catch);
        } catch ($boundEx) {
          return $Try_4_Catch($boundEx);
        }
      }, $Try_4_Catch);
    } catch (e) {
      $Try_4_Catch(e);
    }
  });
}
function register(_ref4) {
  return new Promise(function ($return, $error) {
    var email, password, authProcess, registerCountryCode, redirectURL, formData, response, data, errorBody;
    email = _ref4.email, password = _ref4.password, authProcess = _ref4.authProcess, registerCountryCode = _ref4.registerCountryCode, redirectURL = _ref4.redirectURL;
    formData = new FormData();
    formData.append('email', email);
    formData.append('password', password);
    formData.append('ap', authProcess);
    formData.append('registerCountryCode', registerCountryCode);
    formData.append('redirectURL', redirectURL);
    formData.append('suppressredirect', true);

    var $Try_5_Post = function () {
      try {
        return $return();
      } catch ($boundEx) {
        return $error($boundEx);
      }
    };

    var $Try_5_Catch = function (e) {
      try {
        if (e.json) {
          return Promise.resolve(e.json()).then(function ($await_22) {
            try {
              errorBody = $await_22;
              throw {
                status: e.status,
                error: errorBody
              };
            } catch ($boundEx) {
              return $error($boundEx);
            }
          }.bind(this), $error);
        } else {
          throw {
            error: e
          };
        }

        function $If_10() {
          return $Try_5_Post();
        }
      } catch ($boundEx) {
        return $error($boundEx);
      }
    }.bind(this);

    try {
      return Promise.resolve(fetch(ROUTES.registerPost, {
        method: 'POST',
        body: formData
      })).then(function ($await_23) {
        try {
          response = $await_23;

          if (!response.ok) {
            throw response;
          }

          return Promise.resolve(response.json()).then(function ($await_24) {
            try {
              data = $await_24;
              return $return({
                code: REGISTER_RESPONSE_CODES.SUCCESS,
                data: data
              });
            } catch ($boundEx) {
              return $Try_5_Catch($boundEx);
            }
          }, $Try_5_Catch);
        } catch ($boundEx) {
          return $Try_5_Catch($boundEx);
        }
      }, $Try_5_Catch);
    } catch (e) {
      $Try_5_Catch(e);
    }
  });
}
function otpRegister(_ref5) {
  return new Promise(function ($return, $error) {
    var email, otp, _ref5$authProcess, authProcess, registerCountryCode, formData, response, data, errorBody;

    email = _ref5.email, otp = _ref5.otp, _ref5$authProcess = _ref5.authProcess, authProcess = _ref5$authProcess === void 0 ? '' : _ref5$authProcess, registerCountryCode = _ref5.registerCountryCode;
    formData = new FormData();
    formData.append('email', email);
    formData.append('ap', authProcess);
    formData.append('otp', otp);
    formData.append('registerCountryCode', registerCountryCode);
    formData.append('suppressredirect', true);

    var $Try_6_Post = function () {
      try {
        return $return();
      } catch ($boundEx) {
        return $error($boundEx);
      }
    };

    var $Try_6_Catch = function (e) {
      try {
        if (e.json) {
          return Promise.resolve(e.json()).then(function ($await_25) {
            try {
              errorBody = $await_25;
              throw {
                status: e.status,
                error: errorBody
              };
            } catch ($boundEx) {
              return $error($boundEx);
            }
          }.bind(this), $error);
        } else {
          throw {
            error: e
          };
        }

        function $If_11() {
          return $Try_6_Post();
        }
      } catch ($boundEx) {
        return $error($boundEx);
      }
    }.bind(this);

    try {
      return Promise.resolve(fetch(ROUTES.OTPRegister, {
        method: 'POST',
        body: formData
      })).then(function ($await_26) {
        try {
          response = $await_26;

          if (!response.ok) {
            throw response;
          }

          return Promise.resolve(response.json()).then(function ($await_27) {
            try {
              data = $await_27;
              return $return({
                code: REGISTER_RESPONSE_CODES.SUCCESS,
                data: data
              });
            } catch ($boundEx) {
              return $Try_6_Catch($boundEx);
            }
          }, $Try_6_Catch);
        } catch ($boundEx) {
          return $Try_6_Catch($boundEx);
        }
      }, $Try_6_Catch);
    } catch (e) {
      $Try_6_Catch(e);
    }
  });
}

/**
 * useOTPFlow hook
 * @param { Object } useOTPFlowProps
 * @param { Function } userOTPFlowProps.login - login function
 * @param { Function } userOTPFlowProps.register - register function
 * @param { string } userOTPFlowProps.defaultEmail - default email
 * @returns {{
 *     state: OTPFlowState,
 *     handleChangeEmail: Function,
 *     handleUsePasswordSignIn: Function,
 *     handleEmailContinue: Function,
 *     handleEmailBlur: Function,
 *     handleSendCode: Function,
 *     handlePasswordSignInSubmit: Function,
 *     handleCodeSubmit: Function,
 *     handleOTPResend: Function,
 * }}
 */

function useOTPFlow(_ref) {
  var login = _ref.login,
      register = _ref.register,
      _ref$defaultEmail = _ref.defaultEmail,
      defaultEmail = _ref$defaultEmail === void 0 ? '' : _ref$defaultEmail,
      _ref$entryPoint = _ref.entryPoint,
      entryPoint = _ref$entryPoint === void 0 ? 'reglogin' : _ref$entryPoint;
  var initialState = {
    screen: OTP_SCREENS.WELCOME,
    email: defaultEmail,
    error: '',
    emailSuggestion: '',
    isRegister: false,
    isLoading: false,
    isResending: false
  };

  function reducer(state, action) {
    switch (action.type) {
      case ACTIONS.START_FLOW:
        return initialState;

      case ACTIONS.CHECK_EMAIL_LOADING:
        return Object.assign({}, state, {
          email: action.payload,
          emailSuggestion: '',
          isLoading: true
        });

      case ACTIONS.SET_EMAIL_SUGGESTION:
        return Object.assign({}, state, {
          emailSuggestion: action.payload,
          isLoading: false,
          error: ''
        });

      case ACTIONS.SET_ERROR:
        var isSendingCode = state.screen === OTP_SCREENS.SENDING_CODE;
        var newState = Object.assign({}, state, {
          error: action.payload,
          isLoading: false,
          isResending: false
        });
        return isSendingCode ? Object.assign({}, newState, {
          screen: OTP_SCREENS.ENTER_CODE
        }) : newState;

      case ACTIONS.SET_IS_REGISTER:
        return Object.assign({}, state, {
          isRegister: action.payload
        });

      case ACTIONS.SET_IS_LOADING:
        return Object.assign({}, state, {
          error: '',
          isLoading: true
        });

      case ACTIONS.CHOOSE_SIGN_IN_METHOD:
        return Object.assign({}, state, {
          screen: OTP_SCREENS.CHOOSE_SIGN_IN_METHOD,
          isLoading: false,
          isRegister: false
        });

      case ACTIONS.CHANGE_EMAIL:
        return Object.assign({}, state, {
          email: '',
          screen: OTP_SCREENS.WELCOME,
          error: ''
        });

      case ACTIONS.SEND_CODE:
        return Object.assign({}, state, {
          screen: OTP_SCREENS.SENDING_CODE,
          error: ''
        });

      case ACTIONS.RESEND_CODE:
        return Object.assign({}, state, {
          error: '',
          isResending: true
        });

      case ACTIONS.CODE_SENT:
        return Object.assign({}, state, {
          screen: OTP_SCREENS.ENTER_CODE,
          isLoading: false,
          isResending: false
        });

      case ACTIONS.USE_PASSWORD:
        return Object.assign({}, state, {
          screen: state.isRegister ? OTP_SCREENS.CREATE_PASSWORD : OTP_SCREENS.PASSWORD_SIGN_IN,
          error: ''
        });

      default:
        return initialState;
    }
  }

  var _React$useReducer = React__default.useReducer(reducer, initialState),
      _React$useReducer2 = _slicedToArray(_React$useReducer, 2),
      state = _React$useReducer2[0],
      dispatch = _React$useReducer2[1];

  var sendCode = function sendCode(email) {
    return new Promise(function ($return, $error) {
      dispatch({
        type: ACTIONS.SEND_CODE
      });

      var $Try_1_Post = function () {
        try {
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      var $Try_1_Catch = function (e) {
        try {
          dispatch({
            type: ACTIONS.SET_ERROR,
            payload: e.message
          });
          return $Try_1_Post();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      try {
        return Promise.resolve(sendOTP({
          email: email,
          entryPoint: entryPoint
        })).then(function ($await_9) {
          try {
            dispatch({
              type: ACTIONS.CODE_SENT
            });
            return $Try_1_Post();
          } catch ($boundEx) {
            return $Try_1_Catch($boundEx);
          }
        }, $Try_1_Catch);
      } catch (e) {
        $Try_1_Catch(e);
      }
    });
  };

  var handleEmailCheckError = function handleEmailCheckError(error, isSubmitting, email) {
    return new Promise(function ($return, $error) {
      var _ref2, isFreePass, errorKey, accountExists, errorMessage;

      _ref2 = _typeof(error) === 'object' ? error : '', isFreePass = _ref2.isFreePass, errorKey = _ref2.key;
      accountExists = errorKey === CHECK_EMAIL_RETURN_CODES.DuplicateEmail.id;

      if (!accountExists) {
        errorMessage = (CHECK_EMAIL_RETURN_CODES[errorKey] || CHECK_EMAIL_RETURN_CODES.GenericError).message;
        dispatch({
          type: ACTIONS.SET_ERROR,
          payload: errorMessage
        });
        return $return();
      }

      if (!isSubmitting) {
        dispatch({
          type: ACTIONS.SET_ERROR,
          payload: ''
        });
        return $return();
      }

      if (!isFreePass) {
        dispatch({
          type: ACTIONS.CHOOSE_SIGN_IN_METHOD
        });
        return $return();
      }

      return Promise.resolve(sendCode(email)).then(function ($await_10) {
        try {
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      }, $error);
    });
  };
  /**
   * @typedef { Object } AccountStatus
   * @field { boolean } validated
   * @field { boolean } isFreePass
   * @field { boolean } newEmail
   */

  /**
   * Handle email submitted from welcome screen and return the account status from backend
   * @param { string } email
   * @returns { Promise<AccountStatus>}
   */


  var handleEmailContinue = function handleEmailContinue(email) {
    return new Promise(function ($return, $error) {
      var validation;
      validation = validateEmail(email);

      if (!validation.validated) {
        dispatch({
          type: ACTIONS.SET_ERROR,
          payload: validation.message
        });
        return $return({
          validated: false
        });
      }

      dispatch({
        type: ACTIONS.CHECK_EMAIL_LOADING,
        payload: email
      });

      var $Try_2_Catch = function (e) {
        try {
          return Promise.resolve(handleEmailCheckError(e, true, email)).then(function ($await_11) {
            try {
              return $return({
                validated: true,
                isFreePass: e.isFreePass,
                newEmail: false
              });
            } catch ($boundEx) {
              return $error($boundEx);
            }
          }, $error);
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      try {
        return Promise.resolve(checkEmail(email)).then(function ($await_12) {
          try {
            dispatch({
              type: ACTIONS.SET_IS_REGISTER,
              payload: true
            });
            return Promise.resolve(sendCode(email)).then(function ($await_13) {
              try {
                return $return({
                  validated: true,
                  isFreePass: false,
                  newEmail: true
                });
              } catch ($boundEx) {
                return $Try_2_Catch($boundEx);
              }
            }, $Try_2_Catch);
          } catch ($boundEx) {
            return $Try_2_Catch($boundEx);
          }
        }, $Try_2_Catch);
      } catch (e) {
        $Try_2_Catch(e);
      }
    });
  };

  var handleEmailBlur = function handleEmailBlur(email) {
    return new Promise(function ($return, $error) {
      var validation, data, suggestion;

      if (!email) {
        return $return();
      }

      validation = validateEmail(email);

      if (!validation.validated) {
        dispatch({
          type: ACTIONS.SET_ERROR,
          payload: validation.message
        });
        return $return();
      }

      var $Try_3_Post = function () {
        try {
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      var $Try_3_Catch = function (e) {
        try {
          return Promise.resolve(handleEmailCheckError(e, false, email)).then(function ($await_14) {
            try {
              return $Try_3_Post();
            } catch ($boundEx) {
              return $error($boundEx);
            }
          }, $error);
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      try {
        return Promise.resolve(checkEmail(email)).then(function ($await_15) {
          try {
            data = $await_15;
            suggestion = data.suggestion ? CHECK_EMAIL_RETURN_CODES.EmailSuggestion.message + " " + data.suggestion + "?" : '';
            dispatch({
              type: ACTIONS.SET_EMAIL_SUGGESTION,
              payload: suggestion
            });
            return $Try_3_Post();
          } catch ($boundEx) {
            return $Try_3_Catch($boundEx);
          }
        }, $Try_3_Catch);
      } catch (e) {
        $Try_3_Catch(e);
      }
    });
  };

  var handleChangeEmail = function handleChangeEmail() {
    dispatch({
      type: ACTIONS.CHANGE_EMAIL
    });
  };

  var handleUsePassword = function handleUsePassword() {
    return new Promise(function ($return, $error) {
      dispatch({
        type: ACTIONS.USE_PASSWORD
      });
      return $return();
    });
  };

  var handleUseCode = function handleUseCode() {
    return new Promise(function ($return, $error) {
      return Promise.resolve(sendCode(state.email)).then(function ($await_16) {
        try {
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      }, $error);
    });
  };

  var handlePasswordSignInSubmit = function handlePasswordSignInSubmit(_ref3) {
    return new Promise(function ($return, $error) {
      var password;
      password = _ref3.password;

      var $Try_4_Post = function () {
        try {
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      var $Try_4_Catch = function (e) {
        try {
          dispatch({
            type: ACTIONS.SET_ERROR,
            payload: e.message
          });
          throw e;
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      try {
        dispatch({
          type: ACTIONS.SET_IS_LOADING
        });
        return Promise.resolve(login({
          email: state.email,
          password: password,
          entryPoint: entryPoint
        })).then(function ($await_17) {
          try {
            return $Try_4_Post();
          } catch ($boundEx) {
            return $Try_4_Catch($boundEx);
          }
        }, $Try_4_Catch);
      } catch (e) {
        $Try_4_Catch(e);
      }
    });
  };

  var handlePasswordRegisterSubmit = function handlePasswordRegisterSubmit(password) {
    return new Promise(function ($return, $error) {
      var $Try_5_Post = function () {
        try {
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      var $Try_5_Catch = function (e) {
        try {
          dispatch({
            type: ACTIONS.SET_ERROR,
            payload: e.message
          });
          throw e;
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      try {
        dispatch({
          type: ACTIONS.SET_IS_LOADING
        });
        return Promise.resolve(register({
          email: state.email,
          password: password
        })).then(function ($await_18) {
          try {
            return $Try_5_Post();
          } catch ($boundEx) {
            return $Try_5_Catch($boundEx);
          }
        }, $Try_5_Catch);
      } catch (e) {
        $Try_5_Catch(e);
      }
    });
  };

  var handleCodeSubmit = function handleCodeSubmit(_ref4) {
    return new Promise(function ($return, $error) {
      var otp;
      otp = _ref4.otp;
      dispatch({
        type: ACTIONS.SET_IS_LOADING
      });

      var $Try_6_Post = function () {
        try {
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      var $Try_6_Catch = function (e) {
        try {
          dispatch({
            type: ACTIONS.SET_ERROR,
            payload: e.message
          });
          throw e;
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      try {
        if (state.isRegister) {
          return Promise.resolve(register({
            email: state.email,
            otp: otp
          })).then(function ($await_19) {
            try {
              return $If_8.call(this);
            } catch ($boundEx) {
              return $Try_6_Catch($boundEx);
            }
          }.bind(this), $Try_6_Catch);
        } else {
          return Promise.resolve(login({
            email: state.email,
            otp: otp,
            entryPoint: entryPoint
          })).then(function ($await_20) {
            try {
              return $If_8.call(this);
            } catch ($boundEx) {
              return $Try_6_Catch($boundEx);
            }
          }.bind(this), $Try_6_Catch);
        }

        function $If_8() {
          return $Try_6_Post();
        }
      } catch (e) {
        $Try_6_Catch(e);
      }
    });
  };

  var handleOTPResend = function handleOTPResend() {
    return new Promise(function ($return, $error) {
      var errorMessage;
      dispatch({
        type: ACTIONS.RESEND_CODE
      });

      var $Try_7_Post = function () {
        try {
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      var $Try_7_Catch = function (e) {
        try {
          errorMessage = OTP_ERROR_MESSAGES.GENERIC.message;

          if (e.status === OTP_ERROR_MESSAGES.TOO_MANY_REQUESTS.code) {
            errorMessage = OTP_ERROR_MESSAGES.TOO_MANY_REQUESTS.message;
          }

          dispatch({
            type: ACTIONS.SET_ERROR,
            payload: errorMessage
          });
          throw new Error(errorMessage);
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      try {
        return Promise.resolve(sendOTP(state.email)).then(function ($await_21) {
          try {
            dispatch({
              type: ACTIONS.CODE_SENT
            });
            return $Try_7_Post();
          } catch ($boundEx) {
            return $Try_7_Catch($boundEx);
          }
        }, $Try_7_Catch);
      } catch (e) {
        $Try_7_Catch(e);
      }
    });
  };

  return {
    state: state,
    handleEmailContinue: handleEmailContinue,
    handleChangeEmail: handleChangeEmail,
    handleUsePassword: handleUsePassword,
    handleEmailBlur: handleEmailBlur,
    handlePasswordSignInSubmit: handlePasswordSignInSubmit,
    handlePasswordRegisterSubmit: handlePasswordRegisterSubmit,
    handleCodeSubmit: handleCodeSubmit,
    handleOTPResend: handleOTPResend,
    handleUseCode: handleUseCode
  };
}

function _templateObject$8() {
  var data = _taggedTemplateLiteralLoose(["\n    display: flex;\n    flex-direction: column;\n    text-align: center;\n    align-items: center;\n    min-height: 500px;\n    margin: 0 auto 40px;\n    max-width: ", ";\n"]);

  _templateObject$8 = function _templateObject() {
    return data;
  };

  return data;
}
var StyledWrapper = styled.div(_templateObject$8(), function (props) {
  return props.maxWidth;
});

function OTPAuthWrapper(props) {
  var children = props.children,
      maxWidth = props.maxWidth,
      heading = props.heading,
      _props$showLogo = props.showLogo,
      showLogo = _props$showLogo === void 0 ? true : _props$showLogo;
  return /*#__PURE__*/React__default.createElement(StyledWrapper, {
    maxWidth: maxWidth
  }, showLogo && /*#__PURE__*/React__default.createElement(ZillowHomeLogo, {
    style: {
      width: '54px',
      marginTop: '12px',
      marginBottom: '32px'
    }
  }), heading && /*#__PURE__*/React__default.createElement(Heading, {
    level: "1",
    fontType: "h5",
    fontFamily: "serif",
    marginBottom: "sm"
  }, heading), children);
}

OTPAuthWrapper.propTypes = process.env.NODE_ENV !== "production" ? {
  children: PropTypes.node.isRequired,
  maxWidth: PropTypes.string,
  showLogo: PropTypes.bool,
  heading: PropTypes.string
} : {};

var HEADING = 'Sign in or register a Zillow account';
var SUBHEADING = 'Ensure your information stays safe by signing in or creating an account.';
var EMAIL_INPUT_LABEL = 'Email address';
var EMAIL_INPUT_PLACEHOLDER = 'Enter your email address';
var EMAIL_INPUT_ID = 'authOTPEmailField';
var SUBMIT_BUTTON_LABEL = 'Continue';
var OR_LABEL = 'or';
var TRACK_PROPS = {
  authForm: {
    title_nm: HEADING,
    auth_method_cd: AUTH_METHOD.EmailOTP
  },
  clickstreamTrigger: {
    triggerLocation: 'otp_enter_email|auth_form',
    triggerSource: 'otp_enter_email|auth_form'
  }
};
var getPageSpecificTrackingProps = function getPageSpecificTrackingProps(_ref) {
  var entryPoint = _ref.entryPoint,
      topicTags = _ref.topicTags;
  return {
    clickstream_trigger: {
      trigger_location_nm: TRIGGER_LOCATION.OTPEnterEmailForm,
      trigger_source_nm: TRIGGER_SOURCE.OTPEnterEmailForm
    },
    auth_form: {
      title_nm: HEADING,
      idp_cd: IDP.Zillow,
      entry_point_nm: entryPoint !== null && entryPoint !== void 0 ? entryPoint : UNSPECIFIED,
      auth_method_cd: AUTH_METHOD.EmailOTP
    },
    semantic: {
      topic_tag_txt: topicTags
    }
  };
};

function _templateObject3() {
  var data = _taggedTemplateLiteralLoose(["\n    height: 1px;\n    width: 100%;\n    background-color: ", ";\n"]);

  _templateObject3 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$1() {
  var data = _taggedTemplateLiteralLoose(["\n    background: white;\n    position: absolute;\n    left: 50%;\n    transform: translateX(-50%);\n    top: -14px;\n    padding: 0 12px;\n"]);

  _templateObject2$1 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$9() {
  var data = _taggedTemplateLiteralLoose(["\n    position: relative;\n    margin: ", " 0;\n    width: 100%;\n"]);

  _templateObject$9 = function _templateObject() {
    return data;
  };

  return data;
}
var StyledContainer = styled.div(_templateObject$9(), function (props) {
  return props.marginY;
});
var StyledLabel = styled(Text)(_templateObject2$1());
var StyledHorizontalRule = styled.div(_templateObject3(), function (props) {
  return props.theme.constellation.colors.gray400;
});

function LabeledHr(props) {
  var label = props.label,
      _props$marginY = props.marginY,
      marginY = _props$marginY === void 0 ? '1.5rem' : _props$marginY;
  return /*#__PURE__*/React__default.createElement(StyledContainer, {
    marginY: marginY
  }, /*#__PURE__*/React__default.createElement(StyledHorizontalRule, null), /*#__PURE__*/React__default.createElement(StyledLabel, {
    fontType: "bodySmall"
  }, label));
}

LabeledHr.propTypes = process.env.NODE_ENV !== "production" ? {
  label: PropTypes.string.isRequired,
  marginY: PropTypes.string
} : {};

function OTPAuthWelcomeScreen(props) {
  var loginHint = props.loginHint,
      onEmailContinue = props.onEmailContinue,
      onEmailBlur = props.onEmailBlur,
      isLoading = props.isLoading,
      error = props.error,
      emailSuggestion = props.emailSuggestion,
      googleClientId = props.googleClientId,
      facebookAppId = props.facebookAppId,
      showLogo = props.showLogo,
      _props$onPageView = props.onPageView,
      onPageView = _props$onPageView === void 0 ? NOOP : _props$onPageView,
      gaLabel = props.gaLabel,
      topicTags = props.topicTags,
      socialAuth = props.socialAuth,
      _props$entryPoint = props.entryPoint,
      entryPoint = _props$entryPoint === void 0 ? UNSPECIFIED : _props$entryPoint;

  var _useState = useState(loginHint),
      _useState2 = _slicedToArray(_useState, 2),
      email = _useState2[0],
      setEmail = _useState2[1];

  var _useState3 = useState(INPUT_SELECTOR.Keyboard),
      _useState4 = _slicedToArray(_useState3, 2),
      inputSelector = _useState4[0],
      setInputSelector = _useState4[1];

  var pageTrackingProps = Object.assign({}, TRACK_PROPS, {
    semantic: {
      topic_tag_txt: topicTags
    }
  });
  var authUtilsPageTrackingProps = getPageSpecificTrackingProps({
    entryPoint: entryPoint,
    topicTags: topicTags
  });

  var handleFormSubmit = function handleFormSubmit(event) {
    return new Promise(function ($return, $error) {
      var emailValue, accountStatus;
      event.preventDefault();
      emailValue = event.target[EMAIL_INPUT_ID].value;
      return Promise.resolve(onEmailContinue(emailValue)).then(function ($await_1) {
        try {
          accountStatus = $await_1;

          if (accountStatus.validated) {
            track({
              action: 'email_submit',
              category: 'auth',
              label: gaLabel
            }, {
              newLaneEvent: getEmailSubmitNewLaneProps(mergeNewLaneProps(pageTrackingProps, {
                clickstreamTrigger: {
                  triggerSource: 'button_to_submit_email',
                  inputSelector: inputSelector
                },
                authForm: {
                  account_type_nm: getAccountTypeName(accountStatus.newEmail, accountStatus.isFreePass)
                }
              }))
            });
          }

          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      }, $error);
    });
  };

  var handleEmailBlur = function handleEmailBlur(event) {
    onEmailBlur(event.target.value);
  };

  var handleEmailChange = function handleEmailChange(event) {
    setEmail(event.target.value);
  };

  var handleContinueButtonClick = function handleContinueButtonClick(event) {
    var _event$nativeEvent;

    setInputSelector(getInputSelector((_event$nativeEvent = event.nativeEvent) === null || _event$nativeEvent === void 0 ? void 0 : _event$nativeEvent.detail));
  };

  useEffect(function () {
    // set header text for parent component to add to tracking props
    setRegLoginUiConfig({
      headerText: HEADING
    });
    onPageView(pageTrackingProps, authUtilsPageTrackingProps);
  }, []);
  return /*#__PURE__*/React__default.createElement(OTPAuthWrapper, {
    maxWidth: "320px",
    heading: HEADING,
    showLogo: showLogo
  }, /*#__PURE__*/React__default.createElement(Paragraph, null, SUBHEADING), /*#__PURE__*/React__default.createElement("form", {
    onSubmit: handleFormSubmit,
    style: {
      textAlign: 'left',
      width: '100%'
    }
  }, /*#__PURE__*/React__default.createElement(FormField, {
    marginTop: "xl",
    label: /*#__PURE__*/React__default.createElement(Label, null, EMAIL_INPUT_LABEL),
    controlId: EMAIL_INPUT_ID,
    error: !!error,
    description: /*#__PURE__*/React__default.createElement(FormHelp, null, error || emailSuggestion),
    control: /*#__PURE__*/React__default.createElement(Input, {
      onChange: handleEmailChange,
      placeholder: EMAIL_INPUT_PLACEHOLDER,
      name: EMAIL_INPUT_ID,
      onBlur: handleEmailBlur,
      value: email
    })
  }), /*#__PURE__*/React__default.createElement(Button, {
    buttonType: "primary",
    fluid: true,
    marginTop: "md",
    disabled: !email || isLoading || error,
    onClick: handleContinueButtonClick
  }, SUBMIT_BUTTON_LABEL)), /*#__PURE__*/React__default.createElement(LabeledHr, {
    label: OR_LABEL,
    marginY: "2rem"
  }), /*#__PURE__*/React__default.createElement(SocialSignIns, {
    buttonText: SOCIAL_BUTTON_TEXT.SignInWith,
    socialAuth: socialAuth,
    trackingProps: {
      gaLabel: gaLabel,
      entryPoint: entryPoint,
      pageProps: authUtilsPageTrackingProps
    },
    entryPoint: entryPoint
  }, /*#__PURE__*/React__default.createElement(GoogleSignIn$1, {
    clientId: googleClientId,
    entryPoint: entryPoint
  }), /*#__PURE__*/React__default.createElement(AppleSignIn, {
    entryPoint: entryPoint
  }), /*#__PURE__*/React__default.createElement(FacebookSignIn, {
    appId: facebookAppId,
    entryPoint: entryPoint
  })));
}

OTPAuthWelcomeScreen.propTypes = process.env.NODE_ENV !== "production" ? {
  onPageView: PropTypes.func,
  onEmailContinue: PropTypes.func.isRequired,
  onEmailBlur: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  emailSuggestion: PropTypes.string,
  facebookAppId: PropTypes.string,
  googleClientId: PropTypes.string,
  loginHint: PropTypes.string,
  showLogo: PropTypes.bool,
  gaLabel: PropTypes.string,
  topicTags: PropTypes.arrayOf(PropTypes.string),
  // eslint-disable-next-line zillow/react/forbid-prop-types
  socialAuth: PropTypes.array,
  entryPoint: PropTypes.string
} : {};

var SUBHEADING$1 = 'Zillow has made signing in easier and safer! You can now sign in with a one-time passcode.';
var CODE_SIGN_IN_BUTTON_ID = 'codeSignInButton';
var PASSWORD_SIGN_IN_BUTTON_ID = 'passwordSignInButton';
var CHANGE_EMAIL_LABEL = 'Change email';
var CODE_SIGN_IN_DESCRIPTION = 'Sign in with a one-time passcode';
var PASSWORD_SIGN_IN_DESCRIPTION = 'Sign in with a password';
var TRACK_PROPS$1 = {
  authForm: {
    title_nm: SUBHEADING$1,
    auth_method_cd: AUTH_METHOD.EmailOTP
  },
  clickstreamTrigger: {
    triggerLocation: 'otp_option|auth_form',
    triggerSource: 'otp_option|auth_form'
  },
  semantic: {
    topic_tag_txt: ['account_login']
  }
};

function _templateObject$a() {
  var data = _taggedTemplateLiteralLoose(["\n    font-weight: 600;\n    font-size: 14px;\n"]);

  _templateObject$a = function _templateObject() {
    return data;
  };

  return data;
}
var StyledMethodButton = styled(Button)(_templateObject$a());

function OTPAuthSignInMethodScreen(props) {
  var email = props.email,
      onEmailChangeClick = props.onEmailChangeClick,
      onCodeSignInClick = props.onCodeSignInClick,
      onPasswordSignInClick = props.onPasswordSignInClick,
      showLogo = props.showLogo,
      _props$onPageView = props.onPageView,
      onPageView = _props$onPageView === void 0 ? NOOP : _props$onPageView,
      topicTags = props.topicTags;
  var pageTrackingProps = Object.assign({}, TRACK_PROPS$1, {
    semantic: {
      topic_tag_txt: TRACK_PROPS$1.semantic.topic_tag_txt.concat(topicTags)
    }
  });
  useEffect(function () {
    // set header text for parent component to add to tracking props
    setRegLoginUiConfig({
      headerText: SUBHEADING$1
    });
    onPageView(pageTrackingProps);
  }, []);

  var handleEmailChangeClick = function handleEmailChangeClick(e) {
    var _e$nativeEvent;

    onEmailChangeClick();
    event(getEmailChangeNewLaneProps(mergeNewLaneProps(pageTrackingProps, {
      clickstreamTrigger: {
        inputSelector: getInputSelector((_e$nativeEvent = e.nativeEvent) === null || _e$nativeEvent === void 0 ? void 0 : _e$nativeEvent.detail)
      }
    })));
  };

  var handleCodeSignInClick = function handleCodeSignInClick(e) {
    var _e$nativeEvent2;

    event(getSignInOtpOptionSelectedNewLaneProps(mergeNewLaneProps(pageTrackingProps, {
      clickstreamTrigger: {
        triggerSource: 'button_to_sign_in_otp',
        inputSelector: getInputSelector((_e$nativeEvent2 = e.nativeEvent) === null || _e$nativeEvent2 === void 0 ? void 0 : _e$nativeEvent2.detail)
      }
    })));
    onCodeSignInClick();
  };

  var handlePasswordSignInClick = function handlePasswordSignInClick(e) {
    var _e$nativeEvent3;

    event(getSignInOtpOptionSelectedNewLaneProps(mergeNewLaneProps(pageTrackingProps, {
      clickstreamTrigger: {
        triggerSource: 'button_to_sign_in_password',
        inputSelector: getInputSelector((_e$nativeEvent3 = e.nativeEvent) === null || _e$nativeEvent3 === void 0 ? void 0 : _e$nativeEvent3.detail)
      }
    })));
    onPasswordSignInClick();
  };

  return /*#__PURE__*/React__default.createElement(OTPAuthWrapper, {
    maxWidth: "390px",
    showLogo: showLogo
  }, /*#__PURE__*/React__default.createElement(Paragraph, null, SUBHEADING$1), /*#__PURE__*/React__default.createElement(Paragraph, {
    fontType: "bodySmallHeading",
    marginTop: "sm"
  }, email), /*#__PURE__*/React__default.createElement(TextButton, {
    fontType: "bodySmallHeading",
    onClick: handleEmailChangeClick
  }, CHANGE_EMAIL_LABEL), /*#__PURE__*/React__default.createElement(Flex, {
    display: "flex",
    gap: "sm",
    marginTop: "md"
  }, /*#__PURE__*/React__default.createElement(StyledMethodButton, {
    id: CODE_SIGN_IN_BUTTON_ID,
    onClick: handleCodeSignInClick,
    buttonType: "tertiary",
    fontType: "",
    fluid: true,
    style: {
      flexDirection: 'column'
    }
  }, /*#__PURE__*/React__default.createElement(DetailedIconKeyHome, {
    marginBottom: "sm"
  }), CODE_SIGN_IN_DESCRIPTION), /*#__PURE__*/React__default.createElement(StyledMethodButton, {
    id: PASSWORD_SIGN_IN_BUTTON_ID,
    onClick: handlePasswordSignInClick,
    buttonType: "tertiary",
    fluid: true,
    style: {
      flexDirection: 'column'
    }
  }, /*#__PURE__*/React__default.createElement(DetailedIconPencilDollar, {
    marginBottom: "sm"
  }), PASSWORD_SIGN_IN_DESCRIPTION)));
}

OTPAuthSignInMethodScreen.propTypes = process.env.NODE_ENV !== "production" ? {
  onPageView: PropTypes.func,
  email: PropTypes.string.isRequired,
  onEmailChangeClick: PropTypes.func.isRequired,
  onCodeSignInClick: PropTypes.func.isRequired,
  onPasswordSignInClick: PropTypes.func.isRequired,
  showLogo: PropTypes.bool,
  topicTags: PropTypes.arrayOf(PropTypes.string)
} : {};

var HEADING$1 = "We're sending your sign in code to the email you've provided";

function OTPAuthSendingCodeScreen(_ref) {
  var showLogo = _ref.showLogo;
  return /*#__PURE__*/React__default.createElement(OTPAuthWrapper, {
    maxWidth: "320px",
    heading: HEADING$1,
    showLogo: showLogo
  }, /*#__PURE__*/React__default.createElement(Spinner, {
    size: "sm",
    marginTop: "md"
  }));
}

OTPAuthSendingCodeScreen.propTypes = process.env.NODE_ENV !== "production" ? {
  showLogo: PropTypes.bool
} : {};

var HEADING$2 = 'Almost done!';
var SUBHEADING$2 = 'We sent a one-time passcode to ';
var SIGN_IN_INSTRUCTIONS = 'Enter it below to sign in.';
var REGISTER_INSTRUCTIONS = 'Enter it below to create a Zillow account.';
var CODE_INPUT_ID = 'verification-code';
var CODE_INPUT_LABEL = 'Enter 6-digit code';
var SIGN_IN_LABEL = 'Sign in';
var REGISTER_LABEL = 'Create an account';
var NO_CODE_DISCLAIMER = "Haven't received the code? ";
var RESEND_BUTTON_LABEL = 'Resend the code';
var RESENDING_LABEL = 'Resending';
var CHANGE_EMAIL_LABEL$1 = 'Change email';
var REGISTER_WITH_PASSWORD_LABEL = 'Create an account with a password';
var OR_LABEL$1 = 'or';
var RESENT_TIMEOUT_DURATION = 5000;
var RESENT_MESSAGE = 'Code resent';
var TRACK_PROPS$2 = {
  authForm: {
    title_nm: HEADING$2,
    auth_method_cd: AUTH_METHOD.EmailOTP
  },
  clickstreamTrigger: {
    triggerLocation: 'otp_enter_code|auth_form',
    triggerSource: 'otp_enter_code|auth_form'
  }
};

/**
 * Stub ClientProfiler and warn if not available
 * */
// eslint-disable-next-line no-unused-vars
var noop = function noop(methodName) {
  return function (eventName) {// For debugging purposes only
    // console.log(`ClientProfiler not available for method ${methodName} with event:
    // ${eventName}`)
  };
};

var noopProfileIntervalBegin = noop();
var noopProfileIntervalEnd = noop();
/**
 * Mark ClientProfiler profileIntervalBegin event
 * @param {String} eventName
 * */

var profileIntervalBegin = function profileIntervalBegin(eventName) {
  var _window, _window$ClientProfile;

  (((_window = window) === null || _window === void 0 ? void 0 : (_window$ClientProfile = _window.ClientProfiler) === null || _window$ClientProfile === void 0 ? void 0 : _window$ClientProfile.profileIntervalBegin) || noopProfileIntervalBegin)(eventName);
};
/**
 * Mark ClientProfiler profileIntervalEnd event
 * @param {String} eventName
 * */

var profileIntervalEnd = function profileIntervalEnd(eventName) {
  var _window2, _window2$ClientProfil;

  (((_window2 = window) === null || _window2 === void 0 ? void 0 : (_window2$ClientProfil = _window2.ClientProfiler) === null || _window2$ClientProfil === void 0 ? void 0 : _window2$ClientProfil.profileIntervalEnd) || noopProfileIntervalEnd)(eventName);
};

function OTPAuthCodeEnterCodeScreen(props) {
  var isRegister = props.isRegister,
      email = props.email,
      error = props.error,
      isLoading = props.isLoading,
      onCodeSubmit = props.onCodeSubmit,
      onResendClick = props.onResendClick,
      isResending = props.isResending,
      onEmailChangeClick = props.onEmailChangeClick,
      onPasswordRegisterClick = props.onPasswordRegisterClick,
      showLogo = props.showLogo,
      _props$onPageView = props.onPageView,
      onPageView = _props$onPageView === void 0 ? NOOP : _props$onPageView,
      gaLabel = props.gaLabel,
      topicTags = props.topicTags;

  var _React$useState = React__default.useState(false),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      showResent = _React$useState2[0],
      setShowResent = _React$useState2[1];

  var _useState = useState(INPUT_SELECTOR.Other),
      _useState2 = _slicedToArray(_useState, 2),
      inputSelector = _useState2[0],
      setInputSelector = _useState2[1];

  var resentTimeoutRef = React__default.useRef(null);
  var pageTrackingProps = Object.assign({}, TRACK_PROPS$2, {
    semantic: {
      topic_tag_txt: (isRegister ? [AUTH_TOPIC_TAG.AccountRegister] : [AUTH_TOPIC_TAG.AccountLogin]).concat(topicTags)
    }
  });
  useEffect(function () {
    // set header text for parent component to add to tracking props
    setRegLoginUiConfig({
      headerText: HEADING$2
    });
    onPageView(pageTrackingProps);
    return function () {
      clearTimeout(resentTimeoutRef.current);
    };
  }, []);

  var handleFormSubmit = function handleFormSubmit(e) {
    return new Promise(function ($return, $error) {
      var newLaneProps, codeValue, errorNewLaneProps;
      e.preventDefault();
      profileIntervalBegin(OTP_CODE_SUBMIT);
      newLaneProps = mergeNewLaneProps(pageTrackingProps, {
        clickstreamTrigger: {
          inputSelector: inputSelector
        }
      });
      // This clickstream event reuses the two step signin code submit event
      track(isRegister ? {
        category: 'auth',
        action: 'register/email/',
        label: gaLabel
      } : {
        category: AUTH_EVENT_CATEGORY,
        action: 'submit_2-step_verification',
        label: '2-step/signin'
      }, {
        newLaneEvent: isRegister ? getAccountRegisterNewLaneProps(newLaneProps) : getTwoStepSigninNewLaneProps(newLaneProps)
      });
      codeValue = e.target[CODE_INPUT_ID].value;

      var $Try_1_Post = function () {
        try {
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      var $Try_1_Catch = function (errorThrown) {
        try {
          errorNewLaneProps = mergeNewLaneProps(pageTrackingProps, {
            clickstreamTrigger: {
              inputSelector: inputSelector
            },
            authForm: {
              error_message_txt: errorThrown.message
            }
          });
          track({
            action: 'error//',
            category: 'auth',
            label: gaLabel
          }, {
            newLaneEvent: isRegister ? getAccountRegisterErrorNewLaneProps(errorNewLaneProps) : getAccountLoginErrorNewLaneProps(errorNewLaneProps)
          });
          return $Try_1_Post();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      try {
        return Promise.resolve(onCodeSubmit({
          otp: codeValue
        })).then(function ($await_3) {
          try {
            profileIntervalEnd(OTP_CODE_SUBMIT);
            return $Try_1_Post();
          } catch ($boundEx) {
            return $Try_1_Catch($boundEx);
          }
        }, $Try_1_Catch);
      } catch (errorThrown) {
        $Try_1_Catch(errorThrown);
      }
    });
  };

  var handleChangeEmailClick = function handleChangeEmailClick(e) {
    var _e$nativeEvent;

    event(getEmailChangeNewLaneProps(mergeNewLaneProps(pageTrackingProps, {
      clickstreamTrigger: {
        inputSelector: getInputSelector((_e$nativeEvent = e.nativeEvent) === null || _e$nativeEvent === void 0 ? void 0 : _e$nativeEvent.detail)
      }
    })));
    onEmailChangeClick();
  };

  var handleSubmitButtonClick = function handleSubmitButtonClick(e) {
    var _e$nativeEvent2;

    setInputSelector(getInputSelector((_e$nativeEvent2 = e.nativeEvent) === null || _e$nativeEvent2 === void 0 ? void 0 : _e$nativeEvent2.detail));
  };

  var handleResendButtonClick = function handleResendButtonClick(e) {
    return new Promise(function ($return, $error) {
      var _e$nativeEvent3;

      event(getResendEmailOTPCodeNewLaneProps(mergeNewLaneProps(pageTrackingProps, {
        clickstreamTrigger: {
          inputSelector: getInputSelector((_e$nativeEvent3 = e.nativeEvent) === null || _e$nativeEvent3 === void 0 ? void 0 : _e$nativeEvent3.detail)
        }
      })));

      var $Try_2_Post = function () {
        try {
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      var $Try_2_Catch = function (err) {
        try {
          setShowResent(false);
          return $Try_2_Post();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      try {
        return Promise.resolve(onResendClick()).then(function ($await_4) {
          try {
            setShowResent(true);
            resentTimeoutRef.current = setTimeout(function () {
              setShowResent(false);
            }, RESENT_TIMEOUT_DURATION);
            return $Try_2_Post();
          } catch ($boundEx) {
            return $Try_2_Catch($boundEx);
          }
        }, $Try_2_Catch);
      } catch (err) {
        $Try_2_Catch();
      }
    });
  };

  var handlePasswordRegisterClick = function handlePasswordRegisterClick(e) {
    var _e$nativeEvent4;

    event(getSignInOtpOptionSelectedNewLaneProps(mergeNewLaneProps(pageTrackingProps, {
      clickstreamTrigger: {
        triggerSource: 'button_to_register_with_password',
        inputSelector: getInputSelector((_e$nativeEvent4 = e.nativeEvent) === null || _e$nativeEvent4 === void 0 ? void 0 : _e$nativeEvent4.detail)
      }
    })));
    onPasswordRegisterClick();
  };

  return /*#__PURE__*/React__default.createElement(OTPAuthWrapper, {
    heading: HEADING$2,
    maxWidth: "320px",
    showLogo: showLogo
  }, /*#__PURE__*/React__default.createElement(Paragraph, null, SUBHEADING$2, /*#__PURE__*/React__default.createElement(Text, {
    fontType: "h6"
  }, email)), /*#__PURE__*/React__default.createElement(Paragraph, null, isRegister ? REGISTER_INSTRUCTIONS : SIGN_IN_INSTRUCTIONS), /*#__PURE__*/React__default.createElement(TextButton, {
    fontType: "bodySmallHeading",
    onClick: handleChangeEmailClick,
    marginTop: "xs",
    marginBottom: "lg"
  }, CHANGE_EMAIL_LABEL$1), /*#__PURE__*/React__default.createElement("form", {
    onSubmit: handleFormSubmit,
    style: {
      width: '100%'
    }
  }, /*#__PURE__*/React__default.createElement(VisuallyHidden, null, /*#__PURE__*/React__default.createElement(Label, {
    controlId: CODE_INPUT_ID
  }, CODE_INPUT_LABEL)), /*#__PURE__*/React__default.createElement(CodeInput, {
    id: CODE_INPUT_ID,
    name: CODE_INPUT_ID,
    maxLength: 6,
    "aria-hidden": true,
    errorMsg: error,
    isDisabled: isLoading
  }), /*#__PURE__*/React__default.createElement(Button, {
    buttonType: "primary",
    fluid: true,
    marginTop: "lg",
    disabled: isLoading,
    onClick: handleSubmitButtonClick
  }, isRegister ? REGISTER_LABEL : SIGN_IN_LABEL)), showResent ? /*#__PURE__*/React__default.createElement(InlineFeedback, {
    marginTop: "sm",
    appearance: "success"
  }, RESENT_MESSAGE) : /*#__PURE__*/React__default.createElement(Paragraph, {
    marginTop: "sm",
    fontType: "bodySmall"
  }, NO_CODE_DISCLAIMER, /*#__PURE__*/React__default.createElement(TextButton, {
    onClick: handleResendButtonClick,
    disabled: isResending,
    fontType: "bodySmallHeading"
  }, isResending ? RESENDING_LABEL : RESEND_BUTTON_LABEL)), isRegister && /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(LabeledHr, {
    label: OR_LABEL$1
  }), /*#__PURE__*/React__default.createElement(Button, {
    fontType: "bodySmallHeading",
    buttonType: "tertiary",
    fluid: true,
    icon: /*#__PURE__*/React__default.createElement(DetailedIconKeyHome, {
      size: "sm"
    }),
    size: "lg",
    onClick: handlePasswordRegisterClick
  }, REGISTER_WITH_PASSWORD_LABEL)));
}

OTPAuthCodeEnterCodeScreen.propTypes = process.env.NODE_ENV !== "production" ? {
  onPageView: PropTypes.func,
  isRegister: PropTypes.bool,
  onCodeSubmit: PropTypes.func.isRequired,
  onResendClick: PropTypes.func.isRequired,
  onEmailChangeClick: PropTypes.func.isRequired,
  onPasswordRegisterClick: PropTypes.func.isRequired,
  email: PropTypes.string.isRequired,
  error: PropTypes.string,
  isLoading: PropTypes.bool,
  isResending: PropTypes.bool,
  showLogo: PropTypes.bool,
  gaLabel: PropTypes.string,
  topicTags: PropTypes.arrayOf(PropTypes.string)
} : {};

var CHANGE_EMAIL_LABEL$2 = 'Change email';
var PASSWORD_INPUT_LABEL = 'Password';
var PASSWORD_INPUT_ID = 'authOTPPasswordField';
var FORGOT_PASSWORD_LABEL = 'Forgot your password?';
var SIGN_IN_LABEL$1 = 'Sign in';
var CODE_SIGN_IN_LABEL = 'Sign in with a one-time code instead';
var OR_LABEL$2 = 'or';
var TERMS_DISCLAIMER = "By submitting, I accept Zillow's ";
var TERMS_DISCLAIMER_LINK_LABEL = 'terms of use.';
var TERMS_DISCLAIMER_LINK = 'https://www.qa.zillow.net/corp/Terms.htm';
var PASSWORD_INPUT_PLACEHOLDER = 'Enter password';
var TRACK_PROPS$3 = {
  authForm: {
    title_nm: CHANGE_EMAIL_LABEL$2,
    auth_method_cd: AUTH_METHOD.EmailOTP
  },
  clickstreamTrigger: {
    triggerLocation: 'otp_enter_password|auth_form',
    triggerSource: 'otp_enter_password|auth_form'
  },
  semantic: {
    topic_tag_txt: ['account_login']
  }
};

var SHOW_LABEL = 'Show';
var HIDE_LABEL = 'Hide';

function PasswordInput(props) {
  var _props$showStrengthMe = props.showStrengthMeter,
      showStrengthMeter = _props$showStrengthMe === void 0 ? false : _props$showStrengthMe,
      inputProps = _objectWithoutProperties(props, ["showStrengthMeter"]);

  var _React$useState = React__default.useState(false),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      isShowing = _React$useState2[0],
      setIsShowing = _React$useState2[1];

  var handlePasswordShowToggle = function handlePasswordShowToggle() {
    setIsShowing(function (showing) {
      return !showing;
    });
  };

  return /*#__PURE__*/React__default.createElement(AdornedInput, {
    input: /*#__PURE__*/React__default.createElement(Input, _extends({}, inputProps, {
      value: inputProps.value,
      type: isShowing ? 'text' : 'password'
    })),
    rightAdornment: showStrengthMeter ? /*#__PURE__*/React__default.createElement(Adornment, null, /*#__PURE__*/React__default.createElement(StrengthMeter, {
      password: inputProps.value
    })) : /*#__PURE__*/React__default.createElement(Adornment, null, /*#__PURE__*/React__default.createElement(TextButton, {
      type: "button",
      fontType: "finePrintHeading",
      onClick: handlePasswordShowToggle
    }, isShowing ? HIDE_LABEL : SHOW_LABEL))
  });
}

PasswordInput.propTypes = process.env.NODE_ENV !== "production" ? {
  showStrengthMeter: PropTypes.bool
} : {};

function OTPAuthPasswordSignInScreen(props) {
  var email = props.email,
      onEmailChangeClick = props.onEmailChangeClick,
      onCodeSignInClick = props.onCodeSignInClick,
      onForgotPasswordClick = props.onForgotPasswordClick,
      error = props.error,
      onSignIn = props.onSignIn,
      showLogo = props.showLogo,
      gaLabel = props.gaLabel,
      topicTags = props.topicTags,
      _props$onPageView = props.onPageView,
      onPageView = _props$onPageView === void 0 ? NOOP : _props$onPageView;

  var _useState = useState(INPUT_SELECTOR.Other),
      _useState2 = _slicedToArray(_useState, 2),
      inputSelector = _useState2[0],
      setInputSelector = _useState2[1];

  var pageTrackingProps = Object.assign({}, TRACK_PROPS$3, {
    semantic: {
      topic_tag_txt: TRACK_PROPS$3.semantic.topic_tag_txt.concat(topicTags)
    }
  });
  useEffect(function () {
    // set header text for parent component to add to tracking props
    setRegLoginUiConfig({
      headerText: CHANGE_EMAIL_LABEL$2
    });
    onPageView(pageTrackingProps);
  }, []);

  var handlePasswordSignInSubmit = function handlePasswordSignInSubmit(e) {
    return new Promise(function ($return, $error) {
      var password;
      e.preventDefault();
      profileIntervalBegin(OTP_PASSWORD_LOGIN);
      track({
        action: 'signin/email/',
        category: 'auth',
        label: gaLabel
      }, {
        newLaneEvent: getAccountLoginNewLaneProps(mergeNewLaneProps(pageTrackingProps, {
          clickstreamTrigger: {
            triggerSource: 'button_to_login',
            inputSelector: inputSelector
          }
        }))
      });
      password = e.target[PASSWORD_INPUT_ID].value;

      var $Try_1_Post = function () {
        try {
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      var $Try_1_Catch = function (errorThrown) {
        try {
          event(getAccountLoginErrorNewLaneProps(mergeNewLaneProps(pageTrackingProps, {
            clickstreamTrigger: {
              inputSelector: inputSelector
            },
            authForm: {
              error_message_txt: errorThrown.message
            }
          })));
          return $Try_1_Post();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      try {
        return Promise.resolve(onSignIn({
          password: password
        })).then(function ($await_2) {
          try {
            profileIntervalEnd(OTP_PASSWORD_LOGIN);
            return $Try_1_Post();
          } catch ($boundEx) {
            return $Try_1_Catch($boundEx);
          }
        }, $Try_1_Catch);
      } catch (errorThrown) {
        $Try_1_Catch(errorThrown);
      }
    });
  };

  var handleChangeEmailClick = function handleChangeEmailClick(e) {
    var _e$nativeEvent;

    event(getEmailChangeNewLaneProps(mergeNewLaneProps(pageTrackingProps, {
      clickstreamTrigger: {
        inputSelector: getInputSelector((_e$nativeEvent = e.nativeEvent) === null || _e$nativeEvent === void 0 ? void 0 : _e$nativeEvent.detail)
      }
    })));
    onEmailChangeClick();
  };

  var handleSignInButtonClick = function handleSignInButtonClick(e) {
    var _e$nativeEvent2;

    setInputSelector(getInputSelector((_e$nativeEvent2 = e.nativeEvent) === null || _e$nativeEvent2 === void 0 ? void 0 : _e$nativeEvent2.detail));
  };

  var handlePasswordForgotClick = function handlePasswordForgotClick(e) {
    var _e$nativeEvent3;

    event(getForgotPasswordNewLaneProps(mergeNewLaneProps(pageTrackingProps, {
      clickstreamTrigger: {
        triggerSource: 'button_forgot_password',
        inputSelector: getInputSelector((_e$nativeEvent3 = e.nativeEvent) === null || _e$nativeEvent3 === void 0 ? void 0 : _e$nativeEvent3.detail)
      },
      authForm: {
        displayed_button_txt: FORGOT_PASSWORD_LABEL
      }
    })));
    onForgotPasswordClick();
  };

  var handleCodeSignInClick = function handleCodeSignInClick(e) {
    var _e$nativeEvent4;

    event(getSignInOtpOptionSelectedNewLaneProps(mergeNewLaneProps(pageTrackingProps, {
      clickstreamTrigger: {
        triggerSource: 'button_to_sign_in_otp',
        inputSelector: getInputSelector((_e$nativeEvent4 = e.nativeEvent) === null || _e$nativeEvent4 === void 0 ? void 0 : _e$nativeEvent4.detail)
      }
    })));
    onCodeSignInClick();
  };

  return /*#__PURE__*/React__default.createElement(OTPAuthWrapper, {
    maxWidth: "320px",
    showLogo: showLogo
  }, /*#__PURE__*/React__default.createElement(Paragraph, null, email), /*#__PURE__*/React__default.createElement(TextButton, {
    fontType: "bodySmallHeading",
    onClick: handleChangeEmailClick,
    marginTop: "xs",
    marginBottom: "lg"
  }, CHANGE_EMAIL_LABEL$2), /*#__PURE__*/React__default.createElement("form", {
    onSubmit: handlePasswordSignInSubmit,
    style: {
      textAlign: 'left',
      width: '100%'
    }
  }, /*#__PURE__*/React__default.createElement(FormField, {
    label: /*#__PURE__*/React__default.createElement(Label, null, PASSWORD_INPUT_LABEL),
    controlId: PASSWORD_INPUT_ID,
    error: !!error,
    description: /*#__PURE__*/React__default.createElement(FormHelp, null, error),
    control: /*#__PURE__*/React__default.createElement(PasswordInput, {
      placeholder: PASSWORD_INPUT_PLACEHOLDER,
      fluid: true,
      name: PASSWORD_INPUT_ID
    })
  }), /*#__PURE__*/React__default.createElement(Button, {
    buttonType: "primary",
    fluid: true,
    marginTop: "md",
    type: "submit",
    onClick: handleSignInButtonClick
  }, SIGN_IN_LABEL$1)), /*#__PURE__*/React__default.createElement(TextButton, {
    onClick: handlePasswordForgotClick,
    fontType: "bodySmallHeading",
    marginTop: "md"
  }, FORGOT_PASSWORD_LABEL), /*#__PURE__*/React__default.createElement(LabeledHr, {
    label: OR_LABEL$2
  }), /*#__PURE__*/React__default.createElement(Button, {
    fontType: "bodySmallHeading",
    buttonType: "tertiary",
    fluid: true,
    icon: /*#__PURE__*/React__default.createElement(DetailedIconKeyHome, {
      size: "sm"
    }),
    size: "lg",
    onClick: handleCodeSignInClick
  }, CODE_SIGN_IN_LABEL), /*#__PURE__*/React__default.createElement(Text, {
    fontType: "finePrint",
    marginTop: "xs"
  }, TERMS_DISCLAIMER, /*#__PURE__*/React__default.createElement(TextButton, {
    fontType: "finePrint",
    as: "a",
    href: TERMS_DISCLAIMER_LINK,
    target: "_blank"
  }, TERMS_DISCLAIMER_LINK_LABEL)));
}

OTPAuthPasswordSignInScreen.propTypes = process.env.NODE_ENV !== "production" ? {
  onPageView: PropTypes.func,
  gaLabel: PropTypes.string,
  email: PropTypes.string.isRequired,
  error: PropTypes.string,
  onEmailChangeClick: PropTypes.func.isRequired,
  onCodeSignInClick: PropTypes.func.isRequired,
  onForgotPasswordClick: PropTypes.func.isRequired,
  onSignIn: PropTypes.func.isRequired,
  showLogo: PropTypes.bool,
  topicTags: PropTypes.arrayOf(PropTypes.string)
} : {};

function useAuthFunctions(_ref) {
  var globalRedirectURL = _ref.redirectURL,
      globalSuccessHandler = _ref.onSuccess,
      globalFailureHandler = _ref.onFailure,
      handleMfaRequired = _ref.handleMfaRequired,
      setInputValues = _ref.setInputValues,
      authProcess = _ref.authProcess,
      history = _ref.history;

  function login$1(_ref2) {
    return new Promise(function ($return, $error) {
      var email, password, otp, entryPoint, response, redirectURL, message;
      email = _ref2.email, password = _ref2.password, otp = _ref2.otp, entryPoint = _ref2.entryPoint;

      var $Try_1_Post = function () {
        try {
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      var $Try_1_Catch = function (e) {
        try {
          if (e.status) {
            message = handleAuthenticationError(e.error, e.status, !!otp);
          } else {
            message = handleAuthenticationError(e.error, null, !!otp);
          }

          throw new Error(message);
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      try {
        if (otp) {
          return Promise.resolve(otpLogin({
            email: email,
            otp: otp,
            authProcess: authProcess,
            entryPoint: entryPoint
          })).then(function ($await_8) {
            try {
              response = $await_8;
              return $If_3.call(this);
            } catch ($boundEx) {
              return $Try_1_Catch($boundEx);
            }
          }.bind(this), $Try_1_Catch);
        } else {
          return Promise.resolve(login({
            email: email,
            password: password,
            authProcess: authProcess,
            entryPoint: entryPoint
          })).then(function ($await_9) {
            try {
              response = $await_9;
              return $If_3.call(this);
            } catch ($boundEx) {
              return $Try_1_Catch($boundEx);
            }
          }.bind(this), $Try_1_Catch);
        }

        function $If_3() {
          switch (response.code) {
            case LOGIN_RESPONSE_CODES.HAS_CAPTCHA:
              redirectURL = new URL(response.redirectURI);
              redirectURL.searchParams.set('url', '/user/acct');
              window.location.href = response.href;
              break;

            case LOGIN_RESPONSE_CODES.HAS_MFA:
              setInputValues({
                email: email,
                password: password
              });
              handleMfaRequired(response.phoneNumber, history);
              break;

            case LOGIN_RESPONSE_CODES.SUCCESS:
              return Promise.resolve(handleAuthenticationSuccess(response.data)).then(function ($await_10) {
                try {
                  return $return();
                } catch ($boundEx) {
                  return $Try_1_Catch($boundEx);
                }
              }, $Try_1_Catch);

            default:
              console.log(response);
          }

          return $Try_1_Post();
        }
      } catch (e) {
        $Try_1_Catch(e);
      }
    });
  }
  /* TODO: DRY with function above according to BE response */


  function register$1(_ref3) {
    return new Promise(function ($return, $error) {
      var email, otp, password, response, redirectURL, message;
      email = _ref3.email, otp = _ref3.otp, password = _ref3.password;

      var $Try_2_Post = function () {
        try {
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      var $Try_2_Catch = function (e) {
        try {
          message = handleAuthenticationError(e.error, null, !!otp);
          throw new Error(message);
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      try {
        if (otp) {
          return Promise.resolve(otpRegister({
            email: email,
            otp: otp,
            authProcess: authProcess,
            registerCountryCode: 'US'
          })).then(function ($await_11) {
            try {
              response = $await_11;
              return $If_5.call(this);
            } catch ($boundEx) {
              return $Try_2_Catch($boundEx);
            }
          }.bind(this), $Try_2_Catch);
        } else {
          return Promise.resolve(register({
            email: email,
            password: password,
            authProcess: authProcess,
            registerCountryCode: 'US',
            redirectURL: globalRedirectURL
          })).then(function ($await_12) {
            try {
              response = $await_12;
              return $If_5.call(this);
            } catch ($boundEx) {
              return $Try_2_Catch($boundEx);
            }
          }.bind(this), $Try_2_Catch);
        }

        function $If_5() {
          switch (response.code) {
            case REGISTER_RESPONSE_CODES.HAS_CAPTCHA:
              redirectURL = new URL(response.redirectURI);
              redirectURL.searchParams.set('url', '/user/acct');
              window.location.href = response.href;
              break;

            case REGISTER_RESPONSE_CODES.SUCCESS:
              return Promise.resolve(handleAuthenticationSuccess(response.data)).then(function ($await_13) {
                try {
                  return $return();
                } catch ($boundEx) {
                  return $Try_2_Catch($boundEx);
                }
              }, $Try_2_Catch);

            default:
              console.log(response);
          }

          return $Try_2_Post();
        }
      } catch (e) {
        $Try_2_Catch(e);
      }
    });
  }

  function handleAuthenticationSuccess(responseBody) {
    return new Promise(function ($return, $error) {
      var authToken;
      authToken = responseBody.authToken;

      if (authToken) {
        setUserAuthToken(authToken);
      }

      if (globalSuccessHandler) {
        return Promise.resolve(globalSuccessHandler(responseBody, globalRedirectURL)).then(function ($await_14) {
          try {
            return $If_7.call(this);
          } catch ($boundEx) {
            return $error($boundEx);
          }
        }.bind(this), $error);
      }

      function $If_7() {
        if (window && globalRedirectURL) {
          window.location.href = globalRedirectURL;
          return $return();
        }

        setUserLoggedIn();
        hideRegLoginLightbox();
        return $return();
      }

      return $If_7.call(this);
    });
  }

  function handleAuthenticationError(responseBody, status, isOTP) {
    var _errorMessage2;

    var _ref4 = responseBody || {},
        validationErrors = _ref4.validationErrors;

    if (globalFailureHandler) {
      globalFailureHandler(responseBody);
    }

    var errorMessage = validationErrors ? Object.values(validationErrors).join('\n') : null;

    if ((validationErrors === null || validationErrors === void 0 ? void 0 : validationErrors.badCredentials) && isOTP) {
      errorMessage = OTP_ERROR_MESSAGES.INVALID_CODE.message;
    }

    Object.keys(STATUS_ERROR_CODES).forEach(function (key) {
      var errorStatus = STATUS_ERROR_CODES[key];

      if (errorStatus.code === status) {
        var _errorMessage;

        errorMessage = (_errorMessage = errorMessage) !== null && _errorMessage !== void 0 ? _errorMessage : errorStatus.errorMessage;
      }
    });
    errorMessage = (_errorMessage2 = errorMessage) !== null && _errorMessage2 !== void 0 ? _errorMessage2 : STATUS_ERROR_CODES.server.errorMessage;

    if (globalFailureHandler) {
      globalFailureHandler(responseBody, {
        errorMessage: errorMessage
      });
    }

    return errorMessage;
  }

  return {
    login: login$1,
    register: register$1
  };
}

var HEADING$3 = 'Create a password';
var CHANGE_EMAIL_LABEL$3 = 'Change email';
var PASSWORD_INPUT_LABEL$1 = 'Password';
var PASSWORD_INPUT_ID$1 = 'authOTPCreatePasswordField';
var PASSWORD_INPUT_PLACEHOLDER$1 = 'Enter password';
var CREATE_PASSWORD_SUBMIT_LABEL = 'Create password';
var OR_LABEL$3 = 'or';
var CODE_REGISTER_LABEL = 'Create an account with a \n one-time code instead';
var TRACK_PROPS$4 = {
  authForm: {
    title_nm: HEADING$3,
    auth_method_cd: AUTH_METHOD.EmailOTP
  },
  clickstreamTrigger: {
    triggerLocation: 'otp_create_password|auth_form',
    triggerSource: 'otp_create_password|auth_form'
  },
  semantic: {
    topic_tag_txt: ['account_register']
  }
};

function usePasswordValidation(password, email) {
  var validation = useMemo(function () {
    return validatePasswordWithMessages(password, email);
  }, [password, email]);

  var _React$useMemo = useMemo(function () {
    if (!(validation === null || validation === void 0 ? void 0 : validation.messages)) {
      return {
        validationMarkup: null,
        ariaDescribedBy: ''
      };
    }

    var markup = null;
    var ids = [];
    markup = validation.messages.map(function (_ref, index) {
      var validated = _ref.validated,
          message = _ref.message;
      var status = 'pass';

      if (!validated && password) {
        status = 'fail';
      } else if (!validated && !password) {
        status = 'neutral';
      }

      if (!message) {
        return null;
      }

      var instructionId = "validation-text-" + index;
      ids.push(instructionId);
      return /*#__PURE__*/createElement(ValidationMessage, {
        key: instructionId,
        text: message,
        status: status,
        id: instructionId
      });
    });
    return {
      validationMarkup: markup,
      ariaDescribedBy: ids.length > 0 ? ids.join(' ') : ''
    };
  }, [validation, password]),
      validationMarkup = _React$useMemo.validationMarkup,
      ariaDescribedBy = _React$useMemo.ariaDescribedBy;

  return {
    validated: validation.validated,
    validationMarkup: validationMarkup,
    ariaDescribedBy: ariaDescribedBy
  };
}

function OTPAuthCreatePasswordScreen(props) {
  var email = props.email,
      error = props.error,
      onEmailChangeClick = props.onEmailChangeClick,
      onCodeRegisterClick = props.onCodeRegisterClick,
      showLogo = props.showLogo,
      onRegister = props.onRegister,
      _props$onPageView = props.onPageView,
      onPageView = _props$onPageView === void 0 ? NOOP : _props$onPageView,
      gaLabel = props.gaLabel,
      topicTags = props.topicTags;

  var _React$useState = React__default.useState(''),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      newPassword = _React$useState2[0],
      setNewPassword = _React$useState2[1];

  var _usePasswordValidatio = usePasswordValidation(newPassword, email),
      validationMarkup = _usePasswordValidatio.validationMarkup,
      ariaDescribedBy = _usePasswordValidatio.ariaDescribedBy,
      validated = _usePasswordValidatio.validated;

  var _useState = useState(INPUT_SELECTOR.Other),
      _useState2 = _slicedToArray(_useState, 2),
      inputSelector = _useState2[0],
      setInputSelector = _useState2[1];

  var pageTrackingProps = Object.assign({}, TRACK_PROPS$4, {
    semantic: {
      topic_tag_txt: TRACK_PROPS$4.semantic.topic_tag_txt.concat(topicTags)
    }
  });
  useEffect(function () {
    // set header text for parent component to add to tracking props
    setRegLoginUiConfig({
      headerText: HEADING$3
    });
    onPageView(pageTrackingProps);
  }, []);

  var handlePasswordCreateSubmit = function handlePasswordCreateSubmit(e) {
    return new Promise(function ($return, $error) {
      e.preventDefault();
      profileIntervalBegin(OTP_PASSWORD_CREATE);
      track({
        category: 'auth',
        action: 'register/email/',
        label: gaLabel
      }, {
        newLaneEvent: getAccountRegisterNewLaneProps(mergeNewLaneProps(pageTrackingProps, {
          clickstreamTrigger: {
            inputSelector: inputSelector
          }
        }))
      });

      var $Try_1_Post = function () {
        try {
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      var $Try_1_Catch = function (errorThrown) {
        try {
          track({
            action: 'error//',
            category: 'auth',
            label: gaLabel
          }, {
            newLaneEvent: getAccountRegisterErrorNewLaneProps(mergeNewLaneProps(pageTrackingProps, {
              clickstreamTrigger: {
                inputSelector: inputSelector
              },
              authForm: {
                error_message_txt: errorThrown.message
              }
            }))
          });
          return $Try_1_Post();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      try {
        return Promise.resolve(onRegister(newPassword)).then(function ($await_2) {
          try {
            profileIntervalEnd(OTP_PASSWORD_CREATE);
            return $Try_1_Post();
          } catch ($boundEx) {
            return $Try_1_Catch($boundEx);
          }
        }, $Try_1_Catch);
      } catch (errorThrown) {
        $Try_1_Catch(errorThrown);
      }
    });
  };

  var handlePasswordChange = function handlePasswordChange(e) {
    setNewPassword(e.target.value);
  };

  var handleChangeEmailClick = function handleChangeEmailClick(e) {
    var _e$nativeEvent;

    event(getEmailChangeNewLaneProps(mergeNewLaneProps(pageTrackingProps, {
      clickstreamTrigger: {
        inputSelector: getInputSelector((_e$nativeEvent = e.nativeEvent) === null || _e$nativeEvent === void 0 ? void 0 : _e$nativeEvent.detail)
      }
    })));
    onEmailChangeClick();
  };

  var handleCreatePasswordButtonClick = function handleCreatePasswordButtonClick(e) {
    var _e$nativeEvent2;

    setInputSelector(getInputSelector((_e$nativeEvent2 = e.nativeEvent) === null || _e$nativeEvent2 === void 0 ? void 0 : _e$nativeEvent2.detail));
  };

  var handleCodeRegisterClick = function handleCodeRegisterClick(e) {
    var _e$nativeEvent3;

    event(getSignInOtpOptionSelectedNewLaneProps(mergeNewLaneProps(pageTrackingProps, {
      clickstreamTrigger: {
        triggerSource: 'button_to_register_with_code',
        inputSelector: getInputSelector((_e$nativeEvent3 = e.nativeEvent) === null || _e$nativeEvent3 === void 0 ? void 0 : _e$nativeEvent3.detail)
      }
    })));
    onCodeRegisterClick();
  };

  return /*#__PURE__*/React__default.createElement(OTPAuthWrapper, {
    heading: HEADING$3,
    maxWidth: "320px",
    showLogo: showLogo
  }, /*#__PURE__*/React__default.createElement(Paragraph, null, email), /*#__PURE__*/React__default.createElement(TextButton, {
    fontType: "bodySmallHeading",
    onClick: handleChangeEmailClick,
    marginTop: "xs",
    marginBottom: "lg"
  }, CHANGE_EMAIL_LABEL$3), /*#__PURE__*/React__default.createElement("form", {
    onSubmit: handlePasswordCreateSubmit,
    style: {
      textAlign: 'left',
      width: '100%'
    }
  }, /*#__PURE__*/React__default.createElement(FormField, {
    label: /*#__PURE__*/React__default.createElement(Label, null, PASSWORD_INPUT_LABEL$1),
    controlId: PASSWORD_INPUT_ID$1,
    error: !!error,
    description: /*#__PURE__*/React__default.createElement(FormHelp, null, error),
    control: /*#__PURE__*/React__default.createElement(PasswordInput, {
      placeholder: PASSWORD_INPUT_PLACEHOLDER$1,
      "aria-describedby": ariaDescribedBy,
      fluid: true,
      value: newPassword,
      onChange: handlePasswordChange,
      name: PASSWORD_INPUT_ID$1,
      showStrengthMeter: true
    })
  }), validationMarkup, /*#__PURE__*/React__default.createElement(Button, {
    buttonType: "primary",
    fluid: true,
    marginTop: "md",
    type: "submit",
    disabled: !validated,
    onClick: handleCreatePasswordButtonClick
  }, CREATE_PASSWORD_SUBMIT_LABEL), /*#__PURE__*/React__default.createElement(LabeledHr, {
    label: OR_LABEL$3
  }), /*#__PURE__*/React__default.createElement(Button, {
    fontType: "bodySmallHeading",
    buttonType: "tertiary",
    fluid: true,
    icon: /*#__PURE__*/React__default.createElement(DetailedIconKeyHome, {
      size: "sm"
    }),
    size: "lg",
    onClick: handleCodeRegisterClick,
    paddingX: "lg",
    style: {
      whiteSpace: 'pre',
      padding: '0.25rem',
      textAlign: 'left'
    }
  }, CODE_REGISTER_LABEL)));
}

OTPAuthCreatePasswordScreen.propTypes = process.env.NODE_ENV !== "production" ? {
  onPageView: PropTypes.func,
  email: PropTypes.string.isRequired,
  error: PropTypes.string,
  onEmailChangeClick: PropTypes.func.isRequired,
  onCodeRegisterClick: PropTypes.func.isRequired,
  showLogo: PropTypes.bool,
  onRegister: PropTypes.func.isRequired,
  gaLabel: PropTypes.string,
  topicTags: PropTypes.arrayOf(PropTypes.string)
} : {};

function OTPAuth(props) {
  var _OTP_SCREENS$WELCOME$;

  var authProcess = props.authProcess,
      redirectURL = props.redirectURL,
      onFailure = props.onFailure,
      _onSuccess = props.onSuccess,
      history = props.history,
      googleClientId = props.googleClientId,
      facebookAppId = props.facebookAppId,
      gaLabel = props.gaLabel,
      topicTags = props.topicTags,
      loginHint = props.loginHint,
      handleMfaRequired = props.handleMfaRequired,
      setInputValues = props.setInputValues,
      showLogo = props.showLogo,
      isLightbox = props.isLightbox,
      _props$entryPoint = props.entryPoint,
      entryPoint = _props$entryPoint === void 0 ? 'reglogin' : _props$entryPoint;

  var _useAuthFunctions = useAuthFunctions({
    redirectURL: redirectURL,
    onFailure: onFailure,
    onSuccess: _onSuccess,
    history: history,
    authProcess: authProcess,
    handleMfaRequired: handleMfaRequired,
    setInputValues: setInputValues
  }),
      login = _useAuthFunctions.login,
      register = _useAuthFunctions.register;

  var socialAuth = useSocialAuth({
    onSuccess: function onSuccess(res) {
      return handleSocialLoggedIn(res, _onSuccess, redirectURL);
    },
    onFailure: onFailure !== null && onFailure !== void 0 ? onFailure : NOOP
  });

  var _useOTPFlow = useOTPFlow({
    login: login,
    register: register,
    defaultEmail: loginHint,
    entryPoint: entryPoint
  }),
      state = _useOTPFlow.state,
      handleChangeEmail = _useOTPFlow.handleChangeEmail,
      handleUsePassword = _useOTPFlow.handleUsePassword,
      handleUseCode = _useOTPFlow.handleUseCode,
      handleEmailContinue = _useOTPFlow.handleEmailContinue,
      handleEmailBlur = _useOTPFlow.handleEmailBlur,
      handlePasswordSignInSubmit = _useOTPFlow.handlePasswordSignInSubmit,
      handleCodeSubmit = _useOTPFlow.handleCodeSubmit,
      handleOTPResend = _useOTPFlow.handleOTPResend,
      handlePasswordRegisterSubmit = _useOTPFlow.handlePasswordRegisterSubmit;

  var handleForgotPasswordClick = function handleForgotPasswordClick() {
    history.push(ROUTES.changePasswordForgot);
  };

  var onPageView = function onPageView(newLaneProps, authUtilsProps) {
    // this updates global clickstream trigger for modal close button to pickup
    props.updateClickstreamTrigger(newLaneProps.clickstreamTrigger);

    if (authUtilsProps) {
      pageView({
        pageSpecificProps: authUtilsProps,
        pageURL: history.location.pathname,
        isLightbox: isLightbox
      });
    } else {
      pageViewTrack(isLightbox)(history.location.pathname)(newLaneProps);
    }
  };

  return (_OTP_SCREENS$WELCOME$ = {}, _OTP_SCREENS$WELCOME$[OTP_SCREENS.WELCOME] = /*#__PURE__*/React__default.createElement(OTPAuthWelcomeScreen, {
    onPageView: onPageView,
    onEmailContinue: handleEmailContinue,
    onEmailBlur: handleEmailBlur,
    isLoading: state.isLoading,
    error: state.error,
    emailSuggestion: state.emailSuggestion,
    googleClientId: googleClientId,
    facebookAppId: facebookAppId,
    loginHint: state.email,
    showLogo: showLogo,
    gaLabel: gaLabel,
    topicTags: topicTags,
    socialAuth: socialAuth,
    entryPoint: entryPoint
  }), _OTP_SCREENS$WELCOME$[OTP_SCREENS.CHOOSE_SIGN_IN_METHOD] = /*#__PURE__*/React__default.createElement(OTPAuthSignInMethodScreen, {
    onPageView: onPageView,
    email: state.email,
    onEmailChangeClick: handleChangeEmail,
    onCodeSignInClick: handleUseCode,
    onPasswordSignInClick: handleUsePassword,
    showLogo: showLogo,
    gaLabel: gaLabel,
    topicTags: topicTags
  }), _OTP_SCREENS$WELCOME$[OTP_SCREENS.SENDING_CODE] = /*#__PURE__*/React__default.createElement(OTPAuthSendingCodeScreen, {
    showLogo: showLogo
  }), _OTP_SCREENS$WELCOME$[OTP_SCREENS.ENTER_CODE] = /*#__PURE__*/React__default.createElement(OTPAuthCodeEnterCodeScreen, {
    onPageView: onPageView,
    email: state.email,
    isRegister: state.isRegister,
    onPasswordRegisterClick: handleUsePassword,
    onEmailChangeClick: handleChangeEmail,
    onResendClick: handleOTPResend,
    onCodeSubmit: handleCodeSubmit,
    isLoading: state.isLoading,
    isResending: state.isResending,
    error: state.error,
    showLogo: showLogo,
    gaLabel: gaLabel,
    topicTags: topicTags
  }), _OTP_SCREENS$WELCOME$[OTP_SCREENS.PASSWORD_SIGN_IN] = /*#__PURE__*/React__default.createElement(OTPAuthPasswordSignInScreen, {
    onPageView: onPageView,
    email: state.email,
    error: state.error,
    onCodeSignInClick: handleUseCode,
    onEmailChangeClick: handleChangeEmail,
    onForgotPasswordClick: handleForgotPasswordClick,
    onSignIn: handlePasswordSignInSubmit,
    showLogo: showLogo,
    gaLabel: gaLabel,
    topicTags: topicTags
  }), _OTP_SCREENS$WELCOME$[OTP_SCREENS.CREATE_PASSWORD] = /*#__PURE__*/React__default.createElement(OTPAuthCreatePasswordScreen, {
    onPageView: onPageView,
    email: state.email,
    onEmailChangeClick: handleChangeEmail,
    onCodeRegisterClick: handleUseCode,
    showLogo: showLogo,
    onRegister: handlePasswordRegisterSubmit,
    gaLabel: gaLabel,
    topicTags: topicTags
  }), _OTP_SCREENS$WELCOME$)[state.screen];
}

OTPAuth.propTypes = process.env.NODE_ENV !== "production" ? {
  authProcess: PropTypes.string.isRequired,
  redirectURL: PropTypes.string,
  onSuccess: PropTypes.func,
  onFailure: PropTypes.func,
  facebookAppId: PropTypes.string.isRequired,
  googleClientId: PropTypes.string.isRequired,
  handleMfaRequired: PropTypes.func,
  clickstreamTrigger: PropTypes.shape({
    triggerLocation: PropTypes.string,
    triggerObject: PropTypes.string,
    triggerSource: PropTypes.string,
    inputSelector: PropTypes.string
  }),
  loginHint: PropTypes.string,
  showLogo: PropTypes.bool,
  isLightbox: PropTypes.bool,
  gaLabel: PropTypes.string.isRequired,
  updateClickstreamTrigger: PropTypes.func.isRequired,
  topicTags: PropTypes.arrayOf(PropTypes.string)
} : {};
var mapDispatchToProps$b = {
  updateClickstreamTrigger: updateClickstreamTrigger,
  handleMfaRequired: handleMfaRequired,
  setInputValues: setInputValues
};

var mapStateToProps$d = function mapStateToProps(state) {
  return {
    authProcess: state.regLogin.uiConfig.authAP,
    onFailure: state.appConfig.onFailure,
    onSuccess: state.appConfig.onSuccess,
    redirectURL: state.regLogin.redirectUrl,
    facebookAppId: state.appConfig.facebookAppId.toString(),
    googleClientId: state.appConfig.googleClientId,
    clickstreamTrigger: state.clickstream.clickstreamTrigger,
    gaLabel: state.regLogin.uiConfig.gaLabel,
    loginHint: state.appConfig.loginHint,
    showLogo: state.appConfig.showLogo,
    isLightbox: checkIsTruthyBoolOrString(state.appConfig.isLightbox),
    topicTags: state.appConfig.topicTags,
    entryPoint: getEntryPoint(state.appConfig.entryPoint, state.regLogin.uiConfig.entryPoint)
  };
};

var OTPAuth$1 = withRouter(connectToUserSession(mapStateToProps$d, mapDispatchToProps$b)(OTPAuth));

var ProRegisterForm = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(ProRegisterForm, _React$Component);

  function ProRegisterForm() {
    var _this$props$entryPoin;

    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _React$Component.call.apply(_React$Component, [this].concat(args)) || this;
    _this.state = {
      inputSelector: INPUT_SELECTOR$1.Other,
      isSubmitting: false
    };
    _this.pageTrackingProps = {
      clickstream_trigger: {
        trigger_location_nm: TRIGGER_LOCATION.ProRegisterForm
      },
      auth_form: {
        title_nm: _this.props.headerText,
        idp_cd: IDP.Zillow,
        entry_point_nm: (_this$props$entryPoin = _this.props.entryPoint) !== null && _this$props$entryPoin !== void 0 ? _this$props$entryPoin : UNSPECIFIED,
        auth_type_cd: AUTH_TYPE.ProRegister
      }
    };
    _this.handleProCheck = yieldCallback(function () {
      _this.props.resetProfessionalForm([], true);
    });
    _this.onSubmit = yieldCallback(function () {
      var _this$props = _this.props,
          gaLabel = _this$props.gaLabel,
          hideConfirmPassword = _this$props.hideConfirmPassword,
          history = _this$props.history,
          inputConfigs = _this$props.inputConfigs,
          inputs = _this$props.inputs,
          isLightbox = _this$props.isLightbox,
          login = _this$props.login,
          onFailure = _this$props.onFailure,
          onSuccess = _this$props.onSuccess,
          verifyOnRegisterTreatment = _this$props.verifyOnRegisterTreatment,
          zmaHandlerFns = _this$props.zmaHandlerFns; // const { showEmailConsent, overrideProSelection } = login;

      var showEmailConsent = login.showEmailConsent;

      var _this$props$getInputP = _this.props.getInputProps(inputConfigs, inputs),
          areaCode = _this$props$getInputP.areaCode,
          authToken = _this$props$getInputP.authToken,
          emailConsent = _this$props$getInputP.emailConsent,
          firstName = _this$props$getInputP.firstName,
          lastName = _this$props$getInputP.lastName,
          newEmail = _this$props$getInputP.newEmail,
          newPassword = _this$props$getInputP.newPassword,
          phoneExt = _this$props$getInputP.phoneExt,
          phonePrefix = _this$props$getInputP.phonePrefix,
          phoneSuffix = _this$props$getInputP.phoneSuffix,
          proCheck = _this$props$getInputP.proCheck,
          proSelect = _this$props$getInputP.proSelect,
          zipCode = _this$props$getInputP.zipCode; // (proCheck.value || (alwaysPro && !overrideProSelection)) && showProRegCheckbox;


      proCheck.value = true;
      var inputTypes = REG_INPUT_TYPES.requiredTypes;

      if (proCheck.value) {
        inputTypes = REG_PRO_TYPES[proSelect.value] ? REG_PRO_TYPES[proSelect.value].requiredTypes : REG_PRO_TYPES[0].requiredTypes;

        if (proSelect.value !== DEFAULT_PRO_TYPE_OPTIONS['Mortgage Lender']) {
          inputTypes.push('zipCode');
        }
      }

      trackAuth(AUTH_EVENT_TYPE.RegisterAttempt)({
        pageProps: _this.pageTrackingProps,
        componentProps: {
          clickstream_trigger: {
            trigger_source_nm: 'button_to_register',
            input_selector_nm: _this.state.inputSelector
          },
          auth_form: {
            displayed_button_txt: BUTTON_TYPES.Continue.value,
            auth_method_cd: AUTH_METHOD$1.EmailPassword
          }
        }
      });

      if (hideConfirmPassword) {
        var index = inputTypes.indexOf('newPassword');

        if (index > -1) {
          inputTypes.splice(index, 1);
        }
      }

      var hasRequiredVerification = verifyOnRegisterTreatment && verifyOnRegisterTreatment === AB_AUTH_VERIFY_ON_REGISTER.REQUIRED;
      var hasOptionalVerification = verifyOnRegisterTreatment && verifyOnRegisterTreatment === AB_AUTH_VERIFY_ON_REGISTER.OPTIONAL;
      var hasVerification = isLightbox === 'true' && !proCheck.value && (hasOptionalVerification || hasRequiredVerification);

      var redirectURL = _this.getRedirectURL(proCheck.value, proSelect.value); // Force validation of form inputs.


      _this.props.validateInputs(inputConfigs, inputTypes).then(function () {
        if (!_this.state.isSubmitting) {
          _this.setState({
            isSubmitting: true
          });

          profileIntervalBegin$1(REGISTER_PROFILE_SUBMIT);
        }

        _this.props.submitForm({
          path: ROUTES.registerPost,
          data: {
            ap: gaLabel,
            areaCode: areaCode.value,
            authToken: authToken.value,
            registerCountryCode: showEmailConsent ? 'CAN' : 'US',
            email: newEmail.value,
            emailOptInCheckbox: emailConsent.value,
            extension: phoneExt.value,
            firstName: firstName.value,
            lastName: lastName.value,
            number: phoneSuffix.value,
            password: newPassword.value,
            prefix: phonePrefix.value,
            proCheckbox: 1,
            proSelect: proSelect.value,
            suppressredirect: true,
            zip: zipCode.value.toUpperCase().replace(/\s/g, ''),
            redirectURL: redirectURL,
            isLightbox: isLightbox === 'true'
          },
          success: function success(res) {
            var googleAnalyticsId = res.googleAnalyticsId;

            _this.setState({
              isSubmitting: false
            });

            profileIntervalEnd$1(REGISTER_PROFILE_SUBMIT);
            var hasZmaHandler = (zmaHandlerFns === null || zmaHandlerFns === void 0 ? void 0 : zmaHandlerFns.setZMAConsent) && typeof zmaHandlerFns.setZMAConsent === 'function';
            setRegLoginDimensions(googleAnalyticsId);
            trackAuth(AUTH_EVENT_TYPE.AuthSuccess)({
              pageProps: _this.pageTrackingProps,
              componentProps: {
                clickstream_trigger: {
                  trigger_source_nm: TRIGGER_SOURCE.ProRegisterForm
                },
                auth_form: {
                  auth_method_cd: AUTH_METHOD$1.EmailPassword
                }
              }
            });
            handleRegisterActions({
              allowLogin: !hasRequiredVerification,
              onSuccess: onSuccess,
              persistLightbox: hasZmaHandler || hasVerification,
              redirectURL: hasVerification ? '' : redirectURL,
              res: res
            });

            if (hasVerification) {
              history.push(ROUTES.verifyOnRegister);
            }

            if (hasZmaHandler) {
              setRegLoginPath('/user/acct/zma');
            }
          },
          error: function error(err, _ref) {
            var errorMessage = _ref.errorMessage;

            // We have different non-200 events that are not failures, but all 500+ are bad
            if (err.status >= 500 && _this.state.isSubmitting) {
              profileIntervalFail(REGISTER_PROFILE_SUBMIT);
            }

            _this.setState({
              isSubmitting: false
            });

            trackAuth(AUTH_EVENT_TYPE.RegisterError)({
              pageProps: _this.pageTrackingProps,
              componentProps: {
                clickstream_trigger: {
                  trigger_source_nm: TRIGGER_SOURCE.ProRegisterForm
                },
                auth_form: {
                  error_message_txt: errorMessage
                }
              }
            });
            onFailure();
          },
          history: history
        });
      }, function () {});
    });

    _this.getRedirectURL = function (isPro, proLevel) {
      var _this$props2 = _this.props,
          redirectUrl = _this$props2.redirectUrl,
          hideConfirmPassword = _this$props2.hideConfirmPassword;
      var url = redirectUrl; // If redirectUrl not explicitly set and user is a pro try to follow the corresponding redirectUrl

      if (isPro && (!url || url === DEFAULT_REDIRECT_URL) && !hideConfirmPassword) {
        url = REG_PRO_TYPES[proLevel].redirectUrl;
      }

      return url;
    };

    _this.handleFormSubmit = function (event) {
      event.preventDefault();

      _this.onSubmit();
    };

    return _this;
  }

  var _proto = ProRegisterForm.prototype;

  _proto.componentWillMount = function componentWillMount() {
    var props = this.props;
    var newEmail = props.inputs.newEmail,
        register = props.register; // Reset any social errors

    props.hideFacebookError(); // Hide expanded email form

    props.hideEmailForm(); // Update email input with current newEmail value

    if (newEmail.value) {
      props.syncInputValues(newEmail, 'email');
    } // Clear password field when leaving


    props.resetProfessionalForm(['newPassword']); // reset validation for password

    props.resetPasswordValidation(PASSWORD_VALIDATORS);

    if (!register.isGeoIPApiCalled) {
      props.setEmailConsentIfCanada();
    }
  };

  _proto.componentDidMount = function componentDidMount() {
    // using trackAuth instead of pageView because no lightbox version of this form
    trackAuth(AUTH_EVENT_TYPE.AuthPageView)({
      pageProps: this.pageTrackingProps
    });
  };

  _proto.componentWillReceiveProps = function componentWillReceiveProps(nextProps) {
    if (this.props.disableSubmitOnValidate) {
      this.updateFormValidation(nextProps);
    }
  };

  /**
   * Check whether both email and password fields pass validation, and set the validated state
   * @param {Object} props
   */
  _proto.updateFormValidation = function updateFormValidation(_ref2) {
    var inputs = _ref2.inputs,
        inputConfigs = _ref2.inputConfigs,
        register = _ref2.register;

    var _this$props$getInputP2 = this.props.getInputProps(inputConfigs, inputs),
        newEmail = _this$props$getInputP2.newEmail,
        newPassword = _this$props$getInputP2.newPassword;

    var hideConfirmPassword = this.props.hideConfirmPassword;
    var validated = newEmail.value && !newEmail.error;

    if (!hideConfirmPassword) {
      validated = validated && newPassword.value && !newPassword.error;
    }

    if (register.validated !== validated) {
      this.props.validateForm(validated);
    }
  };

  _proto.render = function render() {
    var _this2 = this;

    var _this$props3 = this.props,
        disableEmailForm = _this$props3.disableEmailForm,
        hideConfirmPassword = _this$props3.hideConfirmPassword,
        showEmailOptIn = _this$props3.showEmailOptIn,
        inputs = _this$props3.inputs,
        inputConfigs = _this$props3.inputConfigs,
        disableSubmitOnValidate = _this$props3.disableSubmitOnValidate,
        register = _this$props3.register,
        emailValidationEnabled = _this$props3.emailValidationEnabled;

    var _this$props$getInputP3 = this.props.getInputProps(inputConfigs, inputs),
        areaCode = _this$props$getInputP3.areaCode,
        authToken = _this$props$getInputP3.authToken,
        emailConsent = _this$props$getInputP3.emailConsent,
        firstName = _this$props$getInputP3.firstName,
        lastName = _this$props$getInputP3.lastName,
        newEmail = _this$props$getInputP3.newEmail,
        newPassword = _this$props$getInputP3.newPassword,
        phoneExt = _this$props$getInputP3.phoneExt,
        phonePrefix = _this$props$getInputP3.phonePrefix,
        phoneSuffix = _this$props$getInputP3.phoneSuffix,
        proSelect = _this$props$getInputP3.proSelect,
        zipCode = _this$props$getInputP3.zipCode;

    var formErrorMessage = register.formErrorMessage,
        newPasswordValidationMessages = register.newPasswordValidationMessages,
        showEmailConsent = register.showEmailConsent,
        validated = register.validated,
        emailSuggestion = register.emailSuggestion;
    var isSubmitEnabled = !disableSubmitOnValidate || validated;
    var isRealEstateAgent = proSelect.value === '1';
    var isMortgageLender = proSelect.value === '2';
    var showEmailConsentCheckbox = showEmailConsent || showEmailOptIn; // handling for manually enforced pro login
    // if (!showProRegCheckbox) {
    //     proCheck.value = false;
    // } else if (isPro || (alwaysPro && !overrideProSelection)) {
    //     proCheck.value = true;
    // }

    newPassword.validationMessages = newPasswordValidationMessages;
    return /*#__PURE__*/React__default.createElement(Form, {
      onSubmit: this.handleFormSubmit
    }, /*#__PURE__*/React__default.createElement(ReduxInput$1, authToken), /*#__PURE__*/React__default.createElement(ReduxInputField$1, _extends({
      label: "Email"
    }, newEmail, {
      disabled: disableEmailForm,
      helpText: emailValidationEnabled ? emailSuggestion : ''
    })), !hideConfirmPassword && /*#__PURE__*/React__default.createElement(ReduxPasswordField$1, newPassword), /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(Heading, {
      marginY: "sm",
      as: "h3",
      level: 5
    }, "Professional Information"), /*#__PURE__*/React__default.createElement(ReduxSelectField$1, _extends({
      label: "Professional type"
    }, proSelect)), !isMortgageLender && /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(Flex, {
      display: "flex",
      marginBottom: "sm"
    }, /*#__PURE__*/React__default.createElement(Flex, {
      flex: "1 1 100%",
      marginRight: "sm"
    }, /*#__PURE__*/React__default.createElement(ReduxInputField$1, _extends({
      label: "First name"
    }, firstName))), /*#__PURE__*/React__default.createElement(Flex, {
      flex: "1 1 100%"
    }, /*#__PURE__*/React__default.createElement(ReduxInputField$1, _extends({
      label: "Last name"
    }, lastName)))), /*#__PURE__*/React__default.createElement(ReduxInputField$1, _extends({
      label: "Zip/Postal"
    }, zipCode)), /*#__PURE__*/React__default.createElement(Flex, {
      display: "flex",
      marginBottom: "sm"
    }, /*#__PURE__*/React__default.createElement(Flex, {
      flexGrow: 3,
      marginRight: isRealEstateAgent ? undefined : 'sm'
    }, /*#__PURE__*/React__default.createElement(FormField, null, /*#__PURE__*/React__default.createElement(Label, {
      htmlFor: areaCode._id
    }, isRealEstateAgent ? PHONE_LABEL : 'Phone number'), /*#__PURE__*/React__default.createElement(Flex, {
      display: "flex"
    }, /*#__PURE__*/React__default.createElement(Flex, {
      flexGrow: 1,
      marginRight: "sm"
    }, /*#__PURE__*/React__default.createElement(ReduxInputField$1, areaCode)), /*#__PURE__*/React__default.createElement(Flex, {
      flexGrow: 1,
      marginRight: "sm"
    }, /*#__PURE__*/React__default.createElement(ReduxInputField$1, phonePrefix)), /*#__PURE__*/React__default.createElement(Flex, {
      flexGrow: 1
    }, /*#__PURE__*/React__default.createElement(ReduxInputField$1, phoneSuffix))))), !isRealEstateAgent && /*#__PURE__*/React__default.createElement(Flex, {
      flexGrow: 1
    }, /*#__PURE__*/React__default.createElement(ReduxInputField$1, _extends({
      label: "Ext"
    }, phoneExt)))))), showEmailConsentCheckbox && /*#__PURE__*/React__default.createElement(ReduxCheckboxField$1, emailConsent), /*#__PURE__*/React__default.createElement(FormActions, null, /*#__PURE__*/React__default.createElement(Button, {
      as: "input",
      type: "submit",
      fluid: true,
      buttonType: "primary",
      value: BUTTON_TYPES.Continue.value,
      disabled: !isSubmitEnabled,
      onClick: function onClick(e) {
        var _e$nativeEvent;

        return _this2.setState({
          inputSelector: getInputSelector$1((_e$nativeEvent = e.nativeEvent) === null || _e$nativeEvent === void 0 ? void 0 : _e$nativeEvent.detail)
        });
      }
    })), /*#__PURE__*/React__default.createElement(TermsOfUseBlock, null), formErrorMessage && /*#__PURE__*/React__default.createElement(FormError, {
      text: formErrorMessage
    }));
  };

  return ProRegisterForm;
}(React__default.Component);

ProRegisterForm.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The history object provides by react router's withRouter HOC:
   * https://reactrouter.com/web/api/withRouter
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  history: PropTypes.object,

  /** **********************************************************
   * Props defined inside this RegisterForm component
   *********************************************************** */

  /**
   * Prevent user from clicking on the submit button if the inputs fail validation
   */
  disableSubmitOnValidate: PropTypes.bool.isRequired,
  emailValidationEnabled: PropTypes.bool,

  /**
   * Whether to hide confirm password input
   */
  hideConfirmPassword: PropTypes.bool,

  /**
   * Register state
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  register: PropTypes.object,
  resetPasswordValidation: PropTypes.func,

  /**
   * Pro form reset action
   */
  resetProfessionalForm: PropTypes.func.isRequired,
  setEmailConsentIfCanada: PropTypes.func,

  /**
   * Whether to show email opt in checkbox
   */
  showEmailOptIn: PropTypes.bool,
  validateForm: PropTypes.func,
  verifyOnRegisterTreatment: PropTypes.string,

  /** **********************************************************
   * Props from the direct parent component (LoginRegister)
   *********************************************************** */

  /**
   * Login state
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  login: PropTypes.object,

  /**
   * failure callback for submitForm
   */
  onFailure: PropTypes.func,

  /**
   * success callback for submitForm
   */
  onSuccess: PropTypes.func,

  /**
   * Redirect URL used after login.
   */
  redirectUrl: PropTypes.string,

  /**
   * Input validation action
   */
  validateInputs: PropTypes.func.isRequired,

  /**
   * Submit form action
   */
  submitForm: PropTypes.func.isRequired,

  /**
   * Input props action
   */
  getInputProps: PropTypes.func.isRequired,

  /**
   * input fields
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  inputs: PropTypes.object,

  /**
   * Input configs that might be altered subject to AB tests or other external factors.
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  inputConfigs: PropTypes.object,

  /**
   * disable the email form entry field (requires a valid email)
   */
  disableEmailForm: PropTypes.bool.isRequired,
  views: appSubView,
  hideFacebookError: PropTypes.func,
  hideEmailForm: PropTypes.func,
  gaLabel: PropTypes.string.isRequired,
  syncInputValues: PropTypes.func,
  headerText: PropTypes.string,
  isLightbox: PropTypes.bool,
  entryPoint: PropTypes.string,
  zmaHandlerFns: PropTypes.shape({
    getZMAConsent: PropTypes.func,
    setZMAConsent: PropTypes.func,
    passwordContinueUrl: PropTypes.string
  })
} : {};
var mapDispatchToProps$c = {
  resetPasswordValidation: resetPasswordValidation,
  resetProfessionalForm: resetProfessionalForm,
  setEmailConsentIfCanada: setEmailConsentIfCanada,
  validateForm: validateForm
};

var mapStateToProps$e = function mapStateToProps(state) {
  return {
    // alwaysPro: state.appConfig.alwaysPro,
    disableSubmitOnValidate: state.appConfig.disableSubmitOnValidate,
    emailValidationEnabled: state.appConfig.emailValidationEnabled,
    hideConfirmPassword: state.appConfig.hideConfirmPassword,
    register: state.login,
    showEmailOptIn: state.appConfig.showEmailOptIn,
    // showProRegCheckbox: state.regLogin.uiConfig.showProRegCheckbox,
    verifyOnRegisterTreatment: state.appConfig.verifyOnRegisterTreatment
  };
};

var ProRegisterForm$1 = withRouter(connectToUserSession(mapStateToProps$e, mapDispatchToProps$c)(ProRegisterForm));

var ProRegister = /*#__PURE__*/function (_React$PureComponent) {
  _inheritsLoose(ProRegister, _React$PureComponent);

  function ProRegister() {
    return _React$PureComponent.apply(this, arguments) || this;
  }

  var _proto = ProRegister.prototype;

  _proto.render = function render() {
    var _this$props = this.props,
        headerText = _this$props.headerText,
        login = _this$props.login,
        subHeaderText = _this$props.subHeaderText;
    var contextData = login.contextData;
    return /*#__PURE__*/React__default.createElement(ViewWrapper, null, /*#__PURE__*/React__default.createElement(Header, {
      text: headerText
    }, subHeaderText, contextData && /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("br", null), contextData.message)), /*#__PURE__*/React__default.createElement(BodyWrapper, null, /*#__PURE__*/React__default.createElement(ProRegisterForm$1, this.props)));
  };

  return ProRegister;
}(React__default.PureComponent);

ProRegister.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Header text for view
   */
  headerText: PropTypes.string.isRequired,

  /**
   * subheader text for view
   */
  subHeaderText: PropTypes.string,

  /**
   * failure callback for submitForm
   */
  onFailure: PropTypes.func,

  /**
   * success callback for submitForm
   */
  onSuccess: PropTypes.func,

  /**
   * Redirect URL used after login.
   */
  redirectUrl: PropTypes.string,

  /**
   * Login state
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  login: PropTypes.object,
  views: appSubView,
  entryPoint: PropTypes.string
} : {};
var mapDispatchToProps$d = {
  getInputProps: getInputProps,
  hideEmailForm: hideEmailForm,
  hideFacebookError: hideFacebookError,
  submitForm: submitForm,
  syncInputValues: syncInputValues,
  validateInputs: validateInputs
};

var mapStateToProps$f = function mapStateToProps(state) {
  return {
    headerText: state.regLogin.uiConfig.headerText,
    subHeaderText: state.regLogin.uiConfig.subHeaderText,
    inputConfigs: state.appConfig.inputConfigs,
    inputs: state.inputs,
    login: state.login,
    views: state.appConfig.views,
    onFailure: state.appConfig.onFailure,
    onSuccess: state.appConfig.onSuccess,
    redirectUrl: state.regLogin.redirectUrl,
    entryPoint: getEntryPoint(state.appConfig.entryPoint, state.regLogin.uiConfig.entryPoint),
    zmaHandlerFns: state.regLogin.zmaHandlerFns
  };
};

var ProRegister$1 = withRouter(connectToUserSession(mapStateToProps$f, mapDispatchToProps$d)(ProRegister));

function _templateObject$b() {
  var data = _taggedTemplateLiteralLoose(["\n        ", " {\n            ", "\n            @media ", " {\n                max-width: ", "px;\n                max-height: 110%;\n\n                ", " {\n                    margin-top: 0;\n                    border: 0;\n                }\n            }\n        }\n    "]);

  _templateObject$b = function _templateObject() {
    return data;
  };

  return data;
}
var AuthModalDialog = styled(ModalDialog)(function (props) {
  var screenWidth = window.innerWidth;
  var maxWidth = Math.min(MAX_INLINE_WIDTH, screenWidth - 2 * INLINE_BUFFER); // At md_lte, the modal goes fullscreen and uses default styles

  return css(_templateObject$b(), StyledDialog, props.disableClose && '& > button { display: none }', mediaBreakpointMixin('lg'), maxWidth, StyledDialogBody);
});

var CoreLayout = /*#__PURE__*/function (_Component) {
  _inheritsLoose(CoreLayout, _Component);

  function CoreLayout(props) {
    var _this;

    _this = _Component.call(this, props) || this;
    _this.isLightbox = props.isLightbox === 'true';
    _this.googleClientId = props.googleClientId;
    _this.hideLightbox = _this.hideLightbox.bind(_assertThisInitialized(_this));
    _this.showGoogleOneTap = _this.showGoogleOneTap.bind(_assertThisInitialized(_this));
    _this.renderToLightbox = _this.renderToLightbox.bind(_assertThisInitialized(_this));
    _this.wrapContainer = _this.wrapContainer.bind(_assertThisInitialized(_this));
    _this.renderToNodeTarget = _this.renderToNodeTarget.bind(_assertThisInitialized(_this));
    _this.onLightboxToggle = _this.onLightboxToggle.bind(_assertThisInitialized(_this));

    if (props.path !== '/') {
      props.history.push(props.path);
    }

    return _this;
  }

  var _proto = CoreLayout.prototype;

  _proto.componentDidMount = function componentDidMount() {
    var _this2 = this;

    setRegLoginNode(this.layoutContainer);

    if (this.props.history.location.pathname.includes(ROUTES.employeeLogin)) {
      initiateOktaLogin(function (res) {
        handleLoggedIn(false, res, _this2.props.redirectUrl);
      }, function (err) {
        _this2.props.dispatch(handleError(err.status));
      });
    }

    close();

    if (this.getShouldShowGoogleOneTap()) {
      this.showGoogleOneTap();
    }
  };

  _proto.componentWillReceiveProps = function componentWillReceiveProps(nextProps) {
    var inputValues = nextProps.inputValues,
        path = nextProps.path,
        history = nextProps.history,
        dispatch = nextProps.dispatch;

    if (!_isEqual(this.props.inputValues, inputValues)) {
      setInputValues(_omit(inputValues, BLACKLISTED_INPUTS))(dispatch);
    }

    if (this.props.path !== path) {
      history.push(path);
    }
  };

  _proto.componentDidUpdate = function componentDidUpdate(prevProps) {
    var _this3 = this;

    if (prevProps.showLightbox !== this.props.showLightbox) {
      this.onLightboxToggle(function () {
        _this3.showGoogleOneTap();
      }, function () {
        close(); // DEPRECATED, remove once nothing depends on @zillow/auth-lightbox-api@0.3.1

        trigger(LIGHTBOX_HIDDEN, {
          loggedIn: _this3.props.loggedIn
        });
      });
    }

    if (prevProps.showGoogleOneTap !== this.props.showGoogleOneTap) {
      if (this.props.showGoogleOneTap) {
        this.showGoogleOneTap();
      } else {
        close();
      }
    }

    if (this.props.history.location.pathname.includes(ROUTES.employeeLoginAuthCallback)) {
      handleOktaLoginRedirect(function (res) {
        handleLoggedIn(false, res, _this3.props.redirectUrl);
      }, function (err) {
        _this3.props.dispatch(handleError(err.status));
      });
    }
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    setRegLoginNode();
  };

  _proto.onLightboxToggle = function onLightboxToggle(onShow, onHide) {
    if (this.props.showLightbox) {
      onShow();
    } else {
      onHide();
    }
  }
  /**
   * @param {Array} blacklistPages Array of pages to suspend one tap thingy
   * @return {Boolean}
   */
  ;

  _proto.getShouldShowGoogleOneTap = function getShouldShowGoogleOneTap(blacklistPages) {
    if (blacklistPages === void 0) {
      blacklistPages = [ROUTES.changePasswordForgot, ROUTES.confirmEmail];
    }

    return this.props.disableGoogleOneTap ? false : blacklistPages.indexOf(this.props.history.location.pathname) < 0 && (this.props.showGoogleOneTapOnload || this.getShouldRenderApp());
  };

  _proto.getShouldRenderApp = function getShouldRenderApp() {
    var _this$props = this.props,
        isLightbox = _this$props.isLightbox,
        showLightbox = _this$props.showLightbox;
    return !isLightbox || showLightbox;
  }
  /**
   * Shows Google one tap dialog.
   * @kind function
   */
  ;

  _proto.showGoogleOneTap = function showGoogleOneTap() {
    var _this$props2 = this.props,
        history = _this$props2.history,
        redirectUrl = _this$props2.redirectUrl,
        showLightbox = _this$props2.showLightbox,
        googleClientId = _this$props2.googleClientId,
        dispatch = _this$props2.dispatch,
        onSuccess = _this$props2.onSuccess; // One tap takes a `context` parameter to determine dialog text

    var context = 'signIn';

    try {
      var pathname = history.location.pathname;

      if (pathname.indexOf('/register') > -1) {
        context = 'signUp';
      }
    } catch (err) {
      console.log("Couldnt parse location for one tap: " + err);
    }

    init(googleClientId, context, {
      successHandler: function successHandler(res) {
        return new Promise(function ($return, $error) {
          if (onSuccess) {
            return Promise.resolve(onSuccess(res)).then(function ($await_4) {
              try {
                return $If_3.call(this);
              } catch ($boundEx) {
                return $error($boundEx);
              }
            }.bind(this), $error);
          }

          function $If_3() {
            handleLoggedIn(false, {}, redirectUrl);
            return $return();
          }

          return $If_3.call(this);
        });
      },
      errorHandler: function errorHandler(message) {
        dispatch(handleError(404, message));
      },
      dismissHandler: function dismissHandler() {
        // if one tap was triggered on load and user has dismissed it, set the break so it
        // does not show for the set duration
        if (!showLightbox) {
          setOneTapOnLoadBreak();
        }
      }
    });
  }
  /**
   * Toggles class to hide lightbox and mask, then execute onClose
   * @kind function
   */
  ;

  _proto.hideLightbox = function hideLightbox() {
    hideRegLoginLightbox();
    this.props.onClose();
  }
  /**
   * Wraps the node in act-auth-container
   * @param {Object} node layout
   * @return {Object} node layout
   */
  ;

  _proto.wrapContainer = function wrapContainer(layout) {
    var _this4 = this;

    var _this$props3 = this.props,
        renderNodeTarget = _this$props3.renderNodeTarget,
        showLightbox = _this$props3.showLightbox;
    var shouldRenderLayout = showLightbox || !_isNil(renderNodeTarget) || !this.isLightbox;
    return /*#__PURE__*/React__default.createElement("div", {
      className: "act-auth-container",
      ref: function ref(node) {
        _this4.layoutContainer = node;
      }
    }, shouldRenderLayout && layout);
  }
  /**
   * Renders the passed node into a the nodeTarget prop
   * @param {Object} node layout
   * @return {Object} node layout
   */
  ;

  _proto.renderToNodeTarget = function renderToNodeTarget(layout) {
    if (this.props.renderNodeTarget) {
      return /*#__PURE__*/createPortal(layout, this.props.renderNodeTarget);
    }

    return layout;
  }
  /**
   * Wraps the node with Lightbox component
   * @param {Object} node layout
   * @return {Object} node layout
   */
  ;

  _proto.renderToLightbox = function renderToLightbox(layout) {
    var _this5 = this;

    var _this$props4 = this.props,
        renderNodeTarget = _this$props4.renderNodeTarget,
        showLightbox = _this$props4.showLightbox,
        history = _this$props4.history,
        isActivationExperimentsFeatureEnabled = _this$props4.isActivationExperimentsFeatureEnabled,
        isActivationWorkflow = _this$props4.isActivationWorkflow,
        verifyOnRegisterTreatment = _this$props4.verifyOnRegisterTreatment,
        pageClickstreamTriggers = _this$props4.clickstreamTrigger,
        topicTags = _this$props4.topicTags,
        headerText = _this$props4.headerText,
        inputs = _this$props4.inputs,
        inputConfigs = _this$props4.inputConfigs,
        isOnboardingEligible = _this$props4.isOnboardingEligible,
        isActivationOnboardingEnabled = _this$props4.isActivationOnboardingEnabled;
    var disableClose = verifyOnRegisterTreatment && verifyOnRegisterTreatment === AB_AUTH_VERIFY_ON_REGISTER.REQUIRED;
    var onClose = yieldCallback(function (closeType) {
      var _history$location, _history$location$pat, _history$location2, _history$location2$pa;

      var didUserExitActivationLoginModal = localStorage.getItem(DID_USER_EXIT_ACTIVATION_LOGIN_MODAL);

      var _this5$props$getInput = _this5.props.getInputProps(inputConfigs, inputs),
          email = _this5$props$getInput.email;

      if (isActivationOnboardingEnabled && isOnboardingEligible && (history === null || history === void 0 ? void 0 : (_history$location = history.location) === null || _history$location === void 0 ? void 0 : (_history$location$pat = _history$location.pathname) === null || _history$location$pat === void 0 ? void 0 : _history$location$pat.includes('/passwordless/create-password'))) {
        try {
          setOnboardingConfig({
            email: email.value
          });
          showOnboardingQuestionnaire();
        } catch (err) {
          console.log(err);
        }
      }

      if (isActivationWorkflow && isActivationExperimentsFeatureEnabled && (history === null || history === void 0 ? void 0 : (_history$location2 = history.location) === null || _history$location2 === void 0 ? void 0 : (_history$location2$pa = _history$location2.pathname) === null || _history$location2$pa === void 0 ? void 0 : _history$location2$pa.includes('passwordless/signin')) && ( // Adding check so GA event wouldn't be invoked unwantedly due to AUTH-3717 bug
      didUserExitActivationLoginModal === null || didUserExitActivationLoginModal !== 'true')) {
        localStorage.setItem(DID_USER_EXIT_ACTIVATION_LOGIN_MODAL, 'true');
      } // eslint-disable-next-line camelcase


      var auth_method_cd = _this5.props.enableOTP && (_this5.props.path === '/' || _this5.props.path === ROUTES.OTPAuth) ? 'email_otp' : 'email_pw';
      track({
        category: 'auth',
        action: closeType,
        label: _this5.props.gaLabel
      }, {
        newLaneEvent: getAuthCloseNewLaneProps({
          clickstreamTrigger: Object.assign({}, pageClickstreamTriggers, {
            triggerSource: authCloseTriggerSource(closeType),
            inputSelector: authCloseInputSelector(closeType)
          }),
          authForm: closeType === 'authclose' ? {
            title_nm: headerText,
            displayed_button_txt: 'x',
            auth_method_cd: auth_method_cd
          } : {
            title_nm: headerText,
            auth_method_cd: auth_method_cd
          },
          semantic: {
            topic_tag_txt: topicTags
          }
        })
      });
      setRegLoginUiConfig({
        topicTag: undefined,
        entryPoint: undefined
      });
    });

    if (this.isLightbox && _isNil(renderNodeTarget) && showLightbox) {
      return /*#__PURE__*/React__default.createElement(AuthModalDialog, {
        className: "act-auth-lightbox",
        isOpen: true,
        disableClose: disableClose,
        onClose: this.hideLightbox,
        onCloseButtonClick: function onCloseButtonClick() {
          return onClose('authclose');
        },
        onESCKeyPress: function onESCKeyPress() {
          return onClose('authclose/esc');
        },
        onOutsideClick: function onOutsideClick() {
          return onClose('authclose/bg');
        },
        portal: false,
        wide: true,
        body: layout,
        mediaQuery: {
          md_lte: {
            fullScreen: true
          }
        }
      });
    }

    return layout;
  };

  _proto.render = function render() {
    var isOktaAuthenticationFlow = this.props.history.location.pathname.includes(ROUTES.employeeLogin) || this.props.history.location.pathname.includes(ROUTES.employeeLoginAuthCallback);

    if (isOktaAuthenticationFlow) {
      return /*#__PURE__*/React__default.createElement(PageLoader, null);
    }

    var getLayout = pipe(this.renderToLightbox, this.wrapContainer, this.renderToNodeTarget);
    var defaultRoute = this.props.enableOTP ? OTPAuth$1 : Login;
    return getLayout( /*#__PURE__*/React__default.createElement(Switch, null, /*#__PURE__*/React__default.createElement(Route, {
      path: ROUTES.login,
      component: Login
    }), /*#__PURE__*/React__default.createElement(Route, {
      path: ROUTES.register,
      component: Register
    }), /*#__PURE__*/React__default.createElement(Route, {
      path: ROUTES.proRegister,
      component: ProRegister$1
    }), /*#__PURE__*/React__default.createElement(Route, {
      path: ROUTES.registerAdmin,
      component: Register
    }), /*#__PURE__*/React__default.createElement(Route, {
      path: ROUTES.forgot,
      component: ForgotPasswordRoute
    }), /*#__PURE__*/React__default.createElement(Route, {
      path: ROUTES.confirmEmail,
      component: ConfirmEmailRoute
    }), /*#__PURE__*/React__default.createElement(Route, {
      path: ROUTES.changePasswordForgot,
      component: ForgotPasswordRoute
    }), /*#__PURE__*/React__default.createElement(Route, {
      path: ROUTES.forgotSuccess,
      component: ForgotPasswordSuccessRoute
    }), /*#__PURE__*/React__default.createElement(Route, {
      path: ROUTES.invalidTokenMsg,
      component: InvalidTokenMsgRoute
    }), /*#__PURE__*/React__default.createElement(Route, {
      path: ROUTES.passwordlessSignin,
      component: PasswordlessSigninRoute
    }), /*#__PURE__*/React__default.createElement(Route, {
      path: ROUTES.passwordlessCreatePassword,
      component: PasswordlessCreatePasswordRoute
    }), /*#__PURE__*/React__default.createElement(Route, {
      path: ROUTES.recovery,
      component: PasswordRecoveryRoute
    }), /*#__PURE__*/React__default.createElement(Route, {
      path: ROUTES.multiFactorAuth,
      component: MfaRoute
    }), /*#__PURE__*/React__default.createElement(Route, {
      path: ROUTES.verifyOnRegister,
      component: VerifyOnRegisterRoute
    }), /*#__PURE__*/React__default.createElement(Route, {
      path: ROUTES.zma,
      component: ZmaRoute
    }), /*#__PURE__*/React__default.createElement(Route, {
      path: ROUTES.createPassword,
      component: CreatePasswordRoute
    }), /*#__PURE__*/React__default.createElement(Route, {
      path: ROUTES.verifyAccount,
      component: VerifyAccount
    }), /*#__PURE__*/React__default.createElement(Route, {
      path: ROUTES.OTPAuth,
      component: OTPAuth$1
    }), /*#__PURE__*/React__default.createElement(Route, {
      path: "/",
      component: defaultRoute
    })));
  };

  return CoreLayout;
}(Component);

CoreLayout.propTypes = process.env.NODE_ENV !== "production" ? {
  disableGoogleOneTap: PropTypes.bool,
  dispatch: PropTypes.func,
  googleClientId: PropTypes.string,
  history: PropTypes.object,
  inputValues: PropTypes.object,
  isLightbox: PropTypes.string,
  location: PropTypes.object,
  loggedIn: PropTypes.bool,
  onClose: PropTypes.func,
  path: PropTypes.string,
  renderNodeTarget: function renderNodeTarget(props, propName) {
    // eslint-disable-next-line no-undef
    if (props[propName] && !(props[propName] instanceof Node)) {
      return new Error("reg-login: " + propName + " is not a DOM Node");
    }

    return null;
  },
  redirectUrl: PropTypes.string,
  showGoogleOneTap: PropTypes.bool,
  showGoogleOneTapOnload: PropTypes.bool,
  showLightbox: PropTypes.bool,
  isActivationExperimentsFeatureEnabled: PropTypes.bool,
  isActivationWorkflow: PropTypes.bool,
  gaLabel: PropTypes.string.isRequired,
  verifyOnRegisterTreatment: PropTypes.string,

  /**
   * Clickstream trigger data set when page loads.
   * This will be used by auth close events so that we know which auth page got closed.
   */
  clickstreamTrigger: PropTypes.shape({
    triggerLocation: PropTypes.string,
    triggerObject: PropTypes.string,
    triggerSource: PropTypes.string,
    inputSelector: PropTypes.string
  }),
  topicTags: PropTypes.arrayOf(PropTypes.string),
  headerText: PropTypes.string,

  /**
   * Input props action
   */
  getInputProps: PropTypes.func.isRequired,

  /**
   * input fields
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  inputs: PropTypes.object,

  /**
   * Input configs that might be altered subject
   * to AB tests or other external factors.
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  inputConfigs: PropTypes.object,

  /**
   * if onboarding modal experiment is enabled
   */
  isActivationOnboardingEnabled: PropTypes.bool,

  /**
   * if user is eligible for onboarding experiment
   */
  isOnboardingEligible: PropTypes.bool,
  enableOTP: PropTypes.bool,
  onSuccess: PropTypes.func
} : {};
CoreLayout.defaultProps = {
  onClose: NOOP
};
CoreLayout.contextTypes = {
  store: PropTypes.object
};

var mapDispatchToProps$e = function mapDispatchToProps(dispatch) {
  return Object.assign({
    dispatch: dispatch
  }, bindActionCreators({
    getInputProps: getInputProps
  }, dispatch));
};

var mapStateToProps$g = function mapStateToProps(state) {
  return {
    disableGoogleOneTap: state.appConfig.disableGoogleOneTap,
    googleClientId: state.appConfig.googleClientId,
    inputValues: state.regLogin.inputValues,
    isLightbox: state.appConfig.isLightbox,
    onClose: state.login.onClose,
    path: state.regLogin.path,
    redirectUrl: state.regLogin.redirectUrl,
    renderNodeTarget: state.regLogin.uiConfig.renderNodeTarget,
    showGoogleOneTap: state.regLogin.showGoogleOneTap,
    showGoogleOneTapOnload: state.appConfig.showGoogleOneTapOnload,
    showLightbox: state.regLogin.showLightbox,
    isActivationExperimentsFeatureEnabled: state.appConfig.isActivationExperimentsFeatureEnabled,
    isActivationWorkflow: state.regLogin.uiConfig.isActivationWorkflow,
    verifyOnRegisterTreatment: state.appConfig.verifyOnRegisterTreatment,
    clickstreamTrigger: state.clickstream.clickstreamTrigger,
    headerText: state.regLogin.uiConfig.headerText,
    inputs: state.inputs,
    inputConfigs: state.appConfig.inputConfigs,
    isActivationOnboardingEnabled: state.appConfig.isActivationOnboardingEnabled,
    isOnboardingEligible: state.appConfig.isActivationOnboardingEnabled && state.onboarding && state.onboarding.eligible,
    topicTags: getTopicTags(state.appConfig.topicTags, state.regLogin.uiConfig.topicTags),
    entryPoint: getEntryPoint(state.appConfig.entryPoint, state.regLogin.uiConfig.entryPoint),
    enableOTP: state.appConfig.enableOTP,
    gaLabel: state.regLogin.uiConfig.gaLabel,
    onSuccess: state.appConfig.onSuccess
  };
};

var CoreLayout$1 = withRouter(connectToUserSession(mapStateToProps$g, mapDispatchToProps$e)(CoreLayout));

/* eslint-disable zillow/import/prefer-default-export */
var initialState$1 = {
  isFormHidden: true,
  newPasswordValidationMessages: getNewPasswordValidationMessagesInitialState(),
  showEmailConsent: false,
  isValidEmailToken: APP_CONFIG.isValidEmailToken === 'true',
  isGeoIPApiCalled: false,
  overrideProSelection: false,
  countryCode: US_COUNTRY_CODE,
  emailSuggestion: '',
  hasSentResetPWEmail: false
};
/**
 * Reduces register state.
 * @kind function
 * @param  {Object} state
 *                    current state to be reduced
 * @param  {String} action
 *                    action payload
 * @return {Object} reduced state
 */

var loginReducer = function loginReducer(state, action) {
  if (state === void 0) {
    state = initialState$1;
  }

  switch (action.type) {
    case AUTH_TOKEN:
      return Object.assign({}, state, {
        authToken: action.authToken
      });

    case HIDE_EMAIL_FORM:
      return Object.assign({}, state, {
        isFormHidden: true
      });

    case NETWORK_ERROR:
      return Object.assign({}, state, {
        formErrorMessage: action.message
      });

    case REGISTER_CONTEXT:
      return Object.assign({}, state, {
        contextData: action.contextData
      });

    case RESET_FORM_ERROR:
      return Object.assign({}, state, {
        formErrorMessage: null
      });

    case SHOW_EMAIL_FORM:
      return Object.assign({}, state, {
        isFormHidden: false
      });

    case VALIDATE_ON_KEYUP:
      return Object.assign({}, state, {
        validateOnKeyup: true
      });

    case FORM_VALIDATED:
      return Object.assign({}, state, {
        validated: action.validated
      });

    case SET_ON_CLOSE:
      return Object.assign({}, state, {
        onClose: action.onClose
      });

    case SET_NEW_PASSWORD_VALIDATION_MESSAGES:
      return Object.assign({}, state, {
        newPasswordValidationMessages: action.messages
      });

    case UPDATE_VALID_EMAIL_TOKEN:
      return Object.assign({}, state, {
        isValidEmailToken: action.isValid
      });

    case SET_COUNTRY_CODE:
      return Object.assign({}, state, {
        countryCode: action.countryCode
      });

    case SET_EMAIL_CONSENT_DISPLAY:
      return Object.assign({}, state, {
        showEmailConsent: true
      });

    case HIDE_EMAIL_CONSENT_DISPLAY:
      return Object.assign({}, state, {
        showEmailConsent: false
      });

    case SET_GEOIP_API_CALLED:
      return Object.assign({}, state, {
        isGeoIPApiCalled: true
      });

    case OVERRIDE_PRO_SELECTION:
      return Object.assign({}, state, {
        overrideProSelection: !state.overrideProSelection
      });

    case SET_PHONE_NUMBER:
      return Object.assign({}, state, {
        phoneNumber: action.phoneNumber
      });

    case SET_EMAIL_SUGGESTION:
      return Object.assign({}, state, {
        emailSuggestion: action.emailSuggestion
      });

    case HAS_SENT_RESET_PW_EMAIL:
      return Object.assign({}, state, {
        hasSentResetPWEmail: true
      });

    default:
      return state;
  }
};

/* eslint-disable zillow/import/prefer-default-export */
var initialState$2 = {
  fbErrorMessage: null,
  showFbError: false
};
/**
 * Reduces register state.
 * @kind function
 * @param  {Object} state
 *                    current state to be reduced
 * @param  {String} action
 *                    action payload
 * @return {Object} reduced state
 */

var socialReducer = function socialReducer(state, action) {
  if (state === void 0) {
    state = initialState$2;
  }

  switch (action.type) {
    case HIDE_FB_CONNECT_ERROR:
      return Object.assign({}, state, {
        showFbError: false
      });

    case SET_FB_CONNECT_ERROR:
      return Object.assign({}, state, {
        fbErrorMessage: action.message
      });

    case SHOW_FB_CONNECT_ERROR:
      return Object.assign({}, state, {
        showFbError: true
      });

    case SHOW_SOCIAL_ERROR:
      return Object.assign({}, state, {
        socialErrorMessage: action.message,
        showSocialError: true
      });

    case HIDE_SOCIAL_ERROR:
      return Object.assign({}, state, {
        showSocialError: false
      });

    default:
      return state;
  }
};

// import { routerReducer as router } from 'react-router-redux'

var makeRootReducer = function makeRootReducer(asyncReducers) {
  return combineReducers(Object.assign({
    // Add sync reducers here
    login: loginReducer,
    social: socialReducer,
    clickstream: clickstreamAuthFormReducer,
    appConfig: function appConfig(state) {
      if (state === void 0) {
        state = {};
      }

      return state;
    },
    abtestConfig: function abtestConfig(state) {
      if (state === void 0) {
        state = {};
      }

      return state;
    }
  }, asyncReducers));
};

var createStore = (function (initialState, asyncReducers) {
  if (initialState === void 0) {
    initialState = {};
  }

  // Middleware Configuration
  var middleware = [thunk]; // Store Enhancers

  var enhancers = []; // modified compose to allow for use of Redux Tools extension
  // https://github.com/zalmoxisus/redux-devtools-extension

  var composeEnhancers = (typeof window === "undefined" ? "undefined" : _typeof(window)) === 'object' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose; // Store Instantiation and HMR Setup

  var store = createStore$1(makeRootReducer(asyncReducers), initialState, composeEnhancers.apply(void 0, [applyMiddleware.apply(void 0, middleware)].concat(enhancers)));
  store.asyncReducers = asyncReducers; // Needed to update connected components on user session store update

  store.userSessionStoreUnsubscribe = subscribeToUserSessionStore(function () {
    store.dispatch({
      type: 'REG_LOGIN:ACTION:USER_SESSION_STORE_UPDATE'
    });
  });
  return store;
});

/* eslint-disable zillow/import/prefer-default-export */
/**
 * DEPRECATED
 * Setup event listeners for APP_REDIRECT, which is still being used by consumers of
 * auth-lightbox-api@0.3.0. We should remove this once everyone has updated to 1.0.0
 * @param {Object} config
 */

var addDeprecatedListeners = function addDeprecatedListeners(_ref) {
  var isLightbox = _ref.isLightbox;
  return function (e) {
    var detail = e.detail;
    var authProcess = detail.authProcess,
        hidden = detail.hidden,
        inputValues = detail.inputValues,
        path = detail.path,
        uiConfig = detail.uiConfig;
    var defaultUiConfig = {
      headerText: 'Welcome to Zillow',
      authAP: '',
      showTabs: true,
      gaLabel: 'generic/generic',
      // ensure value is set to empty unless explicitly set during APP_REDIRECT
      renderNodeTarget: null
    };
    setRegLoginUiConfig(Object.assign({}, defaultUiConfig, uiConfig)); // authProcess should be set after default UI Config extension

    if (authProcess) {
      setRegLoginUiConfig({
        gaLabel: authProcess
      });
    }

    if (path) {
      setRegLoginPath(path);
    }

    if (isLightbox) {
      if (!hidden) {
        showRegLoginLightbox();
      } else {
        hideRegLoginLightbox();
      }
    }

    if (inputValues) {
      setRegLoginInputValues(inputValues);
    }
  };
};

/* global ZMOB_nativeAPI */

/**
 * Checks for the existence of the ZMOB_nativeAPI (suggesting code is executed in an app), and
 * calls the native reg-login.
 * @param {string} appRegistrationReason Passed to native API for GA tracking
 * @param {string} appGaLabel Passed to native API for GA event labelling
 * @param {object} history browser's history object
 * @return {boolean} `true` if the ZMOB_nativeAPI was called
 */
var launchNativeRegLogin = function launchNativeRegLogin(appRegistrationReason, appGaLabel, history) {
  // eslint-disable-next-line camelcase
  if (typeof ZMOB_nativeAPI !== 'undefined' && typeof ZMOB_nativeAPI.launchLogin === 'function') {
    var login = true;

    try {
      var pathname = history.location.pathname;

      if (pathname.indexOf('/register') > -1) {
        login = false;
      }
    } catch (err) {
      console.warn("Couldn't parse location for the pathname: " + err);
    }

    ZMOB_nativeAPI.launchLogin(!login, appRegistrationReason, appGaLabel);
    return true;
  }

  return false;
};

var Router;

var App = /*#__PURE__*/function (_Component) {
  _inheritsLoose(App, _Component);

  function App(props) {
    var _this;

    _this = _Component.call(this, props) || this;

    var facebookAppId = props.facebookAppId,
        googleClientId = props.googleClientId,
        fromEmail = props.fromEmail,
        headerText = props.headerText,
        subHeaderText = props.subHeaderText,
        globalFormSubHeader = props.globalFormSubHeader,
        targetEmail = props.targetEmail,
        inputConfigs = props.inputConfigs,
        isLightbox = props.isLightbox,
        isNativeApp = props.isNativeApp,
        isShowEmailOptIn = props.isShowEmailOptIn,
        isAlwaysPro = props.isAlwaysPro,
        hideConfirmPassword = props.hideConfirmPassword,
        proTypes = props.proTypes,
        redirectUrl = props.redirectUrl,
        showProRegCheckbox = props.showProRegCheckbox,
        signInWithAppleEnabled = props.signInWithAppleEnabled,
        emailValidationEnabled = props.emailValidationEnabled,
        is2faOn = props.is2faOn,
        views = props.views,
        disableGoogleOneTap = props.disableGoogleOneTap,
        clientId = props.clientId,
        isActivationExperimentsFeatureEnabled = props.isActivationExperimentsFeatureEnabled,
        isActivationNewLaneMetricsEnabled = props.isActivationNewLaneMetricsEnabled,
        isActivationGAMetricsEnabled = props.isActivationGAMetricsEnabled,
        clickstreamTrigger = props.clickstreamTrigger,
        enableOTP = props.enableOTP,
        loginHint = props.loginHint,
        showLogo = props.showLogo,
        topicTag = props.topicTag,
        entryPoint = props.entryPoint,
        rest = _objectWithoutProperties(props, ["facebookAppId", "googleClientId", "fromEmail", "headerText", "subHeaderText", "globalFormSubHeader", "targetEmail", "inputConfigs", "isLightbox", "isNativeApp", "isShowEmailOptIn", "isAlwaysPro", "hideConfirmPassword", "proTypes", "redirectUrl", "showProRegCheckbox", "signInWithAppleEnabled", "emailValidationEnabled", "is2faOn", "views", "disableGoogleOneTap", "clientId", "isActivationExperimentsFeatureEnabled", "isActivationNewLaneMetricsEnabled", "isActivationGAMetricsEnabled", "clickstreamTrigger", "enableOTP", "loginHint", "showLogo", "topicTag", "entryPoint"]);

    var proTypesOptions = getProTypes(proTypes);
    var updatedProSelectProps = Object.assign({}, INPUT_TYPES.proSelect.props, proTypesOptions ? {
      options: proTypesOptions
    } : {});
    var updatedInputConfigs = Object.assign({}, INPUT_TYPES, {
      proSelect: Object.assign({}, INPUT_TYPES.proSelect, {
        props: updatedProSelectProps
      }),
      newPassword: PASSWORD_INPUT_CONFIG,
      email: Object.assign({}, INPUT_TYPES.email, {
        defaultValue: targetEmail
      }),
      newEmail: Object.assign({}, INPUT_TYPES.newEmail, {
        defaultValue: targetEmail
      })
    }, inputConfigs);
    var facebookAppIdFromDOM;

    if (!facebookAppId) {
      try {
        facebookAppIdFromDOM = document.querySelector('meta[property="fb:app_id"]').content;
      } catch (e) {// No id in DOM
      }
    }

    _this.isLightbox = isLightbox;
    Router = isLightbox || isNativeApp ? MemoryRouter : BrowserRouter;
    _this.store = createStore({
      appConfig: Object.assign({
        isLightbox: isLightbox,
        facebookAppId: facebookAppId || facebookAppIdFromDOM || FACEBOOK_APP_ID,
        googleClientId: googleClientId,
        fromEmail: checkIsTruthyBoolOrString(fromEmail),
        inputConfigs: updatedInputConfigs,
        showEmailOptIn: checkIsTruthyBoolOrString(isShowEmailOptIn),
        alwaysPro: checkIsTruthyBoolOrString(isAlwaysPro),
        hideConfirmPassword: checkIsTruthyBoolOrString(hideConfirmPassword),
        showGoogleOneTapOnload: isNewSession() && !isOneTapOnLoadBreak(),
        signInWithAppleEnabled: checkIsTruthyBoolOrString(signInWithAppleEnabled),
        emailValidationEnabled: checkIsTruthyBoolOrString(emailValidationEnabled),
        is2faOn: checkIsTruthyBoolOrString(is2faOn),
        clientId: clientId,
        views: CUSTOM_CONFIGS[clientId] !== undefined && CUSTOM_CONFIGS[clientId].views ? CUSTOM_CONFIGS[clientId].views : views,
        disableGoogleOneTap: CUSTOM_CONFIGS[clientId] !== undefined && typeof CUSTOM_CONFIGS[clientId].disableGoogleOneTap === 'boolean' ? CUSTOM_CONFIGS[clientId].disableGoogleOneTap : disableGoogleOneTap,
        isActivationExperimentsFeatureEnabled: checkIsTruthyBoolOrString(isActivationExperimentsFeatureEnabled),
        isActivationNewLaneMetricsEnabled: checkIsTruthyBoolOrString(isActivationNewLaneMetricsEnabled),
        isActivationGAMetricsEnabled: checkIsTruthyBoolOrString(isActivationGAMetricsEnabled),
        clickstreamTrigger: clickstreamTrigger !== null && clickstreamTrigger !== void 0 ? clickstreamTrigger : {},
        enableOTP: enableOTP !== null && enableOTP !== void 0 ? enableOTP : false,
        loginHint: loginHint !== null && loginHint !== void 0 ? loginHint : '',
        showLogo: showLogo !== null && showLogo !== void 0 ? showLogo : true,
        topicTags: topicTag ? [topicTag] : [],
        entryPoint: entryPoint
      }, rest),
      clickstream: {
        clickstreamTrigger: clickstreamTrigger !== null && clickstreamTrigger !== void 0 ? clickstreamTrigger : {},
        inputSelector: {
          inputSelectorKeyboard: false,
          inputSelectorMouse: false
        }
      }
    }, {
      inputs: createInputsReducer(updatedInputConfigs)
    }); // set or refresh session expiration

    setNewSessionExpiration();
    /**
     * Should only need to dispatch redirectUrl/headerText
     * if passed to <App/> directly. These values are
     * reset on clicks to topnav/upsell and therefore only
     * serve a purpose on initial load (for non native apps
     * at least).
     */
    // dispatch redirect URL to user session store

    if (redirectUrl) {
      setRegLoginRedirectUrl(redirectUrl);
    } // dispatch headerText/subHeaderText to user session store


    if (headerText || subHeaderText || globalFormSubHeader || typeof showProRegCheckbox === 'boolean' || clientId) {
      var showProRegHelper = function showProRegHelper(pr, cid) {
        if (CUSTOM_CONFIGS[cid] !== undefined && typeof CUSTOM_CONFIGS[cid].showProRegCheckbox === 'boolean') {
          return {
            showProRegCheckbox: CUSTOM_CONFIGS[cid].showProRegCheckbox
          };
        }

        if (typeof pr === 'boolean') {
          return {
            showProRegCheckbox: pr
          };
        }

        return {};
      };

      var uiConfig = Object.assign({}, headerText ? {
        headerText: headerText
      } : {}, subHeaderText ? {
        subHeaderText: subHeaderText
      } : {}, globalFormSubHeader ? {
        globalFormSubHeader: globalFormSubHeader
      } : {}, showProRegHelper(showProRegCheckbox, clientId));
      setRegLoginUiConfig(uiConfig);
    }

    return _this;
  }

  var _proto = App.prototype;

  _proto.componentWillMount = function componentWillMount() {
    profileIntervalBegin(PROFILER_APP_RENDER_EVENT);
    return true;
  };

  _proto.componentDidMount = function componentDidMount() {
    this.removeAppRedirectEventListener = listen(APP_REDIRECT, addDeprecatedListeners({
      isLightbox: this.isLightbox
    }));
    listen('auth:hide_lightbox', hideRegLoginLightbox);
    trigger(APP_LOAD); // Load Google Analytics if not already on page.
    // Not a promise, therefore no callback option available.
    // Initializing in componentWillMount provides ample time
    // for script to load.

    if (typeof document !== 'undefined' && !isInitialized()) {
      requestUAScript();
    }

    profileIntervalEnd(PROFILER_APP_RENDER_EVENT);
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    // remove global subscriber when App unmounts
    this.store.userSessionStoreUnsubscribe();

    if (typeof this.removeAppRedirectEventListener === 'function') {
      this.removeAppRedirectEventListener();
    }
  }
  /**
   * Grabs values from props and checks if the ZMOB_native RegLogin should be used.
   */
  ;

  _proto.useNativeRegLogin = function useNativeRegLogin() {
    var _this$props = this.props,
        nativeAppRegistrationReason = _this$props.nativeAppRegistrationReason,
        nativeAppGaLabel = _this$props.nativeAppGaLabel,
        history = _this$props.history;
    return launchNativeRegLogin(nativeAppRegistrationReason, nativeAppGaLabel, history);
  };

  _proto.render = function render() {
    return /*#__PURE__*/React__default.createElement(Provider, {
      store: this.store
    }, /*#__PURE__*/React__default.createElement(Router, null, /*#__PURE__*/React__default.createElement(CoreLayout$1, this.props)));
  };

  return App;
}(Component);

App.propTypes = process.env.NODE_ENV !== "production" ? {
  authToken: PropTypes.string,

  /**
   * Tells the Facebook sign in API who we are
   */
  facebookAppId: PropTypes.string,
  googleClientId: PropTypes.string,
  fromEmail: PropTypes.string,

  /**
   * Input configs that might be altered subject to AB tests or other external factors.
   */
  inputConfigs: PropTypes.object,

  /**
   * Determines if the app should be rendered in a lightbox.
   */
  isLightbox: PropTypes.string,
  isLoggedIn: PropTypes.bool,
  isShowEmailOptIn: PropTypes.string,

  /**
   * Whether or not to check the pro checkbox on Reg form
   */
  isAlwaysPro: PropTypes.string,

  /**
   * Whether to hide confirm password input
   */
  hideConfirmPassword: PropTypes.string,

  /**
   * A stringified object of professionals to show
   */
  proTypes: PropTypes.string,
  redirectUrl: PropTypes.string,
  disableSubmitOnValidate: PropTypes.bool.isRequired,
  headerText: PropTypes.string,
  history: PropTypes.object,
  subHeaderText: PropTypes.string,
  globalFormSubHeader: PropTypes.string,
  targetEmail: PropTypes.string,
  isNativeApp: PropTypes.bool,
  nativeAppRegistrationReason: PropTypes.string,
  nativeAppGaLabel: PropTypes.string,
  views: appSubView,
  disableGoogleOneTap: PropTypes.bool,
  passwordless: PropTypes.bool,

  /**
   * Sets whether or not the "Professional" checkbox should be shown on registration. Defaults
   * to `true`.
   */
  showProRegCheckbox: PropTypes.bool,
  signInWithAppleEnabled: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  is2faOn: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  clientId: PropTypes.string,
  emailValidationEnabled: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  isActivationExperimentsFeatureEnabled: PropTypes.bool,
  isActivationGAMetricsEnabled: PropTypes.bool,
  isActivationNewLaneMetricsEnabled: PropTypes.bool,
  verifyOnRegisterTreatment: PropTypes.string,

  /**
   * reg-login consumer clickstream trigger data
   */
  clickstreamTrigger: PropTypes.shape({
    // if no value, a util function (`getTriggerLocation`) based on the URL will be used
    triggerLocation: PropTypes.string,
    // for interaction events
    triggerObject: PropTypes.string,
    // for impression/view events
    triggerSource: PropTypes.string,
    // for the triggers that are not in reg-login (e.g. save search button)
    // no value means the consumer won't be able to determine the input selector or the
    // trigger is in reg-login (e.g. sign in button)
    inputSelector: PropTypes.string
  }),
  enableOTP: PropTypes.bool,
  loginHint: PropTypes.string,
  showLogo: PropTypes.bool,

  /**
   * @deprecated - use entryPoint instead
   */
  topicTag: PropTypes.string,

  /**
   * Clickstream identifier for each experience
   * This is required for us to track clickstream events
   */
  entryPoint: PropTypes.string.isRequired
} : {};
App.defaultProps = {
  history: {},
  inputConfigs: {},
  isNativeApp: false,
  nativeAppRegistrationReason: '',
  nativeAppGaLabel: '',
  views: [LOGIN, REGISTER, SOCIAL, PASSWORDLESS],
  showProRegCheckbox: null,
  emailValidationEnabled: false,
  disableGoogleOneTap: false,
  targetEmail: '',
  isActivationExperimentsFeatureEnabled: false,
  isActivationGAMetricsEnabled: false,
  isActivationNewLaneMetricsEnabled: false,
  verifyOnRegisterTreatment: AB_AUTH_VERIFY_ON_REGISTER.CONTROL
};

/* eslint-disable zillow/import/prefer-default-export */

/**
 * Create a url Object from a string
 * @param {String} url - an absolute url string
 * @return {Object}
 */
function getUrlParts(url) {
  if (!url) {
    return {};
  }

  var a = document.createElement('a');
  a.href = url;
  return {
    href: a.href,
    host: a.host,
    hostname: a.hostname,
    port: a.port,
    pathname: a.pathname,
    protocol: a.protocol,
    hash: a.hash,
    search: a.search
  };
}

/**
 * Get google id from the DOM if it exists
 * @returns {String}
 */
function getGoogleClientId() {
  var googleTag;

  if (typeof document !== 'undefined') {
    googleTag = document.querySelector('meta[name="google-signin-clientid"]');
  }

  return googleTag && googleTag.content;
}

var RegLoginApp = function RegLoginApp(props) {
  /**
   * If this is standalone page and no redirectUrl is set either explicitly or in
   * localStorage we need to ensure that this redirects to home page
   */
  if (props.standAlone && !APP_CONFIG.redirectUrl) {
    // Grab url from url parameter or in localStorage potentially
    // Make sure we are just getting the relative path for security
    var redirectUrl = getUrlParts(queryString.parse(window.location.search).url || getRedirectUrl(true)).pathname;
    APP_CONFIG.redirectUrl = redirectUrl || DEFAULT_REDIRECT_URL;
  }

  if (APP_CONFIG.saveRedirectUrl === 'true') {
    setRedirectUrl(APP_CONFIG.redirectUrl || document.referrer);
  }

  createUserSessionStore(); // Mismatch of property name googleAppId.   If still present just assign it to the proper
  // googleClientId property
  // TODO: remove googleAppId from zillow-web and add googleClientId ACT-3699

  if (APP_CONFIG.googleAppId && !APP_CONFIG.googleClientId) {
    APP_CONFIG.googleClientId = APP_CONFIG.googleAppId;
  }

  if (!APP_CONFIG.googleClientId) {
    APP_CONFIG.googleClientId = getGoogleClientId() || GOOGLE_CLIENT_ID;
  }

  return /*#__PURE__*/React__default.createElement(ThemeProvider, {
    theme: props.useVisualRefreshTheme ? ThemeSkunkWorks : ThemeConstellation
  }, /*#__PURE__*/React__default.createElement(App, _extends({
    disableSubmitOnValidate: true,
    isNativeApp: window.inBraavosPreviewer
  }, APP_CONFIG, props)));
};

RegLoginApp.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Flags the reg-login app is on a standalone page. As opposed to being in a lightbox.
   */
  standAlone: PropTypes.bool,

  /**
   * Flag to use the ThemeSkunkWorks Constellation theme
   */
  useVisualRefreshTheme: PropTypes.bool
} : {};
RegLoginApp.defaultProps = {
  standAlone: false,
  useVisualRefreshTheme: false
};

export default RegLoginApp;
